// Authoritative, deterministic, renderer-agnostic simulation. IP-NEUTRAL.
// Coordinates: X/Z ground plane, Y altitude.
import {
  BOONS, BoonId, ENEMIES, EnemyDef, EnemyKind, MAX_PASSIVE_LEVEL, MAX_PASSIVES, MAX_WEAPONS, INNATE, MAX_WEAPON_LEVEL,
  PASSIVES, PassiveId, SEALS_REQUIRED, WEAPONS, WeaponId, WorkshopId, WorkshopRanks, xpForLevel,
} from './content';
import { Grid, Rng } from './util';
import { GEM_MERGE_AT, GEM_MERGE_EVERY, mergeXpGems, xpKindFor } from './gems';
import { PU } from './powerups'; // POWER UPS (Array Workshop) numbers, shared with the shop text
import { PROP_SRC, Terrain, TerrainEvent } from './terrain';
import { FireGrid } from './fire';
// SKILL FUSIONS (owner 2026-11-02): two rank-5 skills merge into one (src/core/fusions.ts)
import { FusionField, FUSIONS, evoMask, isFusionId, type FusionEvent, type FusionId } from './fusions';
import { SURGE_FORMS } from './surgeForms'; // SURGE FORMS (owner 2026-11-03): per-form presentation colours
// MAP SYSTEM (owner 2026-11-02): per-map layout, hazards, time of day + night pressure, boss (src/maps/<id>/core.ts)
import { MapCoreDef, nightness, nightPressure, todClock, TodChoice } from './mapdef';
import { HazardEvent, HazardField, HAZARD_BOSS_BURN, HAZARD_BOSS_CRUSH, HAZARD_ENEMY_BURN, type HazardZone } from './hazards';
import { mapById } from '../maps/registry';
import { MAX_CRATER_R } from './terrain';
// run pace + run modifiers (owner 2026-11-02): src/core/mods.ts
import { RunMods, paceFor, SWARM_TARGET, SWARM_REFILL, SWARM_WAVE, SWARM_RING, SWARM_WAVE_MAX, SWARM_RING_MAX, TOUGH_HP, TOUGH_DMG, RELAXED_HP, RELAXED_DMG, RELAXED_XP, RELAXED_SALVE_ELITE, RELAXED_SALVE_GAP, RELAXED_SALVE_CHANCE, HURRY_K, EARLY_ELITE_FROM, EARLY_ELITE_GAP, ELITE_FROM, applyModBonus, type ChallengeRules, ruleSlots, ruleStartSkills, NOHEAL_TRAITS } from './mods';
// MAP PICKUPS (owner 2026-11-02): relic caches, elite / boss chests, field items (own seeded stream)
import { FieldLoot, PICKUP_INSET, type LootEvent } from './pickupsField';
import { APEX_ARMOR, APEX_BOONS, APEX_DELAY, APEX_DMG, APEX_HP, APEX_STILL, ApexId, ELITE_PACK_FIRST, ELITE_PACK_TIER, elitePackGap, elitePackSize, endlessMilestoneAt, endlessTierName, endlessTierShards, TWIN_GUARDIAN_TIER } from './endless'; // ENDLESS GOALS
// TRAINING (owner 2026-11-02): sandbox mode — tweaks, dummies, armory, loadout API (src/core/training.ts). Null in normal runs.
import { HERALD_BASE_K, HERALD_DPS_SECONDS, HERALD_FIRST, HERALD_GAP, HERALD_HIT_CAP, HERALD_MAX_ALIVE, HERALD_RELAXED, HERALD_SCALE, HERALD_SHARDS, HERALD_SLAM, HERALD_SLAM_CD, HERALD_SLAM_R, HERALD_SLAM_RANGE, HERALD_SLAM_TELL, HERALD_SPAWN_R, HERALD_TOUCH, HERALD_TOUCH_CD, HERALD_WINDOW, heraldDmgK, heraldSpdK } from './herald'; // THE NULL HERALD
import { DEFAULT_TWEAKS, Training, type TrainingOptions, type Tweaks } from './training';
import { LAVA_HIT_COLOR, MapFoeField, type MapFoeEvent } from './mapFoes'; // MAP FOES (owner 2026-11-02: 'more enemy variety')
// BOSS FORMS (owner 2026-11-02): every boss transforms twice mid-fight (data + brain in ./bossForms)
import { bossBrain, bossOverlay, BossBody, BossEvent, BossHost, BossMark, BossState, FORM_CRATER_R, FORM_GATES, FORM_HP_MUL, formOf, gateDamage, newBossState, SHOCK_PUSH, SHOCK_R, SHOCK_UP, SHOVE_DIST, SHOVE_R, SHOVE_T, startTransform, transformStep, updateMarks } from './bossForms';
import {
  DT, BEAM_LEN, BEAM_W, BEAM_CD, BEAM_CHARGE, BEAM_DURATION, BEAM_TICK, MORTAR_SPEED, MORTAR_RANGE, MORTAR_BOMBLETS, MORTAR_BOMBLET_DMG, MORTAR_BOMBLET_R,
  RIFT_OFFSET, RIFT_SPEED, RIFT_WAVE_GAP, RIFT_WAVE2_DMG, SURGE_TIERS, TIER_DMG, TIER_SPEED, TIER_CD, TIER_ARMOR, SURGE_STAGE, SURGE_STAGE_END, SURGE_TIME, SURGE_COOLDOWN, RESONANCE_OVERFLOW, surgeOverflow, TIER_REGEN, AUTO_SURGE_PATIENCE, type SurgeProgress,
  BOLT_SPEED, BOLT_RANGE, BOLT_GAP, cometBlastR, COMET_BLAST_DMG, BOLT_SPLASH_N, ORBIT_REHIT, ARC_RANGE, ARC_JUMP, ARC_SKY_PITY, NOVA_EVO_HEAL, DISC_EVO_HEAL,
  SING_COLLAPSE, SING_TICK, SING_START, SING_REBOUND, SING_HOLD_PULL, SING_HOLD_DMG, SING_IMPLODE_R, SING_CRATER_R, SING_CRATER_STEP, BORE_SPLASH_R, BORE_SPLASH_DMG,
  ZENITH_SPEED, ZENITH_SPLITS, ZENITH_SPLIT_DMG, ZENITH_SPLIT_R, SKYFALL_FLIGHT, skyfallLaunch, SKYFALL_RANGE, GLARE_BOSS, GLARE_ELITE, FROZEN_DMG_MUL,
  SKY_KIND, SHATTER_N, SHATTER_REACH, SHATTER_FLIGHT, SHATTER_STAGGER, SHATTER_DMG, SHATTER_R, STAR_CRITS, STAR_GAP, STAR_FLIGHT, STAR_RANGE, STAR_DENSE_R, STAR_DMG, STAR_R, STAR_SPLINTERS, STAR_SPLINTER_DMG, STAR_CRATER, // Shardstorm (owner 2026-11-03)
  RUSH_GAP, RUSH_HALF_DEG, RUSH_TRIGGER, THORNS_GAP, PASSIVE_K, BASE_CRIT, BASE_CRIT_MUL, BASE_MAGNET, BASE_MOVE, LASTSTAND_AT,
  boltNums, boltPierce, orbitNums, PASSIVE_K as PK2, HH_BIG, HH_HEAVY, HH_ELITE, HH_BOSS, HH_REARM, EXECUTE_AT, AFTERSHOCK_DELAY, AFTERSHOCK_R, SCORCH_GAP, CHAIN_MAX_PENDING, AFTERSHOCK_MAX_PENDING, novaNums, novaBurst, NOVA_BURST_DELAY, NOVA_BURST_KNOCK, NOVA_BURST_LIFT, NOVA_BURST_COLOR, arcNums, lanceNums, discNums, DISC_BULGE, DISC_PATH_K, mortarNums, zenithNums, breakerNums, boreNums, riftNums,
  skyfallNums, mirageNums, glareNums, rushNums, wardNums, fingerNums, FINGER_FAN, FINGER_KNOCK, FINGER_RETARGET, FINGER_VOLLEY_SPREAD,
  barrageNums, BARRAGE_SHOT_R, BARRAGE_SPREAD, BARRAGE_REAIM, BARRAGE_MUZZLE, BARRAGE_HAND, // Flare Barrage (owner 2026-11-02)
  gazeNums, GAZE_WIND, GAZE_PASS, GAZE_NEAR, GAZE_BURN_TICK, GAZE_BURN_DMG, GAZE_BURN_W, // Twin Gaze (owner 2026-11-02)
  splitNums, SPLIT_RING, SPLIT_SPIN, SPLIT_SHOT_R, // Split Self (owner 2026-11-02)
  crescentNums, CRESCENT_GAP, CRESCENT_THICK, CRESCENT_BOW, CRESCENT_KNOCK, CRESCENT_HAND, // Crescent Edge (owner 2026-11-02)
  surgeDurationFor, surgeDmgMul, surgeKiGain, surgeMaxTier,
  // Skills B (owner 2026-11-02): Snare Constellation, Wayward Comet, Binding Rings, Gravity Grip
  snareNums, SNARE_FLY, SNARE_PATH_W, SNARE_RANGE, SNARE_SUCK, SNARE_ECHO, SNARE_ECHO_DMG, cometNums, COMET_R, COMET_SHARD_R, COMET_RANGE, COMET_LIFE, COMET_SPLIT_DMG, COMET_SPLIT_HITS, COMET_HOP,
  ringsNums, RINGS_RANGE, RINGS_BOSS, RINGS_ELITE, RINGS_BLAST_DMG, RINGS_BLAST_PIN, gripNums, GRIP_RANGE, GRIP_LIFT, GRIP_FALL, GRIP_GATHER, GRIP_SHOCK,
} from './skillStats';
// Fire family (owner 2026-11-02): Fire Breath, Flame Ring, the shared burn status, Kindling, Ember Heart
import { eraAt, eraName, eraKindRoll, eraSpeedMul, eraCdMul, eraDmgMul, eraKnockMul, eraSizeMul, eraWeakMul } from './eras';
import { lateKnockMul, breathNums, BREATH_TICK, BREATH_REAIM, BREATH_TURN, BREATH_POP_DMG, BREATH_POP_MAX, BREATH_POP_DELAY, WYRM_DMG, flameRingNums, RING_BAND, RING_GAP, RING2_DMG, RING_LINGER_W, RING_LINGER_TICK, BARRAGE_BURN_K, BARRAGE_BURN_T, SCORCH_AFTERBURN } from './skillStats';
// Elemental families (owner 2026-11-03): frost (chill / freeze) and storm (Static Charge arcs, charged foes)
import { CHILL_MAX, CHILL_THAW_K, CHILL_THAW_T, CHILL_ZONE_TAIL, CHILL_BOSS, CHILL_ELITE, FROST_MAX_PENDING, FROST_DELAY, STATIC_CD, STATIC_TICK_CAP, condBounces, LIGHTNING_SRC } from './skillStats';
// ICE FAMILY (owner 2026-11-03): Rime Breath, Hoarfrost Spikes, Glacier Lance, Blizzard Veil (every slow = Sim.chill, every freeze = Sim.freeze)
import { rimeNums, RIME_TICK, spikesNums, SPIKES_REACH, SPIKES_GAP, SPIKES_RING2_R, SPIKES_RING2_DMG, SPIKES_MARCH, glacierNums, GLACIER_TRAIL_GAP, GLACIER_TURN, RIDGE_FREEZE, RIDGE_SHATTER, RIDGE_TICK,
  blizzardNums, BLIZZARD_CHILL_GAP, BLIZZARD_TICK, BLIZZARD_CHILL_T, BLIZZARD_RING_BAND, ICE_BOSS, ICE_ELITE } from './skillStats';
import { BURN_COLOR, BURN_GEN_MAX, BURN_SPREAD_CAP, BURN_SPREAD_DUR, BURN_SPREAD_GAP, BURN_SPREAD_K, BURN_SPREAD_N, BURN_TICK, BURN_TOUCH, inCone, mergeBurn } from './burn';
import { // Phase Rend / Comet Dive / Force Shout (owner 2026-11-02: 'Make them all')
  phaseNums, PHASE_RANGE, PHASE_JUMP, PHASE_BLINK, PHASE_RETURN, PHASE_SLASH_W, PHASE_CUT_W, PHASE_ECHO_T, PHASE_ECHO_R, PHASE_ECHO_DMG,
  diveNums, DIVE_REACH, DIVE_SHOCK_T, DIVE_SHOCK_R, DIVE_SHOCK_DMG, DIVE_FISSURE_T, DIVE_FISSURE_DMG, DIVE_FISSURE_W, DIVE_FISSURE_FROM, DIVE_FISSURE_TO,
  shoutNums, SHOUT_LOFT, SHOUT_SPEED, SHOUT_ELITE, SHOUT_BOSS, SHOUT_ECHO_T, SHOUT_ECHO_DMG,
} from './skillStats';
// skill tuning lives in skillStats.ts (shared with the HUD cards); re-exported here for existing importers
export { DT, BEAM_LEN, BEAM_W, BEAM_CD, BEAM_CHARGE, BEAM_DURATION, MORTAR_SPEED, MORTAR_RANGE, RIFT_OFFSET, RIFT_SPEED, RIFT_WAVE_GAP,
  SURGE_TIERS, TIER_DMG, TIER_SPEED, TIER_CD, TIER_ARMOR, TIER_REGEN, SURGE_STAGE, SURGE_STAGE_END, SURGE_TIME, SURGE_COOLDOWN };
export const ARENA_R = 160; // owner 2026-11-02: larger map again (110 -> 160, ~2.1x the area; was 72 before 2026-11-01)
/** Storm Lattice fire (owner 2026-11-03): a foe struck again within ARC_FIRE_WINDOW s burns for ARC_FIRE_DUR s at ARC_FIRE_DPS × the per-jump damage per second */
export const ARC_FIRE_WINDOW = 1.5, ARC_FIRE_DUR = 3, ARC_FIRE_DPS = 0.1;
/** auto Nova Lance: a ready lance that finds no 5-foe line fires at its best line after this many seconds */
export const LANCE_WAIT = 1.5;
export const HOVER_Y = 1.6;
export const MAX_Y = 14;
// EDGE WALLS (owner 2026-11-02: 'no invisible barrier… gems stuck on the outside… make sure the enemies are inside the arena').
// The arena edge is now a physical wall drawn right at ARENA_R (render/edgeWall.ts); the hero stops against its face.
/** pickups (XP motes, heals, seals, chests, field items) always rest at least this far inside the wall, so they can be reached */
export const PICKUP_R = ARENA_R - PICKUP_INSET;
/** foes spawned past the wall appear here, just behind it, and walk in */
export const EDGE_SPAWN_R = ARENA_R + 3.5;
/** extra inward walking speed (m/s) for a foe that has not come through the wall yet */
export const EDGE_WALK_IN = 2.5;
/** a foe still outside after this many seconds (blocked, shoved, kited) is set down just inside the wall */
export const EDGE_STUCK_T = 5;
/** bosses and guardians spawn at least this far inside the wall */
export const EDGE_BOSS_IN = 8;
/** speed (m/s) at which a pickup that ended up past PICKUP_R slides back in */
export const PICKUP_DRIFT = 10;

export type FlightMode = 'assisted' | 'manual';
export type GameState = 'playing' | 'levelup' | 'boon' | 'dead' | 'victory';
export type AimMode = 'auto' | 'assist' | 'manual';
/** Gem kinds: 0-2 XP tiers, 3 Lumen Salve (heal), 4 Array Seal, 5 Sweep Beacon (collect all motes). */
export const GEM_SALVE = 3, GEM_SEAL = 4, GEM_SWEEP = 5;
/** gem pool size (owner 2026-11-03: 'the gems need to keep showing up, no matter how many end up on the map'): was 800; the
 *  render layer sizes its gem meshes from this. When it is full, the XP gem farthest from the hero folds into the new drop. */
export const GEM_POOL = 2000;
export { GEM_MERGE_AT, xpKindFor };

export interface InputFrame {
  moveX: number; moveZ: number;      // -1..1, world-relative
  aimX: number; aimZ: number;        // manual aim direction (unit or 0)
  manualAim: boolean;
  ascend: number;                    // -1..1 (manual flight)
  dash: boolean; beam: boolean; transform: boolean; // edge-triggered
}

export const emptyInput = (): InputFrame => ({
  moveX: 0, moveZ: 0, aimX: 0, aimZ: 0, manualAim: false, ascend: 0,
  dash: false, beam: false, transform: false,
});

/** kill event sources (ENEMY-DEATHS render hints; never feed back into gameplay) */
/** Heavy Hands: sources whose every hit counts as a slam / dive (big hit) */
const HH_SLAMS: ReadonlySet<string> = new Set(['dive', 'grip', 'secondwind']);
export const KILL_SRC = { generic: 0, explosion: 1, finger: 2, disc: 3, lance: 4, singularity: 5, contact: 6 } as const;
/** MAP-FIRE: chance that a radialDamage from this technique lights the grass (big blasts usually do; small pops rarely) */
/** owner 2026-11-02: 'the fire created on the map should have a 25% chance of occurring after an attack, so it's less frequent'
 *  — at most FIRE_ATTACK_CHANCE per attack. Single-blast skills roll it once; skills that land many blasts per attack (Skyfall
 *  shards, Flare Barrage pops, the evolved Spirit Bolt's comets) roll a small share per blast so a whole volley stays ≈ 25 % */
export const FIRE_ATTACK_CHANCE = 0.25;
export const FIRE_IGNITE: Record<string, number> = { mortar: 0.25, zenith: 0.25, skyfall: 0.05, mirage: 0.25, bore: 0.25, bolt: 0.08, nova: 0.06, barrage: 0.025, boon: 0.25 };

export type SimEvent =
  | { t: 'hit'; x: number; y: number; z: number; dmg: number; crit: boolean; color: number; /** TRAINING: the target is a dummy */ dum?: boolean }
  /** kill: render hints for death animations (ENEMY-DEATHS): src = what killed it (KILL_SRC), r = blast radius (explosions),
   *  col = colour of the blast / energy that killed it (-1 = none: plain collapse), dx/dz = unit direction (killing hit's
   *  knockback / needle / disc travel, away from a blast centre, or away from the hero), vx/vz = velocity at death,
   *  s = body scale, lift = height airborne, boss = a boss / guardian */
  | { t: 'kill'; x: number; y: number; z: number; kind: EnemyKind; elite: boolean; src: number; r: number; col: number; dx: number; dz: number; vx: number; vz: number; s: number; lift: number; boss: boolean }
  /** sky = a Skyfall shard landing; splinter / star = a Shardstorm splinter / Fallen Star landing (own sounds and VFX) */
  | { t: 'explode'; x: number; y: number; z: number; r: number; color: number; sky?: boolean; lure?: boolean; splinter?: boolean; star?: boolean }
  | { t: 'nova'; x: number; y: number; z: number; r: number; evolved: boolean }
  /** Pulse Nova sphere explodes (owner 2026-11-02): reach r (m) around (x, z), strong radial shove */
  | { t: 'novaBurst'; x: number; y: number; z: number; r: number; evolved: boolean }
  /** Aftershock trait (owner 2026-11-02): a crater pulsed a shockwave of radius r */
  | { t: 'aftershock'; x: number; z: number; r: number }
  /** chain lightning path (x,y,z triples from the caster); k = 'static': a Static Charge trait arc (thin bolt, zap sound) */
  | { t: 'arc'; pts: number[]; k?: 'static' }
  /** Storm Lattice SKY STRIKE: a heavy bolt from above hit the foe at x,y,z, blasting radius r (hits = foes struck) */
  | { t: 'skyStrike'; x: number; y: number; z: number; r: number; hits: number }
  /** Frostbite trait (owner 2026-11-03): a chilled / frozen foe shattered at (x, z), burst radius r */
  | { t: 'frostburst'; x: number; z: number; r: number }
  /** w tags the technique (render picks the hero's gesture); dx/dz = unit fire direction when known */
  | { t: 'shoot'; x: number; y: number; z: number; w?: 'bolt' | 'breaker' | 'bore' | 'finger'; dx?: number; dz?: number }
  /** Finger Beam needle (near-instant, already resolved): start (x,y,z) at the fingertip, drawn end (ex,ey,ez) = full reach
   *  or just past the last foe when its pierce ran out; len = full reach; hits = pierced foes (x,y,z triples, near → far);
   *  shot = volley index (0 first), fan = needle index in a Scarlet Fan shot (0 = centre);
   *  last = the final shot of a Finger Beam volley (render: the big-moment colour wash at max rank / Scarlet Fan) */
  | { t: 'finger'; x: number; y: number; z: number; ex: number; ey: number; ez: number; len: number; hits: number[]; shot: number; fan: number; evolved: boolean; last?: boolean }
  /** Flare Barrage (owner 2026-11-02) shot leaving a hand: id = sim.barrageShots[].id, muzzle (x,y,z), unit direction (dx,dz),
   *  hand -1 left / +1 right, k = shot tick index in the volley */
  | { t: 'barrage'; id: number; x: number; y: number; z: number; dx: number; dz: number; hand: number; k: number; evolved: boolean }
  /** Flare Barrage mini fireball: where a shot burst (r = burst radius), n = foes in the burst, hit = it struck a foe (else
   *  it ran out of range) */
  | { t: 'barragePop'; id: number; x: number; y: number; z: number; r: number; n: number; hit: boolean; evolved: boolean }
  /** Twin Gaze (owner 2026-11-02): eye glint wind-up starts (beams follow GAZE_WIND s later); per-frame state: sim.gaze.
   *  gazePass = a sweep pass begins (k = index); gazeEnd = the beams switch off */
  | { t: 'gaze'; id: number; x: number; z: number; dx: number; dz: number; reach: number; half: number; passes: number; evolved: boolean }
  | { t: 'gazePass'; id: number; k: number } | { t: 'gazeEnd'; id: number }
  /** Split Self (owner 2026-11-02): copies summoned (n = copies alive after it, fresh = new ones); per-frame state: sim.splits.
   *  splitShot = a copy (copy = its id) fires a light bolt (id = sim.splitShots[].id); splitPop = that bolt is gone (hit = it
   *  struck a foe); splitEnd = a copy fades out at (x, z) */
  | { t: 'split'; n: number; fresh: number; evolved: boolean }
  | { t: 'splitShot'; id: number; copy: number; x: number; y: number; z: number; dx: number; dz: number; evolved: boolean }
  | { t: 'splitPop'; id: number; x: number; y: number; z: number; hit: boolean }
  | { t: 'splitEnd'; id: number; x: number; z: number }
  /** Crescent Edge (owner 2026-11-02): a wave leaves a hand (hand -1 left / +1 right, k = index in the throw); per-frame state:
   *  sim.crescents (by id). crescentEnd = it reached full range at (x, z) */
  | { t: 'crescent'; id: number; x: number; z: number; dx: number; dz: number; w: number; hand: number; k: number; evolved: boolean }
  | { t: 'crescentEnd'; id: number; x: number; z: number }
  | { t: 'boltHit'; x: number; y: number; z: number }
  | { t: 'enemyShoot'; x: number; y: number; z: number }
  | { t: 'pickup'; kind: number }
  | { t: 'levelup'; level: number }
  | { t: 'dash' }
  | { t: 'beamStart' }
  | { t: 'beamFire' }
  | { t: 'hurt'; dmg: number }
  /** SURGE FORMS: the hero transformed into form `tier` (1..SURGE_TIERS); look = surgeForm(tier) in src/core/surgeForms.ts */
  | { t: 'transform'; tier: number }
  | { t: 'transformEnd' }
  /** SURGE FORMS: a higher form just became reachable this run (now `tier` is the top unlocked form); HUD notice, no gameplay */
  | { t: 'surgeUnlock'; tier: number }
  | { t: 'land'; x: number; z: number; r: number }
  | { t: 'bossSpawn' }
  /** THE NULL HERALD (src/core/herald.ts): the n-th (0-based) one has arrived; its slam telegraph ('tell') and strike ('hit'); it fell */
  | { t: 'heraldSpawn'; n: number }
  | { t: 'heraldSlam'; x: number; z: number; r: number; phase: 'tell' | 'hit' }
  | { t: 'heraldDown' }
  /** MAP SYSTEM: a hazard zone changed phase (warn = telegraph starts, active / fire = it acts, end) */
  | HazardEvent
  /** a burn hazard is hurting the hero (throttled, ~2/s) */
  | { t: 'hazardHurt'; dmg: number }
  | { t: 'bossTele'; kind: 'charge' | 'ring' | 'summon' | 'fan' | 'spiral' | 'slam' | 'rain' }
  /** BOSS FORMS: transformation beat (start / burst / end), braced slams, impact marks (src/core/bossForms.ts) */
  | Exclude<BossEvent, { t: 'explode' } | { t: 'bossTele' }>
  | { t: 'elite' }
  | { t: 'swarm' }
  | { t: 'era'; era: number; name: string }
  | { t: 'victory' }
  | { t: 'guardianSpawn'; n: number; name: string }
  | { t: 'endless' }
  /** ENDLESS GOALS: a milestone past the final boss was reached (tier n, its name, the shard bonus banked, endless s it
   *  fell due). Hook for achievements / quests. */
  | { t: 'endlessTier'; tier: number; name: string; shards: number; at: number }
  /** ENDLESS GOALS: endless-only escalation (an elite pack of n, or a twin-guardian arrival) at the current tier */
  | { t: 'endlessEvent'; kind: 'elitePack' | 'twinGuardians'; tier: number; n: number }
  /** ENDLESS GOALS: an Apex Boon was taken at a milestone */
  | { t: 'apexBoon'; id: ApexId }
  | { t: 'wave'; n: number; name: string }
  | { t: 'death' }
  | { t: 'seal'; n: number }
  | { t: 'convergence' }
  | { t: 'boon'; id: BoonId }
  | { t: 'revive' }
  /** Second Wind trait (group C): a lethal blow was turned aside; `left` = saves still banked */
  | { t: 'secondWind'; x: number; z: number; r: number; left: number }
  | { t: 'sweep' }
  | { t: 'disc'; dx?: number; dz?: number }
  /** Rift Mortar: charge starts (render grows the sphere in the hands over dur seconds); size = sphere radius */
  | { t: 'mortarCharge'; dur: number; size: number }
  /** Rift Mortar: the charged sphere leaves the hands along (dx, dz) */
  | { t: 'mortar'; x: number; y: number; z: number; dx: number; dz: number; size: number }
  /** Rift Mortar sphere bursts (render: compact fiery blast); color = sim hit colour (follows Surge II/III) */
  | { t: 'mortarImpact'; x: number; z: number; r: number; evo: boolean; color: number }
  /** Skyfall volley of n shots left the hands (evolved = Shardstorm: its own cast sound) */
  | { t: 'skyfall'; n: number; evolved?: boolean } | { t: 'zenithCharge'; dur: number } | { t: 'zenithThrow' } | { t: 'zenithImpact'; x: number; z: number; r: number; comet: boolean }
  /** Shardstorm (owner 2026-11-03): a shard's landing crit shattered it into n splinters at (x, z); a Fallen Star was called
   *  onto (x, z) (blast radius r, lands in dur s). The star's landing is an 'explode' with star = true. */
  | { t: 'skyShatter'; x: number; z: number; n: number } | { t: 'starfall'; x: number; z: number; r: number; dur: number }
  | { t: 'riftStart' }
  /** Riftline wall erupted at (x, z) (its centre), rolling along (dx, dz) at speed for len metres; w = width across.
   *  back = Horizon Cutter rear wall; wave 2 = the rank 4+ follow-up. Per-frame position: sim.rift.walls (by id). */
  | { t: 'riftWall'; id: number; x: number; z: number; dx: number; dz: number; w: number; len: number; speed: number; evolved: boolean; back: boolean; wave: number }
  | { t: 'glare'; x: number; z: number; r: number; evolved: boolean }
  | { t: 'rush'; x: number; z: number; dx: number; dz: number; reach: number; full: boolean }
  | { t: 'overdrive'; cost: number }
  /** Twin Bore point-blank cone: origin, unit aim, reach, half-angle, pellet count, foes hit (x,z pairs, up to 12) */
  | { t: 'boreBlast'; x: number; z: number; dx: number; dz: number; r: number; half: number; n: number; hits: number[]; evolved: boolean }
  /** Prism Ward: dome formed (charges) / a hit absorbed (left) / the dome shattered (r = shatter radius) */
  | { t: 'wardUp'; charges: number } | { t: 'wardHit'; left: number } | { t: 'wardBreak'; x: number; z: number; r: number; evolved: boolean }
  | { t: 'lure'; x: number; z: number }
  /** Singularity launched (render: black orb + red electricity + ground vortex); per-frame state: sim.sings (by id) */
  | { t: 'sing'; id: number; x: number; z: number; dx: number; dz: number; r: number; evolved: boolean }
  /** Singularity shrank into nothing and imploded at (x, z); r = implosion radius; last = gone (evolved re-forms once first) */
  /** Collapse Point: the evolved orb has reached the end of its path and hangs there for `dur` s before collapsing */
  | { t: 'singHold'; id: number; x: number; z: number; dur: number }
  | { t: 'singCollapse'; id: number; x: number; z: number; r: number; evolved: boolean; last: boolean }
  | { t: 'stomp'; x: number; z: number; r: number }
  | { t: 'charge'; x: number; z: number }
  | { t: 'bomberPop'; x: number; z: number }
  | { t: 'dodge' }
  // ---- Phase Rend / Comet Dive / Force Shout (owner 2026-11-02). Per-frame state: sim.phaseRend / phaseCuts, sim.dive, sim.shoutWaves ----
  /** Phase Rend cast from the hero at (x, z) */
  | { t: 'phaseStart'; x: number; z: number; evolved: boolean }
  /** one blink: the body left (fx, fz) and arrived at (x, z) just past the foe at (tx, tz), slashing along unit (dx, dz) a
   *  line `len` long centred on that foe; hits = foes slashed; cut = id of the cut line it left (0 = none) */
  | { t: 'phaseBlink'; k: number; fx: number; fz: number; x: number; z: number; tx: number; tz: number; dx: number; dz: number; len: number; hits: number; cut: number; evolved: boolean }
  /** the body snapped back from (fx, fz) to the hero at (x, z) */
  | { t: 'phaseEnd'; fx: number; fz: number; x: number; z: number }
  /** Fractured Instant: an afterimage slashed again around (x, z) */
  | { t: 'phaseEcho'; x: number; z: number; r: number }
  /** Comet Dive: leaps from (x0, z0), lands at (x1, z1) after dur seconds */
  | { t: 'diveStart'; x0: number; z0: number; x1: number; z1: number; dur: number; evolved: boolean }
  /** Comet Dive crash at (x, z): shockwave radius r, crater radius cr, centre launch speed, foes hit */
  | { t: 'diveLand'; x: number; z: number; r: number; cr: number; launch: number; hits: number; evolved: boolean; /** SKILL FUSIONS: the landing of a fusion leap (Fault Drop / Roaring Meteor) */ fuse?: FusionId }
  /** Comet Dive aftershock ring (rank 4+) */
  | { t: 'diveShock'; x: number; z: number; r: number }
  /** Titan Drop fissure erupting along unit (dx, dz) from `from` to `to` metres out of (x, z) */
  | { t: 'diveFissure'; x: number; z: number; dx: number; dz: number; from: number; to: number }
  /** Force Shout wave leaving (x, z) along unit (dx, dz): cone half-angle, reach, front speed (m/s); echo = Sundering Roar's second wave */
  | { t: 'shout'; id: number; x: number; z: number; dx: number; dz: number; half: number; r: number; speed: number; echo: boolean; evolved: boolean; /** SKILL FUSIONS: the fusion whose roar ring this is (Siren Decoy / Roaring Meteor) */ src?: FusionId }
  // ---- Skills B (owner 2026-11-02) ----
  /** Snare Constellation cast: a ring of n orbs (radius r) around (x, z), orb k at angle a0 + k·2π/n; per frame: sim.snares (by id) */
  | { t: 'snare'; id: number; x: number; z: number; r: number; n: number; a0: number; evolved: boolean }
  /** the orbs leave the ring and slam inward, reaching the centre after fly s */
  | { t: 'snareSlam'; id: number; x: number; z: number; r: number; fly: number; evolved: boolean }
  /** the ring imploded at (x, z): br = implosion radius, n = foes caught; echo = Starcage's echo burst */
  | { t: 'snareCollapse'; id: number; x: number; z: number; br: number; n: number; echo: boolean; evolved: boolean }
  /** Wayward Comet thrown from (x, z) along (dx, dz) (id = sim.comets[].id); shard = a Splitstar shard */
  | { t: 'comet'; id: number; x: number; z: number; dx: number; dz: number; shard: boolean; evolved: boolean }
  /** the comet struck a foe at (x, y, z) and turned hard to (dx, dz) (0, 0 = nothing left to turn to); left = hits left */
  | { t: 'cometHit'; id: number; x: number; y: number; z: number; dx: number; dz: number; left: number; shard: boolean; evolved: boolean }
  /** the comet is spent (split = it burst into Splitstar shards) or fizzled out */
  | { t: 'cometEnd'; id: number; x: number; z: number; split: boolean; shard: boolean }
  /** Binding Rings cast around (x, z) (catch radius r): n rings snapped shut; per frame: sim.ringHolds */
  | { t: 'rings'; x: number; z: number; r: number; n: number; evolved: boolean }
  /** a ring shattered at (x, y, z) around a foe of body radius s: burst = held to the end (it hurt the foe), else its foe died
   *  first; blastR = Shackle Nova blast radius (0 = none) */
  | { t: 'ringBreak'; id: number; x: number; y: number; z: number; s: number; burst: boolean; blastR: number; evolved: boolean }
  /** Gravity Grip seized n foes around (x, z) (catch radius r) and starts lifting them h m; per frame: sim.grips, enemy.gripY */
  | { t: 'grip'; id: number; x: number; z: number; r: number; n: number; h: number; evolved: boolean }
  /** the slam at (x, z): n lifted foes hit the ground; shockR = radius of the ground shock */
  | { t: 'gripSlam'; id: number; x: number; z: number; r: number; shockR: number; n: number; evolved: boolean }
  // ---- Fire family (owner 2026-11-02). Per-frame state: sim.breath, sim.flameRings, sim.flameLingers, sim.wyrms; burning foes:
  // enemy.burnT > 0 ----
  /** Fire Breath gout starts from the hero at (x, z) along unit (dx, dz): reach, cone half-angle, sweep each side (rad), length (s) */
  | { t: 'breath'; id: number; x: number; z: number; dx: number; dz: number; range: number; half: number; sweep: number; dur: number; evolved: boolean }
  | { t: 'breathEnd'; id: number }
  /** Wyrmstorm: a fire wyrm leaves (x0, z0) and dives to (x1, z1), arriving after dur s; r = eruption radius */
  | { t: 'wyrm'; id: number; x0: number; z0: number; x1: number; z1: number; dur: number; r: number }
  | { t: 'wyrmBurst'; id: number; x: number; z: number; r: number }
  /** Fire Breath rank 5: a foe that died burning popped at (x, z) */
  | { t: 'firePop'; x: number; z: number; r: number }
  /** Flame Ring k (0 = first) erupts around (x, z) and rolls out to r at speed m/s; rings = rings in this cast */
  | { t: 'flameRing'; id: number; x: number; z: number; r: number; speed: number; k: number; rings: number; evolved: boolean }
  /** the burning circle left where the rings stopped: centre (x, z), radius r, band width w, lasting dur s */
  | { t: 'flameLinger'; id: number; x: number; z: number; r: number; w: number; dur: number; evolved: boolean }
  // ---- ICE FAMILY (owner 2026-11-03). Per-frame state: sim.rime, sim.frostFields, sim.iceLances, sim.iceRings, sim.iceRidges,
  // sim.blizzard; chilled foes: enemy.chillT > 0 (icy tint), frozen foes: enemy.stunT > 0 ----
  /** Rime Breath cone starts from the hero at (x, z) along unit (dx, dz): reach, half-angle, sweep each side (rad), length (s);
   *  fused = the Hailstorm cone (rides with the hero, no sweep; its direction stays (dx, dz)) */
  | { t: 'rime'; id: number; x: number; z: number; dx: number; dz: number; range: number; half: number; sweep: number; dur: number; evolved: boolean; fused?: boolean }
  | { t: 'rimeEnd'; id: number }
  /** a foe froze solid at (x, y, z), body radius s: the ice encasement pop (capped per tick) */
  | { t: 'iceFreeze'; x: number; y: number; z: number; s: number }
  /** Glacial Maw: the glacier that bursts at the end of the cone */
  | { t: 'glacierBurst'; x: number; z: number; r: number }
  /** Hoarfrost Spikes eruption k (0 = first) at (x, z), radius r, n foes thrown */
  | { t: 'spikes'; id: number; x: number; z: number; r: number; k: number; n: number; evolved: boolean }
  /** a frost field was laid (live: sim.frostFields by id); src = the skill / fusion that laid it */
  | { t: 'frostField'; id: number; x: number; z: number; r: number; dur: number; evolved: boolean; src: string }
  /** a Glacier Lance left the hand (live: sim.iceLances by id); iceLanceHit = it pierced a foe; iceLanceEnd = spent (shatter = rank 5 burst) */
  | { t: 'iceLance'; id: number; x: number; z: number; dx: number; dz: number; evolved: boolean }
  | { t: 'iceLanceHit'; id: number; x: number; y: number; z: number }
  | { t: 'iceLanceEnd'; id: number; x: number; z: number; shatter: boolean }
  /** Permafrost Spear: a ridge of ice stands across unit (dx, dz) centred at (x, z), len long, for dur s (live: sim.iceRidges) */
  | { t: 'iceRidge'; id: number; x: number; z: number; dx: number; dz: number; len: number; dur: number }
  | { t: 'iceRidgeEnd'; id: number; x: number; z: number; len: number }
  /** Blizzard Veil flash-freeze at (x, z) radius r (the veil itself is per-frame state, sim.blizzard) */
  | { t: 'blizzardPulse'; x: number; z: number; r: number; evolved: boolean }
  /** Eye of Winter avalanche ring rolling out from (x, z) to r at speed m/s (live: sim.iceRings by id) */
  | { t: 'iceRing'; id: number; x: number; z: number; r: number; speed: number }
  | TerrainEvent
  | LootEvent
  | FusionEvent // SKILL FUSIONS
  /** MAP FOES: render / audio cues of the map-only foes (src/core/mapFoes.ts) */
  | MapFoeEvent;

/** Fire Breath gout in progress (render reads; never writes). (ax, az) = where it is aimed, (dx, dz) = the flame's direction
 *  this tick with the sweep added, (pdx, pdz) = last tick's (interpolation); t counts up to dur. */
export interface BreathState { active: boolean; id: number; t: number; dur: number; ax: number; az: number; dx: number; dz: number; pdx: number; pdz: number;
  tx: number; tz: number; range: number; half: number; sweep: number; lvl: number; evo: boolean; ign: boolean; hits: number }
export interface FlameRingWave { alive: boolean; id: number; x: number; z: number; r: number; d: number; speed: number; dmg: number; burnDps: number; burnDur: number;
  delay: number; k: number; rings: number; linger: number; ign: boolean; evo: boolean; hit: Set<number> }
export interface FlameLinger { alive: boolean; id: number; x: number; z: number; r: number; w: number; t: number; life: number; burnDps: number; burnDur: number; evo: boolean }
export interface Wyrm { alive: boolean; id: number; x0: number; z0: number; x1: number; z1: number; t: number; dur: number; r: number; dmg: number; burnDps: number; burnDur: number; ign: boolean }
export const BREATH_HIT_COLOR = 0xff8c2a, RING_HIT_COLOR = 0xff7024, FIRE_POP_COLOR = 0xff9a30, WYRM_COLOR = 0xffb050;
/** Elemental families (owner 2026-11-03): hit colours of a Frostbite shatter and a Static Charge arc */
export const FROST_COLOR = 0xbfefff, STATIC_COLOR = 0xdff4ff;
// ---- ICE FAMILY (owner 2026-11-03): live state the render reads (never writes) ----
/** Rime Breath cone in progress: same shape as BreathState (aim, sweep, timing) */
export type RimeState = BreathState;
/** a frost field on the ground: foes on it are chilled (k) and take dps cold damage; src = the skill / fusion credited */
export interface FrostField { alive: boolean; id: number; x: number; z: number; r: number; t: number; life: number; k: number; dps: number; src: string; evo: boolean }
/** a Glacier Lance in flight: unit (dx, dz), distance flown d of range; pierce left; trailD = distance since the last frost drop */
export interface IceLance { alive: boolean; id: number; x: number; z: number; dx: number; dz: number; speed: number; r: number; dmg: number; pierce: number; d: number; range: number; trailD: number; chillK: number; chillT: number; freeze: number; lvl: number; evo: boolean; hit: Set<number> }
/** an avalanche ring (Eye of Winter): front at d of r, rolling at speed m/s */
export interface IceRing { alive: boolean; id: number; x: number; z: number; r: number; d: number; speed: number; dmg: number; chillK: number; chillT: number; hit: Set<number> }
/** a ridge of ice (Permafrost Spear): a wall across unit (dx, dz) centred at (x, z), len long, standing for life s */
export interface IceRidge { alive: boolean; id: number; x: number; z: number; dx: number; dz: number; len: number; t: number; life: number; dmg: number; hit: Set<number> }
/** Blizzard Veil (always on while held): radius, evolved, seconds since it rose (render spin-up), seconds to the next freeze */
export interface BlizzardState { on: boolean; r: number; evo: boolean; t: number; next: number }
export const RIME_HIT_COLOR = 0xcdf4ff, SPIKES_HIT_COLOR = 0x9fe0ff, GLACIER_HIT_COLOR = 0xe4fbff, BLIZZARD_HIT_COLOR = 0xb4ecff, ICE_FIELD_COLOR = 0x8fd8ff;
/** freeze cues emitted per tick at most (a crowd freezing at once still pops a few crystals, not hundreds) */
export const ICE_FREEZE_CUES = 10;
/** ticks between a frost field's chill / cold checks (staggered by field id) */
export const ICE_FIELD_TICK = 10;

export interface Enemy {
  alive: boolean; id: number; def: EnemyDef; elite: boolean;
  /** 1 = a HORDE ELITE (late-run elites mixed into the normal stream): elite size / health class but lighter, no special drops */
  lite: number;
  /** ENEMY ERA this foe was born in (src/core/eras.ts); bosses, guardians and the herald stay 0 */
  era: number;
  x: number; y: number; z: number; vx: number; vz: number; kx: number; kz: number;
  /** knock-up physics: height above the hover plane, vertical speed, seconds airborne (render tumbles by it) */
  lift: number; ky: number; airT: number;
  hp: number; maxHp: number; flash: number; phase: number;
  atkCd: number; orbCd: number; scale: number;
  /** seconds frozen by Glare Burst */ stunT: number;
  /** Heavy Hands stagger (group C, owner 2026-11-03): > 0 = seconds staggered (no move/attack, no frozen damage bonus);
   *  < 0 = re-arm time before it can be staggered again; 0 = ready */ stagT: number;
  /** Skills B: seconds pinned by Binding Rings (rooted: can't move or attack); Gravity Grip cast id holding it (0 = none) and
   *  the height it is held above its plane (render draws it there) */
  pinT: number; gripId: number; gripY: number;
  /** BURN (owner 2026-11-02, src/core/burn.ts): seconds left, damage per second, source skill id (damage credit), spread
   *  generation, damage banked since the last burn tick */
  burnT: number; burnDps: number; burnSrc: string; burnGen: number; burnAcc: number;
  /** ELEMENTAL FAMILIES (owner 2026-11-03). CHILL: seconds left and strength (fraction of speed removed; render tints the foe
   *  icy). CHARGED (Conductor): seconds left (lightning hurts it more). zapCd: seconds before this foe can spark another
   *  Static Charge arc. */
  chillT: number; chillK: number; chargeT: number; zapCd: number;
  // boss
  mode: number; modeT: number; dirX: number; dirZ: number;
  /** BOSS FORMS: current form 0..2 (-1 = no forms: every regular foe), seconds left in a transformation beat (0 = none;
   *  the boss cannot be hurt while > 0) */
  form: number; formT: number;
  discCd: number;
  /** EDGE WALLS: seconds spent outside the wall since spawning (walking in), -1 once it has come inside. While >= 0 the foe
   *  can't be hit, targeted or killed and can't hurt the hero; once inside it never leaves again (knockback stops at the wall). */
  edgeT: number;
  /** TRAINING: a training dummy (DUMMY_TYPE_CODE; 0 = a real foe). Dummies never move by AI, attack, die, drop or count. */
  dummy: number;
}

export interface Projectile {
  alive: boolean; x: number; y: number; z: number; vx: number; vy: number; vz: number;
  dmg: number; r: number; life: number; pierce: number; comet: boolean; target: number;
  lastHit: number; hostile: boolean;
  /** 0 bolt, 1 comet, 2 disc, 3 cluster bomblet shell (comet=evolved, pierce=1 bomblet), 4 zenith orb, 5 (retired Breaker Shot), 6 bore shell, 7 Rift Mortar sphere (out = sphere radius) */ kind: number; out: number; ret: boolean;
  /** MAP-DESTRUCTION: props this projectile already damaged (each prop once per projectile / disc pass; never blocks it) */
  ph?: number[];
  /** Scythe Disc boomerang (owner 2026-11-02): throw origin, unit throw direction, path length out (m), side bulge (m, signed),
   *  flight time (s) and time flown; see updateDisc */
  bx?: number; bz?: number; bdx?: number; bdz?: number; bL?: number; bW?: number; bT?: number; bt?: number;
}

export interface Gem { alive: boolean; x: number; y: number; z: number; value: number; kind: number; pull: boolean }

export interface Player {
  x: number; y: number; z: number; px: number; py: number; pz: number;
  vx: number; vz: number; faceX: number; faceZ: number; aimX: number; aimZ: number;
  hp: number; maxHp: number; iframes: number; hurtT: number;
  dashT: number; dashCd: number; dashX: number; dashZ: number;
  level: number; xp: number; xpNext: number;
  ki: number; transformT: number;
  /** Surge form: 0 = base form, 1..SURGE_TIERS = Radiant … Shatterlight (src/core/surgeForms.ts) */
  surgeTier: number;
  beamCd: number; beamCharge: number; beamT: number; beamX: number; beamZ: number;
  /** Direction the auto-beam steers toward (densest crowd). */
  beamTX: number; beamTZ: number;
}

export interface Choice { kind: 'weapon' | 'passive' | 'evolve' | 'heal' | 'bonus' | 'fusion'; id: string; name: string; desc: string; level: number }

export interface SimOptions { seed?: number; bossTime?: number; maxEnemies?: number; godMode?: boolean; autoSkills?: boolean; autoTransform?: boolean; meta?: WorkshopRanks;
  /** after the final boss: true = keep going (endless), false = the run ends in victory, null/undefined = endless only on runs of 10:00+ */
  endless?: boolean | null;
  /** MAP SYSTEM: map id (src/maps/<id>) or a full def (tests); default map #1. tod default = the map's own default. */
  map?: string | MapCoreDef; tod?: TodChoice;
  /** run modifiers chosen on Run Setup (src/core/mods.ts); default none */
  mods?: RunMods;
  /** travel pace (src/core/mods.ts PACE); default PACE, or PACE_HYPER with the Hyper modifier. Tests pass 1 for the original tuning. */
  pace?: number;
  /** CHALLENGES (2026-11-02): challenge-only rule flags (src/core/mods.ts ChallengeRules); default none */
  rules?: ChallengeRules;
  /** TRAINING sandbox (src/core/training.ts): only the Training title-menu entry sets it; nothing from it is ever recorded */
  training?: TrainingOptions | null;
  /** TUTORIAL (owner-approved 2026-11-02): a scripted, calm run (the Basics lesson). The spawn director stays off — no horde,
   *  waves, elites, swarms, guardians or boss; foes only arrive through spawnEnemy() calls from the script. Default false.
   *  Same mechanism as Training's "waves paused": both switch off spawnDirector() (see Sim.spawnsPaused). */
  calm?: boolean;
  /** POWER UPS unlocks (Run Setup): each only applies with its node bought in `meta` (Opening Gambit / Trait Primer /
   *  Exile Seal), so Power Ups Off (meta {}) ignores them. Unknown or retired ids are ignored. */
  startSkill?: string; startTrait?: string; banish?: string[] }

/** themed waves (original names). kind null = mixed assault. from = earliest time (s) the theme can appear. */
export const WAVES: { name: string; kind: EnemyKind | null; from: number }[] = [
  { name: 'FOOTMAN TIDE', kind: 'drone', from: 0 },
  { name: 'SCRABBLER RUSH', kind: 'skitter', from: 60 },
  { name: 'SPINE LANCER LINE', kind: 'lancer', from: 120 },
  { name: 'CANTOR CHOIR', kind: 'caster', from: 180 },
  { name: 'HUSK BARRAGE', kind: 'bomber', from: 240 },
  { name: 'MOTH SWARM', kind: 'wisp', from: 270 },
  { name: 'MAULER MARCH', kind: 'brute', from: 300 },
  { name: 'BROOD STORM', kind: 'splitter', from: 360 },
  { name: 'FOUNDRY QUAKE', kind: 'stomper', from: 420 },
  { name: 'BULWARK WALL', kind: 'warden', from: 480 },
  { name: 'MIXED ASSAULT', kind: null, from: 180 },
];
const WAVE_MIX: { kind: EnemyKind; from: number }[] = [
  { kind: 'drone', from: 0 }, { kind: 'skitter', from: 15 }, { kind: 'lancer', from: 40 }, { kind: 'caster', from: 45 }, { kind: 'bomber', from: 60 },
  { kind: 'brute', from: 70 }, { kind: 'splitter', from: 80 }, { kind: 'stomper', from: 110 }, { kind: 'warden', from: 150 },
];
/** mini-boss roster (original): giant elites of existing kinds plus the Tyrant's guardian. hp = multiple of the boss def's hp. */
export const MINIBOSSES: { name: string; kind: EnemyKind; scale: number; hp: number }[] = [
  { name: 'IRONHIDE COLOSSUS', kind: 'stomper', scale: 3.2, hp: 0.45 },
  { name: 'PRISM SOVEREIGN', kind: 'caster', scale: 3.0, hp: 0.5 },
  { name: 'TYRANT GUARDIAN', kind: 'boss', scale: 1, hp: 0.6 },
];
/** when each mini-boss arrives, as a fraction of the run (30-min run: 10:00, 15:00, 20:00) */
export const MINIBOSS_AT = [1 / 3, 1 / 2, 2 / 3];
/** knockback physics (sim-authoritative): gravity for launched foes, ground slide friction per tick */
/** gradual opening: seconds of light warm-up spawning before the horde builds (owner 2026-11-01) */
/** Zenith Orb craters are this many times deeper than other blast craters (render caps at the ground map's max depth) */
export const ZENITH_CRATER_DEEP = 2.2;
export const WARMUP_END = 30;
/** owner 2026-11-02 ('by the 3-minute mark there's just too many enemies too soon … I'm leveling up too quickly … something
 *  similar to Vampire Survivors'): the spawner no longer floods at a rising rate; it keeps the number of foes on the field near
 *  a target that grows with time (minute → foes), topping up at a limited rate. Waves / swarm rings / elites come on top.
 *  The quality tier's maxEnemies still caps everything. */
// owner 2026-11-03 (at 28:00 on a regular run): 'I wish there was more mobs swarming me … after 15-20 minute marks'. Up to
// 12:00 unchanged; then the field climbs much harder (was 230 @16, 280 @20, 340 @25, 400 @30). The quality tier's maxEnemies
// (low 360 / medium 600 / high 1000) still caps it.
/** HORDE ELITES: share of ordinary spawns that arrive as elites. 0 until 12:00, 40% at 20:00, 65% at 30:00, 80% cap
 *  (Endless / overtime). Their health is HORDE_ELITE_HP x a normal foe (full elites: x9), XP x HORDE_ELITE_XP (full: x12). */
export const HORDE_ELITE_HP = 5, HORDE_ELITE_XP = 4;
export const lateEliteShare = (t: number): number => (t < 720 ? 0 : t < 1200 ? 0.4 * (t - 720) / 480 : Math.min(0.8, 0.4 + 0.25 * (t - 1200) / 600));
export const HORDE_TARGET: [number, number][] = [[0, 8], [1, 25], [2, 40], [3, 55], [5, 85], [8, 130], [12, 180], [15, 260], [20, 420], [25, 560], [30, 700]];
export function hordeTarget(t: number): number {
  const m = t / 60, T = HORDE_TARGET;
  if (m >= T[T.length - 1][0]) return T[T.length - 1][1];
  for (let i = 0; i < T.length - 1; i++) if (m < T[i + 1][0]) { const [m0, a] = T[i], [m1, b] = T[i + 1]; return a + (b - a) * (m - m0) / (m1 - m0); }
  return T[0][1];
}
/** top-up speed (foes / s) when below the target: 3/s at the start → 20/s by 15:00, then (owner 2026-11-03: more swarm after
 *  15-20 min) climbing to 40/s by 25:00 so a strong build can't clear the field faster than it refills */
export const hordeRefill = (t: number) => 3 + Math.min(17, t / 60 * 1.15) + Math.min(20, Math.max(0, t / 60 - 15) * 2);
/** XP per foe grows after 3:00 so levelling keeps pace with the gentler horde (x1 to 3:00, x2.2 at 10:00, x4 at 20:00) */
export const xpTimeMul = (t: number) => 1 + Math.max(0, t - 180) / 360;
/** OVERTIME (owner 2026-11-02): past the 30-min mark foe health doubles every OVERTIME_HP_DOUBLE s (x2 at +2:30, x16 at +10:00),
 *  foe hits gain +100% every OVERTIME_DMG_S s, the field fills to the cap within ~3 min and elites arrive every few seconds */
export const OVERTIME_HP_DOUBLE = 150, OVERTIME_DMG_S = 120;
export const overtimeHpK = (ot: number) => Math.pow(2, ot / OVERTIME_HP_DOUBLE);
/** OVERTIME BITE (Sim.lateBite): how much of a fusion's bite bosses / guardians and elites feel */
export const LATE_BITE_BOSS = 0.2, LATE_BITE_ELITE = 0.5;
/** owner 2026-11-02: 'make the first wave of enemies low HP, to help with leveling' — foes spawned in the opening wave
 *  (the first WARMUP_END s) die to any single hit; health eases back to full by 60 s */
export const FIRST_WAVE_HP = 1 / 40; // owner: 'shouldn't take more than 1 hit per guy' — under 1 HP for every regular foe in the wave (the weakest hit is >= 1)
export const firstWaveHpK = (t: number): number => (t < WARMUP_END ? FIRST_WAVE_HP : t < 2 * WARMUP_END ? FIRST_WAVE_HP + (1 - FIRST_WAVE_HP) * (t - WARMUP_END) / WARMUP_END : 1);
export const KNOCK_GRAVITY = 30;
/** Surge activation shove (no damage): base radius and push speed for level I; +15% per level */
export const SURGE_PUSH_RADIUS = 7;
export const SURGE_PUSH = 9;
export const KNOCK_FRICTION = 0.93;
// Riftline rising wall wave (owner 2026-11-02)
/** wall face thickness ahead of the travelled distance / hit band behind it (m); shove impulse and upward loft (m/s) */
export const RIFT_THICK = 0.6, RIFT_BAND = 1.4, RIFT_PUSH = 22, RIFT_LOFT = 4;
/** sim hit colour for Riftline damage; the view routes hits with this colour to the wall's own impact sparks */
export const RIFT_HIT_COLOR = 0xff80ff;
/** Singularity hit colours: damage ticks inside the orb, and the implosion when it shrinks into nothing */
export const SING_HIT_COLOR = 0xff2a3a, SING_IMPLODE_COLOR = 0xff1450;
/** sim hit colour for Finger Beam needles (render pops crimson sparks on these) */
export const FINGER_HIT_COLOR = 0xff1a3c;
/** sim hit colour for Flare Barrage bursts (owner 2026-11-02) */
export const BARRAGE_HIT_COLOR = 0xffb02a;
/** Flare Barrage pellet (render reads these: barrageFx.ts). Non-homing: (vx, vz) never changes after it is fired. */
export interface BarrageShot { alive: boolean; id: number; x: number; y: number; z: number; vx: number; vz: number; dist: number; range: number; r: number; blast: number; dmg: number; hand: number; evo: boolean }
// ---- Phase Rend / Comet Dive / Force Shout (owner 2026-11-02: 'Make them all') ----
/** sim hit colours (render pops sparks in these): Phase Rend slashes / cut lines / afterimages, Comet Dive shockwaves, Force Shout */
export const PHASE_HIT_COLOR = 0xc3a6ff, DIVE_HIT_COLOR = 0xff8a3a, SHOUT_HIT_COLOR = 0xd2ecff;
/** Phase Rend cut line left by a slash: segment (x0, z0) → (x1, z1); hurts each foe that crosses it once (hit = foe ids) */
export interface PhaseCut { id: number; x0: number; z0: number; x1: number; z1: number; t: number; life: number; dmg: number; evo: boolean; hit: Set<number> }
/** Phase Rend in progress. The hero's gameplay position never moves: (bx, bz) is where his body is drawn (sim-owned), facing
 *  (dx, dz). left = blinks still to make, next = tick of the next blink (or of the snap back when left = 0). */
export interface PhaseRendState { active: boolean; left: number; k: number; next: number; bx: number; bz: number; dx: number; dz: number; lvl: number; evo: boolean; visited: Set<number> }
/** Comet Dive in the air: t seconds of dur, from (x0, z0) to the landing (x1, z1); the hero's position follows the path */
export interface DiveState { active: boolean; t: number; dur: number; x0: number; z0: number; x1: number; z1: number; lvl: number; evo: boolean; /** SKILL FUSIONS: a fusion leap (Fault Drop / Roaring Meteor) lands through FusionField.land */ fx?: FusionId }
/** Force Shout wave: front d metres out of (x, z) along the cone (dx, dz, half-angle), up to r; delay = seconds before an echo
 *  starts (it leaves from wherever the hero is then); hit = foe ids already struck by this wave */
export interface ShoutWave { id: number; x: number; z: number; dx: number; dz: number; half: number; r: number; d: number; dmg: number; stun: number; hurl: number; evo: boolean; echo: boolean; delay: number; hit: Set<number> }
// Twin Gaze / Split Self / Crescent Edge (owner 2026-11-02: 'Make them all')
/** sim hit colours (render routes hits with these colours to each skill's own impact sparks) */
export const GAZE_HIT_COLOR = 0xff8a2a, GAZE_BURN_COLOR = 0xff5a1a, SPLIT_HIT_COLOR = 0x6ff0e0, CRESCENT_HIT_COLOR = 0xb8a6ff;
/** Twin Gaze live state (render reads it, never writes). phase 0 off, 1 eye-glint wind-up, 2 sweeping. (ax, az) = aim (unit,
 *  fixed for the cast); beam b's direction = aim turned by th[b] toward (-az, ax); it lands d[b] m out; thP / dP = last
 *  tick's values (render interpolates); pass = index of the current pass, u = 0..1 through it. Beam 0 starts on the
 *  (-az, ax) side, beam 1 on the other; they sweep past each other (scissor X). */
export interface GazeState {
  phase: number; t: number; id: number; ax: number; az: number; reach: number; half: number; passes: number; pass: number; u: number;
  dmg: number; halfW: number; burn: number; evo: boolean; th: number[]; d: number[]; thP: number[]; dP: number[];
}
/** Pyre Sight burning line segment (evolved Twin Gaze); t = seconds left, life = its full life */
export interface GazeBurn { alive: boolean; x0: number; z0: number; x1: number; z1: number; t: number; life: number; dmg: number }
/** Split Self energy copy (render reads these). slot = place in the ring; t = seconds left (Infinity = for good); age =
 *  seconds since it formed; cd = seconds to its next shot; (faceX, faceZ) = where it looks (target while shooting) */
export interface SplitCopy { alive: boolean; id: number; slot: number; x: number; z: number; vx: number; vz: number; t: number; dur: number; age: number; cd: number; fire: number; dmg: number; faceX: number; faceZ: number; evo: boolean; shots: number }
/** Split Self light bolt (straight, single target) */
export interface SplitShot { alive: boolean; id: number; copy: number; x: number; y: number; z: number; vx: number; vz: number; life: number; dmg: number; evo: boolean }
/** Crescent Edge wave: leaves (x0, z0) along (dx, dz); d = distance travelled; centre (x, z); w = tip-to-tip width; hit =
 *  foes it already cut (each once) */
export interface CrescentWave { alive: boolean; id: number; x0: number; z0: number; x: number; z: number; dx: number; dz: number; d: number; range: number; w: number; speed: number; dmg: number; hand: number; evo: boolean; hit: Set<number> }
/** Lunar Cross (evolved Crescent Edge): each arm of the X is turned this far off the across-line (rad) */
export const CRESCENT_X_TILT = 0.62;
// ---- Skills B (owner 2026-11-02): live state the render reads (pooled; render never writes) ----
/** sim hit colours: Snare orb paths / implosion, Wayward Comet, Binding Rings shatter / Shackle Nova blast, Gravity Grip slam /
 *  ground shock */
export const SNARE_HIT_COLOR = 0xffe08c, SNARE_BURST_COLOR = 0xfff0b8, COMET_HIT_COLOR = 0x5affd8, RINGS_HIT_COLOR = 0xc08aff, RINGS_BLAST_COLOR = 0xd8b8ff, GRIP_HIT_COLOR = 0xff6ad0, GRIP_SHOCK_COLOR = 0xff9ae0;
/** Snare Constellation cast. phase 0 = the orbs hang in the ring (t < hang), 1 = they slam inward (fly s), 2 = imploded and
 *  waiting for the Starcage echo (echoT s). hit = foes an orb path already struck this cast. */
export interface SnareCast { alive: boolean; id: number; x: number; z: number; r: number; n: number; a0: number; t: number; hang: number; fly: number; phase: number; dmg: number; burst: number; br: number; evo: boolean; echoT: number; hit: Set<number> }
/** Wayward Comet (or a Splitstar shard). tid = foe it is turning toward (-1 = flying straight); hits = foes struck so far. */
export interface CometShot { alive: boolean; id: number; x: number; z: number; vx: number; vz: number; speed: number; r: number; dmg: number; left: number; tid: number; life: number; seek: number; splits: number; shard: boolean; evo: boolean; hits: number; hit: Set<number> }
/** Binding Rings: one ring around foe enemies[ei] (id eid), held t / dur s; x, y, z follow the foe (y includes a Grip lift); s =
 *  its body radius. dmg 0 = a Shackle Nova after-pin (no shatter damage). */
export interface RingHold { alive: boolean; id: number; ei: number; eid: number; x: number; y: number; z: number; s: number; t: number; dur: number; dmg: number; blastR: number; evo: boolean;
  /** the cast it belongs to (a Shackle Nova blast hurts each foe at most once per cast) */ cast: number }
/** Gravity Grip cast: t s since the seize; lift GRIP_LIFT, hold, fall GRIP_FALL, then the slam. Caught foes k < n: enemy index,
 *  id, where they were seized and a per-foe height share. */
export interface GripCast { alive: boolean; id: number; x: number; z: number; r: number; h: number; t: number; hold: number; dmg: number; shockR: number; deep: number; evo: boolean; n: number; ei: number[]; eid: number[]; x0: number[]; z0: number[]; hk: number[] }
/** Starcage: inward shove per tick on foes near the hanging ring (m/s, before mass) */
const SNARE_CAGE = 0.35;
/** Bosses resist the Singularity's pull strongly: they feel this share of it */
const SING_BOSS_PULL = 0.08;
/** Foes in the Singularity's pull lose this share of their own running speed every tick (÷ √mass) */
const SING_GRIP = 0.15;
/** Singularity (owner 2026-11-02, replaces Breaker Shot): one big black orb fired straight ahead. It drifts slowly and grows
 *  (r0 → rMax over `life` s), dragging every foe within r × pullMul toward its centre and hurting the ones inside every
 *  SING_TICK ticks; then it shrinks into nothing over SING_COLLAPSE s and implodes. Evolved, it re-forms in place once and
 *  collapses again. phase: 0 flying/growing, 1 collapsing, 2 re-forming (evolved); pt = seconds into the phase. */
export interface Singularity {
  id: number; x: number; z: number; dx: number; dz: number; speed: number; life: number;
  r: number; rMax: number; rC: number; pullMul: number; pull: number; dmg: number; implode: number; evo: boolean;
  /** 0 flight, 1 collapsing, 2 re-forming, 3 Collapse Point hold at the end of its path */ phase: number; pt: number; tk: number; /** collapses still to come */ left: number;
  /** Collapse Point: seconds it hangs at the end of its path before the first collapse (0 unevolved) */ hold: number;
  /** ground gouge: crater depth multiplier and where the last crater was dug */ deep: number; cx: number; cz: number;
}
export interface RiftWall { id: number; x0: number; z0: number; x: number; z: number; dx: number; dz: number; w: number; len: number; speed: number; d: number; dmg: number; back: boolean; hit: Set<number>; /** MAP-FIRE: this wall may light the grass */ ign?: boolean }
export interface RiftState {
  active: boolean; a: number; t: number; w: number; len: number; speed: number; dmg: number; evo: boolean; nextId: number;
  walls: RiftWall[]; queue: { t: number; dx: number; dz: number; k: number; back: boolean }[];
}
/** presentation colour per Surge form (index = tier; 0 = the base-form blue): each form's inner flame from surgeForms.ts */
export const TIER_COLOR = [0x4ab8ff, ...SURGE_FORMS.map((f) => f.hud ?? f.inner)];
export const TRANSFORM_TIME = 20;
export const DASH_CD = 0.55;
/** Global pace knobs (faster, more responsive feel). */
export const ENEMY_SPEED_MUL = 1.0;
/** owner 2026-11-02: 'make the enemies run faster to keep up with the player… just slightly slower than the player':
 *  regular foes run 1.7x their base speed (the footman 4.4 -> 7.5 m/s vs the hero's 8.5), capped at 95% of the hero's
 *  base speed so the fast families (wisps, scrabblers, husks) never outrun him. Bosses keep their own pace. */
export const ENEMY_RUN_MUL = 1.7, ENEMY_SPEED_CAP = 8.5 * 0.95;
/** ENEMY ERAS raise the cap with the era but never past this: just under the hero's base speed (BASE_MOVE 8.5) */
export const ERA_SPEED_CAP = 8.5 * 0.985;
export const SHELL_G = 40;

export class Sim {
  readonly rng: Rng;
  readonly opts: Required<SimOptions>;
  state: GameState = 'playing';
  time = 0;
  tick = 0;
  kills = 0;
  shards = 0;
  events: SimEvent[] = [];
  choices: Choice[] = [];
  pendingLevels = 0;

  player: Player;
  weapons = new Map<WeaponId, number>();
  evolved = new Set<WeaponId>();
  passives = new Map<PassiveId, number>();
  wTimers: Record<WeaponId, number> = { bolt: 0, orbit: 0, nova: 0, arc: 0, lance: 0, surge: 0, disc: 0, mortar: 0, zenith: 3, breaker: 0, bore: 0, riftline: 1, skyfall: 0, mirage: 1, glare: 1, rush: 0, overdrive: 2, ward: 1, finger: 0, barrage: 0,
    phase: 0, dive: 0, shout: 0, gaze: 0.5, split: 0.5, crescent: 0, snare: 1, comet: 0, rings: 1, grip: 1, breath: 0.5, flamering: 1, // Phase Rend / Comet Dive / Force Shout (owner 2026-11-02)
    rime: 0.5, spikes: 1, glacier: 0, blizzard: 1 }; // ICE FAMILY (owner 2026-11-03)

  enemies: Enemy[] = [];
  projectiles: Projectile[] = [];
  gems: Gem[] = [];
  orbs: { x: number; y: number; z: number }[] = [];
  boss: Enemy | null = null;
  bossSpawned = false;
  // ---- THE NULL HERALD (src/core/herald.ts) ----
  /** heralds spawned so far (the next one is index heraldN) and the scheduled second of the next (-1 = not set yet) */
  heraldN = 0; nextHeraldAt = -1;
  /** per-herald brain state, by enemy id */
  private heralds = new Map<number, { n: number; touchCd: number; slamCd: number; slamT: number; sx: number; sz: number }>();
  /** damage dealt per second over the last HERALD_WINDOW s (ring buffer sampled from dmgBySrc sums; deterministic) */
  private dmgRing: number[] = new Array(HERALD_WINDOW).fill(0); private dmgRingTotal = 0; private dmgRingFill = 0;
  /** the herald whose health the boss bar shows when no other boss is active (null = none) */
  get heraldBar(): Enemy | null {
    if (this.boss && this.boss.alive) return null;
    for (const e of this.enemies) if (e.alive && e.def.kind === 'herald') return e;
    return null;
  }
  /** owner 2026-11-01: a full run is 30 min. Guardians (non-final bosses) at 1/3 and 2/3 of the run; beating the final
   *  boss at bossTime records the victory and the run continues in endless mode, with a guardian every 5 min. */
  endless = false;
  victoryAt = 0;
  private guardiansSpawned = 0;
  private finalBoss: Enemy | null = null;
  /** Spirit Bolt presentation colour (owner 2026-11-01): yellow, following the Surge level — Radiant yellow, Crimson red,
   *  Azure blue. Display only (event/hit colours); no gameplay effect. */
  get boltColor(): number { return 0xffd23a; } // owner 2026-11-02: skills keep their own colours at every Surge level (no red II / blue III recolour)
  /** live enemy by id (null if dead / recycled) */
  enemyById(id: number): Enemy | null { for (const e of this.enemies) if (e.alive && e.id === id) return e; return null; }
  get bossIsFinal(): boolean { return !!this.boss && this.boss === this.finalBoss; }
  /** name shown on the boss health bar */
  bossName = 'HOLLOW TYRANT';
  private endlessRot = 0;
  bossesDefeated = 0;
  /** SURGE FORMS: the highest form reached this run (records / feats) and the top form unlocked at the last step (surgeUnlock events) */
  bestSurgeTier = 0; private lastMaxSurge = 0;
  /** SURGE FORMS: seconds a form has been ready while the next stage has not completed (AUTO Surge patience) */
  kiStallT = 0;
  /** TRAINING: every form up to this one counts as unlocked (the SURGE I…VIII buttons; 0 = the run's own rules) */
  surgeFloor = 0;
  private lastSalveDrop = -999; private lastSweepDrop = -999;
  /** owner 2026-11-01: enemies arrive in themed waves (one per minute from 1:00) */
  wave = 0; waveName = ''; private waveKind: EnemyKind | null = null; private waveDir = 0; private lastWaveTheme = -1;
  private nextEndlessGuardian = 0;
  flight: FlightMode = 'assisted';
  aimMode: AimMode = 'auto';
  // Array Seals / Convergence Boons
  seals = 0;
  convergences = 0;
  boonChoices: BoonId[] = [];
  private pendingBoons = 0;
  // ---- ENDLESS GOALS (src/core/endless.ts) ----
  /** endless tier reached (0 = none yet) */
  endlessTier = 0;
  /** shards banked by endless milestones: paid with the run's shards when it ends, death included */
  endlessBank = 0;
  /** what the 'boon' state is offering: a Convergence boon or an Apex Boon (endless milestone) */
  boonKind: 'convergence' | 'apex' = 'convergence';
  apexChoices: ApexId[] = [];
  private pendingApex = 0; private apexOpenT = 0;
  /** Apex Boon multipliers: damage dealt, damage taken */
  apexDmg = 1; apexArmor = 1;
  private nextElitePack = 0;
  /** the current twin guardian's id (Shatter Pulse spares it, like the boss) */
  private twinId = -1;
  /** real seconds since the final boss fell (0 before endless) */
  /** natural spawning off (one mechanism for both): the Basics lesson (SimOptions.calm: its script spawns its own foes) and
   *  Training's "natural waves" toggle (Training.directorPaused). Scripted spawnEnemy() calls still work. */
  /** a PRACTICE run records nothing (no shards, records, map / endless bests, Encyclopedia, achievements, quests or
   *  challenges): the Training sandbox, or the Basics lesson (calm). Read by settleRun, CodexTracker and FeatTracker. */
  get practice(): boolean { return !!this.training || !!this.opts.calm; }
  get spawnsPaused(): boolean { return this.opts.calm || !!this.training?.directorPaused; }
  get endlessTime(): number { return this.endless ? Math.max(0, this.time - this.victoryAt) : 0; }
  /** endless seconds left until the next milestone */
  get nextMilestoneIn(): number { return Math.max(0, endlessMilestoneAt(this.endlessTier + 1) - this.endlessTime); }
  windfall = 0;
  stillT = 0;
  // draft tools + revives (from the Array Workshop)
  rerolls = 0;
  bans = 0;
  revives = 0;
  /** Second Wind saves already spent this run (Training's trait panel refills them when the rank changes) */
  swUsed = 0;
  banned = new Set<string>();
  /** Damage dealt per technique id (for the pause-screen stats). */
  dmgBySrc: Record<string, number> = {};
  /** sim.time when each source first dealt damage (results-screen DPS; deterministic, not part of the state hash). */
  dmgSince: Record<string, number> = {};
  private curSrc = 'other';
  /** render hint (ENEMY-DEATHS): the blast / energy source currently dealing damage (KILL_SRC), its colour, centre and radius */
  private bSrc = 0; private bCol = -1; private blastX = 0; private blastZ = 0; private blastR = 0;
  /** mark the following damage as coming from a blast / energy source centred at (x, z); reset with `this.bSrc = 0` right after */
  private blast(src: number, col: number, x: number, z: number, r: number): void { this.bSrc = src; this.bCol = col; this.blastX = x; this.blastZ = z; this.blastR = r; }
  /** Destructible battlefield props + blast craters (deterministic; render reads only). */
  terrain: Terrain;
  /** MAP-FIRE grass / tree fires (deterministic, own Rng; visual destruction only — never damages the player or foes) */
  fire: FireGrid;
  /** MAP PICKUPS: relic caches, chests and field items (src/core/pickupsField.ts; render + HUD read it) */
  loot: FieldLoot;
  /** MAP FOES (owner 2026-11-02): this map's own foes — telegraph marks, ground patches, hero status (render reads) */
  readonly foes: MapFoeField;
  /** what opened the current draft: '' = a level-up, 'relic' = a relic cache's free pick, 'fusion' = a chest's FUSION offer (HUD heading) */
  draftTag = '';
  /** MAP-DESTRUCTION: the prop-damage style of the technique currently dealing damage (render hint, PROP_SRC) */
  private propSrc(): number {
    switch (this.curSrc) {
      case 'disc': return PROP_SRC.cut;
      case 'finger': return PROP_SRC.pierce;
      case 'lance': return PROP_SRC.beam;
      case 'breaker': return PROP_SRC.vortex;
      case 'riftline': return PROP_SRC.wall;
      case 'bolt': case 'barrage': case 'rush': case 'thorns': case 'orbit': return PROP_SRC.chip;
      default: return PROP_SRC.blast;
    }
  }
  // ----- live technique state (render reads these) -----
  /** Zenith Orb charge: t counts up to dur while the orb grows overhead */
  zenith = { charging: false, t: 0, dur: 1.6, size: 1 };
  /** Rift Mortar charge: t counts up to dur while the sphere grows in the hands to radius size (aim = player.aimX/Z) */
  mortar = { charging: false, t: 0, dur: 0.55, size: 0.5 };
  /** Riftline rising wall wave (owner 2026-11-02, replaces the old sweep): the ground splits across the hero's front and
   *  a wall erupts and rolls forward, hitting each foe once and shoving it along. active = a wall is travelling or a
   *  queued wave is pending (the hero's 'rift' hold reads it); a = aim angle atan2(z, x); t = seconds since the cast.
   *  walls: live walls, centre (x, z) = origin (x0, z0) + (dx, dz) * d; w = width across, len = travel, back = the
   *  Horizon Cutter rear wall. Render reads these each frame and never writes them. */
  rift: RiftState = { active: false, a: 0, t: 0, w: 8, len: 10, speed: RIFT_SPEED, dmg: 0, evo: false, nextId: 1, walls: [], queue: [] };
  /** Skyfall shards in flight (telegraphed on the ground) */
  /** Skyfall volley: homing energy shots fired up from the hands that rain down (owner 2026-11-01). tid = homing target
   *  enemy id (-1 = fixed spot), t0 = total flight time, k = shot index (render staggers launches/hands by it).
   *  Shardstorm (owner 2026-11-03): kind = SKY_KIND (shard from the hands; splinter: leaps from the shatter point (ox, oz);
   *  star: the Fallen Star, from the hands). r0 / d0 = the volley's shard radius / damage (what splinters are cut from). */
  falls: { x: number; z: number; t: number; t0: number; k: number; tid: number; r: number; dmg: number; kind: number; ox: number; oz: number; r0: number; d0: number }[] = [];
  /** Shardstorm: critical hits banked toward the next Fallen Star; seconds until another star may be called */
  skyCrits = 0;
  private starCd = 0;
  /** critical hits landed so far this run (Shardstorm counts the ones its blasts land) */
  critHits = 0;
  /** map-fire ignition chance multiplier for the next radialDamage (Shardstorm splinters never light the grass) */
  private igniteK = 1;
  /** Mirage lures */
  lures: { x: number; z: number; t: number; dur: number; r: number; dmg: number; br: number; heal: number }[] = [];
  /** Singularity orbs (skill id 'breaker'). Render reads these each frame and never writes them. */
  sings: Singularity[] = [];
  private singId = 1;
  /** Spirit Bolt burst in progress (shots left, index, target ids, damage) */
  /** Pulse Nova sphere bursts still to go off (owner 2026-11-02) */
  private novaBursts: { t: number; x: number; y: number; z: number; r: number; dmg: number; evo: boolean }[] = [];
  /** new traits (owner 2026-11-02): Chain Reaction blasts / Aftershock pulses still to go off; Surge Battery seconds added this Surge */
  private chainQ: { x: number; z: number; t: number }[] = [];
  private shockQ: { x: number; z: number; r: number; t: number }[] = [];
  private batteryAdded = 0;
  /** ELEMENTAL FAMILIES (owner 2026-11-03): Frostbite bursts still to go off; Static Charge arcs sparked this tick; an arc is
   *  being resolved (its hits never spark arcs of their own) */
  private frostQ: { x: number; z: number; r: number; dmg: number; t: number }[] = [];
  private zapsThisTick = 0; private inZap = false;
  /** MAP-FIRE: this Nova Lance beam will light the grass along its path (25 % per beam) */
  private beamIgnite = false;
  private boltBurst: { left: number; k: number; tids: number[]; dmg: number; evo: boolean; lvl: number; wait: number } | null = null;
  /** Finger Beam volley in progress (shots left, next shot index, ticks to the next one) */
  private fingerBurst: { left: number; k: number; wait: number; lvl: number; evo: boolean; used: number[] } | null = null;
  /** Finger Beam (render: the fingertip charge glow): seconds until the next shot is due — the next beam of a running volley,
   *  else the cooldown; Infinity without the skill, or (auto aim) while nothing is in reach, since the shot then waits.
   *  Read-only (no Rng, no state change), so calling it never alters a run. */
  fingerNextIn(): number {
    const lvl = this.wlv('finger');
    if (lvl <= 0) return Infinity;
    if (this.fingerBurst) return this.fingerBurst.wait * DT;
    const t = Math.max(0, this.wTimers.finger) / Math.max(1e-6, this.foes.cdRate);
    if (t < 0.5 && this.aimMode !== 'manual' && this.nearestEnemy(this.player.x, this.player.z, fingerNums(lvl, this.evolved.has('finger'), this).range) < 0) return Infinity;
    return t;
  }
  // Flare Barrage (owner 2026-11-02)
  /** volley in progress (render reads it for the hands-forward pose): shot ticks left, next shot tick index, tick of the next
   *  shot, current aim (unit), tick of the last re-aim */
  barrageVolley: { left: number; k: number; next: number; dx: number; dz: number; aimTick: number; lvl: number; evo: boolean } | null = null;
  /** pooled pellets (alive flag); render reads */
  readonly barrageShots: BarrageShot[] = Array.from({ length: 160 }, () => ({ alive: false, id: 0, x: 0, y: 0, z: 0, vx: 0, vz: 0, dist: 0, range: 0, r: 0, blast: 0, dmg: 0, hand: 1, evo: false }));
  private barrageId = 1;
  // Phase Rend / Comet Dive / Force Shout (owner 2026-11-02). Render reads these and never writes them.
  readonly phaseRend: PhaseRendState = { active: false, left: 0, k: 0, next: 0, bx: 0, bz: 0, dx: 0, dz: 1, lvl: 1, evo: false, visited: new Set() };
  phaseCuts: PhaseCut[] = [];
  /** Fractured Instant afterimage slashes still to land */
  phaseEchoes: { x: number; z: number; t: number; r: number; dmg: number }[] = [];
  private phaseCutId = 1;
  readonly dive: DiveState = { active: false, t: 0, dur: 0, x0: 0, z0: 0, x1: 0, z1: 0, lvl: 1, evo: false };
  /** Comet Dive aftershocks (kind 0) and Titan Drop fissures (kind 1) still to erupt */
  diveQueue: { t: number; kind: number; x: number; z: number; dx: number; dz: number; r: number; dmg: number; launch: number }[] = [];
  shoutWaves: ShoutWave[] = [];
  private shoutId = 1;
  // Twin Gaze / Split Self / Crescent Edge (owner 2026-11-02)
  /** Twin Gaze sweep in progress (render reads it) */
  readonly gaze: GazeState = { phase: 0, t: 0, id: 0, ax: 0, az: 1, reach: 9, half: 0.6, passes: 2, pass: 0, u: 0, dmg: 0, halfW: 0.3, burn: 0, evo: false, th: [0, 0], d: [0, 0], thP: [0, 0], dP: [0, 0] };
  /** Pyre Sight burning lines (pooled; render reads) */
  readonly gazeBurns: GazeBurn[] = Array.from({ length: 160 }, () => ({ alive: false, x0: 0, z0: 0, x1: 0, z1: 0, t: 0, life: 1, dmg: 0 }));
  private gazeHit = [new Set<number>(), new Set<number>()]; private gazeBurnHit = new Set<number>();
  private gazeLand = [0, 0, 0, 0]; private gazeTk = 0;
  /** Split Self copies and their bolts (pooled; render reads) */
  readonly splits: SplitCopy[] = Array.from({ length: 8 }, () => ({ alive: false, id: 0, slot: 0, x: 0, z: 0, vx: 0, vz: 0, t: 0, dur: 0, age: 0, cd: 0, fire: 1, dmg: 0, faceX: 0, faceZ: 1, evo: false, shots: 0 }));
  readonly splitShots: SplitShot[] = Array.from({ length: 96 }, () => ({ alive: false, id: 0, copy: 0, x: 0, y: 0, z: 0, vx: 0, vz: 0, life: 0, dmg: 0, evo: false }));
  private splitId = 1; private splitShotId = 1; private splitAng = 0;
  /** Crescent Edge waves (pooled; render reads) and the throw in progress */
  readonly crescents: CrescentWave[] = Array.from({ length: 24 }, () => ({ alive: false, id: 0, x0: 0, z0: 0, x: 0, z: 0, dx: 0, dz: 1, d: 0, range: 0, w: 0, speed: 0, dmg: 0, hand: 1, evo: false, hit: new Set<number>() }));
  crescentThrow: { left: number; k: number; wait: number; lvl: number; evo: boolean } | null = null;
  private crescentId = 1;
  // Skills B (owner 2026-11-02): pooled live state (render reads these, never writes)
  readonly snares: SnareCast[] = Array.from({ length: 4 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, n: 0, a0: 0, t: 0, hang: 0, fly: 0, phase: 0, dmg: 0, burst: 0, br: 0, evo: false, echoT: 0, hit: new Set<number>() }));
  readonly comets: CometShot[] = Array.from({ length: 24 }, () => ({ alive: false, id: 0, x: 0, z: 0, vx: 0, vz: 0, speed: 0, r: 0, dmg: 0, left: 0, tid: -1, life: 0, seek: 0, splits: 0, shard: false, evo: false, hits: 0, hit: new Set<number>() }));
  readonly ringHolds: RingHold[] = Array.from({ length: 96 }, () => ({ alive: false, id: 0, ei: 0, eid: 0, x: 0, y: 0, z: 0, s: 0, t: 0, dur: 0, dmg: 0, blastR: 0, evo: false, cast: 0 }));
  /** Shackle Nova: foes each recent cast has already hurt (its own pinned foes included), ring buffer by cast id */
  private readonly ringCasts = Array.from({ length: 4 }, () => ({ cast: 0, hit: new Set<number>() })); private ringCastNext = 0;
  readonly grips: GripCast[] = Array.from({ length: 3 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, h: 0, t: 0, hold: 0, dmg: 0, shockR: 0, deep: 1, evo: false, n: 0, ei: new Array<number>(24).fill(0), eid: new Array<number>(24).fill(0), x0: new Array<number>(24).fill(0), z0: new Array<number>(24).fill(0), hk: new Array<number>(24).fill(1) }));
  private skbId = 1; private readonly skbSet = new Set<number>(); private readonly skbList: { i: number; d: number; id: number }[] = [];
  // Fire family (owner 2026-11-02): pooled live state (render reads these, never writes)
  readonly breath: BreathState = { active: false, id: 0, t: 0, dur: 0, ax: 0, az: 1, dx: 0, dz: 1, pdx: 0, pdz: 1, tx: 0, tz: 1, range: 0, half: 0, sweep: 0, lvl: 1, evo: false, ign: false, hits: 0 };
  readonly flameRings: FlameRingWave[] = Array.from({ length: 8 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, d: 0, speed: 0, dmg: 0, burnDps: 0, burnDur: 0, delay: 0, k: 0, rings: 1, linger: 0, ign: false, evo: false, hit: new Set<number>() }));
  readonly flameLingers: FlameLinger[] = Array.from({ length: 4 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, w: 0, t: 0, life: 1, burnDps: 0, burnDur: 0, evo: false }));
  readonly wyrms: Wyrm[] = Array.from({ length: 3 }, () => ({ alive: false, id: 0, x0: 0, z0: 0, x1: 0, z1: 0, t: 0, dur: 1, r: 0, dmg: 0, burnDps: 0, burnDur: 0, ign: false }));
  private fireId = 1; private firePops: { x: number; z: number; t: number }[] = [];
  /** Ember Heart health restored in the current second (capped per second) */
  private emberHealed = 0;
  // ICE FAMILY (owner 2026-11-03): pooled live state (render reads these, never writes)
  readonly rime: RimeState = { active: false, id: 0, t: 0, dur: 0, ax: 0, az: 1, dx: 0, dz: 1, pdx: 0, pdz: 1, tx: 0, tz: 1, range: 0, half: 0, sweep: 0, lvl: 1, evo: false, ign: false, hits: 0 };
  /** Rime Breath: cold hits in a row per foe id (the freeze counter); cleared when a foe leaves the cone */
  private readonly rimeExp = new Map<number, number>(); private readonly rimeNow = new Set<number>();
  readonly frostFields: FrostField[] = Array.from({ length: 48 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, t: 0, life: 1, k: 0, dps: 0, src: '', evo: false }));
  readonly iceLances: IceLance[] = Array.from({ length: 18 }, () => ({ alive: false, id: 0, x: 0, z: 0, dx: 1, dz: 0, speed: 0, r: 0, dmg: 0, pierce: 0, d: 0, range: 0, trailD: 0, chillK: 0, chillT: 0, freeze: 0, lvl: 1, evo: false, hit: new Set<number>() }));
  readonly iceRings: IceRing[] = Array.from({ length: 6 }, () => ({ alive: false, id: 0, x: 0, z: 0, r: 0, d: 0, speed: 0, dmg: 0, chillK: 0, chillT: 0, hit: new Set<number>() }));
  readonly iceRidges: IceRidge[] = Array.from({ length: 6 }, () => ({ alive: false, id: 0, x: 0, z: 0, dx: 1, dz: 0, len: 0, t: 0, life: 1, dmg: 0, hit: new Set<number>() }));
  readonly blizzard: BlizzardState = { on: false, r: 0, evo: false, t: 0, next: 0 };
  private iceId = 1; private iceFreezeCues = 0;
  /** Hoarfrost Spikes eruptions still to land (the rank-3 second ring, the evolved march) */
  private readonly spikeQ: { x: number; z: number; r: number; dmg: number; t: number; k: number; lvl: number; evo: boolean }[] = [];
  /** Assisted aim: the player is steering the aim this tick (R-stick / right mouse) */
  private steer = false;
  /** Rush Flurry strikes still to land */
  private rushLeft = 0; private rushDmg = 0; private rushReach = 0;
  /** Overdrive time remaining */
  odT = 0;
  /** Prism Ward: hits the dome can still absorb (0 = no dome) */
  wardCharges = 0;
  private stillFor = 0;
  // ---- group A core stat traits (owner 2026-11-03) ----
  /** Lifedraw: health it may still restore right now (a token bucket refilling at its HP/s cap, holding at most 1 s worth) */
  private drawBudget = 0;
  /** Lifedraw: total health restored this run (tests / QA) */
  lifedrawHealed = 0;
  /** Momentum build-up, 0..1: rises while the hero keeps moving, falls when it stops (HUD chip reads it) */
  momentumK = 0;

  private grid: Grid;
  /** SKILL FUSIONS: owned fusions and their live effects (render reads, never writes) */
  readonly fusion: FusionField;
  private nextId = 1;
  private spawnBudget = 0;
  /** highest enemy era announced so far (ENEMY ERAS) */
  eraNow = 0;
  private lastElite = 0;
  private lastSwarm = 0;
  private orbitAngle = 0;

  /** MAP SYSTEM: the map this run is on (src/maps/<id>/core.ts) and the chosen time of day */
  readonly map: MapCoreDef;
  tod: TodChoice;
  /** telegraphed map hazards (deterministic; render reads zones + 'hazard' events) */
  hazards: HazardField;
  /** time of day 0..1 (0 = midnight, 0.5 = noon); fixed for day / night, advancing for the live cycle */
  get clock(): number { const o = this.training?.clockOverride; return o != null ? o : todClock(this.tod, this.map.time.day, this.schedTime, this.opts.bossTime); }
  // ---- run pace + run modifiers (owner 2026-11-02, src/core/mods.ts) ----
  /** the run modifiers that are on */
  readonly mods: RunMods;
  /** Travel pace (PACE 0.85 by default, 1.10 with Hyper). Multiplies: the hero's run speed; foe run speed (and its cap);
   *  every hero shot / disc / wall / wave in flight (Spirit Bolt, Scythe Disc, Rift Mortar, Zenith Orb, Skyfall shots,
   *  Riftline walls, Force Shout front, Flare Barrage, Split Self bolts, Crescent Edge, Wayward Comet) with flight times
   *  stretched by 1 / pace so every reach stays the same; foe shots, Spine Lancer and Tyrant charges (same reach); and foe
   *  attack wind-ups last 1 / pace as long. Left out on purpose: cooldowns, damage, durations, areas and ranges (damage per
   *  second stays the same), knockback / shoves, pickup pull, Singularity drift (its drift is its reach and its damage is
   *  per second), orbits, sweeps, blinks, leaps and grabs (timed moves). */
  pace: number;
  /** Tougher Foes: foe health / foe hit multipliers (1 when off) */
  hpK: number; dmgK: number;
  /** Hurry: schedule seconds per real second (1 when off) */
  schedK: number;
  /** TRAINING: the sandbox state (null in every normal run) and the tweak values the sim reads (frozen defaults otherwise) */
  readonly training: Training | null = null;
  readonly tw: Readonly<Tweaks> = DEFAULT_TWEAKS;
  /** TRAINING: live run-modifier toggles (Training.setMod) */
  setPace(v: number): void { this.pace = v; (this.opts as { pace: number }).pace = v; }
  setToughness(hp: number, dmg: number): void { this.hpK = hp; this.dmgK = dmg; }
  setSchedK(k: number): void { this.schedK = k; }
  /** the run schedule clock (s): real time, sped up by Hurry. Boss / guardian times, waves, elites, the field target and
   *  the difficulty / XP ramps all read this; the HUD timer shows the real `time`. */
  get schedTime(): number { return this.time * this.schedK; }
  /** real seconds until the final boss (bossTime, compressed by Hurry) */
  get runLen(): number { return this.opts.bossTime / this.schedK; }
  /** shard bonus share from the run modifiers is applied in shardPayout */
  /** 0 by day .. 1 at full night (render: moonlit look; sim: spawn pressure) */
  get night(): number { return nightness(this.clock); }
  /** visibility at the hero (fog / storm hazards): 1 clear .. 0.25 */
  get visibility(): number { return this.hazards.visibility(this.player.x, this.player.z); }
  private hazardHurtT = 0;

  constructor(opts: SimOptions = {}) {
    const map = typeof opts.map === 'object' && opts.map ? opts.map : mapById(opts.map as string | undefined);
    const mods: RunMods = { ...(opts.mods ?? {}) };
    this.map = map; this.tod = mods.night ? 'night' : opts.tod ?? map.time.default; // Eternal Night: night whatever was picked
    this.opts = { seed: opts.seed ?? 1234, bossTime: opts.bossTime ?? 300, maxEnemies: opts.maxEnemies ?? 400, godMode: opts.godMode ?? false, autoSkills: opts.autoSkills ?? true, autoTransform: opts.autoTransform ?? false, meta: { ...(opts.meta ?? {}) }, endless: opts.endless ?? null, map: map.id, tod: this.tod,
      mods, pace: opts.pace ?? paceFor(mods), rules: { ...(opts.rules ?? {}) }, training: opts.training ?? null, calm: opts.calm ?? false,
      startSkill: opts.startSkill ?? '', startTrait: opts.startTrait ?? '', banish: [...(opts.banish ?? [])] };
    this.mods = mods; this.pace = this.opts.pace;
    this.hpK = (mods.tough ? TOUGH_HP : 1) * (mods.relaxed ? RELAXED_HP : 1); this.dmgK = (mods.tough ? TOUGH_DMG : 1) * (mods.relaxed ? RELAXED_DMG : 1); this.schedK = mods.hurry ? 1 / HURRY_K : 1;
    this.rng = new Rng(this.opts.seed);
    this.terrain = new Terrain(this.opts.seed, ARENA_R, map.terrain);
    this.fire = new FireGrid(this.opts.seed, ARENA_R, this.terrain, map.fire, map.fireMask); // per-map fire mask (Ember Wastes: ash grass only)
    // hazards: own seeded stream (never perturbs combat randomness)
    this.hazards = new HazardField(map.hazards ? map.hazards({ rng: new Rng((this.opts.seed * 31337 + 101) >>> 0), arenaR: ARENA_R, runLen: this.runLen, props: this.terrain.props }) : []);
    this.bossName = map.boss.name;
    this.loot = new FieldLoot(this.opts.seed, ARENA_R, this.terrain.props);
    this.foes = new MapFoeField({ // MAP FOES: own seeded stream; private helpers lent as closures
      sim: this, arenaR: ARENA_R, hurt: (d) => this.hurtPlayer(d), query: (x, z, r, cb) => this.grid.query(x, z, r, cb),
      dot: (hp) => { if (this.opts.godMode || !(hp > 0)) return; const p = this.player; p.hp -= hp; this.hazardHurtT -= DT; if (this.hazardHurtT <= 0) { this.hazardHurtT = 0.5; p.hurtT = Math.max(p.hurtT, 0.15); this.events.push({ t: 'hazardHurt', dmg: hp * 30 }); } },
      foeDot: (e, d) => { this.curSrc = 'hazard'; this.bSrc = 0; this.damageEnemy(e, d, LAVA_HIT_COLOR, 0, 0, true); },
    }, map.id, this.opts.seed);
    const cap = this.opts.maxEnemies + 8;
    for (let i = 0; i < cap; i++) this.enemies.push(this.blankEnemy());
    for (let i = 0; i < 700; i++) this.projectiles.push({ alive: false, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, dmg: 0, r: 0, life: 0, pierce: 0, comet: false, target: -1, lastHit: -1, hostile: false, kind: 0, out: 0, ret: false, ph: [] });
    for (let i = 0; i < GEM_POOL; i++) this.gems.push({ alive: false, x: 0, y: 0, z: 0, value: 0, kind: 0, pull: false });
    this.grid = new Grid(ARENA_R + 20, 4, cap);
    this.player = {
      x: 0, y: HOVER_Y, z: 0, px: 0, py: HOVER_Y, pz: 0, vx: 0, vz: 0, faceX: 0, faceZ: 1, aimX: 0, aimZ: 1,
      hp: 100, maxHp: 100, iframes: 0, hurtT: 0, dashT: 0, dashCd: 0, dashX: 0, dashZ: 1,
      level: 1, xp: 0, xpNext: xpForLevel(1), ki: 0, transformT: 0, surgeTier: 0,
      beamCd: 2, beamCharge: 0, beamT: 0, beamX: 0, beamZ: 1, beamTX: 0, beamTZ: 1,
    };
    this.weapons.set('bolt', 1);
    this.weapons.set('surge', 1); // owner 2026-11-01: Surge Form is innate; level-ups still rank it up
    // CHALLENGES: extra random starting skills (rank 1), drawn from their own seeded stream so combat randomness is untouched
    for (const id of ruleStartSkills(this.opts.rules, this.opts.seed, this.skillSlots - 1)) this.weapons.set(id, 1);
    this.player.maxHp = this.player.hp = 100 + PU.hp * this.m('core');
    this.rerolls = this.m('relay'); this.bans = this.m('null'); this.revives = this.m('spark') + this.m('ember'); // Last Ember: one extra life
    // SKILL FUSIONS: the behaviours live in src/core/fusions.ts; private helpers are lent as closures
    this.fusion = new FusionField({
      sim: this, query: (x, z, r, cb) => this.grid.query(x, z, r, cb), setSrc: (s) => { this.curSrc = s; },
      blast: (k, c, x, z, r) => this.blast(k, c, x, z, r), endBlast: () => { this.bSrc = 0; },
      crowdSpot: (reach, r, ahead) => this.crowdSpot(reach, r, ahead), countAround: (x, z, r) => this.countAround(x, z, r),
      flameAim: (range, half) => this.flameAim(range, half), arenaR: ARENA_R,
    });
    if (opts.training) { const t = new Training(this, opts.training); this.training = t; this.tw = t.tw; }
    this.powerUpStart(this.opts);
  }

  /** POWER UPS unlocks: starting skill (Opening Gambit), starting trait (Trait Primer), pre-run banishes (Exile Seal) */
  private powerUpStart(opts: SimOptions): void {
    const sk = opts.startSkill as WeaponId | undefined;
    if (this.m('opener') > 0 && sk && Object.prototype.hasOwnProperty.call(WEAPONS, sk) && !WEAPONS[sk].retired && !INNATE.includes(sk) && !this.weapons.has(sk) && this.techSlotsUsed < MAX_WEAPONS)
      this.applyChoice({ kind: 'weapon', id: sk, name: WEAPONS[sk].name, desc: '', level: 1 });
    const tr = opts.startTrait as PassiveId | undefined;
    if (this.m('primer') > 0 && tr && Object.prototype.hasOwnProperty.call(PASSIVES, tr) && !this.passives.has(tr))
      this.applyChoice({ kind: 'passive', id: tr, name: PASSIVES[tr].name, desc: '', level: 1 });
    let n = this.m('exile');
    for (const id of opts.banish ?? []) {
      if (n <= 0) break;
      const known = Object.prototype.hasOwnProperty.call(WEAPONS, id) || Object.prototype.hasOwnProperty.call(PASSIVES, id);
      if (!known || this.banned.has(id) || this.weapons.has(id as WeaponId) || this.passives.has(id as PassiveId)) continue;
      this.banned.add(id); n--;
    }
  }

  private blankEnemy(): Enemy {
    return { alive: false, id: 0, def: ENEMIES.drone, elite: false, lite: 0, era: 0, x: 0, y: 0, z: 0, vx: 0, vz: 0, kx: 0, kz: 0, lift: 0, ky: 0, airT: 0, stagT: 0, hp: 0, maxHp: 0, flash: 0, phase: 0, atkCd: 0, orbCd: 0, scale: 1, mode: 0, modeT: 0, dirX: 0, dirZ: 0, discCd: 0, stunT: 0, pinT: 0, gripId: 0, gripY: 0, form: -1, formT: 0, burnT: 0, burnDps: 0, burnSrc: '', burnGen: 0, burnAcc: 0, chillT: 0, chillK: 0, chargeT: 0, zapCd: 0, edgeT: -1, dummy: 0 };
  }

  // ---------- derived stats ----------
  plv(id: PassiveId): number { return this.passives.get(id) ?? 0; }
  /** Array Workshop rank (persistent meta upgrade). */
  m(id: WorkshopId): number { return this.opts.meta[id] ?? 0; }
  get xpMul(): number { return (1 + PU.xp * this.m('memory')) * (1 + PASSIVE_K.scholar * this.plv('scholar')) * (1 + PASSIVE_K.curseXp * this.plv('curse')) * (this.mods.relaxed ? RELAXED_XP : 1) * this.tw.xp; } // Relaxed: richer motes; TRAINING xp tweak
  /** Shards paid into the persistent wallet when the run ends. Practice (Training / the Basics lesson) earns nothing. */
  get shardPayout(): number { return this.practice ? 0 : applyModBonus(Math.floor((Math.floor(this.shards * (1 + PU.salvage * this.m('salvage'))) + this.endlessBank) * this.shardTraitMul) + this.windfall, this.mods); } // run modifiers add their bonus share
  /** Shard Magnet + Overload (Group D traits): their share of the run's shards and endless milestone bank (not the Shard
   *  Windfall boon, a fixed gift). Additive with each other; the run-modifier bonus multiplies on top in shardPayout. */
  get shardTraitMul(): number { return 1 + PASSIVE_K.greed * this.plv('greed') + PASSIVE_K.curseShard * this.plv('curse'); }
  /** loot luck in Fortune Lens ranks (FieldLoot reads it: chest odds, elite chest chance, field items): the Power Up plus
   *  the Fortune trait (PASSIVE_K.fortuneLuck Fortune Lens ranks per trait rank) */
  get luck(): number { return this.m('luck') + PASSIVE_K.fortuneLuck * this.plv('fortune'); }
  /** Overload (risk trait): foe health / foe hit / foe speed multipliers, on top of difficulty and modifiers (1 at rank 0) */
  get curseHpK(): number { return 1 + PASSIVE_K.curseHp * this.plv('curse'); }
  get curseDmgK(): number { return 1 + PASSIVE_K.curseDmg * this.plv('curse'); }
  get curseSpdK(): number { return 1 + PASSIVE_K.curseSpd * this.plv('curse'); }
  get transformed(): boolean { return this.player.transformT > 0; }
  get surgeTier(): number { return this.player.transformT > 0 ? this.player.surgeTier : 0; }
  /** the run progression the higher Surge forms are gated on (skillStats.surgeMaxTier) */
  get surgeProgress(): SurgeProgress { return { level: this.player.level, bosses: this.bossesDefeated, endless: this.endless, endlessTier: this.endlessTier }; }
  /** Highest Surge form unlocked: I-III by Surge rank (1 / 3 / 5), IV-VIII by Ascendant Form plus run progression (see
   *  skillStats.surgeMaxTier); Training can raise it with surgeFloor */
  get maxSurgeTier(): number { return Math.max(surgeMaxTier(this.wlv('surge'), this.evolved.has('surge'), this.surgeProgress), Math.min(SURGE_TIERS, this.surgeFloor)); }
  /** Ki charges only outside Surge and after the post-Surge cooldown (owner 2026-11-01: 'needs a bit of a cooldown after use') */
  get kiOpen(): boolean { return this.player.transformT <= 0 && this.surgeCdT <= 0; }
  /** seconds left on the post-Surge cooldown (HUD) */
  surgeCdT = 0;
  get kiTierMul(): number { return 1; }
  /** owner 2026-11-01: the bar charges in stages, and each higher level takes longer to fill (100, then 130, then 170) */
  get kiCap(): number { return SURGE_STAGE_END[Math.max(1, this.maxSurgeTier) - 1]; }
  /** levels fully charged right now (what a transform would give) */
  get chargedTier(): number { let n = 0; while (n < this.maxSurgeTier && this.player.ki >= SURGE_STAGE_END[n]) n++; return n; }
  /** 0..1 progress of the stage currently filling (HUD bar) */
  get kiStageFrac(): number {
    const c = this.chargedTier; if (c >= this.maxSurgeTier) return 1;
    const lo = c > 0 ? SURGE_STAGE_END[c - 1] : 0; return Math.max(0, Math.min(1, (this.player.ki - lo) / SURGE_STAGE[c]));
  }
  wlv(id: WeaponId): number { return this.weapons.get(id) ?? 0; }
  /** damage multiplier from the build alone (passives, Workshop, attunements): what the skill cards show */
  get baseDmgMul(): number { return (1 + PASSIVE_K.power * this.plv('power') + this.bonusDmg) * (1 + PU.dmg * this.m('lattice')) * (1 + PASSIVE_K.curseMight * this.plv('curse')); } // Overload: its damage share
  get dmgMul(): number {
    const p = this.player, hpF = p.hp / p.maxHp;
    const last = hpF < LASTSTAND_AT ? PASSIVE_K.laststand * this.plv('laststand') * (1 - hpF / LASTSTAND_AT) : 0;
    return this.baseDmgMul * (this.transformed ? this.surgeDmg * TIER_DMG[this.surgeTier] : 1) * (1 + last) * (1 + this.momentumBonus) * (this.odT > 0 ? this.odDmg : 1) * this.loot.dmgMul * this.apexDmg * this.tw.dmg; // Fervor field item; Apex Current; TRAINING damage tweak
  }
  get odDmg(): number { const l = this.wlv('overdrive'); return this.evolved.has('overdrive') ? 1.6 : 1.35 + (l >= 3 ? 0.1 : 0) + (l >= 5 ? 0.1 : 0); }
  get persistMul(): number { return 1 + PASSIVE_K.persist * this.plv('persist'); }
  /** damage multiplier of a critical hit: ×2, plus Overcharge Strike */
  get critMul(): number { return BASE_CRIT_MUL + PASSIVE_K.critdmg * this.plv('critdmg'); }
  /** Lancet rank (skill mods: pierce counts, or damage on skills that pierce everything) */
  get lancet(): number { return this.plv('lancet'); }
  /** Momentum damage bonus right now (0 .. +8% per rank), eased so it ramps in and out smoothly */
  get momentumBonus(): number { const k = this.momentumK; return PASSIVE_K.momentum * this.plv('momentum') * k * k * (3 - 2 * k); }
  get critChance(): number { return this.tw.crit >= 0 ? this.tw.crit : BASE_CRIT + PASSIVE_K.keen * this.plv('keen') + PU.crit * this.m('edge'); } // Fault Sight
  /** +1 projectile for multi-shot techniques */
  get echo(): number { return this.plv('echo') + this.m('prism'); } // Twin Prism
  // Nova Lance (auto beam) stats by level
  private get lance(): ReturnType<typeof lanceNums> { return lanceNums(this.wlv('lance'), this.evolved.has('lance'), this); }
  get beamDmg(): number { return this.lance.dmgBase; }
  get beamCdMax(): number { return this.lance.cd; }
  get beamWidth(): number { return this.lance.width; }
  get beamDuration(): number { return this.lance.duration; }
  /** Nova Lance charge time (s): BEAM_CHARGE, shortened by Lancecraft (render and audio read this, not the constant) */
  get beamChargeTime(): number { return this.lance.charge; }
  /** Lancecraft rank (SkillMods.lancecraft: lanceNums reads it) */
  get lancecraft(): number { return this.plv('lancecraft'); }
  // Surge Form (auto transform) stats by level
  /** owner 2026-11-01: stronger levels don't last as long — 20 / 15 / 11 s base (current level while transformed) */
  get surgeDuration(): number { return this.surgeDurationFor(Math.max(1, this.player.surgeTier)); }
  surgeDurationFor(tier: number): number { return surgeDurationFor(this.wlv('surge'), this.evolved.has('surge'), tier); }
  get surgeDmg(): number { return surgeDmgMul(this.evolved.has('surge')); }
  get kiGain(): number { return surgeKiGain(this.wlv('surge'), this.evolved.has('surge'), this.plv('resonant')) * (1 + PU.coil * this.m('coil')) * this.tw.ki; } // Resonant Coil
  /** cooldown multiplier from the build alone (passives, Workshop, attunements): what the skill cards show */
  get baseCdMul(): number { return (1 - this.bonusCd) * (1 - PU.cd * this.m('gears')) * Math.pow(PASSIVE_K.haste, this.plv('haste')); }
  get cdMul(): number { return this.baseCdMul * TIER_CD[this.surgeTier] * (this.odT > 0 ? 0.85 : 1) * (this.surgeTier > 0 && surgeOverflow(this.wlv('surge'), this.evolved.has('surge')) ? RESONANCE_OVERFLOW : 1) * this.tw.cd; } // Resonance Overflow (Surge rank 4+); TRAINING cooldown tweak
  /** Demolition (owner 2026-11-03): explosion radius / explosion damage multipliers. Explosive hits only: the explosive
   *  *Nums in skillStats.ts read them through SkillMods, Chain Reaction and Comet Volley below, and fusion detonations
   *  (fusions.ts radial(), KS.explosion only). Beams, lines, slashes and every other area ignore them. */
  get blastMul(): number { return 1 + PASSIVE_K.demoR * this.demoRank; }
  get blastDmgMul(): number { return 1 + PASSIVE_K.demoDmg * this.demoRank; }
  /** Demolition rank, capped at its max (a hand-edited preset or test can't push it past rank 5) */
  private get demoRank(): number { return Math.min(PASSIVES.demolition.max ?? MAX_PASSIVE_LEVEL, this.plv('demolition')); }
  get areaMul(): number { return (1 + PASSIVE_K.focus * this.plv('focus') + this.bonusArea + PU.area * this.m('bloom')) * this.tw.area; } // Wide Bloom
  /** Slipstream passive: projectile speed (Spirit Bolt, Scythe Discs, Singularity, Rift Mortar) */
  get projSpeedMul(): number { return (1 + PASSIVE_K.velocity * this.plv('velocity')) * this.tw.proj; }
  get moveSpeed(): number { return this.pace * BASE_MOVE * (1 + PASSIVE_K.swift * this.plv('swift') + PU.speed * this.m('mesh') + this.bonusSpeed) * TIER_SPEED[this.surgeTier] * (this.odT > 0 ? 1.12 : 1) * this.tw.move; }
  get magnetR(): number { return BASE_MAGNET * (1 + PASSIVE_K.magnet * this.plv('magnet') + PU.pull * this.m('pull')) * this.tw.magnet; }
  /** live foes (training dummies never count) */
  get aliveEnemies(): number { let n = 0; for (const e of this.enemies) if (e.alive && !e.dummy) n++; return n; }

  // ---------- main step ----------
  step(input: InputFrame): void {
    if (this.state !== 'playing') return;
    this.events.length = 0;
    this.tick++;
    this.time += DT;
    const p = this.player;
    p.px = p.x; p.py = p.y; p.pz = p.z;
    this.zapsThisTick = 0; // ELEMENTAL FAMILIES: Static Charge's per-tick arc budget

    this.rebuildGrid();
    this.hazards.update(this.time, DT, this.events); // MAP SYSTEM: hazard timelines first, so moves below see this tick's zones
    this.steer = input.manualAim; // Assisted aim: fire skills follow the aim while the player steers it
    this.updatePlayer(input);
    if (this.tick % 60 === 0) this.sampleDmgRing(); // THE NULL HERALD: damage dealt per second, for its adaptive health
    if (!this.spawnsPaused) this.spawnDirector(); // TRAINING (waves paused) / TUTORIAL (calm lesson): no natural spawning
    this.updateEnemies();
    if (this.foes.active) this.foes.update(); // MAP FOES: marks / patches age, root / push / burn / jamming on the hero
    this.edgePass();
    if (this.bossMarks.length || this.bossShove.t > 0) this.updateBossWorld(); // BOSS FORMS: impact marks land, the burst's shove
    this.rebuildGrid();
    if (this.hazards.size) this.applyHazards();
    this.updateWeapons();
    this.updateBeam();
    this.updateProjectiles();
    this.updateTechniques();
    this.updateFireSkills(); // Fire family (owner 2026-11-02): breath, rings, wyrms, pops, then every burning foe
    this.updateIceSkills(); // ICE FAMILY (owner 2026-11-03): rime cone, spike eruptions, lances, ridges, avalanche rings, frost fields, the veil
    { const e0 = this.events.length; this.fusion.update(); this.fixFusionBlasts(e0); } // SKILL FUSIONS (before the burn tick so fusion-lit foes burn this frame)
    this.updateBurns();
    this.updateGems();
    if (this.tick % GEM_MERGE_EVERY === 0) this.consolidateGems();
    this.loot.update(this, DT);
    this.terrain.update(DT);
    this.fire.update(DT, this.terrain, this.events);
    // Aftershock trait (owner 2026-11-02): every crater dug this tick (by any skill) queues a pulse
    if (this.plv('aftershock')) for (const ev of this.events) if (ev.t === 'crater' && this.shockQ.length < AFTERSHOCK_MAX_PENDING) this.shockQ.push({ x: ev.x, z: ev.z, r: ev.r, t: AFTERSHOCK_DELAY });

    if (this.endless) this.updateEndless(); // ENDLESS GOALS: milestones bank before a death this tick
    if (this.stillT > 0) this.stillT = Math.max(0, this.stillT - DT);
    // SECOND WIND (group C, owner 2026-11-03): the trait's save comes first, then a Power Ups / Workshop revive. One save per
    // tick at most: after either, hp > 0, so the next check can't fire. God mode (Training) never reaches here.
    if (p.hp <= 0 && !this.opts.godMode && this.swUsed < this.plv('secondwind')) this.secondWind();
    if (p.hp <= 0 && !this.opts.godMode && this.revives > 0) {
      this.revives--;
      p.hp = p.maxHp * 0.5; p.iframes = 2.5;
      this.curSrc = 'revive';
      this.blast(KILL_SRC.explosion, 0x9fffd0, p.x, p.z, 10);
      this.radialDamage(p.x, p.y, p.z, 10, 80, true, 0x9fffd0, 0.6);
      this.bSrc = 0;
      this.events.push({ t: 'revive' }, { t: 'explode', x: p.x, y: p.y, z: p.z, r: 10, color: 0x9fffd0 });
    }
    if (p.hp <= 0 && !this.opts.godMode) {
      p.hp = 0;
      this.state = 'dead';
      this.events.push({ t: 'death' });
    }
    this.training?.tick();
  }

  private rebuildGrid(): void {
    this.grid.clear();
    const es = this.enemies;
    for (let i = 0; i < es.length; i++) if (es[i].alive && es[i].edgeT < 0 && es[i].mode >= 0) this.grid.insert(i, es[i].x, es[i].z); // MAP FOES: mode < 0 = underground (out of reach like a foe behind the wall) // EDGE WALLS: foes still outside the wall are invisible to every query (targeting, AoE, chains, homing)
  }
  /** EDGE WALLS: a foe that has come inside the wall (only these can be hit, targeted or killed) */
  inArena(e: Enemy): boolean { return e.edgeT < 0; }

  // ---------- player ----------
  private updatePlayer(inp: InputFrame): void {
    const p = this.player;
    let mx = inp.moveX, mz = inp.moveZ;
    const ml = Math.hypot(mx, mz);
    if (ml > 1) { mx /= ml; mz /= ml; }

    p.iframes = Math.max(0, p.iframes - DT);
    p.hurtT = Math.max(0, p.hurtT - DT);
    p.dashCd = Math.max(0, p.dashCd - DT);
    p.beamCd = Math.max(0, p.beamCd - DT * this.foes.cdRate); // MAP FOES: a Hush Beacon aura slows cooldowns

    // Dash removed (owner, 2026-11-01): inp.dash is ignored; dashT/dashCd stay 0.
    this.odT = Math.max(0, this.odT - DT);
    this.stillFor = ml < 0.1 ? this.stillFor + DT : 0;
    // group A (owner 2026-11-03): Lifedraw's bucket refills at its cap; Momentum builds while the hero actually moves
    const ld = this.plv('lifedraw'); this.drawBudget = ld ? Math.min(PK2.lifedrawCap * ld, this.drawBudget + PK2.lifedrawCap * ld * DT) : 0;
    if (this.plv('momentum')) {
      const moving = ml >= 0.1 && Math.hypot(p.vx, p.vz) > 1.5; // pressing into a wall doesn't count
      this.momentumK = moving ? Math.min(1, this.momentumK + DT / PK2.momentumUp) : Math.max(0, this.momentumK - DT / PK2.momentumDown);
    } else this.momentumK = 0;
    const st = this.plv('still');
    if (st > 0 && this.stillFor > 0.4) {
      this.heal(PASSIVE_K.still * st * DT);
      if (this.kiOpen && this.wlv('surge') > 0) p.ki = Math.min(this.kiCap, p.ki + PASSIVE_K.stillKi * st * DT * this.kiTierMul);
    }

    const spd = this.moveSpeed;
    if (p.dashT > 0) {
      p.dashT -= DT;
      p.vx = p.dashX * 32; p.vz = p.dashZ * 32;
    } else {
      let slow = p.beamT > 0 || p.beamCharge > 0 ? 0.9 : 1; // auto-beam must not bog the player down
      let grip = 1;
      if (this.hazards.live.length) { slow *= this.hazards.moveMul(p.x, p.z, false); grip = 1 - 0.9 * this.hazards.slip(p.x, p.z, false); } // MAP SYSTEM: slow / fog / dark ice
      if (this.foes.active) { slow *= this.foes.heroMul; grip = Math.min(grip, this.foes.heroGrip); } // MAP FOES: spore clouds, roots, frozen ground
      const tx = mx * spd * slow, tz = mz * spd * slow;
      const k = 1 - Math.exp(-DT * 45 * grip); // snappy start/stop (owner: movement felt sluggish) // near-instant acceleration / stopping; slippery on dark ice
      p.vx += (tx - p.vx) * k; p.vz += (tz - p.vz) * k;
    }
    p.x += p.vx * DT; p.z += p.vz * DT;
    this.terrain.collide(p, 0.6);
    const d = Math.hypot(p.x, p.z);
    if (d > ARENA_R) { p.x *= ARENA_R / d; p.z *= ARENA_R / d; }
    if (this.dive.active) this.diveMove(); // Comet Dive (owner 2026-11-02): airborne, the hero follows the dive path

    if (ml > 0.15) { p.faceX = mx / ml; p.faceZ = mz / ml; }

    // altitude
    // grounded hero: walks/runs on the arena floor (no flight); y stays on the combat plane
    p.y = HOVER_Y;

    // aim
    if (inp.manualAim && (inp.aimX !== 0 || inp.aimZ !== 0)) {
      const l = Math.hypot(inp.aimX, inp.aimZ); p.aimX = inp.aimX / l; p.aimZ = inp.aimZ / l;
    } else {
      const t = this.nearestEnemy(p.x, p.z, 24);
      if (t >= 0) {
        const e = this.enemies[t]; const dx = e.x - p.x, dz = e.z - p.z; const l = Math.hypot(dx, dz) || 1;
        p.aimX = dx / l; p.aimZ = dz / l;
      } else { p.aimX = p.faceX; p.aimZ = p.faceZ; }
    }

    // regen
    const regen = PASSIVE_K.regen * this.plv('regen') + PU.regen * this.m('repair') + TIER_REGEN[this.surgeTier] + this.tw.regen;
    this.heal(regen * DT);

    // transform

    const sl = this.wlv('surge');
    // SURGE FORMS: a higher form opened (rank, evolution, level, boss, Endless) → one notice; the bar keeps its Ki
    { const mx = sl > 0 ? this.maxSurgeTier : 0; if (mx > this.lastMaxSurge && this.lastMaxSurge > 0) this.events.push({ t: 'surgeUnlock', tier: mx }); this.lastMaxSurge = mx; }
    // optional auto-transform fires on a bar fully charged to the top unlocked form (as before: I-III wait for the full
    // bar), or — once at least form III is charged and higher forms are open — when the next stage has not completed for
    // AUTO_SURGE_PATIENCE s (eight forms: the full bar can be minutes away in a normal run)
    { const c = this.chargedTier, mx = this.maxSurgeTier; this.kiStallT = c >= Math.min(3, mx) && c < mx && this.kiOpen ? this.kiStallT + DT : 0; }
    const autoTf = this.opts.autoTransform && (p.ki >= this.kiCap || this.kiStallT >= AUTO_SURGE_PATIENCE) && (this.countNear(p.x, p.z, 18, 3) >= 3 || !!(this.boss && this.boss.alive));
    if (sl > 0 && (inp.transform || autoTf || !!this.training?.takeSurge()) && p.ki >= SURGE_STAGE_END[0] && this.kiOpen) {
      // transform into the highest form whose charge is complete, spending the whole bar
      p.surgeTier = Math.max(1, this.chargedTier);
      p.transformT = this.surgeDuration; p.ki = 0; this.batteryAdded = 0; this.kiStallT = 0;
      const tier = p.surgeTier; this.bestSurgeTier = Math.max(this.bestSurgeTier, tier);
      this.events.push({ t: 'transform', tier });
      // owner 2026-11-01: activation only pushes foes back a little — no damage, no launch; each level pushes slightly further
      this.surgePush(p.x, p.z, tier);
    }
    // SURGE BATTERY (owner 2026-11-03: 'should let you continue to gain surge even up to the highest level you have unlocked'):
    // while transformed the bar keeps charging from kills; once it holds a HIGHER form than the current one, Surge (or Auto
    // Surge) steps straight up into it: a fresh duration for the new form, the bar spent, the burst plays again
    if (sl > 0 && p.transformT > 0 && this.plv('battery') > 0 && this.chargedTier > p.surgeTier && (inp.transform || (this.opts.autoTransform && this.chargedTier >= this.maxSurgeTier) || !!this.training?.takeSurge())) {
      p.surgeTier = this.chargedTier; p.transformT = this.surgeDuration; p.ki = 0; this.batteryAdded = 0;
      const tier = p.surgeTier; this.bestSurgeTier = Math.max(this.bestSurgeTier, tier);
      this.events.push({ t: 'transform', tier }); this.surgePush(p.x, p.z, tier);
    }
    if (this.surgeCdT > 0) this.surgeCdT = Math.max(0, this.surgeCdT - DT);
    if (p.transformT > 0) {
      p.transformT -= DT;
      if (p.transformT <= 0) { p.transformT = 0; p.surgeTier = 0; this.surgeCdT = SURGE_COOLDOWN; this.events.push({ t: 'transformEnd' }); }
    }

    // beam special
    if (this.wlv('lance') > 0 && p.beamCd <= 0 && p.beamT <= 0 && p.beamCharge <= 0) {
      let fire = inp.beam || !!this.training?.takeBeam(); // TRAINING: fire now (tapped icon)
      const auto = this.opts.autoSkills && (!this.training || this.training.mayFire('lance')); // TRAINING: Manual fire only
      if (inp.manualAim) {
        p.beamTX = p.aimX; p.beamTZ = p.aimZ;
        if (auto && this.countInLine(p.aimX, p.aimZ) >= 2) fire = true;
      } else if (auto || fire) {
        const hits = this.bestBeamDir();
        // owner 2026-11-03: 'Nova Lance not firing sometimes' — auto only fired into a line of 5+ foes, so with a thin crowd or
        // a lone boss it never fired. Now it still prefers a big line, but once ready for LANCE_WAIT s it fires at the best
        // line it has (any foe in reach)
        this.lanceWaitT = hits > 0 ? this.lanceWaitT + DT : 0;
        if (auto && (hits >= 5 || (hits >= 1 && this.lanceWaitT >= LANCE_WAIT))) fire = true;
      }
      if (fire) {
        if (inp.manualAim) { p.beamTX = p.aimX; p.beamTZ = p.aimZ; }
        p.beamCharge = this.beamChargeTime; p.beamX = p.beamTX; p.beamZ = p.beamTZ; this.lanceWaitT = 0; // Lancecraft shortens the charge
        this.events.push({ t: 'beamStart' });
        this.beamIgnite = this.fire.roll(FIRE_ATTACK_CHANCE); // MAP-FIRE: one 25 % roll per beam (owner 2026-11-02)
      }
    }
  }

  /** auto Nova Lance: seconds the ready lance has waited for a 5-foe line (fires at the best line after LANCE_WAIT) */
  private lanceWaitT = 0;
  private updateBeam(): void {
    const p = this.player;
    if (p.beamCharge > 0) {
      p.beamCharge -= DT;
      p.beamX = p.beamTX; p.beamZ = p.beamTZ;
      if (p.beamCharge <= 0) { p.beamCharge = 0; p.beamT = this.beamDuration; this.events.push({ t: 'beamFire' }); }
      return;
    }
    if (p.beamT <= 0) return;
    // the lance fires dead straight: direction is locked at release (no sweep / no re-aim mid-beam)
    p.beamT -= DT;
    if (p.beamT <= 0) { p.beamT = 0; p.beamCd = this.beamCdMax; }
    if (this.tick % BEAM_TICK !== 0) return;
    const len = BEAM_LEN, w = this.beamWidth;
    const cx = p.x + p.beamX * len / 2, cz = p.z + p.beamZ * len / 2;
    const dmg = this.beamDmg * this.dmgMul;
    this.curSrc = 'lance';
    this.terrain.damageLine(p.x, p.z, p.beamX, p.beamZ, len, w, dmg * 2.5, this.events, PROP_SRC.beam);
    // beams burn a trail through the grass and gouge a crater where they end (grows with Nova Lance rank)
    for (let d = 3; d < len; d += 2.6) this.terrain.addScorch(p.x + p.beamX * d, p.z + p.beamZ * d, w * 1.1, this.tick);
    if (this.tick % 18 === 0) {
      const lv = this.wlv('lance');
      this.terrain.addCrater(p.x + p.beamX * len, p.z + p.beamZ * len, w * (0.9 + 0.12 * lv) * (this.evolved.has('lance') ? 1.3 : 1), this.tick, this.events);
      // MAP-FIRE: the beam sets the grass alight somewhere along its path
      if (this.beamIgnite) { this.beamIgnite = false; const d = 4 + this.fire.rand() * (len - 4); this.fire.ignite(p.x + p.beamX * d, p.z + p.beamZ * d, Math.max(1, w * 0.8), 6 + w, 0); }
    }
    const lanceBig = this.wlv('lance') >= 4 || this.evolved.has('lance'); // rank 4+ / evolved Nova Lance disintegrates
    this.grid.query(cx, cz, len / 2 + w + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const rx = e.x - p.x, rz = e.z - p.z;
      const along = rx * p.beamX + rz * p.beamZ;
      if (along < 0 || along > len) return;
      const perp = Math.abs(rx * -p.beamZ + rz * p.beamX);
      if (perp > w + e.def.radius * e.scale) return;
      { const a = Math.max(0, along - 2); this.blast(KILL_SRC.lance, lanceBig ? 0x8fd8ff : -1, p.x + p.beamX * a, p.z + p.beamZ * a, 0); } // dissolve flakes blow off along the beam
      this.damageEnemy(e, dmg, 0xffe28a, 0, 0);
      this.bSrc = 0;
      if (e.alive) { const side = (rx * -p.beamZ + rz * p.beamX) >= 0 ? 1 : -1; this.impulse(e, p.beamX * 13 - p.beamZ * side * 4, p.beamZ * 13 + p.beamX * side * 4, along < 4 ? 3 : 0); } // 9 -> 13: still drives the faster foes back
    });
  }

  /** Pick the beam direction that crosses the most foes (24 sectors); sets beamTX/TZ, returns weighted hit count. */
  private bestBeamDir(): number {
    const p = this.player;
    const SECT = 24, bins = this.sectorBins; bins.fill(0);
    this.grid.query(p.x, p.z, BEAM_LEN, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - p.x, dz = e.z - p.z; const d = Math.hypot(dx, dz);
      if (d > BEAM_LEN || d < 0.01) return;
      const w = e.def.kind === 'boss' ? 12 : e.elite ? 4 : e.def.kind === 'brute' ? 2 : 1;
      const a = Math.atan2(dz, dx);
      const s = ((Math.round((a / (Math.PI * 2)) * SECT) % SECT) + SECT) % SECT;
      bins[s] += w; bins[(s + 1) % SECT] += w * 0.5; bins[(s + SECT - 1) % SECT] += w * 0.5;
    });
    let best = 0, bi = -1;
    for (let s = 0; s < SECT; s++) if (bins[s] > best) { best = bins[s]; bi = s; }
    if (bi >= 0) { const a = (bi / SECT) * Math.PI * 2; p.beamTX = Math.cos(a); p.beamTZ = Math.sin(a); }
    else { p.beamTX = p.aimX; p.beamTZ = p.aimZ; }
    return best;
  }
  private sectorBins = new Float32Array(24);

  /** Weighted foes inside the beam rectangle along (dx, dz). */
  private countInLine(dx: number, dz: number): number {
    const p = this.player, w = this.beamWidth; let n = 0;
    this.grid.query(p.x + dx * BEAM_LEN / 2, p.z + dz * BEAM_LEN / 2, BEAM_LEN / 2 + w + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const rx = e.x - p.x, rz = e.z - p.z, along = rx * dx + rz * dz;
      if (along < 0 || along > BEAM_LEN || Math.abs(rx * -dz + rz * dx) > w + e.def.radius * e.scale) return;
      n += e.def.kind === 'boss' ? 4 : 1;
    });
    return n;
  }

  /** Count live enemies within r (stops early at cap). */
  countNear(x: number, z: number, r: number, cap: number): number {
    let n = 0; const r2 = r * r;
    this.grid.query(x, z, r, (i) => { const e = this.enemies[i]; if (n < cap && e.alive && (e.x - x) ** 2 + (e.z - z) ** 2 <= r2) n++; });
    return n;
  }

  // ---------- spawning ----------
  private spawnDirector(): void {
    const t = this.schedTime; // Hurry squeezes this whole timeline (run modifiers, owner 2026-11-02)
    const M = this.mods;
    const p = this.player;
    const bt = this.opts.bossTime;
    // owner 2026-11-01: different mini-bosses on long runs (>= 10 min). On a 30-min run: 10:00 Ironhide Colossus,
    // 15:00 Prism Sovereign, 20:00 Tyrant Guardian, 30:00 Hollow Tyrant (final). Shorter long runs use the same fractions.
    if (bt >= 600 && this.guardiansSpawned < MINIBOSSES.length && t >= bt * MINIBOSS_AT[this.guardiansSpawned] && !(this.boss && this.boss.alive)) {
      this.spawnMiniboss(this.guardiansSpawned, t); this.guardiansSpawned++;
    }
    if (!this.bossSpawned && t >= bt) {
      this.bossSpawned = true;
      const a = this.rng.range(0, Math.PI * 2);
      const b = this.spawnEnemy('boss', p.x + Math.cos(a) * 26, p.z + Math.sin(a) * 26, false, this.bossHpMul(t) * (this.map.boss.hpMul ?? 1));
      if (b) { this.boss = b; this.finalBoss = b; this.bossName = this.map.boss.name; this.attachForms(b, this.bossName); this.events.push({ t: 'bossSpawn' }); } // the map's own final boss (map #1: HOLLOW TYRANT)
    }
    if (this.endless && t >= this.nextEndlessGuardian && !(this.boss && this.boss.alive)) {
      this.nextEndlessGuardian = t + 300; // endless: the mini-bosses rotate back every 5 min, tougher each lap
      this.spawnMiniboss(this.endlessRot++ % MINIBOSSES.length, t);
      if (this.endlessTier >= TWIN_GUARDIAN_TIER) this.spawnTwinGuardian(t); // ENDLESS GOALS: from tier III they come in pairs
    }
    this.heraldDirector(t);
    const eraT = eraAt(t, this.endlessTime);
    if (eraT > this.eraNow) { this.eraNow = eraT; this.events.push({ t: 'era', era: eraT, name: eraName(eraT) }); } // ENEMY ERAS: the horde hardens
    // owner 2026-11-02 (VS-style pacing): keep the field near hordeTarget(t), refilling at a limited rate. Bosses halve the
    // pressure; endless keeps climbing; night pressure (map system) scales the target a little.
    const night = nightPressure(this.map, this.clock);
    const ot = this.overtime(t);
    const target = (hordeTarget(t) * (M.swarm ? SWARM_TARGET : 1) + Math.min(1, ot / 180) * this.opts.maxEnemies) * (this.boss && this.boss.alive && !ot ? 0.6 : 1) * night; // Swarm: denser field
    const rate = this.aliveEnemies < target ? (hordeRefill(t) * (M.swarm ? SWARM_REFILL : 1) + (ot ? 25 : 0)) * night : 0;
    const earlyCap = target;
    this.spawnBudget += rate * DT;
    // enemy toughness: +1x per 100 s for the first 5 min (unchanged), then gentler (+1x per 240 s) so a 30-min run stays
    // fair (x10.25 at 30:00); endless keeps climbing (+1x per 120 s after the victory)
    const hpMul = this.enemyHpMul(t);
    // ---- waves: each minute from 1:00 a themed wave pushes in from one side; the last 5 s of a minute is a lull ----
    const waveNow = t >= 60 ? Math.floor(t / 60) : 0;
    if (waveNow > this.wave) {
      this.wave = waveNow;
      const open = WAVES.map((w, i) => (t >= w.from && i !== this.lastWaveTheme ? i : -1)).filter((i) => i >= 0);
      const wi = open.length ? this.rng.pick(open) : 0; this.lastWaveTheme = wi;
      const W = WAVES[wi]; this.waveName = W.name; this.waveKind = W.kind; this.waveDir = this.rng.range(0, Math.PI * 2);
      const n = M.swarm ? Math.min(SWARM_WAVE_MAX, Math.round((6 + waveNow * 3) * SWARM_WAVE)) : Math.min(40, 6 + waveNow * 3); // owner 2026-11-02: smaller waves (3:00 → 15, was 27); Swarm: 1.5x
      for (let k = 0; k < n; k++) {
        const kind = W.kind ?? this.rng.pick(WAVE_MIX.filter((m) => t >= m.from).map((m) => m.kind));
        const a = this.waveDir + this.rng.range(-0.7, 0.7), d = this.rng.range(28, 38);
        this.spawnEnemy(kind, p.x + Math.cos(a) * d, p.z + Math.sin(a) * d, false, hpMul);
      }
      this.events.push({ t: 'wave', n: waveNow, name: W.name });
    }
    if (this.tw.spawn !== 1) this.spawnBudget += rate * DT * (this.tw.spawn - 1); // TRAINING spawn-rate tweak
    const lull = t >= 60 && t % 60 > 55 ? 0.35 : 1;
    this.spawnBudget -= rate * DT * (1 - lull);
    while (this.spawnBudget >= 1 && this.aliveEnemies < earlyCap) {
      this.spawnBudget -= 1;
      const r = this.rng.next();
      const spEra = eraAt(t, this.endlessTime);
      let kind: EnemyKind = 'drone';
      if (spEra > 0) kind = eraKindRoll(r, spEra); // ENEMY ERAS: weakest kinds fade out (same single draw; era 0 keeps the classic cascade below)
      else if (t > 15 && r < 0.22) kind = 'skitter';
      else if (t > 40 && r < 0.3) kind = 'lancer';
      else if (t > 45 && r < 0.38) kind = 'caster';
      else if (t > 60 && r < 0.45) kind = 'bomber';
      else if (t > 70 && r < 0.52) kind = 'brute';
      else if (t > 80 && r < 0.58) kind = 'splitter';
      else if (t > 110 && r < 0.63) kind = 'stomper';
      else if (t > 150 && r < 0.67) kind = 'warden';
      // during a themed wave most spawns are the theme's foe, coming from the wave's side
      if (this.waveKind && t >= 60 && this.rng.next() < 0.6) kind = this.waveKind;
      // fliers arrive in small packs
      if (t > 45 && this.rng.next() < (spEra > 0 ? 0.03 * eraWeakMul(spEra) : 0.03)) {
        const a0 = this.rng.range(0, Math.PI * 2), d0 = this.rng.range(34, 40);
        for (let k = 0; k < 5; k++) this.spawnEnemy('wisp', p.x + Math.cos(a0) * d0 + k * 0.8, p.z + Math.sin(a0) * d0 - k * 0.6, false, hpMul);
      }
      const a = this.wave > 0 && this.rng.next() < 0.55 ? this.waveDir + this.rng.range(-0.9, 0.9) : this.rng.range(0, Math.PI * 2), dist = this.rng.range(28, 36);
      const sx0 = p.x + Math.cos(a) * dist, sz0 = p.z + Math.sin(a) * dist;
      if (this.foes.active) { const extra = this.foes.substitute(t, sx0, sz0, hpMul); if (extra >= 0) { this.spawnBudget -= extra; continue; } } // MAP FOES: this map's own foes take this spawn
      // HORDE ELITES (owner 2026-11-03: 'the screen should be filled up with elites towards the later part of the game'):
      // from 12:00 a growing share of ordinary spawns arrive as elites (lateEliteShare); no rng is drawn before then
      const le = lateEliteShare(t) * (M.relaxed ? 0.5 : 1), horde = le > 0 && this.rng.next() < le;
      const se = this.spawnEnemy(kind, sx0, sz0, horde || (this.tw.elite > 0 && this.rng.next() < this.tw.elite), hpMul); // TRAINING elite-chance tweak (no rng drawn at 0)
      if (se && horde) { se.lite = 1; se.maxHp = se.hp = se.hp * HORDE_ELITE_HP / 9; se.scale = 1.45 * eraSizeMul(se.era); }
      // packs: the horde arrives in clumps, not single file
      if (t > 180 && this.rng.next() < 0.15) for (let k = 0; k < 3; k++) { const pe = this.spawnEnemy(kind, sx0 + this.rng.range(-2.5, 2.5), sz0 + this.rng.range(-2.5, 2.5), horde, hpMul); if (pe && horde) { pe.lite = 1; pe.maxHp = pe.hp = pe.hp * HORDE_ELITE_HP / 9; pe.scale = 1.45 * eraSizeMul(pe.era); } this.spawnBudget -= 0.5; } // a horde-elite spawn brings a horde-elite pack // packs only after 3:00 (owner 2026-11-02)
    }
    this.spawnBudget = Math.min(this.spawnBudget, 3); // never bank a flood while the opening cap holds spawns back
    if (t >= (M.elites ? EARLY_ELITE_FROM : ELITE_FROM) && t - this.lastElite >= (ot ? Math.max(4, 12 - ot / 30) : Math.max(15, 40 - Math.max(0, t - 300) / 60)) * (M.elites ? EARLY_ELITE_GAP : 1) && (t < this.opts.bossTime || this.endless)) { // Early Elites: from 1:00, gaps x0.6 // elites from 1:30 (owner 2026-11-02) // elites come faster as the run goes on
      this.lastElite = t;
      const kinds: EnemyKind[] = t > 150 ? ['warden', 'stomper', 'lancer', 'splitter'] : t > 90 ? ['brute', 'caster', 'lancer', 'splitter'] : ['drone', 'skitter', 'lancer'];
      const a = this.rng.range(0, Math.PI * 2);
      if (this.spawnEnemy(this.rng.pick(kinds), p.x + Math.cos(a) * 24, p.z + Math.sin(a) * 24, true, hpMul)) this.events.push({ t: 'elite' });
    }
    if (t > 120 && t - this.lastSwarm >= 60 && (t < this.opts.bossTime || this.endless)) { // owner 2026-11-02: from 2:00, once a minute, growing
      this.lastSwarm = t;
      const n = M.swarm ? Math.min(SWARM_RING_MAX, Math.round((12 + Math.floor(t / 60) * 3) * SWARM_RING)) : Math.min(48 + Math.max(0, Math.floor(t / 60) - 15) * 6, 12 + Math.floor(t / 60) * 3); // owner 2026-11-03: rings keep growing past 15:00 (cap 48 -> 138 at 30:00)
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        this.spawnEnemy(t > 120 ? 'skitter' : 'drone', p.x + Math.cos(a) * 22, p.z + Math.sin(a) * 22, false, hpMul);
      }
      this.events.push({ t: 'swarm' });
    }
  }

  private spawnMiniboss(i: number, t: number): void {
    const mb = MINIBOSSES[i], p = this.player, a = this.rng.range(0, Math.PI * 2);
    const hp = this.bossHpMul(t) * mb.hp * (this.endless ? 1.3 : 1);
    const e = mb.kind === 'boss'
      ? this.spawnEnemy('boss', p.x + Math.cos(a) * 26, p.z + Math.sin(a) * 26, false, hp)
      : this.spawnEnemy(mb.kind, p.x + Math.cos(a) * 24, p.z + Math.sin(a) * 24, true, 1, true);
    if (!e) return;
    e.era = 0; // guardians are not era foes
    if (mb.kind !== 'boss') { e.scale = mb.scale; e.maxHp = e.hp = ENEMIES.boss.hp * hp; }
    this.boss = e; this.bossName = mb.name; this.attachForms(e, mb.name);
    this.events.push({ t: 'guardianSpawn', n: i + 1, name: mb.name });
  }

  /** owner 2026-11-01: "enemies need to keep getting much more difficult, a constant threat". Unchanged to 5:00 (x4), then
   *  accelerating: x9.8 @10, x16.7 @15, x24.3 @20, x41 @30. Endless keeps adding on top. */
  enemyHpMul(t: number): number {
    const base = (t <= 300 ? 1 + t / 100 : 4 * Math.pow(1 + (t - 300) / 300, 1.3)) * firstWaveHpK(t);
    return base * overtimeHpK(this.overtime(t)) * this.hpK * this.curseHpK; // Tougher Foes / Relaxed; Overload
  }
  /** enemy hits scale with time too: x1.6 @5, x2.25 @10, x3.5 @20, x4.75 @30; then overtime (owner 2026-11-02) */
  enemyDmgMul(t: number): number { return (1 + t / 480) * (1 + this.overtime(t) / OVERTIME_DMG_S) * this.dmgK * this.curseDmgK; } // Tougher Foes / Relaxed; Overload
  /** enemies get quicker: +30% by 18 min, up to +55% deep into overtime */
  enemySpeedMul(t: number): number { return (1 + Math.min(0.3, t / 3600) + Math.min(0.25, this.overtime(t) / 600)) * this.curseSpdK; } // Overload (the ENEMY_SPEED_CAP still holds)
  /** owner 2026-11-02: 'after the 30-minute mark everything should get insanely more difficult … nearly unkillable, but it
   *  needs to be killable in order to survive' — seconds past the final-boss time on long (endless) runs, else 0 */
  overtime(t: number): number { return this.opts.bossTime >= 600 && (this.endless || this.bossSpawned) ? Math.max(0, t - this.opts.bossTime) : 0; }
  /** bosses follow the same curve relative to the 5-min boss */
  bossHpMul(t: number): number { return Math.max(this.hpK * this.curseHpK, this.enemyHpMul(t) / 4); }
  /** OVERTIME BITE (owner 2026-11-03, 'each fusion needs to keep up in endless'): the share of a foe's health that was grown
   *  past the 30-minute mark (0 before overtime, 1/2 at +2:30, 15/16 at +10:00). Skill Fusions strip a percentage of it on
   *  their signature hits (FUSIONS[id].n.bite), so their kill rate stays scale-free while foe health doubles every 150 s. */
  get lateK(): number { const ot = this.overtime(this.schedTime); return ot > 0 ? 1 - 1 / overtimeHpK(ot) : 0; }
  /** extra damage of a hit that strips pct (0.02 = 2%) of this foe's overtime-grown health; bosses / guardians feel 20% of
   *  it and elites 50% (the same ladder as fusion freezes), so the endless escalation keeps its weight */
  lateBite(e: Enemy, pct: number): number {
    if (!(pct > 0)) return 0;
    const k = this.lateK; if (k <= 0) return 0;
    return pct * e.maxHp * k * (e.def.kind === 'boss' || e === this.boss ? LATE_BITE_BOSS : e.elite ? LATE_BITE_ELITE : 1);
  }

  spawnEnemy(kind: EnemyKind, x: number, z: number, elite: boolean, hpMul = 1, force = false): Enemy | null {
    const d = Math.hypot(x, z);
    // EDGE WALLS: bosses / guardians always arrive inside; anything else asked for past the wall appears just behind it and
    // walks in (out of reach until it comes through)
    if (kind === 'boss' || force) { const lim = ARENA_R - EDGE_BOSS_IN; if (d > lim) { x *= lim / d; z *= lim / d; } }
    else if (d > ARENA_R) { x *= EDGE_SPAWN_R / d; z *= EDGE_SPAWN_R / d; }
    let slot: Enemy | null = null;
    const limit = kind === 'boss' || force ? this.enemies.length : this.tw.maxE > 0 ? Math.min(this.tw.maxE, this.opts.maxEnemies) : this.opts.maxEnemies; // bosses/mini-bosses ignore the horde cap; TRAINING field cap
    let alive = 0;
    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i];
      if (e.alive) { if (!e.dummy || force) alive++; continue; }
      if (!slot) slot = e;
    }
    if (!slot || (alive >= limit)) return null;
    const def = ENEMIES[kind];
    const e = slot;
    e.alive = true; e.id = this.nextId++; e.def = def; e.elite = elite; e.lite = 0;
    e.era = kind === 'boss' || kind === 'herald' ? 0 : eraAt(this.schedTime, this.endlessTime); // ENEMY ERAS: stamped at birth
    e.x = x; e.z = z; e.y = kind === 'boss' ? 2.6 : HOVER_Y; e.vx = 0; e.vz = 0; e.kx = 0; e.kz = 0; e.lift = 0; e.ky = 0; e.airT = 0;
    e.maxHp = e.hp = def.hp * hpMul * (elite ? 9 : 1) * this.tw.ehp; // bosses pass bossHpMul explicitly (default 1); TRAINING enemy-HP tweak
    e.dummy = 0;
    e.flash = 0; e.phase = this.rng.range(0, 6.28); e.atkCd = this.rng.range(1, 3); e.orbCd = 0;
    e.scale = (elite ? 1.7 : 1) * eraSizeMul(e.era); e.mode = 0; e.modeT = 3; e.dirX = 0; e.dirZ = 0; e.stunT = 0; e.stagT = 0;
    e.pinT = 0; e.gripId = 0; e.gripY = 0; // Skills B
    e.form = -1; e.formT = 0; // BOSS FORMS (bosses get theirs from attachForms)
    e.burnT = 0; e.burnDps = 0; e.burnSrc = ''; e.burnGen = 0; e.burnAcc = 0; // BURN
    e.chillT = 0; e.chillK = 0; e.chargeT = 0; e.zapCd = 0; // ELEMENTAL FAMILIES
    e.edgeT = Math.hypot(x, z) > ARENA_R ? 0 : -1; // EDGE WALLS: spawned behind the wall = walking in
    return e;
  }

  /** EDGE WALLS (after the foes moved): foes inside stay inside (knockback / shoves stop at the wall face and lose their
   *  outward push); foes outside walk in, and one still outside after EDGE_STUCK_T is set down just inside. */
  private edgePass(): void {
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const d = Math.hypot(e.x, e.z); if (d < 1e-6) continue;
      const ux = e.x / d, uz = e.z / d;
      if (e.edgeT < 0) {
        if (d > ARENA_R) {
          e.x = ux * ARENA_R; e.z = uz * ARENA_R;
          const kr = e.kx * ux + e.kz * uz; if (kr > 0) { e.kx -= kr * ux; e.kz -= kr * uz; }
          const vr = e.vx * ux + e.vz * uz; if (vr > 0) { e.vx -= vr * ux; e.vz -= vr * uz; }
        }
        continue;
      }
      if (d <= ARENA_R) { e.edgeT = -1; continue; }
      e.edgeT += DT;
      const nd = d - EDGE_WALK_IN * DT;
      if (e.edgeT >= EDGE_STUCK_T || nd <= ARENA_R) { const r = Math.min(nd, ARENA_R - 0.3); e.x = ux * r; e.z = uz * r; e.edgeT = -1; continue; }
      e.x = ux * nd; e.z = uz * nd;
    }
  }

  // ---------- enemies ----------
  private updateEnemies(): void {
    const p = this.player;
    const es = this.enemies;
    const g = this.grid, gd = g.dim, gh = g.half, gc = g.cell, gHead = g.head, gNext = g.next;
    for (let i = 0; i < es.length; i++) {
      const e = es[i];
      if (!e.alive) continue;
      // ELEMENTAL FAMILIES (owner 2026-11-03): the chill / charged timers and the Static Charge spark cooldown run on every foe (dummies too)
      if (e.chillT > 0) { e.chillT -= DT; if (e.chillT <= 1e-9) { e.chillT = 0; e.chillK = 0; } }
      if (e.chargeT > 0) e.chargeT -= DT;
      if (e.zapCd > 0) e.zapCd -= DT;
      if (e.dummy) { this.training?.updateDummy(e); continue; } // TRAINING: dummies have no AI
      e.flash = Math.max(0, e.flash - DT);
      if (e.stagT < 0) e.stagT = Math.min(0, e.stagT + DT); // Heavy Hands re-arm
      e.phase += DT;
      const dx = p.x - e.x, dz = p.z - e.z;
      const dist = Math.hypot(dx, dz) || 0.001;
      const nx = dx / dist, nz = dz / dist;
      // the warm-up keeps the old gentle pace, then foes ramp up to the full run speed over the next 30 s
      const runK = 1 + (ENEMY_RUN_MUL - 1) * Math.min(1, Math.max(0, (this.time - WARMUP_END) / 30));
      let spd = Math.min(e.era ? Math.min(ERA_SPEED_CAP, ENEMY_SPEED_CAP * eraSpeedMul(e.era)) : ENEMY_SPEED_CAP, e.def.speed * ENEMY_SPEED_MUL * runK * this.enemySpeedMul(this.schedTime) * (e.elite ? 1.1 : 1) * eraSpeedMul(e.era)) * this.pace * (this.stillT > 0 ? 0.25 : 1) * this.tw.espd; // pace: cap scales with it (still just under the hero); TRAINING speed tweak
      // TRAINING: frozen foes stand still and never attack (a launched foe still lands; a transforming boss finishes its beat)
      if (this.training?.frozen && !(e.lift > 0 || e.ky > 0) && !(e.form >= 0 && e.formT > 0)) { e.vx = e.vz = 0; e.kx *= 0.8; e.kz *= 0.8; e.x += e.kx * DT; e.z += e.kz * DT; if (e.def.kind === 'boss') { e.mode = 0; e.modeT = Math.max(e.modeT, 1); } continue; }

      if (e.def.kind === 'boss') { this.updateBoss(e, nx, nz, dist); continue; }
      if (e.def.kind === 'herald') { this.updateHerald(e, nx, nz, dist); continue; }
      if (e.mode < 0) { this.foes.buried(e, dist); continue; } // MAP FOES: underground (src/core/mapFoes.ts)
      if (e.form >= 0) { const st = this.bossSt.get(e.id); if (st) { // BOSS FORMS: guardians on family AI (beat, overlay attacks, pace)
        if (e.formT > 0) { transformStep(e, st, this.bossHost); continue; }
        bossOverlay(e, st, this.bossHost, nx, nz); spd *= formOf(st, e).speed; } }
      if (e.gripId || e.pinT > 0) { this.restrain(e); continue; } // Skills B: pinned / gripped foes can't move or attack
      if (e.lift > 0 || e.ky > 0) { // launched: fly the arc, can't steer or attack until it lands
        e.ky -= KNOCK_GRAVITY * DT; e.lift += e.ky * DT; e.airT += DT;
        e.x += e.kx * DT; e.z += e.kz * DT; e.kx *= 0.985; e.kz *= 0.985;
        if (e.lift <= 0) { e.lift = 0; e.ky = 0; e.airT = 0; e.kx *= 0.45; e.kz *= 0.45; this.events.push({ t: 'land', x: e.x, z: e.z, r: e.def.radius * e.scale }); }
        const ad = Math.hypot(e.x, e.z); if (ad > ARENA_R) { e.x *= ARENA_R / ad; e.z *= ARENA_R / ad; }
        continue;
      }
      if (e.stunT > 0) { e.stunT -= DT; e.vx *= 0.8; e.vz *= 0.8; e.x += e.kx * DT; e.z += e.kz * DT; e.kx *= 0.88; e.kz *= 0.88; continue; }
      if (e.stagT > 0) { e.stagT -= DT; if (e.stagT <= 0) e.stagT = -HH_REARM; e.vx *= 0.8; e.vz *= 0.8; e.x += e.kx * DT; e.z += e.kz * DT; e.kx *= 0.88; e.kz *= 0.88; continue; } // Heavy Hands stagger

      let tx = nx, tz = nz;
      // Mirage Lure: nearby foes chase the after-image instead of you
      for (const l of this.lures) {
        const lx = l.x - e.x, lz = l.z - e.z, ld = Math.hypot(lx, lz);
        if (ld < l.r) { tx = lx / (ld || 1); tz = lz / (ld || 1); break; }
      }
      // archetype behaviours (all melee)
      switch (e.def.kind) {
        case 'lancer': {
          e.atkCd -= DT; e.modeT -= DT;
          // pace (owner 2026-11-02): the wind-up lasts 1 / pace as long; the dash is pace x as fast for 1 / pace as long (same reach)
          if (e.mode === 0 && e.atkCd <= 0 && dist < 13) { e.mode = 1; e.modeT = 0.75 / this.pace; e.dirX = nx; e.dirZ = nz; this.events.push({ t: 'charge', x: e.x, z: e.z }); }
          if (e.mode === 1) { spd = 0; if (e.modeT <= 0) { e.mode = 2; e.modeT = 0.45 / this.pace; } }
          else if (e.mode === 2) { tx = e.dirX; tz = e.dirZ; spd = 21 * this.pace; e.vx = e.dirX * spd; e.vz = e.dirZ * spd; if (e.modeT <= 0) { e.mode = 3; e.modeT = 0.9; } }
          else if (e.mode === 3) { spd *= 0.3; if (e.modeT <= 0) { e.mode = 0; e.atkCd = (3 + this.rng.next()) * eraCdMul(e.era); } }
          break;
        }
        case 'stomper': {
          e.atkCd -= DT; e.modeT -= DT;
          if (e.mode === 0 && e.atkCd <= 0 && dist < 5.5) { e.mode = 1; e.modeT = 0.9 / this.pace; } // pace: slower wind-up
          if (e.mode === 1) {
            spd = 0;
            if (e.modeT <= 0) {
              const R = 4.5 * e.scale;
              if (dist < R && e.edgeT < 0) this.hurtPlayer(20 * (e.elite ? 1.5 : 1) * eraDmgMul(e.era));
              this.events.push({ t: 'stomp', x: e.x, z: e.z, r: R });
              e.mode = 0; e.atkCd = 3.5 * eraCdMul(e.era);
            }
          }
          break;
        }
        case 'bomber':
          if (dist < e.def.radius * e.scale + 0.9 && e.edgeT < 0) {
            this.hurtPlayer(e.def.damage * (e.elite ? 1.6 : 1) * eraDmgMul(e.era));
            this.events.push({ t: 'bomberPop', x: e.x, z: e.z }, { t: 'explode', x: e.x, y: e.y, z: e.z, r: 3, color: 0xffa020 });
            e.alive = false; // pops itself: no reward
            continue;
          }
          break;
        default:
          if (e.def.map) { const o = this.foes.think(e, tx, tz, dist, spd); if (o.skip) continue; tx = o.tx; tz = o.tz; spd = o.spd; } // MAP FOES
      }
      // normal foes are melee-only (owner 2026-11-01); casters chase like the rest, only the boss attacks at range
      // separation (neighbours)
      let sx = 0, sz = 0;
      const rr = e.def.radius * e.scale;
      { // PERF-STRESS (2026-11-02): Grid.query inlined (same cells, same order, same arithmetic — bit-identical; see
        // tests/stressDeterminism.test.ts) so the hottest loop of the sim allocates no closure per foe per step
        const qr = rr + 1.2, ex = e.x, ez = e.z;
        const gx0 = Math.max(0, Math.floor((ex - qr + gh) / gc)), gx1 = Math.min(gd - 1, Math.floor((ex + qr + gh) / gc));
        const gz0 = Math.max(0, Math.floor((ez - qr + gh) / gc)), gz1 = Math.min(gd - 1, Math.floor((ez + qr + gh) / gc));
        for (let cz = gz0; cz <= gz1; cz++) for (let cx = gx0; cx <= gx1; cx++) for (let j = gHead[cz * gd + cx]; j !== -1; j = gNext[j]) {
          if (j === i) continue;
          const o = es[j]; if (!o.alive) continue;
          const ox = e.x - o.x, oz = e.z - o.z; const d2 = ox * ox + oz * oz;
          const min = rr + o.def.radius * o.scale;
          if (d2 < min * min && d2 > 1e-6) {
            const d = Math.sqrt(d2); const push = (min - d) / min;
            const w = o.def.mass / (o.def.mass + e.def.mass);
            sx += (ox / d) * push * w * 2; sz += (oz / d) * push * w * 2;
          }
        }
      }
      let k = 1 - Math.exp(-DT * 5);
      let zone = this.stillT > 0 ? 0.25 : 1; // ELEMENTAL FAMILIES: the slow this foe's position / Still Moment already applied (chill never doubles it)
      if (this.hazards.live.length) { const hm = this.hazards.moveMul(e.x, e.z, true); spd *= hm; zone *= hm; k = 1 - Math.exp(-DT * 5 * (1 - 0.6 * this.hazards.slip(e.x, e.z, true))); } // MAP SYSTEM: same hazards as the hero
      if (this.foes.zones.length) { const fm = this.foes.moveMul(e.x, e.z); spd *= fm; zone *= fm; const sl = this.foes.slip(e.x, e.z); if (sl > 0) k = 1 - Math.exp(-DT * 5 * (1 - 0.6 * sl)); } // MAP FOES: spore clouds / frozen ground hit foes too
      // ELEMENTAL FAMILIES (owner 2026-11-03): with a frost trait held, slow zones feed the chill status (Deep Chill then makes
      // them harder and longer, Frostbite counts the foe as chilled); the hardest slow wins, a chill never stacks on a zone
      if (zone < 1 && this.frostOn) this.chill(e, 1 - zone, CHILL_ZONE_TAIL);
      if (e.chillT > 0 && zone > 1e-6) spd *= Math.min(zone, 1 - e.chillK) / zone;
      e.vx += (tx * spd - e.vx) * k; e.vz += (tz * spd - e.vz) * k;
      e.x += (e.vx + sx * 4 + e.kx) * DT; e.z += (e.vz + sz * 4 + e.kz) * DT;
      if ((e.id + this.tick) % 2 === 0) this.terrain.collide(e, e.def.radius * e.scale);
      e.kx *= KNOCK_FRICTION; e.kz *= KNOCK_FRICTION;
      // altitude follow
      const ty = Math.max(HOVER_Y * 0.8, p.y);
      e.y += (ty - e.y) * (1 - Math.exp(-DT * 1.8));

      // contact damage
      if (e.edgeT < 0 && Math.abs(e.y - p.y) < 1.6 && dist < rr + 0.55) { // EDGE WALLS: a foe outside the wall can't touch the hero
        this.hurtPlayer(e.def.damage * (e.elite ? 1.5 : 1) * eraDmgMul(e.era));
        const th = this.plv('thorns');
        if (th > 0 && (this.tick + e.id) % THORNS_GAP === 0) { this.curSrc = 'thorns'; this.damageEnemy(e, PASSIVE_K.thorns * th * this.dmgMul, 0x9fff9f, -nx * 3, -nz * 3); }
      }
    }
  }

  // ---------- BOSS FORMS (owner 2026-11-02: every boss transforms twice mid-fight; data + brain in ./bossForms) ----------
  /** live impact marks ("rain" attacks): render draws their telegraphs, they land after their warning */
  bossMarks: BossMark[] = [];
  private bossSt = new Map<number, BossState>();
  /** the burst's shove on the hero: unit direction, speed (m/s), seconds left */
  private bossShove = { x: 0, z: 0, v: 0, t: 0 };
  private _bossHost: BossHost | null = null;
  private get bossHost(): BossHost {
    if (this._bossHost) return this._bossHost;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const sim = this;
    this._bossHost = {
      get rng() { return sim.rng; }, get tick() { return sim.tick; }, get time() { return sim.time; },
      get speedK() { return ENEMY_SPEED_MUL * sim.pace * (sim.stillT > 0 ? 0.5 : 1) * sim.tw.espd; }, get pace() { return sim.pace; }, // run pace (mods.ts); TRAINING speed tweak
      get player() { return sim.player; }, get events() { return sim.events; }, get marks() { return sim.bossMarks; },
      fire: (x, y, z, vx, vz, d) => sim.fireHostile(x, y, z, vx, vz, d),
      spawn: (k, x, z) => { const c = sim.inside(x, z, ARENA_R - 0.3); sim.spawnEnemy(k, c.x, c.z, false, (1 + sim.schedTime / 100) * sim.hpK * sim.curseHpK); }, // (Hurry timeline, Tougher Foes: as the Tyrant's summon)
      hurt: (d) => sim.hurtPlayer(d),
      shockwave: (b) => sim.formShockwave(b),
      impact: (x, z, r) => { sim.terrain.damageCircle(x, z, r + 1, 1500, sim.events, PROP_SRC.crush); sim.terrain.addCrater(x, z, Math.min(MAX_CRATER_R, r), sim.tick, sim.events); },
    };
    return this._bossHost;
  }
  /** give a freshly spawned boss / guardian its three forms (health trimmed by FORM_HP_MUL: see bossForms.ts) */
  private attachForms(e: Enemy, name: string): void {
    e.form = 0; e.formT = 0; e.maxHp *= FORM_HP_MUL; e.hp = e.maxHp;
    this.bossSt.set(e.id, newBossState(name));
  }
  /** the damage a boss with forms takes from a blow of d (0 while transforming); crossing a gate starts the transformation */
  private bossGate(e: Enemy, d: number): number {
    const st = this.bossSt.get(e.id); if (!st) return d;
    const g = gateDamage(e.hp, e.maxHp, e.form, e.formT, d);
    if (g.cross) startTransform(e, st, this.bossHost);
    return g.d;
  }
  /** the burst: foes thrown back, the hero shoved (no damage), the ground dented, props broken */
  private formShockwave(b: BossBody): void {
    this.grid.query(b.x, b.z, SHOCK_R, (i) => {
      const o = this.enemies[i]; if (!o.alive || o.id === b.id || this.bossLike(o)) return;
      const dx = o.x - b.x, dz = o.z - b.z, d = Math.hypot(dx, dz); if (d > SHOCK_R || d < 1e-6) return;
      const k = 1 - d / SHOCK_R; this.impulse(o, (dx / d) * SHOCK_PUSH * k, (dz / d) * SHOCK_PUSH * k, SHOCK_UP * k);
    });
    const p = this.player, dx = p.x - b.x, dz = p.z - b.z, d = Math.hypot(dx, dz);
    if (d < SHOVE_R) { const ux = d > 1e-6 ? dx / d : 0, uz = d > 1e-6 ? dz / d : 1; this.bossShove = { x: ux, z: uz, v: (SHOVE_DIST * (1 - d / SHOVE_R)) / SHOVE_T, t: SHOVE_T }; }
    this.terrain.damageCircle(b.x, b.z, 8, 2500, this.events, PROP_SRC.crush);
    this.terrain.addCrater(b.x, b.z, FORM_CRATER_R, this.tick, this.events);
  }
  private updateBossWorld(): void {
    if (this.bossMarks.length) updateMarks(this.bossHost);
    const s = this.bossShove;
    if (s.t > 0) {
      const p = this.player, step = Math.min(s.t, DT) * s.v; s.t -= DT;
      p.x += s.x * step; p.z += s.z * step; this.terrain.collide(p, 0.6);
      const d = Math.hypot(p.x, p.z); if (d > ARENA_R) { p.x *= ARENA_R / d; p.z *= ARENA_R / d; }
    }
  }
  /** a boss's form (HUD, render, codex): encounter name, form index, the form, every form; null for foes without forms */
  bossFormInfo(e: Enemy | null): { boss: string; form: number; name: string; accent: number; forms: readonly { name: string; accent: number }[]; transforming: number; slamR: number } | null {
    if (!e || e.form < 0) return null;
    const st = this.bossSt.get(e.id); if (!st) return null;
    const f = formOf(st, e);
    return { boss: st.name, form: e.form, name: f.name, accent: f.accent, forms: st.set.forms, transforming: e.formT, slamR: st.atk && st.atk.k === 'slam' ? st.atk.r : 0 };
  }
  /** overlay guardians: seconds left on the telegraph of their next overlay attack (render wind-up), else 0 */
  bossOverlayTele(e: Enemy): number { return e.form > 0 ? this.bossSt.get(e.id)?.otele ?? 0 : 0; }

  // ---------- THE NULL HERALD (src/core/herald.ts; owner 2026-11-03) ----------
  /** once a second: how much damage the hero dealt in it (the sum of dmgBySrc grew by that much) */
  private sampleDmgRing(): void {
    let total = 0; for (const k in this.dmgBySrc) total += this.dmgBySrc[k];
    const i = (this.tick / 60) % HERALD_WINDOW;
    this.dmgRing[i] = Math.max(0, total - this.dmgRingTotal); this.dmgRingTotal = total;
    if (this.dmgRingFill < HERALD_WINDOW) this.dmgRingFill++;
  }
  /** damage per second the hero dealt over the last HERALD_WINDOW s */
  get recentDps(): number { let s = 0; for (let i = 0; i < HERALD_WINDOW; i++) s += this.dmgRing[i]; return s / Math.max(1, this.dmgRingFill); }
  private heraldAlive(): number { let n = 0; for (const e of this.enemies) if (e.alive && e.def.kind === 'herald') n++; return n; }
  /** first Herald at bossTime + 60 (scheduled s), then one every 180; never more than 2 alive; none in Training */
  private heraldDirector(t: number): void {
    const bt = this.opts.bossTime;
    if (bt < 600 || !this.bossSpawned || this.training) return;
    if (this.nextHeraldAt < 0) this.nextHeraldAt = bt + HERALD_FIRST;
    if (t < this.nextHeraldAt || this.heraldAlive() >= HERALD_MAX_ALIVE) return;
    const p = this.player, a = this.rng.range(0, Math.PI * 2);
    const e = this.spawnEnemy('herald', p.x + Math.cos(a) * HERALD_SPAWN_R, p.z + Math.sin(a) * HERALD_SPAWN_R, false, 1, true);
    if (!e) return; // no free slot: try again next tick
    const n = this.heraldN++;
    e.scale = HERALD_SCALE; e.y = HOVER_Y;
    e.maxHp = e.hp = Math.max(ENEMIES.herald.hp * this.enemyHpMul(t) * HERALD_BASE_K, this.recentDps * HERALD_DPS_SECONDS); // adaptive: it outlasts what you have been dealing
    this.heralds.set(e.id, { n, touchCd: HERALD_TOUCH_CD, slamCd: 2, slamT: 0, sx: 0, sz: 0 });
    this.nextHeraldAt = t > this.nextHeraldAt + HERALD_GAP ? t + HERALD_GAP : this.nextHeraldAt + HERALD_GAP;
    this.events.push({ t: 'heraldSpawn', n });
  }
  /** a Herald hit: a share of the hero's MAX HP that ignores every reduction, never above HERALD_HIT_CAP (Relaxed: x0.7, later ones hit harder) */
  private heraldHit(frac: number, n: number): void {
    const p = this.player, k = heraldDmgK(n) * (this.mods.relaxed ? HERALD_RELAXED : 1);
    this.hurtPlayer(Math.min(frac * k, HERALD_HIT_CAP) * p.maxHp, true);
  }
  private updateHerald(e: Enemy, nx: number, nz: number, dist: number): void {
    const p = this.player;
    let st = this.heralds.get(e.id);
    if (!st) { st = { n: 0, touchCd: HERALD_TOUCH_CD, slamCd: 2, slamT: 0, sx: 0, sz: 0 }; this.heralds.set(e.id, st); } // (summoned outside the director, e.g. Training)
    // immune to every control: whatever a skill set this tick is wiped (the sources also skip it: impulse / freeze / chill / grips / pulls)
    e.stunT = 0; e.pinT = 0; e.gripId = 0; e.gripY = 0; e.stagT = 0; e.chillT = 0; e.chillK = 0; e.lift = 0; e.ky = 0; e.airT = 0; e.kx = 0; e.kz = 0;
    // slam: telegraph at where the hero stood, then strike that spot
    st.slamCd -= DT;
    if (st.slamT > 0) {
      st.slamT -= DT;
      if (st.slamT <= 0) {
        st.slamT = 0;
        this.events.push({ t: 'heraldSlam', x: st.sx, z: st.sz, r: HERALD_SLAM_R, phase: 'hit' });
        if (Math.hypot(p.x - st.sx, p.z - st.sz) <= HERALD_SLAM_R) this.heraldHit(HERALD_SLAM, st.n);
      }
    } else if (st.slamCd <= 0 && dist < HERALD_SLAM_RANGE) {
      st.slamCd = HERALD_SLAM_CD; st.slamT = HERALD_SLAM_TELL; st.sx = p.x; st.sz = p.z;
      this.events.push({ t: 'heraldSlam', x: st.sx, z: st.sz, r: HERALD_SLAM_R, phase: 'tell' });
    }
    // chase at the hero's BASE move speed (no Surge speed, no enemy speed cap): Surge / dashing outruns it
    const spd = st.slamT > 0 ? 0 : BASE_MOVE * this.pace * heraldSpdK(st.n) * (this.stillT > 0 ? 0.25 : 1);
    e.vx = nx * spd; e.vz = nz * spd;
    e.x += e.vx * DT; e.z += e.vz * DT;
    if ((e.id + this.tick) % 2 === 0) this.terrain.collide(e, e.def.radius * e.scale);
    e.y += (Math.max(HOVER_Y * 0.8, p.y) - e.y) * (1 - Math.exp(-DT * 1.8));
    // touch: every second while in contact (respects i-frames and god mode through hurtPlayer)
    st.touchCd -= DT;
    if (st.touchCd <= 0 && e.edgeT < 0 && Math.abs(e.y - p.y) < 2.4 && dist < e.def.radius * e.scale + 0.55 && p.iframes <= 0) { st.touchCd = HERALD_TOUCH_CD; this.heraldHit(HERALD_TOUCH, st.n); }
  }

  private updateBoss(e: Enemy, nx: number, nz: number, dist: number): void {
    const p = this.player;
    const st = e.form >= 0 ? this.bossSt.get(e.id) : undefined; // BOSS FORMS
    if (st && e.formT > 0) { transformStep(e, st, this.bossHost); return; } // the transformation beat: holds still, can't be hurt
    if (e.pinT > 0) { e.pinT = Math.max(0, e.pinT - DT); e.vx *= 0.5; e.vz *= 0.5; e.kx = e.kz = 0; e.flash = Math.max(0, e.flash - DT); return; } // Skills B: Binding Rings (short hold on bosses)
    e.modeT -= DT;
    e.y += (Math.max(2.6, p.y) - e.y) * (1 - Math.exp(-DT * 1.5));
    const enraged = e.hp < e.maxHp * 0.4;
    if (st) bossBrain(e, st, this.bossHost, nx, nz); // BOSS FORMS: the form's attack list (form 1 = the Tyrant kit below)
    else switch (e.mode) {
      case 0: { // chase
        let s = e.def.speed * ENEMY_SPEED_MUL * (enraged ? 1.3 : 1) * this.pace * (this.stillT > 0 ? 0.5 : 1);
        if (e.chillT > 0) s *= 1 - e.chillK; // ELEMENTAL FAMILIES: a chilled boss crawls (its chill is CHILL_BOSS of a foe's)
        e.vx += (nx * s - e.vx) * 0.08; e.vz += (nz * s - e.vz) * 0.08;
        if (e.modeT <= 0) {
          const r = this.rng.int(3);
          e.mode = r + 1; e.modeT = (r === 0 ? 0.9 : r === 1 ? 0.6 : 0.7) / this.pace; // pace: telegraphs last 1 / pace as long
          e.dirX = nx; e.dirZ = nz;
          this.events.push({ t: 'bossTele', kind: r === 0 ? 'charge' : r === 1 ? 'ring' : 'summon' });
        }
        break;
      }
      case 1: // charge telegraph
        e.vx *= 0.85; e.vz *= 0.85; e.dirX = nx; e.dirZ = nz;
        if (e.modeT <= 0) { e.mode = 4; e.modeT = 0.75 / this.pace; } // pace: same charge reach at pace x the speed
        break;
      case 4: // charging
        e.vx = e.dirX * 30 * this.pace; e.vz = e.dirZ * 30 * this.pace;
        if (this.tick % 4 === 0) this.events.push({ t: 'explode', x: e.x, y: 0.2, z: e.z, r: 1.6, color: 0xff4060 });
        if (e.modeT <= 0) { e.mode = 0; e.modeT = enraged ? 2.6 : 4; }
        break;
      case 2: // ring burst
        e.vx *= 0.9; e.vz *= 0.9;
        if (e.modeT <= 0) {
          const n = enraged ? 28 : 20; const off = this.rng.next();
          for (let i = 0; i < n; i++) { const a = ((i + off) / n) * Math.PI * 2; this.fireHostile(e.x, e.y, e.z, Math.cos(a) * 12 * this.pace, Math.sin(a) * 12 * this.pace, 16); }
          this.events.push({ t: 'explode', x: e.x, y: e.y, z: e.z, r: 4, color: 0xff3af0 });
          e.mode = 0; e.modeT = enraged ? 2.4 : 3.6;
        }
        break;
      case 3: // summon
        e.vx *= 0.9; e.vz *= 0.9;
        if (e.modeT <= 0) {
          for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2, c = this.inside(e.x + Math.cos(a) * 4, e.z + Math.sin(a) * 4, ARENA_R - 0.3); this.spawnEnemy(i % 2 ? 'skitter' : 'drone', c.x, c.z, false, (1 + this.schedTime / 100) * this.hpK * this.curseHpK); }
          this.events.push({ t: 'explode', x: e.x, y: e.y, z: e.z, r: 5, color: 0xa040ff });
          e.mode = 0; e.modeT = 3.5;
        }
        break;
    }
    e.x += (e.vx + e.kx) * DT; e.z += (e.vz + e.kz) * DT; e.kx *= 0.8; e.kz *= 0.8;
    // the Tyrant tramples through trees, rocks and spires
    if (this.tick % 6 === 0) this.terrain.damageCircle(e.x, e.z, e.def.radius * e.scale * 1.6 + 1, e.mode === 4 ? 600 : 160, this.events, PROP_SRC.crush);
    const d = Math.hypot(e.x, e.z); if (d > ARENA_R) { e.x *= ARENA_R / d; e.z *= ARENA_R / d; }
    e.flash = Math.max(0, e.flash - DT);
    if (Math.abs(e.y - p.y) < 3 && dist < e.def.radius + 0.6) this.hurtPlayer((e.mode === 4 ? 30 : e.def.damage) * (st ? formOf(st, e).contact : 1));
  }

  /** Prism Ward absorbs one hit; the last charge (or every charge when evolved) shatters outward */
  private wardAbsorb(): void {
    { const e0 = this.events.length, own = this.fusion.absorb(); this.fixFusionBlasts(e0); if (own) return; } // SKILL FUSIONS: Prism Nova owns the dome
    const p = this.player, lvl = this.wlv('ward'), evo = this.evolved.has('ward');
    this.wardCharges--; p.iframes = 0.45;
    this.events.push({ t: 'wardHit', left: this.wardCharges });
    if (this.wardCharges > 0 && !evo) return;
    const { R, dmg } = wardNums(lvl, evo, this);
    this.curSrc = 'ward';
    this.radialDamage(p.x, HOVER_Y, p.z, R, dmg, true, 0xbfeaff, 0, 1.4, 0.6);
    this.events.push({ t: 'wardBreak', x: p.x, z: p.z, r: R, evolved: evo });
  }

  // ---------- map hazards (MAP SYSTEM) ----------
  /** Burn and crush on the hero and the foes alike (slow / slip / fog act inside the movement code). */
  private applyHazards(): void {
    const H = this.hazards, p = this.player;
    if (H.live.length) {
      const pb = H.burn(p.x, p.z, false);
      if (pb > 0 && !this.opts.godMode) {
        p.hp -= pb * DT;
        this.hazardHurtT -= DT; if (this.hazardHurtT <= 0) { this.hazardHurtT = 0.5; p.hurtT = Math.max(p.hurtT, 0.15); this.events.push({ t: 'hazardHurt', dmg: pb * 0.5 }); }
      }
      for (const z of H.live) {
        if (z.kind !== 'burn' || z.who === 'player') continue;
        this.forZone(z, (e) => {
          const boss = this.bossLike(e);
          const hd = e.maxHp * HAZARD_ENEMY_BURN * z.power * DT * (boss ? HAZARD_BOSS_BURN : 1) * this.kindleDmg; // Kindling (owner 2026-11-02)
          e.hp -= e.form >= 0 ? this.bossGate(e, hd) : hd; // BOSS FORMS: phase gates
          if (e.hp <= 0) { this.curSrc = 'hazard'; this.bSrc = 0; this.killEnemy(e); }
        });
      }
    }
    for (const z of H.fired) this.crush(z);
  }
  /** visit live foes inside a zone */
  private forZone(z: HazardZone, fn: (e: Enemy) => void): void {
    const visit = (e: Enemy) => { if (e.alive && e.edgeT < 0 && !e.dummy && HazardField.contains(z, e.x, e.z, e.def.radius * e.scale * 0.5)) fn(e); }; // (TRAINING: hazards skip dummies)
    if (z.global) { for (const e of this.enemies) if (e.alive) visit(e); return; }
    this.grid.query(z.x, z.z, HazardField.reach(z) + 3, (i) => visit(this.enemies[i]));
  }
  /** a collapsing set piece lands: hurts the hero (telegraphed, fixed damage), crushes regular foes, dents bosses, breaks props */
  private crush(z: HazardZone): void {
    const p = this.player;
    if (z.who !== 'enemies' && HazardField.contains(z, p.x, p.z, 0.4) && !this.opts.godMode && p.iframes <= 0) {
      if (this.wardCharges > 0) this.wardAbsorb();
      else { const d = Math.max(1, z.power * TIER_ARMOR[this.surgeTier]); p.hp -= d; p.iframes = 0.55; p.hurtT = 0.3; this.events.push({ t: 'hurt', dmg: d }); }
    }
    if (z.who !== 'player') this.forZone(z, (e) => {
      this.curSrc = 'hazard'; this.bSrc = 0;
      if (this.bossLike(e)) { e.hp -= this.bossGate(e, e.maxHp * HAZARD_BOSS_CRUSH); e.flash = 0.2; if (e.hp <= 0) this.killEnemy(e); } else this.killEnemy(e);
    });
    if (z.global) return;
    if (z.l) {
      const c = Math.cos(z.rot ?? 0), s = Math.sin(z.rot ?? 0);
      this.terrain.damageLine(z.x - c * z.l, z.z - s * z.l, c, s, z.l * 2, z.w ?? 1, 5000, this.events, PROP_SRC.crush);
      if (z.crater !== false) this.terrain.addCrater(z.x, z.z, Math.min(MAX_CRATER_R, (z.w ?? 1) * 1.2), this.tick, this.events);
    } else {
      this.terrain.damageCircle(z.x, z.z, z.r, 5000, this.events, PROP_SRC.crush);
      if (z.crater !== false) this.terrain.addCrater(z.x, z.z, Math.min(MAX_CRATER_R, z.r * 0.6), this.tick, this.events);
    }
  }

  /** Second Wind: set health (not a heal, so No Healing allows it), safety, and a knockback burst (Heavy Hands boosts it) */
  private secondWind(): void {
    const p = this.player, lv = this.plv('secondwind');
    this.swUsed++;
    p.hp = Math.max(1, p.maxHp * (lv >= 2 ? PK2.swHp2 : PK2.swHp1)); p.iframes = Math.max(p.iframes, PK2.swSafe); p.hurtT = 0;
    const R = PK2.swR * this.areaMul;
    this.curSrc = 'secondwind';
    this.blast(KILL_SRC.explosion, 0xfff0b0, p.x, p.z, R);
    this.radialDamage(p.x, HOVER_Y, p.z, R, PK2.swDmg * this.dmgMul, true, 0xfff0b0, 0, 1.6, 0.6);
    this.bSrc = 0;
    this.events.push({ t: 'secondWind', x: p.x, z: p.z, r: R, left: lv - this.swUsed });
  }

  /** every heal goes through here; the No Healing run modifier turns them all off (a Reserve Spark revive is not a heal) */
  private heal(n: number): void {
    if (this.mods.noheal || !(n > 0)) return;
    const p = this.player; p.hp = Math.min(p.maxHp, p.hp + n);
  }

  /** QA/balance: enemy hits that reached the player (counted even in god mode, before i-frames) */
  threatHits = 0;
  private hurtPlayer(dmg: number, bypass = false): void { // bypass (THE NULL HERALD): the damage is a final share of max HP: no time / endless scaling, armour, Surge reduction or plating
    const p = this.player;
    if (!this.tw.attack) return; // TRAINING: passive foes
    dmg *= this.tw.edmg; // TRAINING enemy-damage tweak
    if (p.iframes <= 0) this.threatHits++;
    if (p.iframes > 0 || this.opts.godMode) return;
    if (this.wardCharges > 0) { this.wardAbsorb(); return; } // Prism Ward eats the hit
    if (this.rng.next() < PASSIVE_K.sidestep * this.plv('sidestep')) { p.iframes = 0.3; this.events.push({ t: 'dodge' }); return; }
    if (!bypass) { dmg *= this.enemyDmgMul(this.schedTime); if (this.endless) dmg *= 1 + Math.max(0, this.time - this.victoryAt) / 600; } // endless: hits get harder too
    const actual = bypass ? dmg : Math.max(1, dmg * TIER_ARMOR[this.surgeTier] * this.apexArmor * (1 - PU.ward * this.m('ward')) - PASSIVE_K.plating * this.plv('plating') - PU.plate * this.m('plate')); // Bastion Plating
    p.hp -= actual; p.iframes = 0.55; p.hurtT = 0.3;
    this.events.push({ t: 'hurt', dmg: actual });
  }

  private fireHostile(x: number, y: number, z: number, vx: number, vz: number, dmg: number): void {
    if (!this.tw.attack) return; // TRAINING: passive foes
    const pr = this.allocProjectile(); if (!pr) return;
    pr.x = x; pr.y = y; pr.z = z; pr.vx = vx; pr.vy = 0; pr.vz = vz; pr.dmg = dmg; pr.r = 0.45; pr.life = 4 / this.pace; // callers pass paced speeds: same reach
    pr.pierce = 0; pr.comet = false; pr.target = -1; pr.lastHit = -1; pr.hostile = true; pr.kind = 0; pr.out = 0; pr.ret = false;
  }

  // ---------- weapons ----------
  private updateWeapons(): void {
    const p = this.player;
    for (const [id, lvl] of this.weapons) {
      const evo = this.evolved.has(id);
      this.curSrc = id;
      if (id === 'orbit') { this.updateOrbit(lvl, evo); continue; }
      if (id === 'lance' || id === 'surge') continue; // handled by updatePlayer/updateBeam
      if (id === 'mortar' && this.mortar.charging) { this.chargeMortar(lvl, evo); continue; }
      if (this.training && !this.training.mayFire(id)) { this.wTimers[id] = Math.max(this.wTimers[id], 0.05); } // TRAINING: Manual fire only holds the cast
      this.wTimers[id] -= DT * this.foes.cdRate; // MAP FOES: a Hush Beacon aura slows cooldowns
      if (this.wTimers[id] > 0) continue;
      if (id === 'disc') { this.fireDiscs(lvl, evo); continue; }
      if (id === 'mortar') { this.fireMortar(lvl, evo); continue; }
      if (this.fireTechnique(id, lvl, evo)) continue;
      if (id === 'bolt') {
        const { cd, count, dmg } = boltNums(lvl, evo, this);
        const manual = this.aimMode === 'manual';
        const targets = manual ? this.coneTargets(24, count) : this.nearestN(p.x, p.z, BOLT_RANGE, count);
        if (targets.length === 0 && !manual) { this.wTimers[id] = 0.15; continue; }
        this.wTimers[id] = cd;
        // owner 2026-11-01: higher ranks fire their bolts in quick succession (1, 2, 3…) instead of all at once — the
        // first leaves now, the rest follow every 5 ticks (~0.083 s), each at the next target
        this.boltBurst = { left: count, k: 0, tids: targets.map((i) => this.enemies[i].id), dmg, evo, lvl, wait: 0 };
        this.fireBurstBolt();
      } else if (id === 'nova') {
        const { cd, r, dmg, knock, lift } = novaNums(lvl, evo, this); // cd already includes the evolved 0.55x
        this.wTimers[id] = cd;
        // owner 2026-11-01: the nova always pushes foes back (mostly a ground shove, little launch); rank 5 / evolved push harder
        if (lvl >= 5 || evo) this.blast(KILL_SRC.explosion, evo ? 0xffc040 : 0x7fe8ff, p.x, p.z, r); // Cataclysm Ring / rank 5 blows foes apart
        const hits = this.radialDamage(p.x, p.y, p.z, r, dmg, true, 0x7fe8ff, 0, knock, lift);
        this.bSrc = 0;
        if (evo) this.heal(hits * NOVA_EVO_HEAL);
        this.events.push({ t: 'nova', x: p.x, y: p.y, z: p.z, r, evolved: evo });
        const nb = novaBurst(r, dmg, this); this.novaBursts.push({ t: NOVA_BURST_DELAY, x: p.x, y: p.y, z: p.z, r: nb.r, dmg: nb.dmg, evo });
      } else if (id === 'arc') {
        const { cd, chains, dmg, forks, sky, skyR, skyDmg } = arcNums(lvl, evo, this);
        this.wTimers[id] = cd;
        // Storm Lattice SKY STRIKE (owner 2026-11-03): now and then a heavy bolt from above hits the densest foe in reach
        // first, and the forks leap out from where it landed (seeded roll + pity: deterministic)
        let ox = p.x, oy = p.y, oz = p.z;
        if (sky > 0) {
          this.arcSkyMiss++;
          if (this.arcSkyMiss >= ARC_SKY_PITY || this.rng.next() < sky) {
            const t = this.skyStrike(skyR, skyDmg);
            if (t) { this.arcSkyMiss = 0; ox = t.x; oy = t.y; oz = t.z; }
          }
        }
        this.arcIgnite = evo ? dmg : 0; // Storm Lattice: foes struck more than once catch fire (owner 2026-11-03)
        for (let f = 0; f < forks; f++) this.chainArc(ox, oy, oz, chains, dmg, f);
        this.arcIgnite = 0;
      }
    }
  }

  /** Scythe Disc: angular blades that fly out and return, hitting each foe once per pass. */
  private fireDiscs(lvl: number, evo: boolean): void {
    const p = this.player;
    const ds = discNums(lvl, evo, this);
    this.wTimers.disc = ds.cd;
    const count = ds.count;
    let dx = p.aimX, dz = p.aimZ;
    if (this.aimMode !== 'manual') {
      const t = this.nearestEnemy(p.x, p.z, 20);
      if (t < 0) { this.wTimers.disc = 0.2; return; }
      const e = this.enemies[t]; dx = e.x - p.x; dz = e.z - p.z;
    }
    const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
    const sp = ds.speed;
    const range = ds.range;
    for (let k = 0; k < count; k++) {
      const pr = this.allocProjectile(); if (!pr) break;
      // owner 2026-11-02: 'thrown like a boomerang, thrown out and curves around hitting everything in its path' — each disc
      // flies a teardrop loop (out along one side, swings round at the far end, back along the other side to the hero).
      // Several discs fan out and alternate which side they curve to; evolved, they loop out in every direction.
      const a = evo ? (k / count) * Math.PI * 2 : (k - (count - 1) / 2) * 0.35;
      const c = Math.cos(a), s = Math.sin(a), side = evo || k % 2 === 0 ? 1 : -1;
      const ux = dx * c - dz * s, uz = dx * s + dz * c;
      pr.x = p.x; pr.y = p.y; pr.z = p.z;
      pr.vx = ux * sp; pr.vz = uz * sp; pr.vy = 0;
      pr.dmg = ds.dmg;
      pr.r = ds.r;
      pr.bx = p.x; pr.bz = p.z; pr.bdx = ux; pr.bdz = uz; pr.bL = range; pr.bW = range * DISC_BULGE * side;
      pr.bT = range * DISC_PATH_K / sp; pr.bt = 0;
      pr.life = pr.bT + 1; pr.pierce = 9999; pr.comet = false; pr.target = -1; pr.lastHit = -1; pr.hostile = false;
      pr.kind = 2; pr.out = range; pr.ret = false;
    }
    this.events.push({ t: 'disc', dx, dz });
  }

  /** Nearest foes inside a cone around the player's aim (manual aim mode). */
  private coneTargets(maxR: number, n: number): number[] {
    const p = this.player, out: { i: number; d: number }[] = [];
    this.grid.query(p.x, p.z, maxR, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - p.x, dz = e.z - p.z, d = Math.hypot(dx, dz);
      if (d > maxR || d < 0.01) return;
      if ((dx * p.aimX + dz * p.aimZ) / d < 0.9) return; // ~25 degrees
      out.push({ i, d });
    });
    out.sort((a, b) => a.d - b.d);
    return out.slice(0, n).map((o) => o.i);
  }

  /** Storm Lattice casts since the last Sky Strike (the pity counter) */
  private arcSkyMiss = 0;
  /** Storm Lattice SKY STRIKE: the foe in Chain Arc reach with the most foes within r of it (up to 24 sampled, nearest
   *  first) is struck from above: dmg to everything within r (CHARGED by Conductor), a mild outward shove; emits
   *  'skyStrike'. Returns the struck foe, or null when nothing is in reach. */
  private skyStrike(r: number, dmg: number): Enemy | null {
    const p = this.player, reach = ARC_RANGE * this.areaMul, cand: { i: number; d: number }[] = [];
    this.grid.query(p.x, p.z, reach, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const d = (e.x - p.x) ** 2 + (e.z - p.z) ** 2; if (d < reach * reach) cand.push({ i, d });
    });
    if (cand.length === 0) return null;
    cand.sort((a, b) => a.d - b.d || a.i - b.i);
    const step = Math.max(1, Math.ceil(cand.length / 24));
    let best = cand[0].i, bestN = -1;
    for (let k = 0; k < cand.length; k += step) {
      const c = this.enemies[cand[k].i]; let n = 0;
      this.grid.query(c.x, c.z, r, (j) => { const o = this.enemies[j]; if (o.alive && (o.x - c.x) ** 2 + (o.z - c.z) ** 2 <= r * r) n++; });
      if (n > bestN) { bestN = n; best = cand[k].i; }
    }
    const t = this.enemies[best], x = t.x, z = t.z;
    let hits = 0;
    this.grid.query(x, z, r + 2, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - x, dz = e.z - z, d = Math.hypot(dx, dz);
      if (d > r + e.def.radius * e.scale) return;
      hits++;
      this.bigHit = true; this.damageEnemy(e, dmg, 0xd8ccff, 0, 0); this.bigHit = false;
      this.chargeHit(e);
      if (e.alive && d > 0.01) { const f = Math.max(0.2, 1 - d / r) * 7; this.impulse(e, dx / d * f, dz / d * f, 2.5 * f / 7); }
    });
    this.events.push({ t: 'skyStrike', x, y: t.y, z, r, hits });
    return t;
  }

  /** Storm Lattice fire: the per-jump damage while an evolved cast is chaining (0 otherwise), and when each foe was last struck */
  private arcIgnite = 0;
  private readonly arcStruck = new Map<number, { n: number; t: number }>();
  private chainArc(x: number, y: number, z: number, chains: number, dmg: number, skip: number): void {
    if (this.arcStruck.size > 600) for (const [id, h] of this.arcStruck) if (this.time - h.t > ARC_FIRE_WINDOW) this.arcStruck.delete(id); // stale entries
    const hit = new Set<number>();
    const pts: number[] = [x, y, z];
    let cx = x, cz = z, range = ARC_RANGE * this.areaMul;
    let first = true;
    for (let c = 0; c < chains; c++) {
      let best = -1, bd = range * range, rank = 0;
      // for forks, the first jump skips to the Nth nearest
      const cand: { i: number; d: number }[] = [];
      this.grid.query(cx, cz, range, (i) => {
        const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
        const d = (e.x - cx) ** 2 + (e.z - cz) ** 2; if (d < bd) cand.push({ i, d });
      });
      if (cand.length === 0) break;
      cand.sort((a, b) => a.d - b.d);
      rank = first ? Math.min(skip, cand.length - 1) : 0;
      best = cand[rank].i;
      first = false;
      const e = this.enemies[best];
      hit.add(e.id);
      pts.push(e.x, e.y, e.z);
      this.damageEnemy(e, dmg, 0x9fd8ff, 0, 0);
      this.chargeHit(e); // Conductor (owner 2026-11-03): struck by lightning → CHARGED
      if (this.arcIgnite > 0 && e.alive) { // STORM LATTICE (owner 2026-11-03: 'trigger fires on the enemies that it hits multiple times')
        const h = this.arcStruck.get(e.id), now = this.time, n = h && now - h.t <= ARC_FIRE_WINDOW ? h.n + 1 : 1;
        this.arcStruck.set(e.id, { n, t: now });
        if (n >= 2) this.burn(e, this.arcIgnite * ARC_FIRE_DPS, ARC_FIRE_DUR, 'arc');
      }
      cx = e.x; cz = e.z; range = ARC_JUMP * this.areaMul;
    }
    if (pts.length > 3) this.events.push({ t: 'arc', pts });
    if (pts.length > 3 && this.fire.roll(0.05)) this.fire.ignite(cx, cz, 1, 5, 0); // MAP-FIRE: a lightning strike can light the grass
  }

  private updateOrbit(lvl: number, evo: boolean): void {
    const p = this.player;
    const { n, radius, spin, dmg, orbR } = orbitNums(lvl, evo, this);
    this.orbitAngle += spin * DT;
    this.orbs.length = n;
    for (let k = 0; k < n; k++) {
      const a = this.orbitAngle + (k / n) * Math.PI * 2;
      const o = this.orbs[k] ?? (this.orbs[k] = { x: 0, y: 0, z: 0 });
      o.x = p.x + Math.cos(a) * radius; o.z = p.z + Math.sin(a) * radius; o.y = p.y + Math.sin(a * 2) * 0.3;
      this.grid.query(o.x, o.z, orbR + 1.5, (i) => {
        const e = this.enemies[i]; if (!e.alive || e.orbCd > this.time) return;
        const rr = orbR + e.def.radius * e.scale;
        if ((e.x - o.x) ** 2 + (e.z - o.z) ** 2 < rr * rr && Math.abs(e.y - o.y) < 2) {
          e.orbCd = this.time + ORBIT_REHIT;
          const dx = e.x - p.x, dz = e.z - p.z, l = Math.hypot(dx, dz) || 1;
          this.damageEnemy(e, dmg, 0xffb347, (dx / l) * 6, (dz / l) * 6);
        }
      });
    }
  }

  /** Rift Mortar sphere radius (m) per rank: grows with rank and evolution (numbers in skillStats.ts). */
  mortarSize(lvl: number, evo: boolean): number { return mortarNums(lvl, evo, this).size; }

  /** Rift Mortar (owner 2026-11-01): gather a blazing sphere in both hands for a short charge, then fire it straight
   *  ahead along the aim; it bursts on the first foe it touches (or at max range) for area damage. */
  private fireMortar(lvl: number, evo: boolean): void {
    const p = this.player;
    // only start charging with something to shoot at (auto aim); manual aim always fires where the player points
    if (this.aimMode !== 'manual' && this.nearestEnemy(p.x, p.z, MORTAR_RANGE) < 0) { this.wTimers.mortar = 0.3; return; }
    const m = this.mortar;
    m.charging = true; m.t = 0; m.dur = mortarNums(lvl, evo, this).charge; m.size = this.mortarSize(lvl, evo); // P1 'Ember Core Cannon' spec: ~0.55 s charge
    this.wTimers.mortar = 1e9; // re-armed when the sphere is fired
    this.events.push({ t: 'mortarCharge', dur: m.dur, size: m.size });
  }

  private chargeMortar(lvl: number, evo: boolean): void {
    const m = this.mortar, p = this.player;
    m.t += DT;
    if (m.t < m.dur) return;
    m.charging = false;
    // the cooldown covers the charge, so the cycle length matches the old shell volley
    const s = mortarNums(lvl, evo, this);
    this.wTimers.mortar = Math.max(0.2, s.cd - m.dur);
    const dmg = s.dmg;
    const r = s.blast, size = m.size;
    let dx = p.aimX, dz = p.aimZ; const l = Math.hypot(dx, dz);
    if (l < 1e-6) { dx = p.faceX; dz = p.faceZ; } else { dx /= l; dz /= l; }
    // Echo trait: extra spheres fan out a little either side
    const n = s.count;
    for (let k = 0; k < n; k++) {
      const a = (k - (n - 1) / 2) * 0.22, c = Math.cos(a), s = Math.sin(a);
      const fx = dx * c - dz * s, fz = dx * s + dz * c;
      const pr = this.allocProjectile(); if (!pr) break;
      pr.x = p.x + fx * 0.9; pr.y = p.y + 0.35; pr.z = p.z + fz * 0.9;
      const ms = MORTAR_SPEED * this.projSpeedMul * this.pace; // same range, faster flight with Slipstream
      pr.vx = fx * ms; pr.vy = 0; pr.vz = fz * ms;
      pr.life = MORTAR_RANGE / ms; pr.dmg = dmg; pr.r = r; pr.out = size;
      pr.comet = evo; pr.pierce = 0; pr.target = -1; pr.lastHit = -1; pr.hostile = false; pr.kind = 7; pr.ret = false;
    }
    this.events.push({ t: 'mortar', x: p.x, y: p.y, z: p.z, dx, dz, size });
  }

  private updateMortarBall(pr: Projectile): void {
    pr.x += pr.vx * DT; pr.z += pr.vz * DT; pr.life -= DT;
    let boom = pr.life <= 0;
    if (!boom) {
      this.grid.query(pr.x, pr.z, pr.out + 2.5, (i) => {
        if (boom) return;
        const e = this.enemies[i]; if (!e.alive) return;
        const rr = pr.out + e.def.radius * e.scale;
        if ((e.x - pr.x) ** 2 + (e.z - pr.z) ** 2 < rr * rr) boom = true;
      });
    }
    if (!boom) return;
    pr.alive = false;
    this.curSrc = 'mortar';
    const col = pr.comet ? 0x9aff5a : 0x46e05a; // green (owner 2026-11-02) at every Surge level
    const craterK = 0.5 + 0.08 * this.wlv('mortar') + (pr.comet ? 0.15 : 0);
    this.blast(KILL_SRC.explosion, col, pr.x, pr.z, pr.r);
    this.radialDamage(pr.x, HOVER_Y, pr.z, pr.r, pr.dmg, true, col, craterK);
    this.bSrc = 0;
    this.events.push({ t: 'mortarImpact', x: pr.x, z: pr.z, r: pr.r, evo: pr.comet, color: col });
    if (pr.comet) { // Starfall Barrage: the burst throws three cluster bomblets
      for (let k = 0; k < MORTAR_BOMBLETS; k++) {
        const a = (k / MORTAR_BOMBLETS) * Math.PI * 2 + this.rng.next() * 1.2, d = 3 + this.rng.next() * 2.5;
        this.launchShell(pr.x, HOVER_Y + 0.3, pr.z, pr.x + Math.cos(a) * d, pr.z + Math.sin(a) * d, 0.4, pr.dmg * MORTAR_BOMBLET_DMG, pr.r * MORTAR_BOMBLET_R, true, true);
      }
    }
  }

  private launchShell(x: number, y: number, z: number, tx: number, tz: number, T: number, dmg: number, r: number, evo: boolean, bomblet: boolean): void {
    const pr = this.allocProjectile(); if (!pr) return;
    pr.x = x; pr.y = y; pr.z = z; pr.vx = (tx - x) / T; pr.vz = (tz - z) / T;
    pr.vy = (SHELL_G * T) / 2 - (y - HOVER_Y) / T; // lands on the combat plane after T seconds
    pr.life = T; pr.dmg = dmg; pr.r = r; pr.comet = evo; pr.pierce = bomblet ? 1 : 0;
    pr.target = -1; pr.lastHit = -1; pr.hostile = false; pr.kind = 3; pr.out = 0; pr.ret = false;
  }

  private updateShell(pr: Projectile): void {
    pr.x += pr.vx * DT; pr.z += pr.vz * DT; pr.y += pr.vy * DT; pr.vy -= SHELL_G * DT;
    pr.life -= DT;
    if (pr.life > 0) return;
    pr.alive = false;
    this.curSrc = 'mortar';
    const col = pr.comet ? 0xffb040 : 0xff7a40;
    // crater grows with Rift Mortar rank (and evolution); bomblets leave small ones
    const craterK = (0.5 + 0.08 * this.wlv('mortar') + (pr.comet ? 0.15 : 0)) * (pr.pierce === 1 ? 0.7 : 1);
    this.blast(KILL_SRC.explosion, col, pr.x, pr.z, pr.r);
    this.radialDamage(pr.x, HOVER_Y, pr.z, pr.r, pr.dmg, true, col, craterK);
    this.bSrc = 0;
    this.events.push({ t: 'explode', x: pr.x, y: HOVER_Y, z: pr.z, r: pr.r, color: col });
    if (pr.comet && pr.pierce === 0) {
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2 + this.rng.next() * 1.2, d = 3 + this.rng.next() * 2.5;
        this.launchShell(pr.x, HOVER_Y + 0.3, pr.z, pr.x + Math.cos(a) * d, pr.z + Math.sin(a) * d, 0.4, pr.dmg * 0.55, pr.r * 0.7, true, true);
      }
    }
  }

  // ================= original technique families =================
  /** Dispatch for the newer techniques. Returns true when handled. */
  private fireTechnique(id: WeaponId, lvl: number, evo: boolean): boolean {
    const p = this.player, cd = this.cdMul;
    switch (id) {
      case 'zenith': {
        if (this.zenith.charging) return true;
        const near = this.nearestN(p.x, p.z, 28, 1);
        if (!near.length) { this.wTimers.zenith = 0.5; return true; }
        const zs = zenithNums(lvl, evo, this);
        this.zenith.charging = true; this.zenith.t = 0; this.zenith.dur = zs.charge;
        this.zenith.size = zs.size; // grows with rank (visual size only)
        this.wTimers.zenith = 1e9; // re-armed when the orb is thrown
        this.events.push({ t: 'zenithCharge', dur: this.zenith.dur });
        return true;
      }
      case 'breaker': {
        // Singularity (owner 2026-11-02): one big black orb fired straight at the nearest crowd (manual aim: along the aim)
        const bs = breakerNums(lvl, evo, this);
        const cands = this.nearestN(p.x, p.z, 18, 12);
        if (!cands.length) { this.wTimers.breaker = 0.3; return true; } // nothing near: wait, don't waste the cooldown
        let dx = p.faceX, dz = p.faceZ;
        if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); if (l > 1e-6) { dx = p.aimX / l; dz = p.aimZ / l; } }
        else {
          let best = cands[0], bestN = -1;
          for (const c of cands) { const e = this.enemies[c], n = this.countAround(e.x, e.z, 4); if (n > bestN) { bestN = n; best = c; } }
          const e = this.enemies[best], l = Math.hypot(e.x - p.x, e.z - p.z) || 1; dx = (e.x - p.x) / l; dz = (e.z - p.z) / l;
        }
        this.wTimers.breaker = bs.cd;
        const r0 = bs.r * SING_START, x = p.x + dx * (r0 + 0.8), z = p.z + dz * (r0 + 0.8), id = this.singId++;
        this.sings.push({ id, x, z, dx, dz, speed: bs.speed, life: bs.life, r: r0, rMax: bs.r, rC: r0, pullMul: bs.pullMul, pull: bs.pull,
          dmg: bs.dmg, implode: bs.implode, evo, phase: 0, pt: 0, tk: 0, left: bs.collapses, hold: bs.hold, deep: bs.craterDeep, cx: 1e9, cz: 1e9 });
        this.events.push({ t: 'shoot', x: p.x, y: p.y, z: p.z, w: 'breaker', dx, dz });
        this.events.push({ t: 'sing', id, x, z, dx, dz, r: r0, evolved: evo });
        return true;
      }
      case 'bore': {
        // owner 2026-11-02 redesign: a point-blank shotgun cone instead of a fan of shells (too close to Spirit Bolt).
        // Instant cone hit (deterministic): every foe inside the reach and angle takes pellet damage that grows the
        // closer it is, and is knocked back; evolved, each foe hit bursts for splash damage.
        const bn = boreNums(lvl, evo, this), R = bn.R;
        const t = this.nearestEnemy(p.x, p.z, R + 1.5);
        if (t < 0) { this.wTimers.bore = 0.2; return true; } // short range: wait until something is close
        this.wTimers.bore = bn.cd;
        const { half, n, pellet } = bn;
        const te = this.enemies[t], tl = Math.hypot(te.x - p.x, te.z - p.z) || 1, ax = (te.x - p.x) / tl, az = (te.z - p.z) / tl;
        const hits: number[] = [], cosH = Math.cos(half);
        this.curSrc = 'bore';
        this.grid.query(p.x, p.z, R + 1.5, (i) => {
          const e = this.enemies[i]; if (!e.alive) return;
          const ex = e.x - p.x, ez = e.z - p.z, d = Math.hypot(ex, ez), reach = R + e.def.radius;
          if (d > reach) return;
          if (d > 0.8 && (ex * ax + ez * az) / d < cosH) return; // outside the cone (anything touching him is always hit)
          const f = 0.25 + 0.75 * Math.max(0, 1 - d / reach);     // pellets that land: all of them point-blank, a quarter at the edge
          const kb = 5 * f * (e.def.kind === 'boss' ? 0.1 : e.elite ? 0.5 : 1);
          this.damageEnemy(e, pellet * n * f, 0xfff0c0, (d > 0.01 ? ex / d : ax) * kb, (d > 0.01 ? ez / d : az) * kb);
          if (hits.length < 24) hits.push(e.x, e.z);
          if (evo) this.radialDamage(e.x, HOVER_Y, e.z, BORE_SPLASH_R * this.areaMul, pellet * n * BORE_SPLASH_DMG, false, 0xffc070, 0, 0.5, 0.3);
        });
        this.events.push({ t: 'boreBlast', x: p.x, z: p.z, dx: ax, dz: az, r: R, half, n, hits, evolved: evo });
        this.events.push({ t: 'shoot', x: p.x, y: p.y, z: p.z, w: 'bore', dx: ax, dz: az }); // hero gesture hook
        return true;
      }
      case 'riftline': { // rising wall wave (owner 2026-11-02)
        if (this.rift.active) return true;
        const manual = this.aimMode === 'manual';
        const rn = riftNums(lvl, evo, this), W = rn.W, L = rn.L;
        let dx = p.faceX, dz = p.faceZ;
        if (manual) { const l = Math.hypot(p.aimX, p.aimZ); if (l > 1e-6) { dx = p.aimX / l; dz = p.aimZ / l; } }
        else {
          // aim at the nearest crowd: of the facing direction and the directions to the nearest foes, take the one whose
          // wall would cover the most of them (facing wins ties); nothing in reach = wait, don't waste the cooldown
          const cands = this.nearestN(p.x, p.z, RIFT_OFFSET + L + 2, 40);
          if (!cands.length) { this.wTimers.riftline = 0.4; return true; }
          const score = (ax: number, az: number) => {
            let n = 0;
            for (const i of cands) {
              const e = this.enemies[i], rx = e.x - p.x, rz = e.z - p.z, al = rx * ax + rz * az;
              if (al > -0.5 && al < RIFT_OFFSET + L + 1 && Math.abs(rx * -az + rz * ax) < W / 2) n++;
            }
            return n;
          };
          let best = score(dx, dz);
          for (const i of cands) {
            const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z); if (l < 0.3) continue;
            const ax = (e.x - p.x) / l, az = (e.z - p.z) / l, sc = score(ax, az);
            if (sc > best) { best = sc; dx = ax; dz = az; }
          }
        }
        const rf = this.rift;
        rf.active = true; rf.t = 0; rf.a = Math.atan2(dz, dx); rf.w = W; rf.len = L; rf.speed = RIFT_SPEED * this.pace; rf.evo = evo;
        rf.dmg = rn.dmg;
        rf.walls.length = 0; rf.queue.length = 0;
        this.spawnRiftWall(dx, dz, 1, false);
        if (evo) this.spawnRiftWall(-dx, -dz, 1, true);
        if (rn.waves > 1) rf.queue.push({ t: RIFT_WAVE_GAP, dx, dz, k: RIFT_WAVE2_DMG, back: false }, ...(evo ? [{ t: RIFT_WAVE_GAP, dx: -dx, dz: -dz, k: RIFT_WAVE2_DMG, back: true }] : []));
        this.wTimers.riftline = rn.cd;
        this.events.push({ t: 'riftStart' });
        return true;
      }
      case 'barrage': { // Flare Barrage (owner 2026-11-02): a stream of tiny shots at the thickest crowd
        if (this.barrageVolley) return true; // the volley is still going
        const bn = barrageNums(lvl, evo, this), aim = this.barrageAim(bn.range);
        if (!aim) { this.wTimers.barrage = 0.3; return true; } // nothing in reach: wait
        this.wTimers.barrage = bn.cd;
        this.barrageVolley = { left: bn.shots, k: 0, next: this.tick, dx: aim.dx, dz: aim.dz, aimTick: this.tick, lvl, evo };
        this.fireBarrageShot();
        return true;
      }
      case 'phase': case 'dive': case 'shout': return this.fireSkillA(id, lvl, evo); // Phase Rend / Comet Dive / Force Shout
      case 'breath': case 'flamering': return this.fireFlame(id, lvl, evo); // Fire family (owner 2026-11-02)
      case 'rime': case 'spikes': case 'glacier': case 'blizzard': return this.fireIce(id, lvl, evo); // ICE FAMILY (owner 2026-11-03)
      case 'gaze': { // Twin Gaze (owner 2026-11-02): two eye lasers sweep a scissor X across the front
        if (this.gaze.phase) return true; // still sweeping
        const gs = gazeNums(lvl, evo, this), aim = this.gazeAim(gs.reach, gs.arc / 2);
        if (!aim) { this.wTimers.gaze = 0.3; return true; } // nothing in front within reach: wait
        this.wTimers.gaze = gs.cd;
        const g = this.gaze;
        Object.assign(g, { phase: 1, t: 0, id: g.id + 1, ax: aim.dx, az: aim.dz, reach: gs.reach, half: gs.arc / 2, passes: gs.passes, pass: 0, u: 0, dmg: gs.dmg, halfW: gs.halfW, burn: gs.burn, evo });
        this.gazePose(); g.thP[0] = g.th[0]; g.thP[1] = g.th[1]; g.dP[0] = g.d[0]; g.dP[1] = g.d[1];
        this.events.push({ t: 'gaze', id: g.id, x: p.x, z: p.z, dx: aim.dx, dz: aim.dz, reach: gs.reach, half: gs.arc / 2, passes: gs.passes, evolved: evo });
        return true;
      }
      case 'split': { // Split Self (owner 2026-11-02): energy copies that fight beside the hero
        const sn = splitNums(lvl, evo, this);
        let alive = 0; for (const c of this.splits) if (c.alive) alive++;
        if (evo && alive >= sn.count) { this.wTimers.split = 0.5; return true; } // the legion is complete
        if (this.nearestEnemy(p.x, p.z, sn.range + 6) < 0) { this.wTimers.split = 0.3; return true; } // nothing near: wait
        this.wTimers.split = sn.cd;
        for (const c of this.splits) if (c.alive) { c.t = sn.dur; c.dur = sn.dur; c.dmg = sn.dmg; c.fire = sn.fire; c.evo = evo; } // refresh
        let fresh = 0;
        for (let k = 0; alive < Math.min(sn.count, this.splits.length) && k < this.splits.length; k++) {
          const c = this.splits[k]; if (c.alive) continue;
          let slot = 0; while (this.splits.some((q) => q.alive && q.slot === slot)) slot++;
          Object.assign(c, { alive: true, id: this.splitId++, slot, x: p.x, z: p.z, vx: 0, vz: 0, t: sn.dur, dur: sn.dur, age: 0, cd: 0.25 + slot * sn.fire / sn.count, fire: sn.fire, dmg: sn.dmg, faceX: p.faceX, faceZ: p.faceZ, evo, shots: 0 });
          alive++; fresh++;
        }
        this.events.push({ t: 'split', n: alive, fresh, evolved: evo });
        return true;
      }
      case 'crescent': { // Crescent Edge (owner 2026-11-02): crescent waves from alternating hands
        if (this.crescentThrow) return true; // the throw is still going
        const cs = crescentNums(lvl, evo, this);
        if (this.aimMode !== 'manual' && this.nearestEnemy(p.x, p.z, cs.range) < 0) { this.wTimers.crescent = 0.3; return true; }
        this.wTimers.crescent = cs.cd;
        this.crescentThrow = { left: cs.waves, k: 0, wait: 0, lvl, evo };
        this.fireCrescent();
        return true;
      }
      case 'finger': { // Finger Beam (owner 2026-11-02): a near-instant crimson needle along the most crowded line
        if (this.fingerBurst) return true; // the volley is still going
        const fs = fingerNums(lvl, evo, this);
        if (this.aimMode !== 'manual' && this.nearestEnemy(p.x, p.z, fs.range) < 0) { this.wTimers.finger = 0.3; return true; } // nothing in reach: wait
        this.wTimers.finger = fs.cd;
        this.fingerBurst = { left: fs.shots, k: 0, wait: 0, lvl, evo, used: [] };
        this.fireFingerShot();
        return true;
      }
      // Skills B (owner 2026-11-02): casts live in castSnare / castComet / castRings / castGrip below
      case 'snare': this.castSnare(lvl, evo); return true;
      case 'comet': this.castComet(lvl, evo); return true;
      case 'rings': this.castRings(lvl, evo); return true;
      case 'grip': this.castGrip(lvl, evo); return true;
      case 'skyfall': {
        const cands = this.nearestN(p.x, p.z, SKYFALL_RANGE, 24);
        if (!cands.length) { this.wTimers.skyfall = 0.3; return true; }
        const ss = skyfallNums(lvl, evo, this);
        this.wTimers.skyfall = ss.cd;
        const n = ss.n;
        for (let k = 0; k < n; k++) {
          const e = this.enemies[cands[this.rng.int(cands.length)]];
          const ft = SKYFALL_FLIGHT / this.pace + skyfallLaunch(k, evo); // pace: the shots fly slower (same launch rhythm) // shots leave in quick succession, arc high, then home down on their targets
          this.falls.push({ x: e.x, z: e.z, t: ft, t0: ft, k, tid: e.id, r: ss.r, dmg: ss.dmg, kind: SKY_KIND.shard, ox: p.x, oz: p.z, r0: ss.r, d0: ss.dmg });
        }
        this.events.push({ t: 'skyfall', n, evolved: evo });
        return true;
      }
      case 'mirage': {
        const { cd: mcd, dur, r, dmg, br, heal } = mirageNums(lvl, evo, this);
        this.wTimers.mirage = mcd;
        const spots = evo ? [0, 2.09, 4.19].map((a) => [p.x + Math.cos(a + this.time) * 6, p.z + Math.sin(a + this.time) * 6]) : [[p.x - p.faceX * 1.5, p.z - p.faceZ * 1.5]];
        for (const [x, z] of spots) { this.lures.push({ x, z, t: dur, dur, r, dmg, br, heal }); this.events.push({ t: 'lure', x, z }); }
        return true;
      }
      case 'glare': {
        const { cd: gcd, R, freeze, dmg } = glareNums(lvl, evo, this);
        this.wTimers.glare = gcd;
        this.curSrc = 'glare';
        this.grid.query(p.x, p.z, R + 2, (i) => {
          const e = this.enemies[i]; if (!e.alive) return;
          if ((e.x - p.x) ** 2 + (e.z - p.z) ** 2 > R * R) return;
          this.freeze(e, freeze * (e.def.kind === 'boss' ? GLARE_BOSS : e.elite ? GLARE_ELITE : 1)); // (Deep Chill lengthens it)
          if (dmg > 0) this.damageEnemy(e, dmg, 0xffffff, 0, 0);
        });
        this.events.push({ t: 'glare', x: p.x, z: p.z, r: R, evolved: evo });
        return true;
      }
      case 'rush': {
        if (this.nearestEnemy(p.x, p.z, RUSH_TRIGGER) < 0) { this.wTimers.rush = 0.15; return true; }
        const rs = rushNums(lvl, evo, this);
        this.wTimers.rush = rs.cd;
        this.rushLeft = rs.strikes;
        this.rushDmg = rs.dmg;
        this.rushReach = rs.reach;
        return true;
      }
      case 'ward': {
        if (this.wardCharges > 0) { this.wTimers.ward = 0.25; return true; } // the recharge only starts once the dome is gone
        const ws = wardNums(lvl, evo, this);
        this.wTimers.ward = ws.cd;
        this.wardCharges = ws.charges;
        this.events.push({ t: 'wardUp', charges: this.wardCharges });
        return true;
      }
      case 'overdrive': {
        this.wTimers.overdrive = (15 - (lvl >= 4 ? 2 : 0)) * cd;
        this.odT = (4 + (lvl >= 2 ? 1 : 0) + (lvl >= 5 ? 1 : 0) + (evo ? 2 : 0)) * this.persistMul;
        // owner 2026-11-01: the price is paid ONCE, the moment the power-up kicks in (a tenth of max health; never below 1),
        // and nothing drains while it runs. Redline (evolved) is free.
        const cost = evo ? 0 : Math.min(Math.round(p.maxHp * 0.1), Math.max(0, p.hp - 1));
        p.hp -= cost;
        this.events.push({ t: 'overdrive', cost });
        return true;
      }
    }
    return false;
  }

  /** Per-tick upkeep for the newer techniques (charges, sweeps, falls, lures, singularities, flurries). */
  private updateTechniques(): void {
    const p = this.player;
    // Zenith Orb: grow overhead, then throw at the densest crowd
    if (this.zenith.charging) {
      this.zenith.t += DT;
      if (this.zenith.t >= this.zenith.dur) {
        this.zenith.charging = false;
        const lvl = this.wlv('zenith'), evo = this.evolved.has('zenith');
        const zs = zenithNums(lvl, evo, this);
        this.wTimers.zenith = zs.cd;
        const cands = this.nearestN(p.x, p.z, 30, 16);
        let best = -1, bestN = -1;
        for (const c of cands) { const e = this.enemies[c]; const n = this.countAround(e.x, e.z, 6); if (n > bestN) { bestN = n; best = c; } }
        const tx = best >= 0 ? this.enemies[best].x : p.x + p.faceX * 12, tz = best >= 0 ? this.enemies[best].z : p.z + p.faceZ * 12;
        const dx = tx - p.x, dz = tz - p.z, d = Math.hypot(dx, dz) || 1, sp = ZENITH_SPEED * this.pace;
        const pr = this.allocProjectile();
        if (pr) Object.assign(pr, { x: p.x, y: p.y + 3.2, z: p.z, vx: dx / d * sp, vy: -(3.2 - 0.2) / (d / sp), vz: dz / d * sp, life: d / sp, hostile: false, kind: 4, comet: evo, pierce: 0, target: -1, lastHit: -1, out: this.zenith.size, ret: false, // out = orb size (render)
          r: zs.r, dmg: zs.dmg });
        this.events.push({ t: 'zenithThrow' });
      }
    }
    // Riftline rising wall wave: queued second waves, then move each wall and hit what it passes
    const rf = this.rift;
    if (rf.active) {
      rf.t += DT;
      for (let k = rf.queue.length - 1; k >= 0; k--) {
        const q = rf.queue[k]; q.t -= DT;
        if (q.t <= 0) { rf.queue.splice(k, 1); this.spawnRiftWall(q.dx, q.dz, q.k, q.back); }
      }
      this.curSrc = 'riftline';
      for (let k = rf.walls.length - 1; k >= 0; k--) {
        const wl = rf.walls[k];
        wl.d = Math.min(wl.len, wl.d + wl.speed * DT);
        wl.x = wl.x0 + wl.dx * wl.d; wl.z = wl.z0 + wl.dz * wl.d;
        const hw = wl.w / 2, hit = wl.hit;
        this.grid.query(wl.x, wl.z, hw + 3, (i) => {
          const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
          const rx = e.x - wl.x0, rz = e.z - wl.z0, al = rx * wl.dx + rz * wl.dz, r = e.def.radius * e.scale;
          // the band the wall face sweeps this tick (generous behind it so nothing tunnels; ahead = the wall's thickness)
          if (al > wl.d + RIFT_THICK + r || al < wl.d - RIFT_BAND - r || al < -RIFT_OFFSET - 0.5) return;
          if (Math.abs(rx * -wl.dz + rz * wl.dx) > hw + r) return;
          hit.add(e.id);
          this.damageEnemy(e, wl.dmg, RIFT_HIT_COLOR, 0, 0);
          if (!e.alive) return;
          // shove along the travel direction: strong and slightly lofted for the rank and file, half for elites,
          // bosses only a nudge (impulse() ignores bosses)
          if (e.def.kind === 'boss') { e.x += wl.dx * 0.35; e.z += wl.dz * 0.35; }
          else this.impulse(e, wl.dx * RIFT_PUSH * (e.elite ? 0.5 : 1), wl.dz * RIFT_PUSH * (e.elite ? 0.5 : 1), e.elite ? 0 : RIFT_LOFT);
        });
        if ((this.tick + wl.id) % 4 === 0) {
          this.terrain.damageLine(wl.x + wl.dz * hw, wl.z - wl.dx * hw, -wl.dz, wl.dx, wl.w, 0.9, wl.dmg * 2, this.events, PROP_SRC.wall, wl.dx, wl.dz);
          if (wl.ign && this.fire.roll(0.2)) { wl.ign = false; const o = (this.fire.rand() - 0.5) * wl.w; this.fire.ignite(wl.x - wl.dz * o, wl.z + wl.dx * o, 1, 6, 0); } // MAP-FIRE: the burning crack (one 25 % roll per wall, owner 2026-11-02)
        }
        if (wl.d >= wl.len) rf.walls.splice(k, 1);
      }
      if (!rf.walls.length && !rf.queue.length) rf.active = false;
    }
    // Skyfall shards (Shardstorm, owner 2026-11-03: + crit shatters into splinters and the Fallen Star every STAR_CRITS crits)
    if (this.starCd > 0) this.starCd -= DT;
    const storm = this.evolved.has('skyfall');
    for (let k = this.falls.length - 1; k >= 0; k--) {
      const f = this.falls[k]; f.t -= DT;
      if (f.tid >= 0) { // homing: follow the target until impact (falls back to the last spot if it dies)
        const e = this.enemyById(f.tid);
        if (e) { f.x = e.x; f.z = e.z; } else f.tid = -1;
      }
      if (f.t > 0) continue;
      this.falls.splice(k, 1);
      this.curSrc = 'skyfall';
      this.blast(KILL_SRC.explosion, this.boltColor, f.x, f.z, f.r);
      const star = f.kind === SKY_KIND.star, splinter = f.kind === SKY_KIND.splinter, c0 = this.critHits;
      this.igniteK = splinter ? 0 : 1;
      if (star) this.radialDamage(f.x, HOVER_Y, f.z, f.r, f.dmg, true, this.boltColor, STAR_CRATER, 0.9, 0.8); // the star shoves and craters
      else this.radialDamage(f.x, HOVER_Y, f.z, f.r, f.dmg, false, this.boltColor, splinter ? 0.15 : 0.4 + 0.05 * this.wlv('skyfall'));
      this.igniteK = 1; this.bSrc = 0;
      this.events.push({ t: 'explode', x: f.x, y: HOVER_Y, z: f.z, r: f.r, color: this.boltColor, sky: true, splinter, star }); // sky: Skyfall landing (own sound)
      if (!storm) continue;
      const crits = this.critHits - c0;
      if (star) this.skyShatter(f, STAR_SPLINTERS, f.d0 * STAR_SPLINTER_DMG);
      else if (!splinter && crits > 0) { this.skyShatter(f, SHATTER_N, f.d0 * SHATTER_DMG); this.events.push({ t: 'skyShatter', x: f.x, z: f.z, n: SHATTER_N }); }
      this.skyCrits += crits;
      if (this.skyCrits >= STAR_CRITS && this.starCd <= 0) this.callStar(f.r0, f.d0);
    }
    // Mirage lures
    for (let k = this.lures.length - 1; k >= 0; k--) {
      const l = this.lures[k]; l.t -= DT;
      if (l.t > 0) continue;
      this.lures.splice(k, 1);
      this.curSrc = 'mirage';
      this.blast(KILL_SRC.explosion, 0x9fd8ff, l.x, l.z, l.br);
      this.radialDamage(l.x, HOVER_Y, l.z, l.br, l.dmg, true, 0x9fd8ff, 0.5);
      this.bSrc = 0;
      this.events.push({ t: 'explode', x: l.x, y: HOVER_Y, z: l.z, r: l.br, color: 0x9fd8ff, lure: true }); // lure: Mirage Lure burst (own sound)
      if (l.heal) this.heal(l.heal);
    }
    // Singularity: the orb drifts straight and grows, dragging foes toward its centre and hurting the ones inside; at the end
    // of its flight it shrinks into nothing and implodes (evolved, Collapse Point: first hangs SING_HOLD s at the end of its
    // path with a stronger pull, absorbing the crowd, then collapses, re-forms once in place and collapses again)
    for (let k = this.sings.length - 1; k >= 0; k--) {
      const s = this.sings[k]; s.pt += DT; s.tk++;
      if (s.phase === 0) {
        const nx = s.x + s.dx * s.speed * DT, nz = s.z + s.dz * s.speed * DT;
        if (nx * nx + nz * nz < (ARENA_R - 1) ** 2) { s.x = nx; s.z = nz; }
        s.r = s.rMax * (SING_START + (1 - SING_START) * Math.min(1, s.pt / s.life));
        // owner 2026-11-02: the orb gouges the ground as it passes — a chain of craters along its path, deeper every rank
        const cr = Math.max(0.85, s.r * SING_CRATER_R);
        if ((s.x - s.cx) ** 2 + (s.z - s.cz) ** 2 >= Math.max(1.3, cr * SING_CRATER_STEP) ** 2) { s.cx = s.x; s.cz = s.z; this.terrain.addCrater(s.x, s.z, cr, this.tick, this.events, s.deep); }
        if (s.pt >= s.life - 1e-9) { s.phase = s.hold > 0 ? 3 : 1; s.pt = 0; s.rC = s.r; if (s.hold > 0) this.events.push({ t: 'singHold', id: s.id, x: s.x, z: s.z, dur: s.hold }); }
      } else if (s.phase === 3) {
        // Collapse Point (owner 2026-11-03): it stops at the end of its path and hangs there, absorbing everyone in reach
        if (s.pt >= s.hold - 1e-9) { s.phase = 1; s.pt = 0; s.rC = s.r; }
      } else if (s.phase === 1) {
        const u = Math.min(1, s.pt / SING_COLLAPSE); s.r = s.rC * (1 - u * u); // slow, then all at once
        if (u >= 1) {
          s.r = 0; s.left--;
          const R = s.rMax * SING_IMPLODE_R;
          this.curSrc = 'breaker';
          this.blast(KILL_SRC.singularity, SING_IMPLODE_COLOR, s.x, s.z, R);
          this.radialDamage(s.x, HOVER_Y, s.z, R, s.implode, false, SING_IMPLODE_COLOR);
          this.bSrc = 0;
          this.events.push({ t: 'singCollapse', id: s.id, x: s.x, z: s.z, r: R, evolved: s.evo, last: s.left <= 0 });
          if (s.left <= 0) { this.sings.splice(k, 1); continue; }
          s.phase = 2; s.pt = 0;
        }
      } else {
        const u = Math.min(1, s.pt / SING_REBOUND); s.r = s.rMax * (1 - (1 - u) * (1 - u));
        if (u >= 1) { s.phase = 1; s.pt = 0; s.rC = s.r; }
      }
      if (s.r < 0.05) continue;
      const holding = s.phase === 3, reach = s.r * s.pullMul, f = s.pull * (s.phase === 1 ? 1.6 : holding ? SING_HOLD_PULL : 1) * DT, hitNow = s.tk % SING_TICK === 0;
      this.curSrc = 'breaker';
      this.grid.query(s.x, s.z, reach + 2, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const dx = s.x - e.x, dz = s.z - e.z, d = Math.hypot(dx, dz), er = e.def.radius * e.scale;
        if (d > reach + er) return;
        if (d > 0.05) {
          const g = f * Math.min(1, d / (s.r * 0.5)); // eases off in the core so foes settle there instead of overshooting
          if (e.def.kind === 'boss') { e.kx += dx / d * g * SING_BOSS_PULL; e.kz += dz / d * g * SING_BOSS_PULL; }
          else {
            this.impulse(e, dx / d * g, dz / d * g, 0);
            const grip = 1 - SING_GRIP * (holding ? 2.5 : 1) / Math.sqrt(e.def.mass); e.vx *= grip; e.vz *= grip; // caught: they can't run off on their own (held: absorbed)
          }
        }
        if (hitNow && d < s.r + er) this.damageEnemy(e, holding ? s.dmg * SING_HOLD_DMG : s.dmg, SING_HIT_COLOR, 0, 0);
      });
      if (hitNow) this.terrain.damageCircle(s.x, s.z, s.r, s.dmg * 3, this.events, PROP_SRC.vortex); // MAP-DESTRUCTION: props inside the orb are crushed and pulled in
    }
    this.updateNewTraits();
    // Pulse Nova sphere bursts (owner 2026-11-02): the sphere explodes as it starts to fade, hurling foes away
    for (let k = this.novaBursts.length - 1; k >= 0; k--) {
      const b = this.novaBursts[k]; b.t -= DT;
      if (b.t > 1e-9) continue;
      this.novaBursts.splice(k, 1);
      this.curSrc = 'nova';
      this.radialDamage(b.x, b.y, b.z, b.r, b.dmg, true, NOVA_BURST_COLOR, 0, NOVA_BURST_KNOCK, NOVA_BURST_LIFT);
      this.bSrc = 0;
      this.events.push({ t: 'novaBurst', x: b.x, y: b.y, z: b.z, r: b.r, evolved: b.evo });
    }
    // Spirit Bolt burst: next shot every 5 ticks
    if (this.boltBurst && --this.boltBurst.wait <= 0) this.fireBurstBolt();
    // Flare Barrage (owner 2026-11-02): re-aim every BARRAGE_REAIM ticks, a shot tick every `gap` ticks, then move the pellets
    { const v = this.barrageVolley;
      if (v && this.tick - v.aimTick >= BARRAGE_REAIM) { v.aimTick = this.tick; const a = this.barrageAim(barrageNums(v.lvl, v.evo, this).range); if (a) { v.dx = a.dx; v.dz = a.dz; } }
      if (v && this.tick >= v.next) this.fireBarrageShot();
      this.updateBarrageShots(); }
    // Twin Gaze / Split Self / Crescent Edge (owner 2026-11-02)
    this.updateGaze(); this.updateSplits();
    if (this.crescentThrow && --this.crescentThrow.wait <= 0) this.fireCrescent();
    this.updateCrescents();
    // Finger Beam volley: next needle every fingerNums().gap ticks
    if (this.fingerBurst && --this.fingerBurst.wait <= 0) this.fireFingerShot();
    // Rush Flurry: one strike every 5 ticks
    if (this.rushLeft > 0 && this.tick % RUSH_GAP === 0) {
      this.rushLeft--;
      const evo = this.evolved.has('rush');
      const t = this.nearestEnemy(p.x, p.z, this.rushReach + 2);
      let dx = p.faceX, dz = p.faceZ;
      if (t >= 0) { const e = this.enemies[t]; const l = Math.hypot(e.x - p.x, e.z - p.z) || 1; dx = (e.x - p.x) / l; dz = (e.z - p.z) / l; }
      const R = this.rushReach, cosHalf = Math.cos(RUSH_HALF_DEG * Math.PI / 180);
      this.curSrc = 'rush';
      this.grid.query(p.x, p.z, R + 1.5, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const rx = e.x - p.x, rz = e.z - p.z, d = Math.hypot(rx, rz) || 1;
        if (d > R + e.def.radius * e.scale) return;
        if (!evo && (rx * dx + rz * dz) / d < cosHalf) return;
        this.damageEnemy(e, this.rushDmg, 0xffe0a0, rx / d * 7, rz / d * 7);
      });
      this.terrain.damageCircle(p.x + dx * R * 0.6, p.z + dz * R * 0.6, R * 0.55, this.rushDmg * 2, this.events, PROP_SRC.chip); // MAP-DESTRUCTION: chips props in front
      this.events.push({ t: 'rush', x: p.x, z: p.z, dx, dz, reach: R, full: evo });
    }
    this.updateSkillsA(); // Phase Rend / Comet Dive / Force Shout (owner 2026-11-02)
    this.updateSkillsB(); // Snare Constellation, Wayward Comet, Binding Rings, Gravity Grip (owner 2026-11-02)
  }

  // ================= Phase Rend / Comet Dive / Force Shout (owner 2026-11-02: 'Make them all') =================
  /** Cast dispatch for the three skills (from fireTechnique). Returns true (handled). */
  private fireSkillA(id: WeaponId, lvl: number, evo: boolean): boolean {
    const p = this.player;
    // the two movement skills never overlap: one waits a moment for the other to finish
    if ((id === 'phase' && this.dive.active) || (id === 'dive' && this.phaseRend.active)) { this.wTimers[id] = 0.1; return true; }
    if (id === 'phase') {
      if (this.phaseRend.active) return true;
      if (this.nearestEnemy(p.x, p.z, PHASE_RANGE) < 0) { this.wTimers.phase = 0.3; return true; } // nothing in reach: wait
      const s = phaseNums(lvl, evo, this), pr = this.phaseRend;
      this.wTimers.phase = s.cd;
      pr.active = true; pr.left = s.blinks; pr.k = 0; pr.next = this.tick; pr.bx = p.x; pr.bz = p.z; pr.dx = p.faceX; pr.dz = p.faceZ; pr.lvl = lvl; pr.evo = evo; pr.visited.clear();
      p.iframes = Math.max(p.iframes, 0.15);
      this.events.push({ t: 'phaseStart', x: p.x, z: p.z, evolved: evo });
      this.phaseBlink(); // the first blink is instant
      return true;
    }
    if (id === 'dive') {
      if (this.dive.active) return true;
      const s = diveNums(lvl, evo, this), spot = this.diveSpot(s.r);
      if (!spot) { this.wTimers.dive = 0.3; return true; } // nothing worth diving at: wait
      this.wTimers.dive = s.cd;
      const d = this.dive;
      d.active = true; d.t = 0; d.dur = s.air; d.x0 = p.x; d.z0 = p.z; d.x1 = spot.x; d.z1 = spot.z; d.lvl = lvl; d.evo = evo;
      p.iframes = Math.max(p.iframes, s.air + s.safe); // untouchable in the air (and a moment after landing)
      this.events.push({ t: 'diveStart', x0: p.x, z0: p.z, x1: spot.x, z1: spot.z, dur: s.air, evolved: evo });
      return true;
    }
    // shout
    const s = shoutNums(lvl, evo, this), aim = this.shoutAim(s.r, s.half);
    if (!aim) { this.wTimers.shout = 0.3; return true; } // nothing in reach: wait
    this.wTimers.shout = s.cd;
    this.spawnShout(aim.dx, aim.dz, s, evo, false, 0);
    if (s.waves > 1) this.spawnShout(aim.dx, aim.dz, s, evo, true, SHOUT_ECHO_T);
    return true;
  }

  /** Per-tick upkeep: blinks, cut lines, afterimage slashes, the dive landing and its aftershocks, shout wave fronts. */
  private updateSkillsA(): void {
    const p = this.player, pr = this.phaseRend;
    // Phase Rend: untouchable while phasing; a blink every PHASE_BLINK ticks, then the snap back
    if (pr.active) {
      p.iframes = Math.max(p.iframes, 0.1);
      if (this.tick >= pr.next) { if (pr.left > 0 && this.wlv('phase') > 0) this.phaseBlink(); else this.phaseReturn(); }
    }
    if (this.phaseCuts.length) {
      this.curSrc = 'phase';
      for (let k = this.phaseCuts.length - 1; k >= 0; k--) {
        const c = this.phaseCuts[k]; c.t += DT;
        if (c.t >= c.life) { this.phaseCuts.splice(k, 1); continue; }
        const dx = c.x1 - c.x0, dz = c.z1 - c.z0, L = Math.hypot(dx, dz) || 1, ux = dx / L, uz = dz / L;
        this.blast(KILL_SRC.disc, -1, (c.x0 + c.x1) / 2, (c.z0 + c.z1) / 2, 0);
        this.grid.query((c.x0 + c.x1) / 2, (c.z0 + c.z1) / 2, L / 2 + PHASE_CUT_W + 3, (i) => {
          const e = this.enemies[i]; if (!e.alive || c.hit.has(e.id)) return;
          const rx = e.x - c.x0, rz = e.z - c.z0, al = rx * ux + rz * uz, er = e.def.radius * e.scale;
          if (al < -er || al > L + er || Math.abs(rx * -uz + rz * ux) > PHASE_CUT_W + er) return;
          c.hit.add(e.id);
          const side = (rx * -uz + rz * ux) >= 0 ? 1 : -1; // knocked back off the line, toward the side it came from
          this.damageEnemy(e, c.dmg, PHASE_HIT_COLOR, -uz * side * 3, ux * side * 3);
        });
        this.bSrc = 0;
      }
    }
    for (let k = this.phaseEchoes.length - 1; k >= 0; k--) {
      const a = this.phaseEchoes[k]; a.t -= DT;
      if (a.t > 0) continue;
      this.phaseEchoes.splice(k, 1);
      this.curSrc = 'phase';
      this.blast(KILL_SRC.disc, -1, a.x, a.z, 0);
      this.radialDamage(a.x, HOVER_Y, a.z, a.r, a.dmg, false, PHASE_HIT_COLOR);
      this.bSrc = 0;
      this.events.push({ t: 'phaseEcho', x: a.x, z: a.z, r: a.r });
    }
    // Comet Dive: crash when the airtime is up, then any queued aftershock / fissures
    if (this.dive.active && this.dive.t >= this.dive.dur - 1e-9) this.diveLand();
    for (let k = this.diveQueue.length - 1; k >= 0; k--) {
      const q = this.diveQueue[k]; q.t -= DT;
      if (q.t > 0) continue;
      this.diveQueue.splice(k, 1);
      this.curSrc = 'dive';
      if (q.kind === 0) {
        this.diveWave(q.x, q.z, q.r, q.dmg, q.launch);
        this.events.push({ t: 'diveShock', x: q.x, z: q.z, r: q.r });
      } else {
        const from = q.r * DIVE_FISSURE_FROM, to = q.r * DIVE_FISSURE_TO, x0 = q.x + q.dx * from, z0 = q.z + q.dz * from, L = to - from;
        this.blast(KILL_SRC.explosion, DIVE_HIT_COLOR, q.x, q.z, to);
        this.grid.query(x0 + q.dx * L / 2, z0 + q.dz * L / 2, L / 2 + DIVE_FISSURE_W + 3, (i) => {
          const e = this.enemies[i]; if (!e.alive) return;
          const rx = e.x - x0, rz = e.z - z0, al = rx * q.dx + rz * q.dz, er = e.def.radius * e.scale;
          if (al < -er || al > L + er || Math.abs(rx * -q.dz + rz * q.dx) > DIVE_FISSURE_W + er) return;
          this.damageEnemy(e, q.dmg, DIVE_HIT_COLOR, q.dx * 6, q.dz * 6);
          if (e.alive) this.impulse(e, 0, 0, q.launch);
        });
        this.bSrc = 0;
        this.terrain.damageLine(x0, z0, q.dx, q.dz, L, DIVE_FISSURE_W, q.dmg * 1.5, this.events);
        for (let s = 0.5; s < L; s += 1.6) this.terrain.addScorch(x0 + q.dx * s, z0 + q.dz * s, 0.9, this.tick);
        this.events.push({ t: 'diveFissure', x: q.x, z: q.z, dx: q.dx, dz: q.dz, from, to });
      }
    }
    // Force Shout: wave fronts roll out; each foe is struck once per wave when the front reaches it
    for (let k = this.shoutWaves.length - 1; k >= 0; k--) {
      const w = this.shoutWaves[k];
      if (w.delay > 0) {
        w.delay -= DT;
        if (w.delay > 1e-9) continue;
        w.delay = 0; w.x = p.x; w.z = p.z; // the echo leaves from wherever the hero is now
        this.events.push({ t: 'shout', id: w.id, x: w.x, z: w.z, dx: w.dx, dz: w.dz, half: w.half, r: w.r, speed: SHOUT_SPEED * this.pace, echo: true, evolved: w.evo });
      }
      w.d = Math.min(w.r, w.d + SHOUT_SPEED * this.pace * DT); // pace: the front travels slower (same reach)
      this.shoutHit(w);
      if (w.d >= w.r) this.shoutWaves.splice(k, 1);
    }
  }

  /** Next Phase Rend blink: the nearest foe not yet visited (first: within PHASE_RANGE of the hero, then within PHASE_JUMP of
   *  the body). The body arrives just past it and slashes along the blink direction through every foe on a short line; with
   *  rank 2+ the slash leaves a cut line. No foe in reach: snap back early. */
  private phaseBlink(): void {
    const pr = this.phaseRend, p = this.player, s = phaseNums(pr.lvl, pr.evo, this);
    const first = pr.k === 0, fx = first ? p.x : pr.bx, fz = first ? p.z : pr.bz, reach = first ? PHASE_RANGE : PHASE_JUMP;
    let best = -1, bd = reach * reach;
    this.grid.query(fx, fz, reach, (i) => {
      const e = this.enemies[i]; if (!e.alive || pr.visited.has(e.id)) return;
      const d = (e.x - fx) ** 2 + (e.z - fz) ** 2; if (d < bd || (d === bd && best >= 0 && e.id < this.enemies[best].id)) { bd = d; best = i; }
    });
    if (best < 0) { this.phaseReturn(); return; }
    const e = this.enemies[best]; pr.visited.add(e.id);
    let dx = e.x - fx, dz = e.z - fz; const l = Math.hypot(dx, dz);
    if (l > 1e-4) { dx /= l; dz /= l; } else { dx = pr.dx; dz = pr.dz; }
    const tx = e.x, tz = e.z, er = e.def.radius * e.scale;
    // the body lands just past the foe (dashing through it), inside the arena and outside solid props
    const body = { x: tx + dx * (er + 0.7), z: tz + dz * (er + 0.7) };
    this.clampArena(body); this.terrain.collide(body, 0.6); this.clampArena(body);
    // slash: every foe on a line `len` long centred on the target, along the blink
    const L = s.len, x0 = tx - dx * L / 2, z0 = tz - dz * L / 2, slashed: number[] = [];
    this.curSrc = 'phase';
    this.blast(KILL_SRC.disc, -1, tx, tz, 0); // slash deaths are cut apart
    this.grid.query(tx, tz, L / 2 + PHASE_SLASH_W + 3, (i) => {
      const o = this.enemies[i]; if (!o.alive) return;
      const rx = o.x - x0, rz = o.z - z0, al = rx * dx + rz * dz, orr = o.def.radius * o.scale;
      if (al < -orr || al > L + orr || Math.abs(rx * -dz + rz * dx) > PHASE_SLASH_W + orr) return;
      slashed.push(o.id);
      this.damageEnemy(o, s.dmg, PHASE_HIT_COLOR, dx * 4, dz * 4);
    });
    this.bSrc = 0;
    this.terrain.damageLine(x0, z0, dx, dz, L, PHASE_SLASH_W, s.dmg, this.events);
    let cut = 0;
    if (s.cut > 0) { // the slash stays in the air as a cut line; the foes it just slashed don't take it again
      cut = this.phaseCutId++;
      this.phaseCuts.push({ id: cut, x0, z0, x1: x0 + dx * L, z1: z0 + dz * L, t: 0, life: s.cut, dmg: s.cutDmg, evo: pr.evo, hit: new Set(slashed) });
    }
    if (s.echo) this.phaseEchoes.push({ x: body.x, z: body.z, t: PHASE_ECHO_T, r: PHASE_ECHO_R * this.areaMul, dmg: s.dmg * PHASE_ECHO_DMG });
    this.events.push({ t: 'phaseBlink', k: pr.k, fx, fz, x: body.x, z: body.z, tx, tz, dx, dz, len: L, hits: slashed.length, cut, evolved: pr.evo });
    pr.bx = body.x; pr.bz = body.z; pr.dx = dx; pr.dz = dz; pr.k++; pr.left--;
    pr.next = this.tick + (pr.left > 0 ? PHASE_BLINK : PHASE_RETURN);
  }

  /** Phase Rend ends: the body snaps back to the hero. */
  private phaseReturn(): void {
    const pr = this.phaseRend, p = this.player;
    this.events.push({ t: 'phaseEnd', fx: pr.bx, fz: pr.bz, x: p.x, z: p.z });
    pr.active = false; pr.left = 0; pr.bx = p.x; pr.bz = p.z;
  }

  /** (x, z) pulled radially to within r of the centre (summons from a foe inside the wall land inside too) */
  inside(x: number, z: number, r: number): { x: number; z: number } { const d = Math.hypot(x, z); return d > r ? { x: x * r / d, z: z * r / d } : { x, z }; }
  private clampArena(o: { x: number; z: number }, r = ARENA_R - 1): void { const d = Math.hypot(o.x, o.z); if (d > r) { o.x *= r / d; o.z *= r / d; } }

  /** Comet Dive landing spot. Manual aim: half the reach along the aim. Auto: of the foes within reach (+ the shockwave), the
   *  one with the most others inside the shockwave radius (the nearer wins ties), pulled in to DIVE_REACH. Always inside the
   *  arena and outside solid props. null = no foe close enough to be worth it. */
  private diveSpot(r: number): { x: number; z: number } | null {
    const p = this.player, cands = this.nearestN(p.x, p.z, DIVE_REACH + r, 24);
    if (!cands.length) return null;
    const o = { x: p.x, z: p.z };
    if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ) || 1; o.x += p.aimX / l * DIVE_REACH * 0.5; o.z += p.aimZ / l * DIVE_REACH * 0.5; }
    else {
      let best = cands[0], bestN = -1;
      for (const c of cands) { const e = this.enemies[c], n = this.countAround(e.x, e.z, r); if (n > bestN) { bestN = n; best = c; } }
      const e = this.enemies[best], dx = e.x - p.x, dz = e.z - p.z, d = Math.hypot(dx, dz), k = d > DIVE_REACH ? DIVE_REACH / d : 1;
      o.x += dx * k; o.z += dz * k;
    }
    this.clampArena(o); this.terrain.collide(o, 0.6); this.clampArena(o);
    return o;
  }

  /** Airborne: the hero follows the dive path (eased; he is over the landing spot for the last 15% so the crash is steep). Input can't steer him. */
  private diveMove(): void {
    const d = this.dive, p = this.player;
    d.t = Math.min(d.dur, d.t + DT);
    const u = d.dur > 0 ? d.t / d.dur : 1, v = Math.min(1, u / 0.85), s = v * v * (3 - 2 * v); // across by 85% of the airtime: the crash comes in steep
    p.x = d.x0 + (d.x1 - d.x0) * s; p.z = d.z0 + (d.z1 - d.z0) * s; p.vx = 0; p.vz = 0;
  }

  /** Comet Dive crash: crater, shockwave that launches foes, then the aftershock (rank 4+) / Titan Drop fissures queued. */
  private diveLand(): void {
    const d = this.dive, p = this.player, s = diveNums(d.lvl, d.evo, this);
    d.active = false;
    if (d.fx) { // SKILL FUSIONS: Fault Drop / Roaring Meteor land their own way
      const f = d.fx; d.fx = undefined;
      p.x = d.x1; p.z = d.z1; this.terrain.collide(p, 0.6); this.clampArena(p, ARENA_R); p.iframes = Math.max(p.iframes, 0.3);
      const dx = d.x1 - d.x0, dz = d.z1 - d.z0, l = Math.hypot(dx, dz) || 1;
      const e0 = this.events.length; this.fusion.land(f, p.x, p.z, dx / l, dz / l); this.fixFusionBlasts(e0);
      return;
    }
    p.x = d.x1; p.z = d.z1; this.terrain.collide(p, 0.6); this.clampArena(p, ARENA_R);
    p.iframes = Math.max(p.iframes, s.safe);
    this.curSrc = 'dive';
    this.terrain.addCrater(p.x, p.z, s.crater, this.tick, this.events, s.deep);
    this.terrain.damageCircle(p.x, p.z, s.r, s.dmg * 1.5, this.events);
    const hits = this.diveWave(p.x, p.z, s.r, s.dmg, s.launch);
    this.events.push({ t: 'diveLand', x: p.x, z: p.z, r: s.r, cr: s.crater, launch: s.launch, hits, evolved: d.evo });
    if (s.aftershock) this.diveQueue.push({ t: DIVE_SHOCK_T, kind: 0, x: p.x, z: p.z, dx: 0, dz: 0, r: s.r * DIVE_SHOCK_R, dmg: s.dmg * DIVE_SHOCK_DMG, launch: s.launch * 0.6 });
    if (s.fissures) {
      const a0 = Math.atan2(d.z1 - d.z0, d.x1 - d.x0); // fissures fan out from the dive line
      for (let k = 0; k < s.fissures; k++) {
        const a = a0 + (k + 0.5) / s.fissures * Math.PI * 2;
        this.diveQueue.push({ t: DIVE_FISSURE_T, kind: 1, x: p.x, z: p.z, dx: Math.cos(a), dz: Math.sin(a), r: s.r, dmg: s.dmg * DIVE_FISSURE_DMG, launch: s.launch * 0.8 });
      }
    }
  }

  /** A Comet Dive shockwave: every foe within r takes dmg and is thrown out and up (strongest at the centre). */
  private diveWave(x: number, z: number, r: number, dmg: number, launch: number): number {
    let hits = 0;
    this.blast(KILL_SRC.explosion, DIVE_HIT_COLOR, x, z, r);
    this.grid.query(x, z, r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - x, dz = e.z - z, d = Math.hypot(dx, dz);
      if (d > r + e.def.radius * e.scale) return;
      hits++;
      this.damageEnemy(e, dmg, DIVE_HIT_COLOR, 0, 0);
      if (!e.alive) return;
      const f = Math.max(0, 1 - d / (r + 0.01)), push = (8 + r * 1.5) * Math.max(0.3, f), nx = d > 0.01 ? dx / d : 1, nz = d > 0.01 ? dz / d : 0;
      this.impulse(e, nx * push, nz * push, launch * (0.6 + 0.4 * f));
    });
    this.bSrc = 0;
    return hits;
  }

  /** Force Shout aim. Manual: the aim. Auto: of the facing and the directions to the nearest foes, the one whose cone holds the
   *  most foes in reach (facing wins ties); Sundering Roar (full circle) just needs a foe in reach. null = none in reach. */
  private shoutAim(r: number, half: number): { dx: number; dz: number } | null {
    const p = this.player, cands = this.nearestN(p.x, p.z, r + 1, 40);
    if (!cands.length) return null;
    if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l } : { dx: p.faceX, dz: p.faceZ }; }
    const e0 = this.enemies[cands[0]], l0 = Math.hypot(e0.x - p.x, e0.z - p.z);
    if (half >= Math.PI - 1e-6) return l0 > 1e-6 ? { dx: (e0.x - p.x) / l0, dz: (e0.z - p.z) / l0 } : { dx: p.faceX, dz: p.faceZ };
    const cosH = Math.cos(half);
    const score = (ax: number, az: number) => {
      let n = 0;
      for (const i of cands) { const e = this.enemies[i], rx = e.x - p.x, rz = e.z - p.z, d = Math.hypot(rx, rz); if (d < 0.8 || (rx * ax + rz * az) / d >= cosH) n++; }
      return n;
    };
    let dx = p.faceX, dz = p.faceZ, best = score(dx, dz);
    for (const i of cands) {
      const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z); if (l < 0.3) continue;
      const ax = (e.x - p.x) / l, az = (e.z - p.z) / l, sc = score(ax, az);
      if (sc > best) { best = sc; dx = ax; dz = az; }
    }
    return best > 0 ? { dx, dz } : null;
  }

  private spawnShout(dx: number, dz: number, s: ReturnType<typeof shoutNums>, evo: boolean, echo: boolean, delay: number): void {
    const p = this.player, id = this.shoutId++, k = echo ? SHOUT_ECHO_DMG : 1;
    this.shoutWaves.push({ id, x: p.x, z: p.z, dx, dz, half: s.half, r: s.r, d: 0, dmg: s.dmg * k, stun: s.stun, hurl: s.hurl, evo, echo, delay, hit: new Set() });
    if (delay <= 0) this.events.push({ t: 'shout', id, x: p.x, z: p.z, dx, dz, half: s.half, r: s.r, speed: SHOUT_SPEED * this.pace, echo, evolved: evo });
  }

  /** Foes the shout front has reached (inside the cone) are hit once: damage, then hurled out (lofted) and stunned. */
  private shoutHit(w: ShoutWave): void {
    const cosH = Math.cos(w.half), full = w.half >= Math.PI - 1e-6;
    this.curSrc = 'shout';
    this.blast(KILL_SRC.explosion, SHOUT_HIT_COLOR, w.x, w.z, w.r);
    this.grid.query(w.x, w.z, w.d + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || w.hit.has(e.id)) return;
      const rx = e.x - w.x, rz = e.z - w.z, d = Math.hypot(rx, rz), er = e.def.radius * e.scale;
      if (d - er > w.d || d > w.r + er) return;
      if (!full && d > 0.8 && (rx * w.dx + rz * w.dz) / d < cosH) return; // outside the cone (anything touching him is caught)
      w.hit.add(e.id);
      const nx = d > 0.01 ? rx / d : w.dx, nz = d > 0.01 ? rz / d : w.dz;
      this.damageEnemy(e, w.dmg, SHOUT_HIT_COLOR, 0, 0);
      if (!e.alive) return;
      const boss = e.def.kind === 'boss', k = e.elite ? 0.5 : 1;
      this.impulse(e, nx * w.hurl * k, nz * w.hurl * k, e.elite ? 0 : SHOUT_LOFT);
      this.freeze(e, w.stun * (boss ? SHOUT_BOSS : e.elite ? SHOUT_ELITE : 1)); // (Deep Chill lengthens it)
    });
    this.bSrc = 0;
  }

  // ---------- Twin Gaze (owner 2026-11-02) ----------
  /** Aim of the sweep. Manual aim: along the aim. Auto: of the facing and the directions to the nearest foes in front (within
   *  100° of the facing, so the beams leave the eyes forward), the one whose arc covers the most foes within reach (the
   *  facing wins ties); null = nothing in front within reach. */
  private gazeAim(reach: number, half: number): { dx: number; dz: number } | null {
    const p = this.player;
    if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l } : { dx: p.faceX, dz: p.faceZ }; }
    // owner 2026-11-02: Twin Gaze follows the auto-lock like the other skills — the best group in ANY direction (Aren turns to
    // face the sweep; render), no longer only foes within 100° of where he faces
    const fl = Math.hypot(p.faceX, p.faceZ) || 1, fx = p.faceX / fl, fz = p.faceZ / fl, front = -2, centre = -2;
    const cands = this.nearestN(p.x, p.z, reach + 1, 32).filter((i) => {
      const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z);
      return l > 0.3 && ((e.x - p.x) * fx + (e.z - p.z) * fz) / l > front;
    });
    if (!cands.length) return null;
    const cosH = Math.cos(half);
    const score = (ax: number, az: number) => {
      let n = 0;
      for (const i of cands) { const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z); if (((e.x - p.x) * ax + (e.z - p.z) * az) / l >= cosH) n++; }
      return n;
    };
    let best = score(fx, fz), bx = fx, bz = fz;
    for (const i of cands) {
      const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z), ax = (e.x - p.x) / l, az = (e.z - p.z) / l;
      if (ax * fx + az * fz < centre) continue; // keep the sweep's centre in front of the face
      const sc = score(ax, az); if (sc > best) { best = sc; bx = ax; bz = az; }
    }
    return { dx: bx, dz: bz };
  }

  /** Beam angles and landing distances for the current pass progress (eased). Even passes: beam 0 sweeps from the (-az, ax)
   *  side to the other while its landing point runs from GAZE_NEAR × reach out to full reach (beam 1 mirrors it), so the
   *  carved lines cross in an X; odd passes run the same lines back. */
  private gazePose(): void {
    const g = this.gaze, e = g.u * g.u * (3 - 2 * g.u), back = g.pass % 2 === 1;
    const a = back ? 2 * e - 1 : 1 - 2 * e, dk = back ? 1 - (1 - GAZE_NEAR) * e : GAZE_NEAR + (1 - GAZE_NEAR) * e;
    g.th[0] = g.half * a; g.th[1] = -g.half * a; g.d[0] = g.d[1] = g.reach * dk;
  }

  private updateGaze(): void {
    const g = this.gaze, p = this.player;
    // Pyre Sight: burning lines age; every GAZE_BURN_TICK ticks each foe on one takes a burn hit (once, however many it touches)
    if (++this.gazeTk % GAZE_BURN_TICK === 0) {
      const hit = this.gazeBurnHit; hit.clear();
      this.curSrc = 'gaze';
      for (const b of this.gazeBurns) {
        if (!b.alive) continue;
        const mx = (b.x0 + b.x1) / 2, mz = (b.z0 + b.z1) / 2, lx = b.x1 - b.x0, lz = b.z1 - b.z0, L2 = lx * lx + lz * lz || 1e-6;
        this.grid.query(mx, mz, Math.sqrt(L2) / 2 + GAZE_BURN_W + 2, (i) => {
          const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
          const u = Math.max(0, Math.min(1, ((e.x - b.x0) * lx + (e.z - b.z0) * lz) / L2));
          const cx = b.x0 + lx * u - e.x, cz = b.z0 + lz * u - e.z, er = e.def.radius * e.scale;
          if (cx * cx + cz * cz > (GAZE_BURN_W + er) ** 2) return;
          hit.add(e.id);
          this.burn(e, b.dmg / (GAZE_BURN_TICK * DT), GAZE_BURN_TICK * DT + SCORCH_AFTERBURN, 'gaze'); // BURN (owner 2026-11-02): the shared burn status
        });
      }
    }
    for (const b of this.gazeBurns) if (b.alive && (b.t -= DT) <= 0) b.alive = false;
    if (!g.phase) return;
    if (this.wlv('gaze') <= 0) { g.phase = 0; return; }
    g.t += DT;
    if (g.phase === 1) {
      if (g.t < GAZE_WIND - 1e-9) return;
      g.phase = 2; g.t = 0; g.pass = 0; g.u = 0; this.gazeHit[0].clear(); this.gazeHit[1].clear();
      this.gazePose(); g.thP[0] = g.th[0]; g.thP[1] = g.th[1]; g.dP[0] = g.d[0]; g.dP[1] = g.d[1];
      for (let b = 0; b < 2; b++) {
        const c = Math.cos(g.th[b]), s = Math.sin(g.th[b]);
        this.gazeLand[b * 2] = p.x + (g.ax * c - g.az * s) * g.d[b]; this.gazeLand[b * 2 + 1] = p.z + (g.az * c + g.ax * s) * g.d[b];
      }
      this.events.push({ t: 'gazePass', id: g.id, k: 0 });
    }
    g.thP[0] = g.th[0]; g.thP[1] = g.th[1]; g.dP[0] = g.d[0]; g.dP[1] = g.d[1];
    g.u = Math.min(1, g.u + DT / GAZE_PASS);
    this.gazePose();
    // each beam hits every foe it crossed this tick (between last tick's angle and this one's), once per pass
    this.curSrc = 'gaze';
    this.blast(KILL_SRC.lance, GAZE_HIT_COLOR, p.x, p.z, 0); // kills: burned away in the beam's colour
    const sx = -g.az, sz = g.ax;
    for (let b = 0; b < 2; b++) {
      const lo = Math.min(g.thP[b], g.th[b]), hi = Math.max(g.thP[b], g.th[b]), R = Math.max(g.dP[b], g.d[b]), hit = this.gazeHit[b];
      this.grid.query(p.x, p.z, R + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
        const rx = e.x - p.x, rz = e.z - p.z, r = Math.hypot(rx, rz), er = e.def.radius * e.scale;
        if (r - er > R) return;
        const phi = Math.atan2(rx * sx + rz * sz, rx * g.ax + rz * g.az), dl = Math.asin(Math.min(1, (g.halfW + er) / Math.max(r, 1e-3)));
        if (phi < lo - dl || phi > hi + dl) return;
        hit.add(e.id);
        const c = Math.cos(phi), s = Math.sin(phi);
        this.damageEnemy(e, g.dmg, GAZE_HIT_COLOR, (g.ax * c + sx * s) * 1.2, (g.az * c + sz * s) * 1.2);
      });
      // where the beam meets the ground: carve props along the beam now and then; Pyre Sight leaves burning segments
      const c = Math.cos(g.th[b]), s = Math.sin(g.th[b]), dx = g.ax * c + sx * s, dz = g.az * c + sz * s;
      const lx = p.x + dx * g.d[b], lz = p.z + dz * g.d[b];
      if ((this.tick + b) % 4 === 0) this.terrain.damageLine(p.x, p.z, dx, dz, g.d[b], 0.6, g.dmg * 1.5, this.events);
      if (g.burn > 0 && (this.tick + b) % 3 === 0) {
        const seg = this.gazeBurns.find((q) => !q.alive) ?? this.gazeBurns.reduce((a, q) => (q.t < a.t ? q : a));
        Object.assign(seg, { alive: true, x0: this.gazeLand[b * 2], z0: this.gazeLand[b * 2 + 1], x1: lx, z1: lz, t: g.burn, life: g.burn, dmg: g.dmg * GAZE_BURN_DMG });
        this.gazeLand[b * 2] = lx; this.gazeLand[b * 2 + 1] = lz;
      } else if (g.burn <= 0) { this.gazeLand[b * 2] = lx; this.gazeLand[b * 2 + 1] = lz; }
    }
    this.bSrc = 0;
    if (g.u < 1) return;
    g.pass++;
    if (g.pass >= g.passes) { g.phase = 0; this.events.push({ t: 'gazeEnd', id: g.id }); return; }
    g.u = 0; this.gazeHit[0].clear(); this.gazeHit[1].clear();
    this.events.push({ t: 'gazePass', id: g.id, k: g.pass });
  }

  // ---------- Split Self (owner 2026-11-02) ----------
  /** Copies keep their place in a slowly turning ring around the hero, shoot the foe nearest to them, and fade when their
   *  time is up (never when evolved). Foes ignore them (no taunt). Then move the bolts. */
  private updateSplits(): void {
    const p = this.player, owned = this.wlv('split') > 0;
    const sn = splitNums(Math.max(1, this.wlv('split')), this.evolved.has('split'), this), cap = Math.max(14, this.moveSpeed * 1.6);
    let n = 0; for (const c of this.splits) if (c.alive) n++;
    if (n) this.splitAng += SPLIT_SPIN * DT;
    for (const c of this.splits) {
      if (!c.alive) continue;
      c.age += DT;
      if (Number.isFinite(c.t)) c.t -= DT;
      if (c.t <= 0 || !owned) { c.alive = false; this.events.push({ t: 'splitEnd', id: c.id, x: c.x, z: c.z }); continue; }
      // place in the ring by slot order, so the ring stays even as copies come and go
      let rank = 0; for (const q of this.splits) if (q.alive && q.slot < c.slot) rank++;
      const a = this.splitAng + (rank / n) * Math.PI * 2, tx = p.x + Math.cos(a) * SPLIT_RING, tz = p.z + Math.sin(a) * SPLIT_RING;
      let vx = (tx - c.x) * 7, vz = (tz - c.z) * 7; const sp = Math.hypot(vx, vz);
      if (sp > cap) { vx *= cap / sp; vz *= cap / sp; }
      c.vx = vx; c.vz = vz; c.x += vx * DT; c.z += vz * DT;
      c.cd -= DT;
      if (c.cd > 1e-6) { if (sp > 1.5 && c.cd < c.fire - 0.3) { c.faceX = vx / Math.hypot(vx, vz); c.faceZ = vz / Math.hypot(vx, vz); } continue; }
      const t = this.nearestEnemy(c.x, c.z, sn.range);
      if (t < 0) { c.cd = 0.15; continue; }
      const e = this.enemies[t], l = Math.hypot(e.x - c.x, e.z - c.z) || 1, dx = (e.x - c.x) / l, dz = (e.z - c.z) / l;
      c.faceX = dx; c.faceZ = dz; c.cd = c.fire; c.shots++;
      const b = this.splitShots.find((q) => !q.alive); if (!b) continue;
      Object.assign(b, { alive: true, id: this.splitShotId++, copy: c.id, x: c.x + dx * 0.5, y: p.y, z: c.z + dz * 0.5, vx: dx * sn.speed, vz: dz * sn.speed, life: (sn.range + 2) / sn.speed, dmg: c.dmg, evo: c.evo });
      this.events.push({ t: 'splitShot', id: b.id, copy: c.id, x: b.x, y: b.y, z: b.z, dx, dz, evolved: c.evo });
    }
    // bolts: straight, single target, gone on the first foe they touch or at the end of their reach
    this.curSrc = 'split';
    for (const b of this.splitShots) {
      if (!b.alive) continue;
      b.x += b.vx * DT; b.z += b.vz * DT; b.life -= DT;
      let best = -1, bd = Infinity;
      this.grid.query(b.x, b.z, SPLIT_SHOT_R + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const rr = SPLIT_SHOT_R + e.def.radius * e.scale, d2 = (e.x - b.x) ** 2 + (e.z - b.z) ** 2;
        if (d2 < rr * rr && d2 < bd) { bd = d2; best = i; }
      });
      if (best < 0 && b.life > 0) continue;
      b.alive = false;
      const sp = Math.hypot(b.vx, b.vz) || 1;
      if (best >= 0) this.damageEnemy(this.enemies[best], b.dmg, SPLIT_HIT_COLOR, b.vx / sp * 1.5, b.vz / sp * 1.5);
      this.events.push({ t: 'splitPop', id: b.id, x: b.x, y: b.y, z: b.z, hit: best >= 0 });
    }
  }

  // ---------- Crescent Edge (owner 2026-11-02) ----------
  /** Throw the next wave of the current throw (hands alternating, right first), aimed along the line with the most foes. */
  private fireCrescent(): void {
    const t = this.crescentThrow;
    if (!t || t.left <= 0 || this.wlv('crescent') <= 0) { this.crescentThrow = null; return; }
    const s = crescentNums(t.lvl, t.evo, this), k = t.k++;
    t.left--; t.wait = CRESCENT_GAP;
    if (t.left <= 0) this.crescentThrow = null;
    const aim = this.fingerAim(s.range, s.w / 2); // the line with the most foes on it (manual: along the aim)
    if (!aim) return; // everything in reach is gone: this wave stays in the hand
    const w = this.crescents.find((q) => !q.alive); if (!w) return;
    const p = this.player, hand = k % 2 ? -1 : 1, sx = -aim.dz, sz = aim.dx;
    const x0 = p.x + aim.dx * 0.6 + sx * hand * CRESCENT_HAND, z0 = p.z + aim.dz * 0.6 + sz * hand * CRESCENT_HAND;
    w.hit.clear();
    Object.assign(w, { alive: true, id: this.crescentId++, x0, z0, x: x0, z: z0, dx: aim.dx, dz: aim.dz, d: 0, range: s.range, w: s.w, speed: s.speed, dmg: s.dmg, hand, evo: t.evo });
    this.events.push({ t: 'crescent', id: w.id, x: x0, z: z0, dx: aim.dx, dz: aim.dz, w: s.w, hand, k, evolved: t.evo });
  }

  /** Move every wave; it cuts each foe once as its front edge passes (the band swept since last tick, so nothing tunnels).
   *  Crescent: the middle bulges ahead of the tips. Lunar Cross: an X, whose arm ends lead. */
  private updateCrescents(): void {
    this.curSrc = 'crescent';
    for (const w of this.crescents) {
      if (!w.alive) continue;
      const d0 = w.d; w.d = Math.min(w.range, w.d + w.speed * DT);
      w.x = w.x0 + w.dx * w.d; w.z = w.z0 + w.dz * w.d;
      const hw = w.w / 2, sx = -w.dz, sz = w.dx, bow = CRESCENT_BOW * w.w, depth = hw * Math.tan(CRESCENT_X_TILT), thick = CRESCENT_THICK * (w.evo ? 1.4 : 1);
      this.grid.query(w.x, w.z, hw + depth + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || w.hit.has(e.id)) return;
        const rx = e.x - w.x0, rz = e.z - w.z0, al = rx * w.dx + rz * w.dz, c = rx * sx + rz * sz, er = e.def.radius * e.scale;
        if (Math.abs(c) > hw + er) return;
        const q = Math.min(1, Math.abs(c) / hw), lead = w.evo ? depth * q : bow * (1 - q * q); // how far this part of the edge leads the centre line
        if (al > w.d + lead + er || al < d0 + lead - thick - er || al < -0.5) return;
        w.hit.add(e.id);
        const kb = CRESCENT_KNOCK * (e.elite ? 0.5 : 1);
        this.damageEnemy(e, w.dmg, CRESCENT_HIT_COLOR, w.dx * kb, w.dz * kb);
      });
      if ((this.tick + w.id) % 4 === 0) this.terrain.damageLine(w.x - sx * hw, w.z - sz * hw, sx, sz, w.w, 0.7, w.dmg * 1.5, this.events);
      if (w.d >= w.range) { w.alive = false; this.events.push({ t: 'crescentEnd', id: w.id, x: w.x, z: w.z }); }
    }
  }

  // ---------- Flare Barrage (owner 2026-11-02) ----------
  /** Aim of the stream. Manual aim: along the aim. Auto: toward the thickest cluster among the nearest foes in reach (the foe
   *  with the most others within 2.5 m; the nearer one wins ties); null = nothing in reach. */
  private barrageAim(range: number): { dx: number; dz: number } | null {
    const p = this.player;
    if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l } : { dx: p.faceX, dz: p.faceZ }; }
    const cands = this.nearestN(p.x, p.z, range, 24);
    let best = -1, bx = 0, bz = 0;
    for (const c of cands) {
      const e = this.enemies[c], l = Math.hypot(e.x - p.x, e.z - p.z); if (l < 0.3) continue;
      const n = this.countAround(e.x, e.z, 2.5);
      if (n > best) { best = n; bx = (e.x - p.x) / l; bz = (e.z - p.z) / l; }
    }
    return best < 0 ? null : { dx: bx, dz: bz };
  }

  /** One shot tick of the current volley: one pellet from the next hand (alternating, right first), or one from each hand
   *  when evolved (Solar Gatling). Each pellet gets a small seeded spread and flies dead straight. */
  private fireBarrageShot(): void {
    const v = this.barrageVolley;
    if (!v || v.left <= 0 || this.wlv('barrage') <= 0) { this.barrageVolley = null; return; }
    const s = barrageNums(v.lvl, v.evo, this), p = this.player;
    if (this.aimMode === 'manual') { const a = this.barrageAim(s.range); if (a) { v.dx = a.dx; v.dz = a.dz; } }
    const sx = -v.dz, sz = v.dx; // sideways (hand offset)
    for (let h = 0; h < s.streams; h++) {
      const hand = s.streams > 1 ? (h ? -1 : 1) : (v.k % 2 ? -1 : 1);
      const b = this.barrageShots.find((q) => !q.alive); if (!b) break;
      const a = (this.rng.next() * 2 - 1) * BARRAGE_SPREAD, c = Math.cos(a), sn = Math.sin(a);
      const dx = v.dx * c - v.dz * sn, dz = v.dx * sn + v.dz * c;
      const x = p.x + v.dx * BARRAGE_MUZZLE + sx * hand * BARRAGE_HAND, z = p.z + v.dz * BARRAGE_MUZZLE + sz * hand * BARRAGE_HAND;
      Object.assign(b, { alive: true, id: this.barrageId++, x, y: p.y, z, vx: dx * s.speed, vz: dz * s.speed, dist: 0, range: s.range, r: BARRAGE_SHOT_R, blast: s.blast, dmg: s.dmg, hand, evo: v.evo });
      this.events.push({ t: 'barrage', id: b.id, x, y: p.y, z, dx, dz, hand, k: v.k, evolved: v.evo });
    }
    v.k++; v.left--; v.next = this.tick + s.gap;
    if (v.left <= 0) this.barrageVolley = null;
  }

  /** Move every pellet; a pellet bursts on the first foe it touches, or at full range, in a mini fireball that hurts every
   *  foe within its burst radius. */
  private updateBarrageShots(): void {
    for (const b of this.barrageShots) {
      if (!b.alive) continue;
      const sp = Math.hypot(b.vx, b.vz) || 1;
      let step = sp * DT, out = false;
      if (b.dist + step >= b.range) { step = b.range - b.dist; out = true; } // never past its reach
      b.x += (b.vx / sp) * step; b.z += (b.vz / sp) * step; b.dist += step;
      let hit = false, bd = Infinity;
      this.grid.query(b.x, b.z, b.r + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const rr = b.r + e.def.radius * e.scale, d2 = (e.x - b.x) ** 2 + (e.z - b.z) ** 2;
        if (d2 < rr * rr && d2 < bd && Math.abs(e.y - b.y) < 1.5 + e.scale) { bd = d2; hit = true; }
      });
      if (!hit && !out) continue;
      b.alive = false;
      this.curSrc = 'barrage';
      const n = this.radialDamage(b.x, b.y, b.z, b.blast, b.dmg, false, BARRAGE_HIT_COLOR);
      this.burnArea(b.x, b.z, b.blast, b.dmg * BARRAGE_BURN_K, BARRAGE_BURN_T, 'barrage', BURN_GEN_MAX); // BURN (owner 2026-11-02): embers
      this.events.push({ t: 'barragePop', id: b.id, x: b.x, y: b.y, z: b.z, r: b.blast, n, hit, evolved: b.evo });
    }
  }

  /** Finger Beam: aim of the next needle. Manual aim: along the aim. Auto: of the directions to the nearest foes in reach,
   *  the one whose line passes through the most of them (the farther foe wins ties); null = nothing in reach.
   *  avoid (barrage follow-ups): unit directions (dx,dz pairs) this volley already used — a candidate line within
   *  FINGER_RETARGET rad of one of them only wins when no other line has a foe on it (the next-best line, else the same). */
  private fingerAim(range: number, halfW: number, avoid: number[] | null = null): { dx: number; dz: number } | null {
    const p = this.player;
    if (this.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l } : { dx: p.faceX, dz: p.faceZ }; }
    const cands = this.nearestN(p.x, p.z, range, 32);
    if (!cands.length) return null;
    const cosMin = Math.cos(FINGER_RETARGET);
    let best = -1, bl = -1, bx = p.faceX, bz = p.faceZ, fresh = false;
    for (const c of cands) {
      const e = this.enemies[c], l = Math.hypot(e.x - p.x, e.z - p.z); if (l < 0.3) continue;
      const ax = (e.x - p.x) / l, az = (e.z - p.z) / l;
      let isNew = true;
      if (avoid) for (let i = 0; i + 1 < avoid.length; i += 2) if (ax * avoid[i] + az * avoid[i + 1] > cosMin) { isNew = false; break; }
      if (fresh && !isNew) continue; // a fresh line already found: used lines can't win any more
      let n = 0;
      for (const j of cands) {
        const o = this.enemies[j], ox = o.x - p.x, oz = o.z - p.z, al = ox * ax + oz * az;
        if (al > 0 && al < range && Math.abs(ox * -az + oz * ax) < halfW + o.def.radius * o.scale) n++;
      }
      if ((isNew && !fresh) || n > best || (n === best && l > bl)) { best = n; bl = l; bx = ax; bz = az; fresh = isNew; }
    }
    return { dx: bx, dz: bz };
  }

  /** Fire the next shot of the current Finger Beam volley (re-aimed each time; Scarlet Fan: three needles per shot). From
   *  rank 4 the volley is a barrage: shots 0.1 s apart, each follow-up on the next-best line (manual aim: stepping aside
   *  FINGER_VOLLEY_SPREAD rad, alternating sides, so it rakes a narrow cone). */
  private fireFingerShot(): void {
    const b = this.fingerBurst;
    if (!b || b.left <= 0 || this.wlv('finger') <= 0) { this.fingerBurst = null; return; }
    const s = fingerNums(b.lvl, b.evo, this), k = b.k++;
    let aim = this.fingerAim(s.range, s.halfW, s.barrage && k > 0 ? b.used : null);
    b.left--; b.wait = s.gap + (k === 0 ? 1 : 0); // the first shot fires from the weapon pass, before this tick's volley countdown
    const last = b.left <= 0;
    if (last) this.fingerBurst = null;
    if (!aim) return; // everything in reach is gone: this follow-up stays in the finger
    if (s.barrage && k > 0 && this.aimMode === 'manual') { // manual aim: rake a narrow cone around the aim (0, +a, -a, +2a…)
      const a = (k % 2 ? 1 : -1) * Math.ceil(k / 2) * FINGER_VOLLEY_SPREAD, c = Math.cos(a), sn = Math.sin(a);
      aim = { dx: aim.dx * c - aim.dz * sn, dz: aim.dx * sn + aim.dz * c };
    }
    b.used.push(aim.dx, aim.dz);
    const p = this.player, hit = new Set<number>(); // a foe takes one hit per shot, even where fan needles overlap
    this.curSrc = 'finger';
    this.events.push({ t: 'shoot', x: p.x, y: p.y, z: p.z, w: 'finger', dx: aim.dx, dz: aim.dz }); // hero gesture + sound
    for (let f = 0; f < s.fan; f++) {
      const a = f === 0 ? 0 : (f % 2 ? 1 : -1) * Math.ceil(f / 2) * FINGER_FAN, c = Math.cos(a), sn = Math.sin(a);
      this.fireNeedle(aim.dx * c - aim.dz * sn, aim.dx * sn + aim.dz * c, s, k, f, b.evo, hit, last);
    }
  }

  /** One Finger Beam needle (hitscan): every foe on the line within reach, nearest first, up to the pierce count. */
  private fireNeedle(dx: number, dz: number, s: ReturnType<typeof fingerNums>, shot: number, fan: number, evo: boolean, hit: Set<number>, last = false): void {
    const p = this.player, R = s.range, on: { e: Enemy; al: number }[] = [];
    this.grid.query(p.x + dx * R / 2, p.z + dz * R / 2, R / 2 + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
      const rx = e.x - p.x, rz = e.z - p.z, al = rx * dx + rz * dz, er = e.def.radius * e.scale;
      if (al < 0 || al > R + er || Math.abs(rx * -dz + rz * dx) > s.halfW + er) return;
      on.push({ e, al });
    });
    on.sort((a, b) => a.al - b.al);
    const n = Math.min(on.length, s.pierce), hits: number[] = [];
    for (let k = 0; k < n; k++) {
      const e = on[k].e; hit.add(e.id); hits.push(e.x, e.y, e.z);
      this.damageEnemy(e, s.dmg, FINGER_HIT_COLOR, dx * FINGER_KNOCK, dz * FINGER_KNOCK);
    }
    const end = on.length > s.pierce ? Math.min(R, on[n - 1].al + 1) : R; // pierce spent: the needle dies just past its last foe
    // MAP-DESTRUCTION: the needle cuts through every prop on its drawn line (props never stop it)
    this.terrain.damageLine(p.x, p.z, dx, dz, end, s.halfW, s.dmg * 3, this.events, PROP_SRC.pierce);
    const y = p.y + 0.3;
    this.events.push({ t: 'finger', x: p.x + dx * 0.7, y, z: p.z + dz * 0.7, ex: p.x + dx * end, ey: y, ez: p.z + dz * end, len: R, hits, shot, fan, evolved: evo, last });
  }

  /** Fire the next bolt of the current Spirit Bolt burst (aim at its target if still alive, else the nearest foe / aim). */
  private fireBurstBolt(): void {
    const b = this.boltBurst; if (!b || b.left <= 0) { this.boltBurst = null; return; }
    const p = this.player;
    let t = b.tids.length ? this.findById(b.tids[b.k % b.tids.length]) : null;
    if (!t) { const n = this.nearestN(p.x, p.z, BOLT_RANGE, 1); t = n.length ? this.enemies[n[0]] : null; }
    const pr = this.allocProjectile();
    if (pr) {
      let dx = t ? t.x - p.x : p.aimX, dz = t ? t.z - p.z : p.aimZ; const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
      const sp = BOLT_SPEED * this.projSpeedMul * this.pace;
      pr.x = p.x; pr.y = p.y + 0.3; pr.z = p.z;
      pr.vx = dx * sp; pr.vz = dz * sp; pr.vy = t ? (t.y - p.y) * 2 : 0;
      pr.dmg = b.dmg; pr.r = b.evo ? 0.7 : 0.4; pr.life = 1.3 / this.pace; pr.pierce = boltPierce(b.lvl, b.evo, this.lancet); // Lancet: +1 per rank
      pr.comet = b.evo; pr.target = t ? t.id : -1; pr.lastHit = -1; pr.hostile = false; pr.kind = b.evo ? 1 : 0; pr.out = 0; pr.ret = false;
      this.events.push({ t: 'shoot', x: p.x, y: p.y, z: p.z, w: 'bolt', dx, dz });
    }
    b.k++; b.left--; b.wait = BOLT_GAP;
    if (b.left <= 0) this.boltBurst = null;
  }

  private updateTechShot(pr: Projectile): void {
    pr.x += pr.vx * DT; pr.y += pr.vy * DT; pr.z += pr.vz * DT; pr.life -= DT;
    let boom = pr.life <= 0;
    if (!boom && pr.kind !== 4) {
      this.grid.query(pr.x, pr.z, 2, (i) => {
        if (boom) return;
        const e = this.enemies[i]; if (!e.alive || e.id === pr.lastHit) return;
        const rr = 0.5 + e.def.radius * e.scale;
        if ((e.x - pr.x) ** 2 + (e.z - pr.z) ** 2 > rr * rr) return;
        boom = true;
      });
    }
    if (!boom) return;
    pr.alive = false;
    if (pr.kind === 4) {
      const lvl = this.wlv('zenith');
      this.curSrc = 'zenith';
      this.blast(KILL_SRC.explosion, 0xbfeaff, pr.x, pr.z, pr.r);
      this.craterDeep = ZENITH_CRATER_DEEP; // owner 2026-11-02: 'let zenith orb impact create a deeper crater by default'
      this.radialDamage(pr.x, HOVER_Y, pr.z, pr.r, pr.dmg, true, 0xbfeaff, 0.7 + 0.08 * lvl + (pr.comet ? 0.2 : 0));
      this.craterDeep = 1;
      this.bSrc = 0;
      this.events.push({ t: 'explode', x: pr.x, y: HOVER_Y, z: pr.z, r: pr.r, color: 0xbfeaff });
      this.events.push({ t: 'zenithImpact', x: pr.x, z: pr.z, r: pr.r, comet: pr.comet });
      if (pr.comet) for (let k = 0; k < ZENITH_SPLITS; k++) { const a = (k / ZENITH_SPLITS) * Math.PI * 2 + 0.4; this.launchShell(pr.x, HOVER_Y + 1, pr.z, pr.x + Math.cos(a) * pr.r * 1.1, pr.z + Math.sin(a) * pr.r * 1.1, 0.6, pr.dmg * ZENITH_SPLIT_DMG, pr.r * ZENITH_SPLIT_R, false, true); }
    } else {
      this.curSrc = 'bore';
      this.radialDamage(pr.x, HOVER_Y, pr.z, pr.r, pr.dmg, true, 0xffa060, pr.comet ? 0.45 : 0.3);
      this.events.push({ t: 'explode', x: pr.x, y: HOVER_Y, z: pr.z, r: pr.r, color: 0xffa060 });
      if (pr.comet) this.radialDamage(pr.x, HOVER_Y, pr.z, pr.r * 1.4, pr.dmg * 0.5, true, 0xffd090); // second burst
    }
  }

  private countAround(x: number, z: number, r: number): number {
    let n = 0;
    this.grid.query(x, z, r + 1, (i) => { const e = this.enemies[i]; if (e.alive && (e.x - x) ** 2 + (e.z - z) ** 2 < r * r) n++; });
    return n;
  }

  /** Demolition: fusion detonations scaled in fusions.ts radial() (r0 → r1, flat x, z, r0, r1 quads) since the last fix */
  private readonly fusBlasts: number[] = [];
  /** fusions.ts radial(): a detonation at (x, z) grew from r0 to r1 (render hint only) */
  noteBlast(x: number, z: number, r0: number, r1: number): void { this.fusBlasts.push(x, z, r0, r1); }
  /** Demolition (render hint only, never gameplay): the fusion's own cue events ('explode', 'fuse', 'mortarImpact', …) carry
   *  the radius the fusion computed before radial() grew it; give every event pushed since e0 at a grown detonation's centre
   *  with that radius the real one, so the VFX shows the actual blast. */
  private fixFusionBlasts(e0: number): void {
    const B = this.fusBlasts; if (!B.length) return;
    for (let i = e0; i < this.events.length; i++) {
      const ev = this.events[i] as { x?: number; z?: number; r?: number };
      if (typeof ev.r !== 'number' || typeof ev.x !== 'number' || typeof ev.z !== 'number') continue;
      for (let k = 0; k < B.length; k += 4) if (ev.x === B[k] && ev.z === B[k + 1] && ev.r === B[k + 2]) { ev.r = B[k + 3]; break; }
    }
    B.length = 0;
  }
  /** Shardstorm (owner 2026-11-03): the landed shard / star f bursts into n homing splinters of dmg each. They leap to the
   *  nearest foes within SHATTER_REACH (spread over distinct foes, cycling when there are fewer; fixed spots in a ring around
   *  the impact when there are none, so a shatter always reads as a radial burst) and land SHATTER_FLIGHT s later. */
  private skyShatter(f: Sim['falls'][number], n: number, dmg: number): void {
    const near = this.nearestN(f.x, f.z, SHATTER_REACH, n), r = f.r0 * SHATTER_R;
    for (let i = 0; i < n; i++) {
      const ft = SHATTER_FLIGHT / this.pace + i * SHATTER_STAGGER; // pace: the splinters fly slower (never hit harder)
      if (near.length) { const e = this.enemies[near[i % near.length]]; this.falls.push({ x: e.x, z: e.z, t: ft, t0: ft, k: i, tid: e.id, r, dmg, kind: SKY_KIND.splinter, ox: f.x, oz: f.z, r0: f.r0, d0: f.d0 }); }
      else { const a = (i / n) * Math.PI * 2 + f.k * 0.7, d = 1.3 + r; this.falls.push({ x: f.x + Math.cos(a) * d, z: f.z + Math.sin(a) * d, t: ft, t0: ft, k: i, tid: -1, r, dmg, kind: SKY_KIND.splinter, ox: f.x, oz: f.z, r0: f.r0, d0: f.d0 }); }
    }
  }
  /** Shardstorm: STAR_CRITS crits banked → a Fallen Star is called from the hands onto the thickest crowd within STAR_RANGE
   *  (the candidate with the most neighbours within STAR_DENSE_R; the nearest wins a tie). r0 / d0 = the volley's shard numbers. */
  private callStar(r0: number, d0: number): void {
    const p = this.player, cands = this.nearestN(p.x, p.z, STAR_RANGE, 32); if (!cands.length) return;
    let best = cands[0], bn = -1;
    for (const i of cands) {
      const e = this.enemies[i]; let n = 0;
      for (const j of cands) { const o = this.enemies[j]; if ((o.x - e.x) ** 2 + (o.z - e.z) ** 2 <= STAR_DENSE_R * STAR_DENSE_R) n++; }
      if (n > bn) { bn = n; best = i; }
    }
    const e = this.enemies[best], ft = STAR_FLIGHT / this.pace, r = r0 * STAR_R;
    this.skyCrits = Math.max(0, this.skyCrits - STAR_CRITS); this.starCd = STAR_GAP;
    this.falls.push({ x: e.x, z: e.z, t: ft, t0: ft, k: 0, tid: e.id, r, dmg: d0 * STAR_DMG, kind: SKY_KIND.star, ox: p.x, oz: p.z, r0, d0 });
    this.events.push({ t: 'starfall', x: e.x, z: e.z, r, dur: ft });
  }

  /** Player-sourced area damage. Also damages battlefield props; crater > 0 leaves a crater of radius r × crater. */
  /** depth multiplier for craters made by the next radialDamage (Zenith sets it around its impact) */
  private craterDeep = 1;
  /** bite = OVERTIME BITE share (Sim.lateBite) added per foe (fusions only; 0 = none) */
  radialDamage(x: number, y: number, z: number, r: number, dmg: number, knock: boolean, color: number, crater = 0, knockMul = 1, liftMul = 1, bite = 0): number {
    let hits = 0;
    this.terrain.damageCircle(x, z, r, dmg * 1.5, this.events, this.bSrc === KILL_SRC.explosion ? PROP_SRC.blast : this.propSrc());
    // MAP-FIRE: explosions can set the grass alight (chance and size by technique)
    const ign = (FIRE_IGNITE[this.curSrc] ?? 0) * this.igniteK;
    if (ign > 0 && this.fire.roll(ign)) this.fire.ignite(x, z, Math.max(1, r * 0.35), Math.min(14, Math.max(5, 4 + r * 1.4)), 0);
    if (crater > 0) this.terrain.addCrater(x, z, r * crater, this.tick, this.events, this.craterDeep);
    this.grid.query(x, z, r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - x, dz = e.z - z; const d = Math.hypot(dx, dz);
      if (d > r + e.def.radius * e.scale || Math.abs(e.y - y) > r) return;
      hits++;
      this.bigHit = knock; // Heavy Hands: a shoving blast (explosion / slam) is a big hit
      this.damageEnemy(e, bite > 0 ? dmg + this.lateBite(e, bite) : dmg, color, 0, 0);
      this.bigHit = false;
      if (knock && e.alive) { // blast: outward shove + launch, strongest at the centre
        const f = Math.max(0.25, 1 - d / (r + 0.01)), push = (10 + r * 2.4) * f * knockMul, nxk = d > 0.01 ? dx / d : 1, nzk = d > 0.01 ? dz / d : 0;
        this.impulse(e, nxk * push, nzk * push, (5 + r * 1.1) * f * liftMul);
      }
    });
    return hits;
  }

  private allocProjectile(): Projectile | null {
    for (const pr of this.projectiles) if (!pr.alive) { pr.alive = true; if (pr.ph) pr.ph.length = 0; return pr; }
    return null;
  }

  private updateProjectiles(): void {
    const p = this.player;
    for (const pr of this.projectiles) {
      if (!pr.alive) continue;
      if (pr.kind === 3 && !pr.hostile) { this.updateShell(pr); continue; }
      if (pr.kind === 7 && !pr.hostile) { this.updateMortarBall(pr); continue; }
      if (pr.kind >= 4 && !pr.hostile) { this.updateTechShot(pr); continue; }
      pr.life -= DT;
      if (pr.life <= 0) { pr.alive = false; continue; }
      if (pr.hostile) {
        pr.x += pr.vx * DT; pr.z += pr.vz * DT;
        pr.y += (p.y - pr.y) * 0.02;
        if ((pr.x - p.x) ** 2 + (pr.z - p.z) ** 2 < (pr.r + 0.5) ** 2 && Math.abs(pr.y - p.y) < 1.5) {
          this.hurtPlayer(pr.dmg); pr.alive = false;
        }
        continue;
      }
      if (pr.kind === 2) { this.updateDisc(pr); continue; }
      this.curSrc = 'bolt';
      // light homing
      if (pr.target >= 0) {
        const t = this.findById(pr.target);
        if (t) {
          const dx = t.x - pr.x, dz = t.z - pr.z, l = Math.hypot(dx, dz) || 1;
          const sp = Math.hypot(pr.vx, pr.vz);
          const k = 0.14;
          pr.vx += ((dx / l) * sp - pr.vx) * k; pr.vz += ((dz / l) * sp - pr.vz) * k;
          pr.vy = (t.y - pr.y) * 4;
        } else pr.target = -1;
      }
      pr.x += pr.vx * DT; pr.y += pr.vy * DT; pr.z += pr.vz * DT;
      if (pr.ph) { const sp = Math.hypot(pr.vx, pr.vz) || 1; this.terrain.damageSweep(pr.x, pr.z, pr.r, pr.dmg * 1.5, this.events, PROP_SRC.chip, pr.vx / sp, pr.vz / sp, pr.ph); } // MAP-DESTRUCTION: chips props it passes
      let consumed = false;
      this.grid.query(pr.x, pr.z, pr.r + 3, (i) => {
        if (consumed) return;
        const e = this.enemies[i]; if (!e.alive || e.id === pr.lastHit) return;
        const rr = pr.r + e.def.radius * e.scale;
        if ((e.x - pr.x) ** 2 + (e.z - pr.z) ** 2 < rr * rr && Math.abs(e.y - pr.y) < 1.5 + e.scale) {
          const sp = Math.hypot(pr.vx, pr.vz) || 1;
          const bc = pr.kind <= 1 ? this.boltColor : pr.comet ? 0xffd27a : 0x8fe9ff;
          this.damageEnemy(e, pr.dmg, bc, (pr.vx / sp) * 4, (pr.vz / sp) * 4);
          if (pr.kind <= 1) this.events.push({ t: 'boltHit', x: pr.x, y: pr.y, z: pr.z }); // Spirit Bolt impact sound
          pr.lastHit = e.id;
          if (pr.kind <= 1 && !pr.comet) this.boltSplash(e, pr.dmg, (pr.vx / sp) * 2, (pr.vz / sp) * 2);
          if (pr.comet) {
            const cr = cometBlastR(this); // Demolition: explosion radius / damage
            this.blast(KILL_SRC.explosion, this.boltColor, pr.x, pr.z, cr);
            this.radialDamage(pr.x, pr.y, pr.z, cr, pr.dmg * COMET_BLAST_DMG * this.blastDmgMul, true, this.boltColor, 0.4 + 0.05 * this.wlv('bolt'));
            this.bSrc = 0;
            this.events.push({ t: 'explode', x: pr.x, y: pr.y, z: pr.z, r: cr, color: this.boltColor });
          }
          if (pr.pierce > 0) { pr.pierce--; pr.target = -1; } else { consumed = true; }
        }
      });
      if (consumed) pr.alive = false;
    }
  }

  /** Spirit Bolt splash: up to BOLT_SPLASH_N other foes packed around the one just hit, nearest first (id breaks ties) */
  private boltSplash(hit: Enemy, dmg: number, kx: number, kz: number): void {
    const s = boltNums(this.wlv('bolt'), false, this), R = s.splashR, near: { e: Enemy; d: number }[] = [];
    this.grid.query(hit.x, hit.z, R + 2, (i) => {
      const e = this.enemies[i]; if (!e.alive || e === hit) return;
      const rr = R + e.def.radius * e.scale, d = (e.x - hit.x) ** 2 + (e.z - hit.z) ** 2;
      if (d < rr * rr) near.push({ e, d });
    });
    near.sort((a, b) => a.d - b.d || a.e.id - b.e.id);
    for (let k = 0; k < Math.min(BOLT_SPLASH_N, near.length); k++) this.damageEnemy(near[k].e, dmg * s.splashDmg, this.boltColor, kx, kz);
  }

  private updateDisc(pr: Projectile): void {
    const p = this.player;
    if (pr.bT !== undefined && pr.bt !== undefined) { // boomerang loop (owner 2026-11-02)
      pr.bt += DT;
      const u = Math.min(1, pr.bt / pr.bT);
      if (!pr.ret && u >= 0.5) { pr.ret = true; if (pr.ph) pr.ph.length = 0; } // the return leg may cut again
      if (u >= 1) {
        pr.alive = false;
        if (this.evolved.has('disc')) this.heal(DISC_EVO_HEAL);
        return;
      }
      // teardrop: along = L sin(pi u), across = (W/2) sin(2 pi u); the anchor slides from the throw point to the hero so it
      // always comes home to wherever he is now
      const along = pr.bL! * Math.sin(Math.PI * u), across = pr.bW! * 0.5 * Math.sin(2 * Math.PI * u), k = u * u;
      const ax = pr.bx! + (p.x - pr.bx!) * k, az = pr.bz! + (p.z - pr.bz!) * k;
      const nx = ax + pr.bdx! * along - pr.bdz! * across, nz = az + pr.bdz! * along + pr.bdx! * across;
      pr.vx = (nx - pr.x) / DT; pr.vz = (nz - pr.z) / DT; // render/knockback read the real heading
      pr.x = nx; pr.z = nz; pr.y += (p.y - pr.y) * 0.1;
      this.discHits(pr);
      return;
    }
    const sp = Math.hypot(pr.vx, pr.vz) || 24;
    if (!pr.ret) {
      pr.out -= sp * DT;
      if (pr.out <= 0) { pr.ret = true; if (pr.ph) pr.ph.length = 0; } // the return pass may cut again
    } else {
      const dx = p.x - pr.x, dz = p.z - pr.z, d = Math.hypot(dx, dz) || 1;
      pr.vx += ((dx / d) * sp * 1.15 - pr.vx) * 0.2; pr.vz += ((dz / d) * sp * 1.15 - pr.vz) * 0.2;
      if (d < 1.4) {
        pr.alive = false;
        if (this.evolved.has('disc')) this.heal(DISC_EVO_HEAL);
        return;
      }
    }
    pr.x += pr.vx * DT; pr.z += pr.vz * DT; pr.y += (p.y - pr.y) * 0.1;
    this.discHits(pr);
  }

  /** Scythe Disc cutting: props in its path, and every foe it passes (once per foe per pass) */
  private discHits(pr: Projectile): void {
    const sp = Math.hypot(pr.vx, pr.vz) || 24;
    this.curSrc = 'disc';
    if (pr.ph) { const l = Math.hypot(pr.vx, pr.vz) || 1; this.terrain.damageSweep(pr.x, pr.z, pr.r, pr.dmg * 5, this.events, PROP_SRC.cut, pr.vx / l, pr.vz / l, pr.ph); } // MAP-DESTRUCTION: the blade cuts props in half
    const pass = pr.ret ? 1 : 0;
    this.grid.query(pr.x, pr.z, pr.r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      // one hit per foe per pass: discCd stores (time + pass marker)
      if (e.discCd > this.time) return;
      const rr = pr.r + e.def.radius * e.scale;
      if ((e.x - pr.x) ** 2 + (e.z - pr.z) ** 2 < rr * rr && Math.abs(e.y - pr.y) < 1.5 + e.scale) {
        e.discCd = this.time + 0.35 + pass * 0.0;
        const l = sp || 1;
        this.damageEnemy(e, pr.dmg, 0x7fffe0, (pr.vx / l) * 5, (pr.vz / l) * 5);
      }
    });
  }

  private findById(id: number): Enemy | null {
    for (const e of this.enemies) if (e.alive && e.id === id) return e;
    return null;
  }

  /** Riftline: a wall erupts RIFT_OFFSET in front of the hero along (dx, dz), perpendicular to it, and starts rolling.
   *  k scales the damage (second wave). The split line gets a few modest scorch marks (the burned crack). */
  private spawnRiftWall(dx: number, dz: number, k: number, back: boolean): void {
    const p = this.player, rf = this.rift, x0 = p.x + dx * RIFT_OFFSET, z0 = p.z + dz * RIFT_OFFSET, id = rf.nextId++;
    rf.walls.push({ id, x0, z0, x: x0, z: z0, dx, dz, w: rf.w, len: rf.len, speed: rf.speed, d: 0, dmg: rf.dmg * k, back, hit: new Set(), ign: k >= 1 && this.fire.roll(FIRE_ATTACK_CHANCE) });
    this.events.push({ t: 'riftWall', id, x: x0, z: z0, dx, dz, w: rf.w, len: rf.len, speed: rf.speed, evolved: rf.evo, back, wave: k < 1 ? 2 : 1 });
    if (k < 1) return; // the second wave rides the same crack
    const n = Math.max(2, Math.round(rf.w / 2.6));
    for (let j = 0; j < n; j++) {
      const o = ((j + 0.5) / n - 0.5) * rf.w;
      this.terrain.addScorch(x0 - dz * o, z0 + dx * o, 1.15, this.tick);
    }
  }

  // ================= Skills B (owner 2026-11-02 'Make them all') =================
  // Snare Constellation (surround and collapse), Wayward Comet (ricochet), Binding Rings (restraint), Gravity Grip (lift, hold,
  // slam). Numbers: snareNums / cometNums / ringsNums / gripNums (skillStats.ts). Deterministic: seeded rng only, every
  // target pick is sorted with the enemy id breaking ties.

  /** a boss or a guardian (mini-boss): rings hold it briefly, Gravity Grip can't lift it */
  private bossLike(e: Enemy): boolean { return e.def.kind === 'boss' || e.def.kind === 'herald' || e === this.boss; }

  /** A pinned (Binding Rings) or held (Gravity Grip) ordinary foe: rooted and disarmed; knockback can't move it (the Grip
   *  places it itself). Its pin and freeze timers keep running. */
  private restrain(e: Enemy): void {
    e.vx = 0; e.vz = 0; e.kx = 0; e.kz = 0; e.ky = 0; e.lift = 0; e.airT = 0;
    if (e.pinT > 0) e.pinT = Math.max(0, e.pinT - DT);
    if (e.stunT > 0) e.stunT -= DT;
  }

  /** Centre of the thickest crowd among the nearest foes in reach: the foe with the most others within r (the nearer wins
   *  ties), then the centroid of the foes within r of it. Manual aim: the point `ahead` m along the aim. null = none in reach. */
  private crowdSpot(reach: number, r: number, ahead: number): { x: number; z: number } | null {
    const p = this.player;
    if (this.aimMode === 'manual') {
      const l = Math.hypot(p.aimX, p.aimZ), ax = l > 1e-6 ? p.aimX / l : p.faceX, az = l > 1e-6 ? p.aimZ / l : p.faceZ;
      return { x: p.x + ax * ahead, z: p.z + az * ahead };
    }
    const cands = this.nearestN(p.x, p.z, reach, 24);
    let best = -1, bi = -1;
    for (const c of cands) { const e = this.enemies[c], n = this.countAround(e.x, e.z, r); if (n > best) { best = n; bi = c; } }
    if (bi < 0) return null;
    const b = this.enemies[bi]; let sx = 0, sz = 0, k = 0;
    this.grid.query(b.x, b.z, r + 1, (i) => { const e = this.enemies[i]; if (e.alive && (e.x - b.x) ** 2 + (e.z - b.z) ** 2 < r * r) { sx += e.x; sz += e.z; k++; } });
    const x = k ? sx / k : b.x, z = k ? sz / k : b.z, d = Math.hypot(x, z), cap = ARENA_R - 1;
    return d > cap ? { x: x * cap / d, z: z * cap / d } : { x, z };
  }

  /** Foes within r of (x, z) (their body may poke out by half its radius) that pass `ok`, nearest first, the lower id winning
   *  ties. Reuses one list (valid until the next call). */
  private catchList(x: number, z: number, r: number, ok: (e: Enemy) => boolean): { i: number; d: number; id: number }[] {
    const L = this.skbList; L.length = 0;
    this.grid.query(x, z, r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || !ok(e)) return;
      const d = Math.hypot(e.x - x, e.z - z); if (d <= r + e.def.radius * e.scale * 0.5) L.push({ i, d, id: e.id });
    });
    L.sort((a, b) => a.d - b.d || a.id - b.id);
    return L;
  }

  private updateSkillsB(): void {
    for (const s of this.snares) if (s.alive) this.updateSnare(s);
    for (const c of this.comets) if (c.alive) this.updateComet(c);
    for (const r of this.ringHolds) if (r.alive) this.updateRing(r);
    for (const g of this.grips) if (g.alive) this.updateGrip(g);
  }

  // ---- Snare Constellation ----
  private castSnare(lvl: number, evo: boolean): void {
    const sn = snareNums(lvl, evo, this), at = this.crowdSpot(SNARE_RANGE, sn.r * 0.8, sn.r + 2), s = this.snares.find((q) => !q.alive);
    if (!at || !s) { this.wTimers.snare = 0.3; return; } // nothing in reach: wait, don't waste the cooldown
    this.wTimers.snare = sn.cd;
    s.alive = true; s.id = this.skbId++; s.x = at.x; s.z = at.z; s.r = sn.r; s.n = sn.n; s.a0 = this.rng.next() * Math.PI * 2; s.t = 0;
    s.hang = sn.hang; s.fly = SNARE_FLY; s.phase = 0; s.dmg = sn.dmg; s.burst = sn.burst; s.br = sn.br; s.evo = evo; s.echoT = 0; s.hit.clear();
    this.events.push({ t: 'snare', id: s.id, x: s.x, z: s.z, r: s.r, n: s.n, a0: s.a0, evolved: evo });
  }

  /** The orbs hang (Starcage herds the foes inside inward), then slam to the centre: each orb's path strikes the foes it
   *  crosses (once per foe per cast), then the ring implodes, hurting the core and dragging it inward. Starcage echoes. */
  private updateSnare(s: SnareCast): void {
    s.t += DT;
    this.curSrc = 'snare';
    if (s.phase === 0) {
      if (s.evo) {
        const lo = s.r * 0.55, hi = s.r + 1.5;
        this.grid.query(s.x, s.z, hi + 1, (i) => {
          const e = this.enemies[i]; if (!e.alive || this.bossLike(e)) return;
          const dx = e.x - s.x, dz = e.z - s.z, d = Math.hypot(dx, dz);
          if (d >= lo && d <= hi) this.impulse(e, -dx / d * SNARE_CAGE, -dz / d * SNARE_CAGE, 0);
        });
      }
      if (s.t < s.hang - 1e-9) return;
      s.phase = 1;
      this.events.push({ t: 'snareSlam', id: s.id, x: s.x, z: s.z, r: s.r, fly: s.fly, evolved: s.evo });
    }
    if (s.phase === 1) {
      const u0 = Math.max(0, (s.t - DT - s.hang) / s.fly), u1 = Math.min(1, (s.t - s.hang) / s.fly);
      const outer = s.r * (1 - u0) + 0.3, inner = s.r * (1 - u1);
      this.grid.query(s.x, s.z, s.r + 2, (i) => {
        const e = this.enemies[i]; if (!e.alive || s.hit.has(e.id)) return;
        const dx = e.x - s.x, dz = e.z - s.z, er = e.def.radius * e.scale;
        for (let k = 0; k < s.n; k++) {
          const a = s.a0 + (k / s.n) * Math.PI * 2, c = Math.cos(a), sn = Math.sin(a), al = dx * c + dz * sn;
          if (al < inner - er || al > outer + er || Math.abs(-dx * sn + dz * c) > SNARE_PATH_W + er) continue;
          s.hit.add(e.id);
          this.damageEnemy(e, s.dmg, SNARE_HIT_COLOR, -c * 2, -sn * 2); // struck toward the centre
          return;
        }
      });
      if (u1 < 1) return;
      let n = 0;
      this.blast(KILL_SRC.explosion, SNARE_BURST_COLOR, s.x, s.z, s.br);
      this.terrain.damageCircle(s.x, s.z, s.br, s.burst * 1.5, this.events);
      this.terrain.addCrater(s.x, s.z, s.br * 0.45, this.tick, this.events, 0.6);
      this.grid.query(s.x, s.z, s.br + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const dx = e.x - s.x, dz = e.z - s.z, d = Math.hypot(dx, dz);
        if (d > s.br + e.def.radius * e.scale) return;
        n++;
        this.damageEnemy(e, s.burst, SNARE_BURST_COLOR, 0, 0);
        if (e.alive && d > 0.3) this.impulse(e, -dx / d * SNARE_SUCK, -dz / d * SNARE_SUCK, 0); // dragged into the centre
      });
      this.bSrc = 0;
      this.events.push({ t: 'snareCollapse', id: s.id, x: s.x, z: s.z, br: s.br, n, echo: false, evolved: s.evo });
      if (s.evo) { s.phase = 2; s.echoT = SNARE_ECHO; } else s.alive = false;
      return;
    }
    if ((s.echoT -= DT) > 1e-9) return; // Starcage: the echo bursts outward
    const R = s.br * 1.25;
    this.blast(KILL_SRC.explosion, SNARE_BURST_COLOR, s.x, s.z, R);
    const n = this.radialDamage(s.x, HOVER_Y, s.z, R, s.burst * SNARE_ECHO_DMG, true, SNARE_BURST_COLOR, 0, 0.8, 0.6);
    this.bSrc = 0;
    this.events.push({ t: 'snareCollapse', id: s.id, x: s.x, z: s.z, br: R, n, echo: true, evolved: true });
    s.alive = false;
  }

  // ---- Wayward Comet ----
  private castComet(lvl: number, evo: boolean): void {
    const p = this.player, cn = cometNums(lvl, evo, this);
    let dx = p.aimX, dz = p.aimZ, tid = -1;
    if (this.aimMode !== 'manual') {
      const t = this.nearestEnemy(p.x, p.z, COMET_RANGE);
      if (t < 0) { this.wTimers.comet = 0.3; return; } // nothing in reach: wait
      const e = this.enemies[t]; dx = e.x - p.x; dz = e.z - p.z; tid = e.id;
    }
    const l = Math.hypot(dx, dz); if (l < 1e-6) { dx = p.faceX; dz = p.faceZ; } else { dx /= l; dz /= l; }
    if (!this.spawnComet(p.x + dx * 0.9, p.z + dz * 0.9, dx, dz, tid, cn.speed, COMET_R, cn.dmg, cn.hits, cn.seek, cn.splits, false, evo, -1)) { this.wTimers.comet = 0.3; return; }
    this.wTimers.comet = cn.cd;
  }

  private spawnComet(x: number, z: number, dx: number, dz: number, tid: number, speed: number, r: number, dmg: number, hits: number, seek: number, splits: number, shard: boolean, evo: boolean, skip: number): CometShot | null {
    const c = this.comets.find((q) => !q.alive); if (!c) return null;
    c.alive = true; c.id = this.skbId++; c.x = x; c.z = z; c.vx = dx * speed; c.vz = dz * speed; c.speed = speed; c.r = r; c.dmg = dmg;
    c.left = hits; c.tid = tid; c.life = COMET_LIFE / this.pace; c.seek = seek; c.splits = splits; c.shard = shard; c.evo = evo; c.hits = 0;
    c.hit.clear(); if (skip >= 0) c.hit.add(skip);
    this.events.push({ t: 'comet', id: c.id, x, z, dx, dz, shard, evolved: evo });
    return c;
  }

  /** Foes within `seek` of (x, z) (to their body) the comet hasn't struck, nearest first (the lower id wins ties). */
  private cometCands(x: number, z: number, seek: number, hit: Set<number>): { i: number; d: number; id: number }[] {
    const L = this.skbList; L.length = 0;
    this.grid.query(x, z, seek + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || hit.has(e.id)) return;
      const d = Math.hypot(e.x - x, e.z - z) - e.def.radius * e.scale; if (d <= seek) L.push({ i, d, id: e.id });
    });
    L.sort((a, b) => a.d - b.d || a.id - b.id);
    return L;
  }
  /** cometCands in the order the comet takes them: foes at least COMET_HOP away first (nearest first), then the closer ones */
  private cometOrder(c: CometShot): { i: number; d: number; id: number }[] {
    const L = this.cometCands(c.x, c.z, c.seek, c.hit);
    return L.filter((o) => o.d >= COMET_HOP).concat(L.filter((o) => o.d < COMET_HOP));
  }

  /** Steer hard at its foe (re-picked if that one died; after its first hit, a comet flying straight keeps looking) and strike
   *  it on contact, passing over the others (flying straight, it strikes the first unstruck foe it touches); then turn sharply
   *  to the next foe (cometOrder: a hop away, so the zig-zag reads). Spent: Splitstar bursts into shards that each take a
   *  different one of the next foes. */
  private updateComet(c: CometShot): void {
    c.life -= DT;
    this.curSrc = 'comet';
    let t = c.tid >= 0 ? this.enemyById(c.tid) : null;
    if ((c.tid >= 0 && !t) || (c.tid < 0 && c.hits > 0)) { const L = this.cometOrder(c); t = L.length ? this.enemies[L[0].i] : null; c.tid = t ? t.id : -1; }
    if (t) { const dx = t.x - c.x, dz = t.z - c.z, l = Math.hypot(dx, dz); if (l > 1e-6) { c.vx = dx / l * c.speed; c.vz = dz / l * c.speed; } }
    const nx = c.x + c.vx * DT, nz = c.z + c.vz * DT;
    if (nx * nx + nz * nz > (ARENA_R - 0.5) ** 2) c.life = 0; else { c.x = nx; c.z = nz; }
    let hi = -1, bd = Infinity;
    this.grid.query(c.x, c.z, c.r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || c.hit.has(e.id) || (c.tid >= 0 && e.id !== c.tid)) return;
      const rr = c.r + e.def.radius * e.scale, d2 = (e.x - c.x) ** 2 + (e.z - c.z) ** 2;
      if (d2 < rr * rr && (d2 < bd || (d2 === bd && e.id < this.enemies[hi].id))) { bd = d2; hi = i; }
    });
    if (hi >= 0) {
      const e = this.enemies[hi], sp = Math.hypot(c.vx, c.vz) || 1, ex = e.x, ey = e.y + e.gripY, ez = e.z;
      c.hit.add(e.id); c.left--; c.hits++; c.life = Math.max(c.life, 0.9 / this.pace);
      this.damageEnemy(e, c.dmg, COMET_HIT_COLOR, c.vx / sp * 3, c.vz / sp * 3);
      if (c.left <= 0) {
        const split = c.splits > 0;
        this.events.push({ t: 'cometEnd', id: c.id, x: c.x, z: c.z, split, shard: c.shard });
        if (split) { // Splitstar: shards fan out, each after a different one of the nearest unstruck foes
          const L = this.cometOrder(c).slice(0, c.splits), a0 = Math.atan2(c.vz, c.vx);
          for (let k = 0; k < c.splits; k++) {
            const o = L[k], a = a0 + ((k + 0.5) / c.splits - 0.5) * 2.4;
            let dx = Math.cos(a), dz = Math.sin(a);
            if (o) { const q = this.enemies[o.i], l = Math.hypot(q.x - c.x, q.z - c.z) || 1; dx = (q.x - c.x) / l; dz = (q.z - c.z) / l; }
            this.spawnComet(c.x, c.z, dx, dz, o ? o.id : -1, c.speed * 0.9, COMET_SHARD_R, c.dmg * COMET_SPLIT_DMG, COMET_SPLIT_HITS, c.seek, 0, true, c.evo, e.id);
          }
        }
        c.alive = false; // after the shards took their pool slots (so none reuses this one mid-split)
        return;
      }
      const L = this.cometOrder(c), nt = L.length ? this.enemies[L[0].i] : null;
      let dx = 0, dz = 0;
      c.tid = nt ? nt.id : -1;
      if (nt) { const l = Math.hypot(nt.x - c.x, nt.z - c.z) || 1; dx = (nt.x - c.x) / l; dz = (nt.z - c.z) / l; c.vx = dx * c.speed; c.vz = dz * c.speed; }
      this.events.push({ t: 'cometHit', id: c.id, x: ex, y: ey, z: ez, dx, dz, left: c.left, shard: c.shard, evolved: c.evo });
    }
    if (c.life <= 0) { c.alive = false; this.events.push({ t: 'cometEnd', id: c.id, x: c.x, z: c.z, split: false, shard: c.shard }); }
  }

  // ---- Binding Rings ----
  private castRings(lvl: number, evo: boolean): void {
    const rn = ringsNums(lvl, evo, this), at = this.crowdSpot(RINGS_RANGE, rn.r, 6);
    const L = at ? this.catchList(at.x, at.z, rn.r, (e) => e.pinT <= 0 && !e.gripId) : [];
    if (!at || !L.length) { this.wTimers.rings = 0.3; return; } // nothing to bind: wait
    this.wTimers.rings = rn.cd;
    const rc = this.ringCasts[this.ringCastNext]; this.ringCastNext = (this.ringCastNext + 1) % this.ringCasts.length;
    rc.cast = this.skbId++; rc.hit.clear();
    let n = 0;
    for (const c of L) {
      if (n >= rn.n) break;
      const e = this.enemies[c.i], ring = this.ringHolds.find((q) => !q.alive); if (!ring) break;
      this.bindRing(ring, c.i, rn.hold * (this.bossLike(e) ? RINGS_BOSS : e.elite ? RINGS_ELITE : 1), rn.dmg, rn.blastR, evo, rc.cast); n++;
      rc.hit.add(e.id); // its own shatter hurts it; the cast's blasts don't
    }
    this.events.push({ t: 'rings', x: at.x, z: at.z, r: rn.r, n, evolved: evo });
  }

  private bindRing(ring: RingHold, ei: number, dur: number, dmg: number, blastR: number, evo: boolean, cast: number): void {
    const e = this.enemies[ei];
    e.pinT = dur; e.vx = 0; e.vz = 0; e.kx = 0; e.kz = 0; e.ky = 0; e.lift = 0; e.airT = 0;
    ring.alive = true; ring.id = this.skbId++; ring.ei = ei; ring.eid = e.id; ring.x = e.x; ring.y = e.y + e.gripY; ring.z = e.z;
    ring.s = e.def.radius * e.scale; ring.t = 0; ring.dur = dur; ring.dmg = dmg; ring.blastR = blastR; ring.evo = evo; ring.cast = cast;
  }

  /** Hold the ring on its foe; when the hold runs out it shatters and hurts the foe (Shackle Nova: and blasts and briefly pins
   *  the foes around it). A foe that dies first just drops its ring. */
  private updateRing(r: RingHold): void {
    const e = this.enemies[r.ei];
    if (!e.alive || e.id !== r.eid) { r.alive = false; this.events.push({ t: 'ringBreak', id: r.id, x: r.x, y: r.y, z: r.z, s: r.s, burst: false, blastR: 0, evolved: r.evo }); return; }
    r.t += DT; r.x = e.x; r.y = e.y + e.gripY; r.z = e.z;
    if (r.t < r.dur - 1e-9) return;
    r.alive = false; e.pinT = 0;
    this.curSrc = 'rings';
    if (r.dmg > 0) { this.blast(KILL_SRC.explosion, RINGS_HIT_COLOR, e.x, e.z, r.s + 0.8); this.damageEnemy(e, r.dmg, RINGS_HIT_COLOR, 0, 0); this.bSrc = 0; }
    if (r.blastR > 0) {
      const done = this.ringCasts.find((q) => q.cast === r.cast)?.hit; // each foe takes one blast per cast
      this.blast(KILL_SRC.explosion, RINGS_BLAST_COLOR, r.x, r.z, r.blastR);
      this.terrain.damageCircle(r.x, r.z, r.blastR, r.dmg, this.events);
      this.grid.query(r.x, r.z, r.blastR + 3, (i) => {
        const o = this.enemies[i]; if (!o.alive || i === r.ei || done?.has(o.id)) return;
        if (Math.hypot(o.x - r.x, o.z - r.z) > r.blastR + o.def.radius * o.scale) return;
        done?.add(o.id);
        this.damageEnemy(o, r.dmg * RINGS_BLAST_DMG, RINGS_BLAST_COLOR, 0, 0);
        if (!o.alive || o.pinT > 0 || o.gripId || this.bossLike(o)) return;
        const ring = this.ringHolds.find((q) => !q.alive);
        if (ring) this.bindRing(ring, i, RINGS_BLAST_PIN, 0, 0, r.evo, r.cast); else o.pinT = RINGS_BLAST_PIN;
      });
      this.bSrc = 0;
    }
    this.events.push({ t: 'ringBreak', id: r.id, x: r.x, y: r.y, z: r.z, s: r.s, burst: r.dmg > 0, blastR: r.blastR, evolved: r.evo });
  }

  // ---- Gravity Grip ----
  private castGrip(lvl: number, evo: boolean): void {
    const gn = gripNums(lvl, evo, this), at = this.crowdSpot(GRIP_RANGE, gn.r, 8), g = this.grips.find((q) => !q.alive);
    const L = at && g ? this.catchList(at.x, at.z, gn.r, (e) => !e.gripId) : [];
    if (!at || !g || !L.length) { this.wTimers.grip = 0.3; return; } // nothing to seize: wait
    this.wTimers.grip = gn.cd;
    g.alive = true; g.id = this.skbId++; g.x = at.x; g.z = at.z; g.r = gn.r; g.h = gn.h; g.t = 0; g.hold = gn.hold; g.dmg = gn.dmg;
    g.shockR = gn.shockR; g.deep = gn.deep; g.evo = evo; g.n = 0;
    for (const c of L) {
      if (g.n >= gn.n || g.n >= g.ei.length) break;
      const e = this.enemies[c.i]; if (this.bossLike(e)) continue; // too heavy to lift: the slam still hits it
      e.gripId = g.id; e.gripY = 0; e.vx = 0; e.vz = 0; e.kx = 0; e.kz = 0; e.ky = 0; e.lift = 0; e.airT = 0;
      const k = g.n++; g.ei[k] = c.i; g.eid[k] = e.id; g.x0[k] = e.x; g.z0[k] = e.z; g.hk[k] = 0.8 + 0.4 * ((e.id * 0.618034) % 1);
    }
    this.events.push({ t: 'grip', id: g.id, x: g.x, z: g.z, r: g.r, n: g.n, h: g.h, evolved: evo });
  }

  /** Lift (eased up, drifting part of the way to the centre), hold (a slow helpless bob), fall, then the slam: every lifted
   *  foe takes the slam, bosses inside the radius too; other foes near it take the ground shock and are thrown back. */
  private updateGrip(g: GripCast): void {
    g.t += DT;
    const t1 = GRIP_LIFT, t2 = t1 + g.hold, t3 = t2 + GRIP_FALL;
    for (let k = 0; k < g.n; k++) {
      const e = this.enemies[g.ei[k]]; if (!e.alive || e.id !== g.eid[k]) continue;
      let y: number, w = 1;
      if (g.t < t1) { const u = g.t / t1; w = 1 - (1 - u) ** 3; y = g.h * g.hk[k] * w; }
      else if (g.t < t2) y = g.h * g.hk[k] + Math.sin((g.t - t1) * 6 + g.eid[k]) * 0.12;
      else { const u = Math.min(1, (g.t - t2) / GRIP_FALL); y = g.h * g.hk[k] * (1 - u * u); }
      e.gripY = Math.max(0, y);
      e.x = g.x0[k] + (g.x - g.x0[k]) * GRIP_GATHER * w; e.z = g.z0[k] + (g.z - g.z0[k]) * GRIP_GATHER * w;
    }
    if (g.t < t3 - 1e-9) return;
    g.alive = false;
    this.curSrc = 'grip';
    const hit = this.skbSet; hit.clear();
    this.blast(KILL_SRC.explosion, GRIP_HIT_COLOR, g.x, g.z, g.r);
    let n = 0;
    for (let k = 0; k < g.n; k++) {
      const e = this.enemies[g.ei[k]]; if (!e.alive || e.id !== g.eid[k]) continue;
      e.gripId = 0; e.gripY = 0; hit.add(e.id); n++;
      this.damageEnemy(e, g.dmg, GRIP_HIT_COLOR, 0, 0);
      if (e.alive) this.impulse(e, 0, 0, 3.5); // a small bounce off the ground
    }
    const R = g.shockR, push = 10 * (g.evo ? 1.6 : 1);
    this.grid.query(g.x, g.z, R + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || hit.has(e.id) || e.gripId) return;
      const dx = e.x - g.x, dz = e.z - g.z, d = Math.hypot(dx, dz), er = e.def.radius * e.scale;
      if (this.bossLike(e)) { if (d <= g.r + er) this.damageEnemy(e, g.dmg, GRIP_HIT_COLOR, 0, 0); return; }
      if (d > R + er) return;
      const f = Math.max(0.3, 1 - d / R);
      this.damageEnemy(e, g.dmg * GRIP_SHOCK, GRIP_SHOCK_COLOR, 0, 0);
      if (e.alive && d > 0.05) this.impulse(e, dx / d * push * f, dz / d * push * f, (g.evo ? 5 : 3) * f);
    });
    this.terrain.damageCircle(g.x, g.z, g.r, g.dmg * 1.5, this.events);
    this.terrain.addCrater(g.x, g.z, g.r * 0.8, this.tick, this.events, g.deep);
    this.bSrc = 0;
    this.events.push({ t: 'gripSlam', id: g.id, x: g.x, z: g.z, r: g.r, shockR: R, n, evolved: g.evo });
  }

  /** Surge activation shockwave: a gentle outward shove (no damage, no launch). Radius and strength grow ~15% per level. */
  surgePush(x: number, z: number, tier: number): void {
    const r = SURGE_PUSH_RADIUS * (1 + 0.15 * (tier - 1)), k = SURGE_PUSH * (1 + 0.15 * (tier - 1));
    this.grid.query(x, z, r + 2, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const dx = e.x - x, dz = e.z - z, d = Math.hypot(dx, dz); if (d > r) return;
      const f = 1 - d / r * 0.6, nx = d > 0.01 ? dx / d : 1, nz = d > 0.01 ? dz / d : 0;
      this.impulse(e, nx * k * f, nz * k * f, 0);
    });
  }

  /** Sim-authoritative knockback. Heavier foes resist (mass^0.75); bosses are immovable. up = launch speed (m/s). */
  impulse(e: Enemy, ix: number, iz: number, up: number): void {
    if (!e.alive || e.def.kind === 'boss' || e.def.kind === 'herald') return; // THE NULL HERALD: immovable
    if (this.tw.knock !== 1) { ix *= this.tw.knock; iz *= this.tw.knock; up *= this.tw.knock; } // TRAINING knockback tweak
    // owner 2026-11-03: 'I can sit still and I'm untouchable' (normal run, no modifiers): foes shrug off knockback more the longer the run (bosses already immovable)
    const lk = lateKnockMul(this.time, this.endlessTime); if (lk < 1) { ix *= lk; iz *= lk; up *= lk; }
    if (e.era) { const ek = eraKnockMul(e.era); ix *= ek; iz *= ek; up *= ek; } // ENEMY ERAS: later foes shrug off more
    const hh = this.plv('heavyhands'); if (hh) { const k = 1 + PK2.hhKnock * hh * Math.min(HH_HEAVY, Math.max(1, Math.sqrt(e.def.mass))); ix *= k; iz *= k; } // Heavy Hands (ground shove only): up to HH_HEAVY x the bonus on heavy foes
    const m = Math.pow(e.def.mass, 0.75);
    e.kx += ix / m; e.kz += iz / m;
    const cap = 34; const sp = Math.hypot(e.kx, e.kz); if (sp > cap) { e.kx *= cap / sp; e.kz *= cap / sp; }
    if (up > 0) e.ky = Math.max(e.ky, Math.min(16, up / Math.sqrt(e.def.mass)));
  }

  // ================= Elemental families (owner 2026-11-03): FROST (chill / freeze) and STORM (Static Charge, Conductor) =================
  /** a frost trait is held: slow zones and thawing freezes feed the chill status (without one, foe movement is untouched) */
  get frostOn(): boolean { return this.plv('deepchill') > 0 || this.plv('frostbite') > 0; }
  /** Freeze a foe for t s (the caller already applied the boss / elite shares). Deep Chill lengthens it; with a frost trait
   *  held the foe thaws into a chill that outlasts the freeze by CHILL_THAW_T. Every freeze source routes through here. */
  freeze(e: Enemy, t: number): void {
    if (!e.alive || !(t > 0) || e.def.kind === 'herald') return; // THE NULL HERALD: never frozen
    const dc = this.plv('deepchill'); if (dc) t *= 1 + PK2.chillDur * dc;
    e.stunT = Math.max(e.stunT, t);
    if (this.frostOn) this.chill(e, CHILL_THAW_K, t + CHILL_THAW_T, false);
  }
  /** Chill a foe: k = fraction of its speed removed, for dur s. Never stacks: the harder strength and the longer time win,
   *  capped at CHILL_MAX. Deep Chill hardens every chill and (scaleDur) lengthens it; bosses / elites take a share. */
  chill(e: Enemy, k: number, dur: number, scaleDur = true): void {
    if (!e.alive || !(k > 0) || !(dur > 0) || e.def.kind === 'herald') return; // THE NULL HERALD: never slowed
    const dc = this.plv('deepchill');
    if (dc) { k *= 1 + PK2.chillHard * dc; if (scaleDur) dur *= 1 + PK2.chillDur * dc; }
    k *= e.def.kind === 'boss' || e === this.boss ? CHILL_BOSS : e.elite ? CHILL_ELITE : 1;
    e.chillK = Math.min(CHILL_MAX, Math.max(e.chillK, k)); e.chillT = Math.max(e.chillT, dur);
  }
  /** Conductor: a foe struck by lightning (Chain Arc, Static Charge, the arc fusions) is CHARGED for condT s */
  chargeHit(e: Enemy): void { if (e.alive && this.plv('conductor')) e.chargeT = PK2.condT; }
  /** Static Charge: roll an arc off a direct hit of d damage on e (burn ticks and an arc's own hits never roll; one spark per
   *  foe per STATIC_CD; at most STATIC_TICK_CAP arcs a tick). The roll only touches the Rng while the trait is held. */
  private staticRoll(e: Enemy, d: number, dot: boolean): void {
    if (dot || this.inZap || e.zapCd > 0 || this.zapsThisTick >= STATIC_TICK_CAP) return;
    const sc = this.plv('static'); if (!sc || this.rng.next() >= PK2.staticP * sc) return;
    e.zapCd = STATIC_CD; this.zapsThisTick++;
    const hops = 1 + condBounces(this.plv('conductor')), R = PK2.staticR * this.areaMul, dmg = d * PK2.staticDmg;
    const pts = [e.x, e.y + 0.4, e.z], done = new Set<number>([e.id]);
    let cx = e.x, cz = e.z;
    const src = this.curSrc, bs = this.bSrc; this.curSrc = 'static'; this.bSrc = 0; this.inZap = true;
    for (let h = 0; h < hops; h++) {
      let best = -1, bd = R * R;
      this.grid.query(cx, cz, R, (i) => { const o = this.enemies[i]; if (!o.alive || o.edgeT >= 0 || o.mode < 0 || done.has(o.id)) return; const d2 = (o.x - cx) ** 2 + (o.z - cz) ** 2; if (d2 < bd) { bd = d2; best = i; } });
      if (best < 0) break;
      const o = this.enemies[best]; done.add(o.id); pts.push(o.x, o.y + 0.4, o.z);
      this.damageEnemy(o, dmg, STATIC_COLOR, 0, 0);
      this.chargeHit(o);
      cx = o.x; cz = o.z;
    }
    this.inZap = false; this.curSrc = src; this.bSrc = bs;
    if (pts.length > 3) this.events.push({ t: 'arc', pts, k: 'static' });
  }

  // ================= Fire family (owner 2026-11-02): Fire Breath, Flame Ring, the shared burn status =================
  /** Kindling: multiplier on every burn's damage (and on lava / fire hazards hurting foes) */
  get kindleDmg(): number { return 1 + PK2.kindDmg * this.plv('kindling'); }
  /** Kindling: multiplier on every burn's length */
  get kindleDur(): number { return 1 + PK2.kindDur * this.plv('kindling'); }

  /** Set a foe burning (src/core/burn.ts rules: never stacks; the stronger rate and the larger remaining pool win).
   *  dps already carries the hero's damage multiplier; kindle = apply Kindling (spread burns inherit it instead). */
  burn(e: Enemy, dps: number, dur: number, src: string, gen = 0, kindle = true): void {
    if (!e.alive) return;
    if (kindle) { dps *= this.kindleDmg; dur *= this.kindleDur; }
    mergeBurn(e, dps, dur, src, gen);
  }
  /** set every foe within r of (x, z) burning */
  private burnArea(x: number, z: number, r: number, dps: number, dur: number, src: string, gen = 0): void {
    this.grid.query(x, z, r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      if (Math.hypot(e.x - x, e.z - z) <= r + e.def.radius * e.scale && Math.abs(e.y - HOVER_Y) < r + 1.5) this.burn(e, dps, dur, src, gen);
    });
  }

  /** Burn ticks: damage banks every tick and lands every BURN_TICK ticks (staggered by foe id) or when the burn runs out,
   *  so a burn deals exactly rate × time. Fire Breath rank 3+: burning foes pass it on to foes they touch. */
  private updateBurns(): void {
    const spread = this.tick % BURN_SPREAD_GAP === 0 && (this.wlv('breath') >= 3 || this.evolved.has('breath') || this.fusion.has('kiln') || this.fusion.has('magma')); // SKILL FUSIONS: the Fire Breath fusions keep its spread
    let lit = 0;
    for (const e of this.enemies) {
      if (!e.alive || e.burnT <= 0) continue;
      e.burnAcc += e.burnDps * Math.min(DT, e.burnT);
      const last = e.burnT <= DT + 1e-9;
      if (last || (this.tick + e.id) % BURN_TICK === 0) {
        const d = e.burnAcc; e.burnAcc = 0;
        this.curSrc = e.burnSrc || 'burn'; this.bSrc = 0;
        this.damageEnemy(e, d, e.burnSrc === 'gaze' ? GAZE_BURN_COLOR : BURN_COLOR, 0, 0, true);
        if (!e.alive) continue;
      }
      if (last) { e.burnT = 0; e.burnAcc = 0; continue; }
      e.burnT -= DT;
      if (spread && e.burnGen < BURN_GEN_MAX && lit < BURN_SPREAD_CAP) lit += this.spreadBurn(e, BURN_SPREAD_CAP - lit);
    }
  }
  /** a burning foe lights up to BURN_SPREAD_N touching foes that aren't burning (grid order: deterministic) */
  private spreadBurn(e: Enemy, cap: number): number {
    let n = 0; const er = e.def.radius * e.scale, max = Math.min(BURN_SPREAD_N, cap);
    this.grid.query(e.x, e.z, er + 2.6, (i) => {
      const o = this.enemies[i]; if (n >= max || o === e || !o.alive || o.burnT > 0) return;
      const t = er + o.def.radius * o.scale + BURN_TOUCH;
      if ((o.x - e.x) ** 2 + (o.z - e.z) ** 2 > t * t) return;
      this.burn(o, e.burnDps * BURN_SPREAD_K, e.burnT * BURN_SPREAD_DUR, e.burnSrc, e.burnGen + 1, false); n++;
    });
    return n;
  }
  /** a foe died while burning: Ember Heart heals (capped per second; No Healing blocks it), Fire Breath rank 5 queues a pop */
  private burningKill(e: Enemy): void {
    this.fusion.burnKill(e); // SKILL FUSIONS: Magma Front's burn victims pop into lava splashes
    const eh = this.plv('emberheart');
    if (eh) { const h = Math.min(PK2.emberHeal * eh, PK2.emberCap * eh - this.emberHealed); if (h > 0) { this.heal(h); this.emberHealed += h; } }
    if ((this.wlv('breath') >= 5 || this.evolved.has('breath')) && this.firePops.length < BREATH_POP_MAX) this.firePops.push({ x: e.x, z: e.z, t: BREATH_POP_DELAY });
  }

  /** Aim of a fire cone. Manual aim (or Assisted while steering): along the aim. Auto: of the facing and the directions to the
   *  nearest foes, the one whose cone (half-angle half, reach range) covers the most of them; null = nothing in reach. */
  private flameAim(range: number, half: number): { dx: number; dz: number } | null {
    const p = this.player;
    const cands = this.nearestN(p.x, p.z, range + 1.5, 40);
    if (this.aimMode === 'manual' || (this.aimMode === 'assist' && this.steer)) {
      if (!cands.length && this.opts.autoSkills) return null;
      const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l } : { dx: p.faceX, dz: p.faceZ };
    }
    if (!cands.length) return null;
    const score = (ax: number, az: number) => {
      let n = 0;
      for (const i of cands) { const e = this.enemies[i]; if (inCone(p.x, p.z, ax, az, half, range, e.x, e.z, e.def.radius * e.scale)) n++; }
      return n;
    };
    const fl = Math.hypot(p.faceX, p.faceZ) || 1;
    let bx = p.faceX / fl, bz = p.faceZ / fl, best = score(bx, bz);
    for (const i of cands) {
      const e = this.enemies[i], l = Math.hypot(e.x - p.x, e.z - p.z); if (l < 0.3) continue;
      const ax = (e.x - p.x) / l, az = (e.z - p.z) / l, sc = score(ax, az);
      if (sc > best) { best = sc; bx = ax; bz = az; }
    }
    return best > 0 ? { dx: bx, dz: bz } : null;
  }

  /** Cast dispatch for Fire Breath / Flame Ring (from fireTechnique). Returns true (handled). */
  private fireFlame(id: WeaponId, lvl: number, evo: boolean): boolean {
    const p = this.player;
    if (id === 'breath') {
      const b = this.breath;
      if (b.active) return true; // still breathing
      const s = breathNums(lvl, evo, this), aim = this.flameAim(s.range, s.half + s.sweep * 0.5);
      if (!aim) { this.wTimers.breath = 0.3; return true; } // nothing in reach: wait
      this.wTimers.breath = s.cd;
      Object.assign(b, { active: true, id: b.id + 1, t: 0, dur: s.dur, ax: aim.dx, az: aim.dz, dx: aim.dx, dz: aim.dz, pdx: aim.dx, pdz: aim.dz, tx: aim.dx, tz: aim.dz,
        range: s.range, half: s.half, sweep: s.sweep, lvl, evo, ign: this.fire.roll(FIRE_ATTACK_CHANCE), hits: 0 }); // MAP-FIRE: one 25 % roll per gout
      this.events.push({ t: 'breath', id: b.id, x: p.x, z: p.z, dx: aim.dx, dz: aim.dz, range: s.range, half: s.half, sweep: s.sweep, dur: s.dur, evolved: evo });
      return true;
    }
    // Flame Ring
    const s = flameRingNums(lvl, evo, this);
    if (this.nearestEnemy(p.x, p.z, s.r + 1) < 0) { this.wTimers.flamering = 0.3; return true; } // nothing in reach: wait
    this.wTimers.flamering = s.cd;
    const ign = this.fire.roll(FIRE_ATTACK_CHANCE); // MAP-FIRE: one 25 % roll per cast
    for (let k = 0; k < s.rings; k++) {
      const w = this.flameRings.find((q) => !q.alive); if (!w) break;
      Object.assign(w, { alive: true, id: this.fireId++, x: p.x, z: p.z, r: s.r, d: 0, speed: s.speed, dmg: s.dmg * (k ? RING2_DMG : 1), burnDps: s.burnDps, burnDur: s.burnDur,
        delay: k * RING_GAP, k, rings: s.rings, linger: k === s.rings - 1 ? s.linger : 0, ign: ign && k === 0, evo });
      w.hit.clear();
      if (k === 0) this.ringErupt(w);
    }
    return true;
  }
  private ringErupt(w: FlameRingWave): void {
    const p = this.player; w.x = p.x; w.z = p.z; // each ring erupts around where the hero stands now
    this.events.push({ t: 'flameRing', id: w.id, x: w.x, z: w.z, r: w.r, speed: w.speed, k: w.k, rings: w.rings, evolved: w.evo });
  }

  /** Per tick: the breath's sweep and flame hits, ring fronts, lingering circles, wyrm dives and rank-5 fire pops. */
  private updateFireSkills(): void {
    if (this.tick % 60 === 0) this.emberHealed = 0;
    const p = this.player, b = this.breath;
    if (b.active) {
      if (this.wlv('breath') <= 0) { b.active = false; this.events.push({ t: 'breathEnd', id: b.id }); }
      else {
        b.t += DT; b.pdx = b.dx; b.pdz = b.dz;
        // aim: manual follows the aim quickly; auto re-picks the best crowd now and then and turns toward it
        const manual = this.aimMode === 'manual' || (this.aimMode === 'assist' && this.steer);
        if (manual) { const l = Math.hypot(p.aimX, p.aimZ); if (l > 1e-6) { b.tx = p.aimX / l; b.tz = p.aimZ / l; } }
        else if (this.tick % BREATH_REAIM === 0) { const a = this.flameAim(b.range, b.half + b.sweep * 0.5); if (a) { b.tx = a.dx; b.tz = a.dz; } }
        const cur = Math.atan2(b.az, b.ax), want = Math.atan2(b.tz, b.tx);
        let da = want - cur; da = Math.atan2(Math.sin(da), Math.cos(da));
        const turn = (manual ? 9 : BREATH_TURN) * DT, na = cur + Math.max(-turn, Math.min(turn, da));
        b.ax = Math.cos(na); b.az = Math.sin(na);
        const sw = na + b.sweep * Math.sin((b.t / b.dur) * Math.PI * 2);
        b.dx = Math.cos(sw); b.dz = Math.sin(sw);
        if (b.hits * BREATH_TICK * DT <= b.t + 1e-9) { b.hits++; this.breathHit(); }
        if (b.ign && !b.evo && b.t >= b.dur * 0.5) { b.ign = false; this.fireIgnite(p.x + b.dx * b.range * 0.7, p.z + b.dz * b.range * 0.7, b.range); }
        if (b.t >= b.dur - 1e-9) {
          b.active = false;
          this.events.push({ t: 'breathEnd', id: b.id });
          if (b.evo) this.launchWyrm();
        }
      }
    }
    // Flame Ring fronts: each foe is hit once as the front passes it
    for (const w of this.flameRings) {
      if (!w.alive) continue;
      if (w.delay > 0) { w.delay -= DT; if (w.delay > 1e-9) continue; w.delay = 0; this.ringErupt(w); }
      w.d = Math.min(w.r, w.d + w.speed * DT);
      this.curSrc = 'flamering';
      this.blast(KILL_SRC.explosion, RING_HIT_COLOR, w.x, w.z, w.d);
      this.grid.query(w.x, w.z, w.d + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || w.hit.has(e.id)) return;
        const rx = e.x - w.x, rz = e.z - w.z, d = Math.hypot(rx, rz), er = e.def.radius * e.scale;
        if (d - er > w.d || d < w.d - RING_BAND - er - w.speed * DT || d > w.r + er) return; // the band just behind the front
        w.hit.add(e.id);
        this.damageEnemy(e, w.dmg, RING_HIT_COLOR, 0, 0);
        if (!e.alive) return;
        this.burn(e, w.burnDps, w.burnDur, 'flamering');
        const k = this.bossLike(e) ? 0 : e.elite ? 2 : 5; if (k && d > 0.01) this.impulse(e, (rx / d) * k, (rz / d) * k, 0);
      });
      this.bSrc = 0;
      if (w.d >= w.r - 1e-9) {
        w.alive = false;
        if (w.ign) { const a = this.fire.rand() * Math.PI * 2; this.fireIgnite(w.x + Math.cos(a) * w.r, w.z + Math.sin(a) * w.r, w.r); }
        if (w.linger > 0) {
          const L = this.flameLingers.find((q) => !q.alive) ?? this.flameLingers.reduce((a, q) => (q.t < a.t ? q : a));
          Object.assign(L, { alive: true, id: this.fireId++, x: w.x, z: w.z, r: w.r, w: RING_LINGER_W, t: w.linger, life: w.linger, burnDps: w.burnDps, burnDur: w.burnDur * 0.5, evo: w.evo });
          this.events.push({ t: 'flameLinger', id: L.id, x: L.x, z: L.z, r: L.r, w: L.w, dur: L.life, evolved: L.evo });
        }
      }
    }
    // lingering burning circles: foes in the band catch fire
    for (const L of this.flameLingers) {
      if (!L.alive) continue;
      if ((L.t -= DT) <= 0) { L.alive = false; continue; }
      if ((this.tick + L.id) % RING_LINGER_TICK) continue;
      this.grid.query(L.x, L.z, L.r + L.w + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive) return;
        const d = Math.hypot(e.x - L.x, e.z - L.z);
        if (Math.abs(d - L.r) <= L.w / 2 + e.def.radius * e.scale) this.burn(e, L.burnDps, L.burnDur, 'flamering');
      });
    }
    // Wyrmstorm dives
    for (const y of this.wyrms) {
      if (!y.alive) continue;
      if ((y.t += DT) < y.dur - 1e-9) continue;
      y.alive = false;
      this.curSrc = 'breath';
      this.blast(KILL_SRC.explosion, WYRM_COLOR, y.x1, y.z1, y.r);
      this.radialDamage(y.x1, HOVER_Y, y.z1, y.r, y.dmg, true, WYRM_COLOR, 0, 0.8, 0.5);
      this.bSrc = 0;
      this.burnArea(y.x1, y.z1, y.r, y.burnDps, y.burnDur, 'breath');
      this.events.push({ t: 'wyrmBurst', id: y.id, x: y.x1, z: y.z1, r: y.r }, { t: 'explode', x: y.x1, y: HOVER_Y, z: y.z1, r: y.r, color: WYRM_COLOR });
      if (y.ign) this.fireIgnite(y.x1, y.z1, y.r * 2);
    }
    // rank-5 pops (queued, so chains resolve over ticks and stay bounded)
    if (this.firePops.length) {
      const s = breathNums(Math.max(1, this.wlv('breath')), this.evolved.has('breath'), this);
      for (let k = this.firePops.length - 1; k >= 0; k--) {
        const q = this.firePops[k]; q.t -= DT; if (q.t > 1e-9) continue;
        this.firePops.splice(k, 1);
        this.curSrc = 'breath';
        this.blast(KILL_SRC.explosion, FIRE_POP_COLOR, q.x, q.z, s.popR);
        this.radialDamage(q.x, HOVER_Y, q.z, s.popR, s.dmg * BREATH_POP_DMG, false, FIRE_POP_COLOR);
        this.bSrc = 0;
        this.burnArea(q.x, q.z, s.popR, s.burnDps, s.burnDur * 0.5, 'breath', BURN_GEN_MAX);
        this.events.push({ t: 'firePop', x: q.x, z: q.z, r: s.popR });
      }
    }
  }
  /** one flame hit: every foe in the cone takes a hit and is set burning */
  private breathHit(): void {
    const p = this.player, b = this.breath, s = breathNums(b.lvl, b.evo, this);
    this.curSrc = 'breath';
    this.grid.query(p.x, p.z, b.range + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      if (!inCone(p.x, p.z, b.dx, b.dz, b.half, b.range, e.x, e.z, e.def.radius * e.scale)) return;
      this.damageEnemy(e, s.dmg, BREATH_HIT_COLOR, 0, 0);
      this.burn(e, s.burnDps, s.burnDur, 'breath');
    });
  }
  /** Wyrmstorm: the flames coil into a fire wyrm that dives to the end of the cone and erupts */
  private launchWyrm(): void {
    const p = this.player, b = this.breath, s = breathNums(b.lvl, true, this);
    const y = this.wyrms.find((q) => !q.alive); if (!y) return;
    const x1 = p.x + b.ax * b.range, z1 = p.z + b.az * b.range, dur = b.range / s.wyrmSpeed;
    Object.assign(y, { alive: true, id: this.fireId++, x0: p.x, z0: p.z, x1, z1, t: 0, dur, r: s.wyrmR, dmg: s.dmg * WYRM_DMG, burnDps: s.burnDps, burnDur: s.burnDur, ign: b.ign });
    b.ign = false;
    this.events.push({ t: 'wyrm', id: y.id, x0: p.x, z0: p.z, x1, z1, dur, r: s.wyrmR });
  }
  /** MAP-FIRE: light the grass at (x, z) (a no-op on maps where fire is off) */
  private fireIgnite(x: number, z: number, reach: number): void { this.fire.ignite(x, z, 1.2, Math.min(12, Math.max(5, 3 + reach * 0.6)), 0); }

  // ================= ICE FAMILY (owner 2026-11-03: 'skills like ice that cause slowness') =================
  // Rime Breath (a sustained cone that chills and, on exposure, freezes), Hoarfrost Spikes (an eruption under the crowd that
  // leaves a slippery frost field), Glacier Lance (a homing, piercing lance with a frost trail) and Blizzard Veil (an aura that
  // slows constantly and flash-freezes). Every slow is Sim.chill, every freeze Sim.freeze: Deep Chill and Frostbite amplify all.
  /** freeze a foe for t s with the ICE_BOSS / ICE_ELITE shares and the encasement cue (public: the ice fusions use it too) */
  iceFreeze(e: Enemy, t: number): void {
    if (!e.alive || !(t > 0)) return;
    const was = e.stunT > 0;
    this.freeze(e, t * (e.def.kind === 'boss' || e === this.boss ? ICE_BOSS : e.elite ? ICE_ELITE : 1));
    if (!was && e.stunT > 0 && this.iceFreezeCues < ICE_FREEZE_CUES) { this.iceFreezeCues++; this.events.push({ t: 'iceFreeze', x: e.x, y: e.y + e.gripY, z: e.z, s: e.def.radius * e.scale }); }
  }
  /** lay a frost field at (x, z): foes on it are chilled k and take dps cold damage a second for life s (public: fusions) */
  frostField(x: number, z: number, r: number, life: number, k: number, dps: number, src: string, evo = false): FrostField | null {
    const f = this.frostFields.find((q) => !q.alive) ?? this.frostFields.reduce((a, q) => (q.life - q.t < a.life - a.t ? q : a));
    Object.assign(f, { alive: true, id: this.iceId++, x, z, r, t: 0, life, k, dps, src, evo });
    this.events.push({ t: 'frostField', id: f.id, x, z, r, dur: life, evolved: evo, src });
    return f;
  }
  /** roll an avalanche ring out from (x, z) to r (public: Eye of Winter, fusions) */
  iceRing(x: number, z: number, r: number, speed: number, dmg: number, chillK: number, chillT: number): void {
    const g = this.iceRings.find((q) => !q.alive); if (!g) return;
    Object.assign(g, { alive: true, id: this.iceId++, x, z, r, d: 0, speed, dmg, chillK, chillT }); g.hit.clear();
    this.events.push({ t: 'iceRing', id: g.id, x, z, r, speed });
  }
  /** Cast dispatch for the four ice skills (from fireTechnique). Returns true (handled). */
  private fireIce(id: WeaponId, lvl: number, evo: boolean): boolean {
    const p = this.player;
    switch (id) {
      case 'rime': {
        const b = this.rime;
        if (b.active) return true;
        const s = rimeNums(lvl, evo, this), aim = this.flameAim(s.range, s.half + s.sweep * 0.5);
        if (!aim) { this.wTimers.rime = 0.3; return true; }
        this.wTimers.rime = s.cd;
        Object.assign(b, { active: true, id: b.id + 1, t: 0, dur: s.dur, ax: aim.dx, az: aim.dz, dx: aim.dx, dz: aim.dz, pdx: aim.dx, pdz: aim.dz, tx: aim.dx, tz: aim.dz, range: s.range, half: s.half, sweep: s.sweep, lvl, evo, ign: false, hits: 0 });
        this.rimeExp.clear();
        this.events.push({ t: 'rime', id: b.id, x: p.x, z: p.z, dx: aim.dx, dz: aim.dz, range: s.range, half: s.half, sweep: s.sweep, dur: s.dur, evolved: evo });
        return true;
      }
      case 'spikes': {
        const s = spikesNums(lvl, evo, this), spot = this.crowdSpot(SPIKES_REACH, s.r * 0.8, s.r + 2);
        if (!spot) { this.wTimers.spikes = 0.3; return true; }
        this.wTimers.spikes = s.cd;
        const dx = spot.x - p.x, dz = spot.z - p.z, l = Math.hypot(dx, dz) || 1, ux = dx / l, uz = dz / l;
        for (let k = 0; k < s.bursts; k++) {
          // rank 3: the second ring is wider and softer around the same spot; evolved: three eruptions march away from the hero
          const march = evo ? k * SPIKES_MARCH : 0, wide = !evo && k === 1;
          this.spikeQ.push({ x: spot.x + ux * march, z: spot.z + uz * march, r: s.r * (wide ? SPIKES_RING2_R : 1), dmg: s.dmg * (wide ? SPIKES_RING2_DMG : 1), t: k * SPIKES_GAP, k, lvl, evo });
        }
        return true;
      }
      case 'glacier': {
        const s = glacierNums(lvl, evo, this);
        let ux: number, uz: number;
        if (this.aimMode === 'manual' || (this.aimMode === 'assist' && this.steer)) { const l = Math.hypot(p.aimX, p.aimZ); if (l > 1e-6) { ux = p.aimX / l; uz = p.aimZ / l; } else { ux = p.faceX; uz = p.faceZ; } if (this.opts.autoSkills && this.nearestEnemy(p.x, p.z, s.range) < 0) { this.wTimers.glacier = 0.2; return true; } }
        else { const spot = this.crowdSpot(s.range, 3, 8); if (!spot) { this.wTimers.glacier = 0.2; return true; } const dx = spot.x - p.x, dz = spot.z - p.z, l = Math.hypot(dx, dz) || 1; ux = dx / l; uz = dz / l; }
        this.wTimers.glacier = s.cd;
        for (let k = 0; k < s.count; k++) {
          const L = this.iceLances.find((q) => !q.alive); if (!L) break;
          const a = (k - (s.count - 1) / 2) * 0.14, c = Math.cos(a), sn = Math.sin(a), dx = ux * c - uz * sn, dz = ux * sn + uz * c;
          Object.assign(L, { alive: true, id: this.iceId++, x: p.x + dx * 0.8, z: p.z + dz * 0.8, dx, dz, speed: s.speed, r: s.r, dmg: s.dmg, pierce: s.pierce, d: 0, range: s.range, trailD: GLACIER_TRAIL_GAP * 0.5, chillK: s.chillK, chillT: s.chillT, freeze: s.freeze, lvl, evo });
          L.hit.clear();
          this.events.push({ t: 'iceLance', id: L.id, x: L.x, z: L.z, dx, dz, evolved: evo });
        }
        return true;
      }
      case 'blizzard': {
        const s = blizzardNums(lvl, evo, this);
        if (this.nearestEnemy(p.x, p.z, s.r + 1.5) < 0) { this.wTimers.blizzard = 0.2; return true; }
        this.wTimers.blizzard = s.cd;
        this.curSrc = 'blizzard';
        if (s.shove) this.blast(KILL_SRC.explosion, BLIZZARD_HIT_COLOR, p.x, p.z, s.r);
        this.grid.query(p.x, p.z, s.r + 3, (i) => {
          const e = this.enemies[i]; if (!e.alive) return;
          const rx = e.x - p.x, rz = e.z - p.z, d = Math.hypot(rx, rz); if (d > s.r + e.def.radius * e.scale) return;
          this.damageEnemy(e, s.dmg, BLIZZARD_HIT_COLOR, 0, 0);
          if (!e.alive) return;
          this.iceFreeze(e, s.freeze);
          if (s.shove && !this.bossLike(e) && d > 0.01) this.impulse(e, (rx / d) * s.shove, (rz / d) * s.shove, 1.5);
        });
        this.bSrc = 0;
        this.events.push({ t: 'blizzardPulse', x: p.x, z: p.z, r: s.r, evolved: evo });
        if (s.ring) this.iceRing(p.x, p.z, s.ringR, s.ringSpeed, s.dmg * s.ringDmg, s.chillK, 2.5);
        return true;
      }
    }
    return false;
  }
  /** Per tick: the rime cone's sweep and cold hits, spike eruptions, lances, ridges, avalanche rings, frost fields, the veil. */
  private updateIceSkills(): void {
    this.iceFreezeCues = 0;
    const p = this.player, b = this.rime;
    // Rime Breath: the cone sweeps like Fire Breath; every RIME_TICK ticks every foe in it takes a cold hit
    if (b.active) {
      if (this.wlv('rime') <= 0) { b.active = false; this.rimeExp.clear(); this.events.push({ t: 'rimeEnd', id: b.id }); }
      else {
        b.t += DT; b.pdx = b.dx; b.pdz = b.dz;
        const manual = this.aimMode === 'manual' || (this.aimMode === 'assist' && this.steer);
        if (manual) { const l = Math.hypot(p.aimX, p.aimZ); if (l > 1e-6) { b.tx = p.aimX / l; b.tz = p.aimZ / l; } }
        else if (this.tick % 15 === 0) { const a = this.flameAim(b.range, b.half + b.sweep * 0.5); if (a) { b.tx = a.dx; b.tz = a.dz; } }
        const cur = Math.atan2(b.az, b.ax), want = Math.atan2(b.tz, b.tx);
        let da = want - cur; da = Math.atan2(Math.sin(da), Math.cos(da));
        const turn = (manual ? 9 : 2.4) * DT, na = cur + Math.max(-turn, Math.min(turn, da));
        b.ax = Math.cos(na); b.az = Math.sin(na);
        const sw = na + b.sweep * Math.sin((b.t / b.dur) * Math.PI * 2);
        b.dx = Math.cos(sw); b.dz = Math.sin(sw);
        if (b.hits * RIME_TICK * DT <= b.t + 1e-9) { b.hits++; this.rimeHit(); }
        if (b.t >= b.dur - 1e-9) {
          b.active = false; this.rimeExp.clear();
          this.events.push({ t: 'rimeEnd', id: b.id });
          if (b.evo) { // Glacial Maw: the mist crystallises into a glacier at the end of the cone and bursts
            const s = rimeNums(b.lvl, true, this), x = p.x + b.ax * b.range, z = p.z + b.az * b.range, R = s.glacierR;
            this.curSrc = 'rime';
            this.blast(KILL_SRC.explosion, RIME_HIT_COLOR, x, z, R);
            this.radialDamage(x, HOVER_Y, z, R, s.dmg * s.glacierDmg, true, RIME_HIT_COLOR, 0, 0.6, 0.4);
            this.bSrc = 0;
            this.grid.query(x, z, R + 3, (i) => { const e = this.enemies[i]; if (e.alive && Math.hypot(e.x - x, e.z - z) <= R + e.def.radius * e.scale) { this.chill(e, s.chillK, s.chillT); this.iceFreeze(e, s.glacierFreeze); } });
            this.events.push({ t: 'glacierBurst', x, z, r: R });
          }
        }
      }
    }
    // Hoarfrost Spikes: queued eruptions land in order
    for (let k = 0; k < this.spikeQ.length;) {
      const q = this.spikeQ[k]; q.t -= DT;
      if (q.t > 1e-9) { k++; continue; }
      this.spikeQ.splice(k, 1); this.spikesErupt(q);
    }
    // Glacier Lances
    for (const L of this.iceLances) if (L.alive) this.updateIceLance(L);
    // avalanche rings: each foe is hit once as the front passes it
    for (const g of this.iceRings) {
      if (!g.alive) continue;
      g.d = Math.min(g.r, g.d + g.speed * DT);
      this.curSrc = 'blizzard';
      this.blast(KILL_SRC.explosion, BLIZZARD_HIT_COLOR, g.x, g.z, g.d);
      this.grid.query(g.x, g.z, g.d + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || g.hit.has(e.id)) return;
        const rx = e.x - g.x, rz = e.z - g.z, d = Math.hypot(rx, rz), er = e.def.radius * e.scale;
        if (d - er > g.d || d < g.d - BLIZZARD_RING_BAND - er - g.speed * DT || d > g.r + er) return;
        g.hit.add(e.id);
        this.damageEnemy(e, g.dmg, BLIZZARD_HIT_COLOR, 0, 0);
        if (!e.alive) return;
        this.chill(e, g.chillK, g.chillT);
        const k = this.bossLike(e) ? 0 : e.elite ? 2 : 5; if (k && d > 0.01) this.impulse(e, (rx / d) * k, (rz / d) * k, 0);
      });
      this.bSrc = 0;
      if (g.d >= g.r - 1e-9) g.alive = false;
    }
    // ice ridges: foes pressed against the wall are held back, chilled and frozen on first touch; the ridge shatters when it melts
    for (const w of this.iceRidges) {
      if (!w.alive) continue;
      w.t += DT;
      if (w.t >= w.life) {
        w.alive = false;
        this.curSrc = 'glacier';
        this.blast(KILL_SRC.explosion, GLACIER_HIT_COLOR, w.x, w.z, w.len * 0.5);
        this.radialDamage(w.x, HOVER_Y, w.z, w.len * 0.5, w.dmg * RIDGE_SHATTER, true, GLACIER_HIT_COLOR, 0, 0.5, 0.3);
        this.bSrc = 0;
        this.events.push({ t: 'iceRidgeEnd', id: w.id, x: w.x, z: w.z, len: w.len }, { t: 'frostburst', x: w.x, z: w.z, r: w.len * 0.5 });
        continue;
      }
      const hw = w.len / 2, nx = -w.dz, nz = w.dx;
      this.grid.query(w.x, w.z, hw + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || this.bossLike(e) || e.gripId) return;
        const rx = e.x - w.x, rz = e.z - w.z, al = rx * w.dx + rz * w.dz, sd = rx * nx + rz * nz, er = e.def.radius * e.scale, thick = 0.6 + er;
        if (Math.abs(al) > hw + er * 0.5 || Math.abs(sd) > thick) return;
        const side = sd >= 0 ? 1 : -1; e.x = w.x + w.dx * al + nx * side * thick; e.z = w.z + w.dz * al + nz * side * thick; // pushed out to the face
        const vn = e.vx * nx * side + e.vz * nz * side; if (vn < 0) { e.vx -= vn * nx * side; e.vz -= vn * nz * side; }
        if ((this.tick + e.id) % RIDGE_TICK === 0) { this.chill(e, 0.6, 1.0); if (!w.hit.has(e.id)) { w.hit.add(e.id); this.iceFreeze(e, RIDGE_FREEZE * this.persistMul); } }
      });
    }
    // frost fields: foes standing on one are chilled and take cold damage (every ICE_FIELD_TICK ticks, staggered by field)
    for (const f of this.frostFields) {
      if (!f.alive) continue;
      f.t += DT; if (f.t >= f.life) { f.alive = false; continue; }
      if ((this.tick + f.id) % ICE_FIELD_TICK) continue;
      this.curSrc = f.src; this.bSrc = 0;
      const d = f.dps * ICE_FIELD_TICK * DT;
      this.grid.query(f.x, f.z, f.r + 3, (i) => {
        const e = this.enemies[i]; if (!e.alive || e.lift > 0 || e.gripId) return;
        if ((e.x - f.x) ** 2 + (e.z - f.z) ** 2 > (f.r + e.def.radius * e.scale * 0.5) ** 2) return;
        this.chill(e, f.k, ICE_FIELD_TICK * DT + 0.4);
        if (d > 0) this.damageEnemy(e, d, ICE_FIELD_COLOR, 0, 0, true);
      });
    }
    // Blizzard Veil: always on while held; chills every BLIZZARD_CHILL_GAP ticks, cold ticks every BLIZZARD_TICK
    const bl = this.blizzard, lvl = this.wlv('blizzard');
    if (lvl > 0 && !this.training?.frozen) {
      const s = blizzardNums(lvl, this.evolved.has('blizzard'), this);
      if (!bl.on) { bl.on = true; bl.t = 0; }
      bl.t += DT; bl.r = s.r; bl.evo = s.ring; bl.next = Math.max(0, this.wTimers.blizzard);
      const chillNow = this.tick % BLIZZARD_CHILL_GAP === 0, hitNow = this.tick % BLIZZARD_TICK === 0;
      if (chillNow || hitNow) {
        this.curSrc = 'blizzard'; this.bSrc = 0;
        this.grid.query(p.x, p.z, s.r + 3, (i) => {
          const e = this.enemies[i]; if (!e.alive) return;
          if ((e.x - p.x) ** 2 + (e.z - p.z) ** 2 > (s.r + e.def.radius * e.scale) ** 2) return;
          if (chillNow) this.chill(e, s.chillK, BLIZZARD_CHILL_T);
          if (hitNow) this.damageEnemy(e, s.tick, BLIZZARD_HIT_COLOR, 0, 0, true);
        });
      }
    } else if (bl.on) { bl.on = false; bl.t = 0; }
  }
  /** one cold hit of the rime cone: every foe in it takes damage and a chill; the freeze counter rises on every foe hit in a
   *  row and resets for foes that left the cone (rimeExp / rimeNow, no allocation per hit) */
  private rimeHit(): void {
    const p = this.player, b = this.rime, s = rimeNums(b.lvl, b.evo, this), now = this.rimeNow;
    now.clear();
    this.curSrc = 'rime';
    this.grid.query(p.x, p.z, b.range + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      if (!inCone(p.x, p.z, b.dx, b.dz, b.half, b.range, e.x, e.z, e.def.radius * e.scale)) return;
      this.damageEnemy(e, s.dmg, RIME_HIT_COLOR, 0, 0);
      if (!e.alive) return;
      now.add(e.id);
      this.chill(e, s.chillK, s.chillT);
      const n = (this.rimeExp.get(e.id) ?? 0) + 1;
      if (n >= s.freezeHits) { this.rimeExp.set(e.id, 0); this.iceFreeze(e, s.freeze); } else this.rimeExp.set(e.id, n);
    });
    for (const id of this.rimeExp.keys()) if (!now.has(id)) this.rimeExp.delete(id);
  }
  /** a Hoarfrost Spikes eruption lands: every foe in the circle is hurt, launched and chilled (evolved: frozen); the shattered
   *  spikes leave a frost field */
  private spikesErupt(q: { x: number; z: number; r: number; dmg: number; k: number; lvl: number; evo: boolean }): void {
    const s = spikesNums(q.lvl, q.evo, this);
    this.curSrc = 'spikes';
    this.blast(KILL_SRC.explosion, SPIKES_HIT_COLOR, q.x, q.z, q.r);
    let n = 0;
    this.grid.query(q.x, q.z, q.r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const rx = e.x - q.x, rz = e.z - q.z, d = Math.hypot(rx, rz); if (d > q.r + e.def.radius * e.scale) return;
      n++;
      this.bigHit = true; this.damageEnemy(e, q.dmg, SPIKES_HIT_COLOR, 0, 0); this.bigHit = false;
      if (!e.alive) return;
      this.chill(e, s.chillK, s.chillT);
      if (s.freeze > 0) this.iceFreeze(e, s.freeze);
      if (!this.bossLike(e)) { const f = Math.max(0.3, 1 - d / (q.r + 0.01)), k = (e.elite ? 1.5 : 3) * f; this.impulse(e, d > 0.01 ? (rx / d) * k : k, d > 0.01 ? (rz / d) * k : 0, s.launch * f); }
    });
    this.bSrc = 0;
    this.terrain.addCrater(q.x, q.z, q.r * 0.35, this.tick, this.events, 0.5);
    this.events.push({ t: 'spikes', id: this.iceId++, x: q.x, z: q.z, r: q.r, k: q.k, n, evolved: q.evo });
    this.frostField(q.x, q.z, q.r * (q.evo ? 1.15 : 1), s.field, s.fieldK, s.fieldDps, 'spikes', q.evo);
  }
  /** a Glacier Lance flies: homes a little toward the nearest foe ahead it has not pierced, chills (evolved: freezes) every foe
   *  it passes, drops frost marks behind it, and shatters (rank 5) or raises a ridge (evolved) when spent */
  private updateIceLance(L: IceLance): void {
    const s = glacierNums(L.lvl, L.evo, this);
    // homing: turn toward the nearest unhit foe within 6 m that lies ahead
    { let best = -1, bd = 36; const sx = L.x, sz = L.z;
      this.grid.query(sx, sz, 6, (i) => { const e = this.enemies[i]; if (!e.alive || L.hit.has(e.id)) return; const rx = e.x - sx, rz = e.z - sz; if (rx * L.dx + rz * L.dz <= 0) return; const d2 = rx * rx + rz * rz; if (d2 < bd) { bd = d2; best = i; } });
      if (best >= 0) { const e = this.enemies[best], cur = Math.atan2(L.dz, L.dx), want = Math.atan2(e.z - sz, e.x - sx); let da = want - cur; da = Math.atan2(Math.sin(da), Math.cos(da)); const turn = GLACIER_TURN * DT, na = cur + Math.max(-turn, Math.min(turn, da)); L.dx = Math.cos(na); L.dz = Math.sin(na); } }
    const step = L.speed * DT;
    L.x += L.dx * step; L.z += L.dz * step; L.d += step; L.trailD += step;
    if (L.trailD >= GLACIER_TRAIL_GAP) { L.trailD -= GLACIER_TRAIL_GAP; this.frostField(L.x, L.z, s.trailR, s.trail, s.trailK, 0, 'glacier', L.evo); }
    this.curSrc = 'glacier';
    let spent = false;
    this.grid.query(L.x, L.z, L.r + 3, (i) => {
      const e = this.enemies[i]; if (!e.alive || L.hit.has(e.id) || spent) return;
      const rr = L.r + e.def.radius * e.scale; if ((e.x - L.x) ** 2 + (e.z - L.z) ** 2 > rr * rr) return;
      L.hit.add(e.id);
      this.damageEnemy(e, L.dmg, GLACIER_HIT_COLOR, L.dx * 2, L.dz * 2);
      this.events.push({ t: 'iceLanceHit', id: L.id, x: e.x, y: e.y, z: e.z });
      if (e.alive) { this.chill(e, L.chillK, L.chillT); if (L.freeze > 0) this.iceFreeze(e, L.freeze); }
      if (L.pierce !== Infinity && --L.pierce < 0) spent = true;
    });
    const out = L.d >= L.range || L.x * L.x + L.z * L.z > (ARENA_R - 0.5) ** 2;
    if (!spent && !out) return;
    L.alive = false;
    const shatter = s.shatter;
    if (shatter) {
      this.blast(KILL_SRC.explosion, GLACIER_HIT_COLOR, L.x, L.z, s.shatterR);
      this.radialDamage(L.x, HOVER_Y, L.z, s.shatterR, L.dmg * s.shatterDmg, false, GLACIER_HIT_COLOR);
      this.bSrc = 0;
      this.grid.query(L.x, L.z, s.shatterR + 3, (i) => { const e = this.enemies[i]; if (e.alive && Math.hypot(e.x - L.x, e.z - L.z) <= s.shatterR + e.def.radius * e.scale) this.chill(e, L.chillK, L.chillT); });
      this.events.push({ t: 'frostburst', x: L.x, z: L.z, r: s.shatterR });
    }
    this.events.push({ t: 'iceLanceEnd', id: L.id, x: L.x, z: L.z, shatter });
    if (s.ridge) { // Permafrost Spear: a ridge of ice rises across the spear's path where it came to rest
      const w = this.iceRidges.find((q) => !q.alive); if (!w) return;
      const d = Math.hypot(L.x, L.z), cap = ARENA_R - s.ridgeLen * 0.5 - 1, x = d > cap ? L.x * cap / d : L.x, z = d > cap ? L.z * cap / d : L.z;
      Object.assign(w, { alive: true, id: this.iceId++, x, z, dx: -L.dz, dz: L.dx, len: s.ridgeLen, t: 0, life: s.ridgeLife, dmg: L.dmg }); w.hit.clear();
      this.events.push({ t: 'iceRidge', id: w.id, x, z, dx: w.dx, dz: w.dz, len: w.len, dur: w.life });
    }
  }

  /** New traits (owner 2026-11-02): Chain Reaction blasts, Aftershock pulses from fresh craters, Scorched Earth ground damage */
  private updateNewTraits(): void {
    for (let k = this.chainQ.length - 1; k >= 0; k--) {
      const c = this.chainQ[k]; c.t -= DT; if (c.t > 1e-9) continue;
      this.chainQ.splice(k, 1);
      const lv = this.plv('chainreact'), R = PK2.chainR * this.areaMul * this.blastMul; // Demolition widens chain blasts
      this.curSrc = 'chainreact';
      this.blast(KILL_SRC.explosion, 0xffa040, c.x, c.z, R);
      this.radialDamage(c.x, HOVER_Y, c.z, R, PK2.chainDmg * lv * this.dmgMul * this.blastDmgMul, true, 0xffa040, 0, 0.6, 0.3);
      this.bSrc = 0;
      this.events.push({ t: 'explode', x: c.x, y: HOVER_Y, z: c.z, r: R, color: 0xffa040 });
    }
    const as = this.plv('aftershock');
    for (let k = this.shockQ.length - 1; k >= 0; k--) {
      const a = this.shockQ[k]; a.t -= DT; if (a.t > 1e-9) continue;
      this.shockQ.splice(k, 1);
      if (!as) continue;
      const R = a.r * AFTERSHOCK_R;
      this.curSrc = 'aftershock';
      this.radialDamage(a.x, HOVER_Y, a.z, R, PK2.aftershock * as * this.dmgMul, true, 0xd8b070, 0, 0.7, 0.25);
      this.events.push({ t: 'aftershock', x: a.x, z: a.z, r: R });
    }
    // Frostbite (owner 2026-11-03): the shatter hurts first, then chills the survivors (so a burst's own kills never re-queue)
    for (let k = this.frostQ.length - 1; k >= 0; k--) {
      const f = this.frostQ[k]; f.t -= DT; if (f.t > 1e-9) continue;
      this.frostQ.splice(k, 1);
      this.curSrc = 'frostbite'; this.bSrc = 0;
      this.radialDamage(f.x, HOVER_Y, f.z, f.r, f.dmg, false, FROST_COLOR);
      this.grid.query(f.x, f.z, f.r + 3, (i) => { const e = this.enemies[i]; if (e.alive && Math.hypot(e.x - f.x, e.z - f.z) <= f.r + e.def.radius * e.scale) this.chill(e, PK2.frostK, PK2.frostT); });
      this.events.push({ t: 'frostburst', x: f.x, z: f.z, r: f.r });
    }
    const sc = this.plv('scorched');
    if (sc && this.tick % SCORCH_GAP === 0) {
      this.curSrc = 'scorched';
      for (const e of this.enemies) {
        if (!e.alive) continue;
        const st = this.fire.stateAt(e.x, e.z);
        if (st === 1) this.burn(e, PK2.scorchBurn * sc * this.dmgMul / (SCORCH_GAP * DT), SCORCH_GAP * DT + SCORCH_AFTERBURN, 'scorched'); // BURN (owner 2026-11-02): the shared burn status
        else if (st === 2) this.damageEnemy(e, PK2.scorchChar * sc * this.dmgMul, 0xb06a40, 0, 0);
      }
    }
  }

  /** Heavy Hands: set inside radialDamage for shoving blasts */
  private bigHit = false;
  /** Heavy Hands: stagger a foe on a big hit (explosion, slam, dive, or HH_BIG of its max health); bosses / elites shorter */
  private heavyStagger(e: Enemy, d: number, hh: number): void {
    const big = this.bigHit || this.bSrc === KILL_SRC.explosion || HH_SLAMS.has(this.curSrc) || d >= e.maxHp * HH_BIG;
    if (!big) return;
    e.stagT = PK2.hhStagger * hh * (e.def.kind === 'boss' || e === this.boss ? HH_BOSS : e.elite ? HH_ELITE : 1);
  }

  /** dot = a burn tick (BURN, owner 2026-11-02): never crits and seeps through Bulwark plating */
  damageEnemy(e: Enemy, dmg: number, color: number, kx: number, kz: number, dot = false): void {
    if (!e.alive || e.edgeT >= 0 || e.mode < 0) return; // EDGE WALLS: out of reach until it comes inside (MAP FOES: or while underground)
    const crit = !dot && this.rng.next() < this.critChance;
    if (crit) this.critHits++;
    let d = crit ? dmg * this.critMul : dmg; // Overcharge Strike raises the ×2
    if (e.elite || e.def.kind === 'boss') d *= 1 + PASSIVE_K.hunter * this.plv('hunter');
    if (e.hp < e.maxHp * EXECUTE_AT) { const ex = this.plv('execute'); if (ex) d *= 1 + PK2.execute * ex; } // Executioner (owner 2026-11-02)
    if (e.stunT > 0 && e.def.kind !== 'herald') { d *= FROZEN_DMG_MUL; const dc = this.plv('deepchill'); if (dc) d *= 1 + PK2.chillFrozen * dc; } // frozen by Glare Burst (+ Deep Chill, owner 2026-11-03)
    if (e.chargeT > 0 && LIGHTNING_SRC.has(this.curSrc)) { const cd = this.plv('conductor'); if (cd) d *= 1 + PK2.condDmg * cd; } // CHARGED (Conductor): lightning hurts more
    const att = this.attuneDmg[this.curSrc]; if (att) d *= 1 + att;  // per-technique Attunement (max-build reward)
    if (e.def.kind === 'warden' && e.stunT <= 0 && !dot) d = Math.max(d * 0.25, d - 8 * (e.elite ? 1.5 : 1)); // plating shrugs off small hits
    if (this.tw.ohk && !e.dummy) d = Math.max(d, e.hp); // TRAINING: one-hit kills (a boss still stops at its form gate)
    if (e.form >= 0) { d = this.bossGate(e, d); if (d <= 0) return; } // BOSS FORMS: untouchable while transforming; no skipping a form
    e.hp -= d; e.flash = 0.1;
    const dealt = Math.min(d, Math.max(0, e.hp + d));
    if (dealt > 0 && this.dmgSince[this.curSrc] === undefined) this.dmgSince[this.curSrc] = this.time;
    this.dmgBySrc[this.curSrc] = (this.dmgBySrc[this.curSrc] ?? 0) + dealt;
    if (this.drawBudget > 0) { const h = Math.min(dealt * PK2.lifedraw * this.plv('lifedraw'), this.drawBudget); if (h > 0 && !this.mods.noheal) { this.drawBudget -= h; this.lifedrawHealed += Math.min(h, this.player.maxHp - this.player.hp); this.heal(h); } } // Lifedraw
    if (kx || kz) this.impulse(e, kx, kz, 0);
    if (!dot && e.hp > 0 && e.stagT === 0) { const hh = this.plv('heavyhands'); if (hh) this.heavyStagger(e, d, hh); } // Heavy Hands
    if (this.training) { this.training.onHit(e, d, this.curSrc); if (e.dummy) { this.events.push({ t: 'hit', x: e.x, y: e.y + 0.6, z: e.z, dmg: d, crit, color, dum: true }); if (e.hp <= 0) e.hp = 1; this.staticRoll(e, d, dot); return; } }
    this.events.push({ t: 'hit', x: e.x, y: e.y + e.gripY, z: e.z, dmg: d, crit, color });
    if (e.hp <= 0) { const k = Math.hypot(kx, kz); this.killEnemy(e, k > 1e-6 ? kx / k : 0, k > 1e-6 ? kz / k : 0); }
    this.staticRoll(e, d, dot); // Static Charge (owner 2026-11-03): a direct hit may spark an arc
  }

  private killEnemy(e: Enemy, dx = 0, dz = 0): void {
    if (e.edgeT >= 0) { e.hp = Math.max(e.hp, 1); return; } // EDGE WALLS: nothing kills a foe that is still outside the wall
    if (e.dummy) { e.hp = Math.max(e.hp, 1); return; } // TRAINING: dummies never die (no kill, XP, drops or codex)
    e.alive = false;
    this.kills++;
    const p = this.player;
    const burning = e.burnT > 0; e.burnT = 0; // BURN (owner 2026-11-02)
    const frosted = e.chillT > 0 || e.stunT > 0; e.chillT = 0; e.chillK = 0; e.chargeT = 0; // ELEMENTAL FAMILIES (owner 2026-11-03)
    { const bt = this.plv('battery'), kiK = this.kiOpen ? 1 : bt && p.transformT > 0 ? PK2.batteryKi * bt : 0; // Surge Battery: the bar keeps charging during Surge
      if (kiK > 0 && this.wlv('surge') > 0) p.ki = Math.min(this.kiCap, p.ki + (e.elite ? 25 : (e.def.ki ?? 1)) * this.kiGain * this.kiTierMul * kiK * (burning ? 1 + PK2.emberKi * this.plv('emberheart') : 1)); } // Ember Heart
    // fall direction: away from a big blast's centre, else the killing hit's knockback, else away from the hero
    // what killed it (render hint): an active blast / energy source, else the technique doing the damage
    let src: number = this.bSrc, col = this.bCol, r = this.blastR;
    if (!src) { col = -1; r = 0; src = this.curSrc === 'finger' ? KILL_SRC.finger : this.curSrc === 'disc' || this.curSrc === 'crescent' ? KILL_SRC.disc : this.curSrc === 'thorns' ? KILL_SRC.contact : this.curSrc === 'breaker' ? KILL_SRC.singularity : KILL_SRC.generic; }
    const radial = src === KILL_SRC.explosion || (src === KILL_SRC.singularity && col >= 0);
    if (radial) { dx = e.x - this.blastX; dz = e.z - this.blastZ; }
    if (radial || (!dx && !dz)) {
      if (!dx && !dz) { dx = e.x - p.x; dz = e.z - p.z; }
      const k = Math.hypot(dx, dz); if (k > 1e-6) { dx /= k; dz /= k; } else { dx = 0; dz = 1; }
    }
    this.events.push({ t: 'kill', x: e.x, y: e.y, z: e.z, kind: e.def.kind, elite: e.elite, dx, dz, vx: e.vx, vz: e.vz, s: e.scale, lift: e.lift + e.gripY, boss: this.boss === e || e.def.kind === 'boss', src, r, col });
    const herald = e.def.kind === 'herald';
    this.loot.onKill(this, e.x, e.z, e.elite, this.boss === e || e.def.kind === 'boss' || herald); // MAP PICKUPS: chests + field items (a fallen Herald always leaves a field chest)
    if (herald) { this.heralds.delete(e.id); this.shards += HERALD_SHARDS; this.events.push({ t: 'explode', x: e.x, y: e.y, z: e.z, r: 12, color: 0xffffff }, { t: 'heraldDown' }); return; }
    if (this.boss === e || e.def.kind === 'boss') this.bossesDefeated++;
    if (e.form >= 0) { this.bossSt.delete(e.id); e.form = -1; e.formT = 0; } // BOSS FORMS
    if (e.def.map) this.foes.onKill(e); // MAP FOES: its attack is cancelled; a Bloat Pod bursts into its spore cloud
    if (this.boss === e && e.def.kind !== 'boss') { this.boss = null; this.shards += 30; this.events.push({ t: 'explode', x: e.x, y: e.y, z: e.z, r: 9, color: 0xffd060 }); }
    if (e.def.kind === 'boss') {
      if (this.boss === e) this.boss = null;
      this.events.push({ t: 'explode', x: e.x, y: e.y, z: e.z, r: 14, color: 0xffffff });
      if (e !== this.finalBoss) { this.shards += 40; return; } // guardian: reward, run continues
      this.shards += 100;
      this.finalBoss = null;
      if (this.opts.endless ?? this.opts.bossTime >= 600) {
        // long run (or endless chosen in run setup): victory is recorded and the run keeps rolling in endless mode
        this.endless = true; this.victoryAt = this.time; this.nextEndlessGuardian = this.schedTime + 300;
        if (this.heraldN === 0) this.nextHeraldAt = Math.max(this.nextHeraldAt < 0 ? this.opts.bossTime + HERALD_FIRST : this.nextHeraldAt, this.schedTime + HERALD_FIRST); // THE NULL HERALD: measured from when Endless began
        this.events.push({ t: 'victory' }); this.events.push({ t: 'endless' });
        return;
      }
      this.state = 'victory';
      this.events.push({ t: 'victory' });
      return;
    }
    if (this.rng.next() < PASSIVE_K.siphon * this.plv('siphon')) this.heal(PASSIVE_K.siphonHeal);
    if (burning) this.burningKill(e); // Fire family (owner 2026-11-02): Ember Heart heal, Fire Breath rank-5 pops
    // Chain Reaction (owner 2026-11-02): foes an explosion kills may blow up too (queued, so chains resolve over ticks)
    { const cr = this.plv('chainreact'); if (cr && src === KILL_SRC.explosion && this.chainQ.length < CHAIN_MAX_PENDING && this.rng.next() < PK2.chainreact * cr) this.chainQ.push({ x: e.x, z: e.z, t: 0.12 }); }
    // Frostbite (owner 2026-11-03): a chilled / frozen foe shatters a moment later (queued and capped, so a crowd resolves over ticks)
    { const fb = this.plv('frostbite'); if (fb && frosted && this.frostQ.length < FROST_MAX_PENDING) this.frostQ.push({ x: e.x, z: e.z, r: PK2.frostR * this.areaMul, dmg: PK2.frostDmg * fb * this.dmgMul + PK2.frostHp * fb * e.maxHp, t: FROST_DELAY }); }
    // Surge Battery (owner 2026-11-02): kills during Surge extend it, up to a cap per Surge
    { const bt = this.plv('battery'); if (bt && p.transformT > 0) { const add = Math.min(PK2.battery * bt, PK2.batteryCap * bt - this.batteryAdded); if (add > 0) { p.transformT += add; this.batteryAdded += add; } } }
    if (e.def.kind === 'splitter') {
      const n = e.elite ? 4 : 2;
      for (let k = 0; k < n; k++) { const a = (k / n) * Math.PI * 2 + e.phase, c = this.inside(e.x + Math.cos(a) * 1.2, e.z + Math.sin(a) * 1.2, ARENA_R - 0.3); this.spawnEnemy('skitter', c.x, c.z, false, this.enemyHpMul(this.schedTime)); } // same curve as other foes (was 1 + t/100: x19 at 30:00)
    }
    const xp = e.def.xp * (e.elite ? (e.lite ? HORDE_ELITE_XP : 12) : 1) * xpTimeMul(this.schedTime); // Hurry: XP keeps up with the squeezed ramp // owner 2026-11-02: later foes are worth more XP
    this.dropGem(e.x, e.z, xp, xp >= 10 ? 2 : xp >= 3 ? 1 : 0);
    if (e.elite && !e.lite) { // horde elites skip the special drops (they would flood shards / seals / heals)
      if (this.rng.next() < (this.mods.relaxed ? RELAXED_SALVE_ELITE : 0.35) && !this.mods.noheal) this.dropGem(e.x + 1, e.z, 0, GEM_SALVE); // owner: fewer health drops (was every elite)
      this.shards += 10;
      this.dropGem(e.x - 1, e.z, 0, GEM_SEAL);
      if (this.rng.next() < 0.12) this.dropGem(e.x, e.z + 1, 0, GEM_SWEEP); // was 35%
    } else if (this.time - this.lastSalveDrop >= (this.mods.relaxed ? RELAXED_SALVE_GAP : 25) && this.rng.next() < 0.0012 * (this.mods.relaxed ? RELAXED_SALVE_CHANCE : 1) && !this.mods.noheal) { this.lastSalveDrop = this.time; this.dropGem(e.x, e.z + 0.8, 0, GEM_SALVE); }
    else if (this.time - this.lastSweepDrop >= 60 && this.rng.next() < 0.0005) { this.lastSweepDrop = this.time; this.dropGem(e.x, e.z + 0.8, 0, GEM_SWEEP); }
    // owner 2026-11-01: 'reduce drop rate of health and magnet pickups' — ordinary kills were giving ~12 heals and ~4-5
    // magnets per minute in dense fights; now at most one heal per 25 s and one magnet per 60 s from ordinary kills
    if (this.rng.next() < 0.05) this.shards++;
  }

  private dropGem(x: number, z: number, value: number, kind: number): void {
    { const d = Math.hypot(x, z); if (d > PICKUP_R) { x *= PICKUP_R / d; z *= PICKUP_R / d; } } // EDGE WALLS: drops always land within reach
    for (const g of this.gems) if (!g.alive) {
      g.alive = true; g.x = x; g.z = z; g.y = 0.6; g.value = value; g.kind = kind; g.pull = false; return;
    }
    // pool full: merge into an existing gem
    if (kind >= GEM_SALVE) { // special drops must never be lost: replace an XP mote
      const g = this.gems.find((q) => q.kind < GEM_SALVE);
      if (g) { g.x = x; g.z = z; g.y = 0.6; g.value = value; g.kind = kind; g.pull = false; }
      return;
    }
    // owner 2026-11-03: every kill must still show its gem. Was: add the value to a random pooled gem (nothing appeared).
    // Now the XP gem farthest from the hero (almost always off-screen) is folded into this drop, which appears where the foe
    // died carrying both values, so no XP is lost and nearby drops never vanish. Deterministic (no Rng).
    const p = this.player; let far: Gem | null = null, fd = -1;
    for (const q of this.gems) if (q.kind < GEM_SALVE && !q.pull) { const d = (q.x - p.x) ** 2 + (q.z - p.z) ** 2; if (d > fd) { fd = d; far = q; } }
    if (!far) { const q = this.gems[0]; if (q.kind < GEM_SALVE) { q.value += value; q.kind = Math.max(q.kind, xpKindFor(q.value)); } return; }
    far.value += value; far.x = x; far.z = z; far.y = 0.6; far.kind = Math.max(far.kind, kind, xpKindFor(far.value)); far.pull = false;
  }

  /** owner 2026-11-03: crowded XP gems fold together (see core/gems.ts); the pass is skipped below GEM_MERGE_AT gems */
  private consolidateGems(): void {
    let n = 0;
    for (const g of this.gems) if (g.alive && g.kind < GEM_SALVE) n++;
    if (n > GEM_MERGE_AT) mergeXpGems(this.gems, GEM_SALVE);
  }

  private updateGems(): void {
    const p = this.player;
    const mr = this.magnetR;
    for (const g of this.gems) {
      if (!g.alive) continue;
      // EDGE WALLS: a pickup past PICKUP_R (shoved, or from an older rule) slides back in quickly, never past the wall
      if (!g.pull) { const gd = Math.hypot(g.x, g.z); if (gd > PICKUP_R) { const nd = Math.max(PICKUP_R, gd - PICKUP_DRIFT * DT); g.x *= nd / gd; g.z *= nd / gd; } }
      const dx = p.x - g.x, dz = p.z - g.z, dy = p.y - g.y; const d = Math.hypot(dx, dz, dy);
      if (d < mr) g.pull = true;
      if (g.pull) {
        const sp = Math.max(14, 30 - d);
        g.x += (dx / d) * sp * DT; g.z += (dz / d) * sp * DT; g.y += (dy / d) * sp * DT;
      }
      if (d < 1.1) {
        g.alive = false;
        if (g.kind === GEM_SALVE) { this.heal(30); this.events.push({ t: 'pickup', kind: 3 }); }
        else if (g.kind === GEM_SEAL) { this.addSeal(); this.events.push({ t: 'pickup', kind: 4 }); }
        else if (g.kind === GEM_SWEEP) {
          for (const q of this.gems) if (q.alive && q.kind < GEM_SALVE) q.pull = true;
          this.events.push({ t: 'pickup', kind: 5 }, { t: 'sweep' });
        } else { this.addXp(g.value * this.xpMul); this.events.push({ t: 'pickup', kind: g.kind }); }
      }
    }
  }

  addXp(v: number): void {
    const p = this.player;
    p.xp += v;
    if (this.training && this.training.tw.noLevel && !this.training.forceLevel) { p.xp = Math.min(p.xp, p.xpNext * 0.999); return; } // TRAINING: NO LEVEL-UPS
    while (p.xp >= p.xpNext) {
      p.xp -= p.xpNext; p.level++; p.xpNext = xpForLevel(p.level);
      this.pendingLevels++;
      this.events.push({ t: 'levelup', level: p.level });
    }
    if (this.pendingLevels > 0 && this.state === 'playing') this.openLevelUp();
  }

  // ---------- Array Seals / Convergence ----------
  addSeal(): void {
    this.seals++;
    this.events.push({ t: 'seal', n: this.seals });
    if (this.seals >= SEALS_REQUIRED) {
      this.seals -= SEALS_REQUIRED;
      this.convergences++;
      this.pendingBoons++;
      this.events.push({ t: 'convergence' });
      if (this.state === 'playing') this.openBoon();
    }
  }

  private openBoon(): void {
    this.pendingBoons--;
    this.boonChoices = this.rollBoons();
    this.boonKind = 'convergence'; this.apexChoices = [];
    this.state = 'boon';
  }

  // ---------- ENDLESS GOALS (src/core/endless.ts) ----------
  private updateEndless(): void {
    const et = this.endlessTime;
    while (et >= endlessMilestoneAt(this.endlessTier + 1)) {
      const n = ++this.endlessTier, sh = endlessTierShards(n);
      this.endlessBank += sh; // banked now: paid at the end of the run even if the hero falls later
      this.events.push({ t: 'endlessTier', tier: n, name: endlessTierName(n), shards: sh, at: endlessMilestoneAt(n) });
      this.pendingApex++; this.apexOpenT = APEX_DELAY;
      if (n === ELITE_PACK_TIER) this.nextElitePack = et + ELITE_PACK_FIRST;
    }
    if (this.endlessTier >= ELITE_PACK_TIER && et >= this.nextElitePack) {
      this.nextElitePack = et + elitePackGap(this.endlessTier);
      this.spawnElitePack();
    }
    if (this.pendingApex > 0 && (this.apexOpenT -= DT) <= 0 && this.state === 'playing' && this.player.hp > 0) this.openApex();
  }

  /** endless tier II+: a pack of elites arrives together from one side */
  private spawnElitePack(): void {
    const p = this.player, n = elitePackSize(this.endlessTier), hpMul = this.enemyHpMul(this.schedTime);
    const kinds: EnemyKind[] = ['warden', 'stomper', 'lancer', 'splitter', 'brute', 'caster'];
    const kind = this.rng.pick(kinds), a0 = this.rng.range(0, Math.PI * 2);
    let got = 0;
    for (let k = 0; k < n; k++) {
      const a = a0 + (k - (n - 1) / 2) * 0.22;
      if (this.spawnEnemy(kind, p.x + Math.cos(a) * 24, p.z + Math.sin(a) * 24, true, hpMul, true)) got++; // packs push past the horde cap
    }
    if (got) this.events.push({ t: 'elite' }, { t: 'endlessEvent', kind: 'elitePack', tier: this.endlessTier, n: got });
  }

  /** endless tier III+: the endless guardian brings a giant elite partner (a non-boss mini-boss; the boss bar keeps the first) */
  private spawnTwinGuardian(t: number): void {
    let i = this.endlessRot % MINIBOSSES.length; if (MINIBOSSES[i].kind === 'boss') i = (i + 1) % MINIBOSSES.length;
    const mb = MINIBOSSES[i], p = this.player, a = this.rng.range(0, Math.PI * 2);
    const e = this.spawnEnemy(mb.kind, p.x - Math.cos(a) * 24, p.z - Math.sin(a) * 24, true, 1, true);
    if (!e) return;
    e.era = 0; e.scale = mb.scale; e.maxHp = e.hp = ENEMIES.boss.hp * this.bossHpMul(t) * mb.hp * 1.3; this.twinId = e.id;
    this.events.push({ t: 'guardianSpawn', n: i + 1, name: mb.name }, { t: 'endlessEvent', kind: 'twinGuardians', tier: this.endlessTier, n: 2 });
  }

  private openApex(): void {
    this.pendingApex--;
    const pool = Object.keys(APEX_BOONS) as ApexId[], out: ApexId[] = [];
    while (out.length < 3 && pool.length) out.push(pool.splice(this.rng.int(pool.length), 1)[0]);
    this.apexChoices = out; this.boonKind = 'apex'; this.boonChoices = [];
    this.state = 'boon';
  }

  chooseApex(i: number): void {
    if (this.state !== 'boon' || this.boonKind !== 'apex') return;
    const id = this.apexChoices[i]; if (!id) return;
    const p = this.player;
    switch (id) {
      case 'current': this.apexDmg *= APEX_DMG; break;
      case 'lattice': this.apexArmor *= APEX_ARMOR; this.heal(p.maxHp); break;
      case 'thread': this.revives++; p.maxHp += APEX_HP; break;
      case 'pulse':
        this.curSrc = 'boon';
        this.blast(KILL_SRC.explosion, 0xffd27a, p.x, p.z, 30);
        for (const e of this.enemies) if (e.alive && e.edgeT < 0 && e.def.kind !== 'boss' && e.def.kind !== 'herald' && e !== this.boss && e.id !== this.twinId) this.killEnemy(e);
        this.bSrc = 0; this.stillT = Math.max(this.stillT, APEX_STILL);
        this.events.push({ t: 'explode', x: p.x, y: p.y, z: p.z, r: 30, color: 0xffd27a });
        break;
    }
    this.events.push({ t: 'apexBoon', id });
    this.apexChoices = []; this.boonKind = 'convergence';
    this.state = 'playing';
    this.resumeQueues();
  }

  boonEligible(id: BoonId): boolean {
    switch (id) {
      case 'overtone': return this.wlv('surge') > 0;
      case 'unwritten': return this.unwrittenTarget() !== null;
      case 'spark': return this.revives < 2;
      case 'reversal': return this.aliveEnemies > 0;
      default: return true;
    }
  }

  private unwrittenTarget(): WeaponId | null {
    for (const [id, lv] of this.weapons) if (lv >= MAX_WEAPON_LEVEL && !this.evolved.has(id)) return id;
    return null;
  }

  rollBoons(): BoonId[] {
    const pool = (Object.keys(BOONS) as BoonId[]).filter((b) => this.boonEligible(b));
    const out: BoonId[] = [];
    while (out.length < 3 && pool.length) out.push(pool.splice(this.rng.int(pool.length), 1)[0]);
    return out;
  }

  chooseBoon(i: number): void {
    if (this.state !== 'boon') return;
    if (this.boonKind === 'apex') { this.chooseApex(i); return; } // ENDLESS GOALS: the same pick screen offers Apex Boons
    const id = this.boonChoices[i]; if (!id) return;
    const p = this.player;
    switch (id) {
      case 'heart': p.maxHp += 20; this.heal(p.maxHp); break; // No Healing: just the +20 max health
      case 'reversal':
        this.curSrc = 'boon';
        this.blast(KILL_SRC.explosion, 0xbff4ff, p.x, p.z, 30);
        for (const e of this.enemies) if (e.alive && e.edgeT < 0 && e.def.kind !== 'boss' && e.def.kind !== 'herald') this.killEnemy(e);
        this.bSrc = 0;
        this.events.push({ t: 'explode', x: p.x, y: p.y, z: p.z, r: 30, color: 0xbff4ff });
        break;
      case 'insight': this.pendingLevels += 2; break;
      case 'stillness': this.stillT = 8; break;
      case 'windfall': this.windfall += 60; break;
      case 'overtone':
        p.surgeTier = this.maxSurgeTier; p.transformT = this.surgeDuration * 2; p.ki = 0; this.batteryAdded = 0; this.kiStallT = 0; // Overtone boon: a free surge at the top unlocked form, twice as long
        this.bestSurgeTier = Math.max(this.bestSurgeTier, p.surgeTier);
        this.events.push({ t: 'transform', tier: p.surgeTier }); break;
      case 'spark': this.revives++; break;
      case 'unwritten': { const w = this.unwrittenTarget(); if (w) this.evolved.add(w); break; }
    }
    this.events.push({ t: 'boon', id });
    this.boonChoices = [];
    this.state = 'playing';
    this.resumeQueues();
  }

  /** After closing any modal, open the next queued one (boons before drafts). */
  private resumeQueues(): void {
    if (this.state !== 'playing') return;
    if (this.pendingApex > 0 && this.apexOpenT <= 0) this.openApex(); // ENDLESS GOALS
    else if (this.pendingBoons > 0) this.openBoon();
    else if (this.pendingLevels > 0) this.openLevelUp();
  }

  /** Re-draw the current draft (Choice Relay). */
  reroll(): boolean {
    if (this.state !== 'levelup' || this.rerolls <= 0) return false;
    if (!this.tw.freeDraft) this.rerolls--;
    this.choices = this.rollChoices();
    return true;
  }

  /** Remove a card's technique/trait from this run's pool and re-draw (Null Register). */
  ban(i: number): boolean {
    const c = this.choices[i];
    if (this.state !== 'levelup' || this.bans <= 0 || !c || c.kind === 'heal' || c.kind === 'bonus' || c.kind === 'fusion') return false;
    if (!this.tw.freeDraft) this.bans--;
    this.banned.add(c.id);
    this.choices = this.rollChoices();
    return true;
  }

  // ---------- level up ----------
  private openLevelUp(): void {
    this.choices = this.rollChoices();
    this.state = 'levelup';
  }

  /** techniques occupying a build slot (innate Surge excluded) */
  get techSlotsUsed(): number { let n = this.fusion.owned.size; for (const id of this.weapons.keys()) if (!INNATE.includes(id)) n++; return n; } // a fusion takes one slot

  // ---------- SKILL FUSIONS (owner 2026-11-02; recipes + behaviours in src/core/fusions.ts) ----------
  /** both partners at max rank (evolved or not) and the fusion not owned yet */
  canFuse(id: FusionId): boolean {
    const r = FUSIONS[id];
    return !this.fusion.has(id) && (this.weapons.get(r.a) ?? 0) >= MAX_WEAPON_LEVEL && (this.weapons.get(r.b) ?? 0) >= MAX_WEAPON_LEVEL;
  }
  /** every fusion the build can make right now, in recipe order */
  fusionsReady(): FusionId[] { return (Object.keys(FUSIONS) as FusionId[]).filter((id) => this.canFuse(id)); }
  /** what a chest / relic cache should offer: the first ready fusion (null = none). Take it with fuse(id). */
  fusionOffer(): FusionId | null { return this.fusionsReady()[0] ?? null; }
  /** the gold FUSION card for a ready recipe (level-up drafts and chests) */
  fusionChoice(id: FusionId): Choice { const f = FUSIONS[id]; return { kind: 'fusion', id: f.id, name: f.name, desc: f.desc, level: 0 }; }
  /** the FUSION card for the first ready recipe (chests / relic caches), else null */
  fusionCard(): Choice | null { const id = this.fusionOffer(); return id ? this.fusionChoice(id) : null; }
  /** Fuse: both partners leave the build and become one fusion in a single slot (frees a slot). Evolved partners carry
   *  their evolution's strength in (Resonance, fusions.ts). Usable from a level-up card (choose) or a chest. Returns false if the recipe isn't ready. */
  fuse(id: FusionId): boolean {
    if (!this.canFuse(id)) return false;
    const r = FUSIONS[id], p = this.player;
    const evo = evoMask(this.evolved.has(r.a), this.evolved.has(r.b)); // Resonance: evolved partners carry their strength in
    for (const w of [r.a, r.b]) {
      this.weapons.delete(w); this.evolved.delete(w); this.wTimers[w] = 0;
      if (w === 'orbit') this.orbs.length = 0;
      if (w === 'lance') { p.beamT = 0; p.beamCharge = 0; }
      if (w === 'mortar') this.mortar.charging = false;
      if (w === 'zenith') this.zenith.charging = false;
    }
    this.fusion.add(id, evo);
    this.events.push({ t: 'fusion', f: id, a: r.a, b: r.b, x: p.x, z: p.z });
    return true;
  }
  /** skill slots this run (MAX_WEAPONS; a challenge rule can lower it, src/core/mods.ts ChallengeRules) */
  get skillSlots(): number { return ruleSlots(this.opts.rules, MAX_WEAPONS); }
  get traitSlotsUsed(): number { let n = 0; for (const lv of this.passives.values()) if (lv > 0) n++; return n; }

  // ---------- TRAINING helpers (src/core/training.ts calls these; never used by a normal run) ----------
  /** drop a skill now: it leaves the build and its in-progress cast state is cleared (shots already flying finish) */
  dropWeapon(id: WeaponId): void {
    const p = this.player;
    this.weapons.delete(id); this.evolved.delete(id); this.wTimers[id] = id === 'zenith' ? 3 : 0;
    switch (id) {
      case 'orbit': this.orbs.length = 0; break;
      case 'lance': p.beamT = 0; p.beamCharge = 0; break;
      case 'mortar': this.mortar.charging = false; break;
      case 'zenith': this.zenith.charging = false; break;
      case 'bolt': this.boltBurst = null; break;
      case 'finger': this.fingerBurst = null; break;
      case 'barrage': this.barrageVolley = null; break;
      case 'crescent': this.crescentThrow = null; break;
      case 'surge': if (p.transformT > 0) { p.transformT = 0; p.surgeTier = 0; this.events.push({ t: 'transformEnd' }); } p.ki = 0; break;
      case 'ward': this.wardCharges = 0; break;
      case 'rush': this.rushLeft = 0; break;
      case 'riftline': this.rift.queue.length = 0; break;
      case 'breath': this.breath.active = false; break;
      // ICE FAMILY (owner 2026-11-03): the cone stops, queued eruptions and lances in flight are dropped; fields and ridges fade on their own
      case 'rime': if (this.rime.active) { this.rime.active = false; this.rimeExp.clear(); this.events.push({ t: 'rimeEnd', id: this.rime.id }); } break;
      case 'spikes': this.spikeQ.length = 0; break;
      case 'glacier': for (const L of this.iceLances) L.alive = false; break;
      case 'blizzard': this.blizzard.on = false; this.blizzard.t = 0; break;
    }
  }
  /** a boss dummy: forms + gates like a real boss (named for the readout) */
  attachDummyForms(e: Enemy): void { this.attachForms(e, 'TRAINING TOTEM'); }
  /** one tick of a boss body's transformation beat (dummies: no AI otherwise) */
  stepBossForm(e: Enemy): void { const st = this.bossSt.get(e.id); if (st && e.formT > 0) transformStep(e, st, this.bossHost); }
  /** back to the first form (boss dummy refill) */
  resetBossForms(e: Enemy): void { const st = this.bossSt.get(e.id); if (!st) return; this.bossSt.set(e.id, newBossState(st.name)); e.form = 0; e.formT = 0; }
  /** forget a removed boss body's form state (and the boss slot if it held it) */
  forgetBoss(e: Enemy): void { this.bossSt.delete(e.id); if (this.boss === e) this.boss = null; if (this.finalBoss === e) this.finalBoss = null; }
  /** spawn a guardian (MINIBOSSES index) or the map's final boss next to the hero, replacing any living boss */
  trainSpawnBoss(which: 'final' | number): Enemy | null {
    if (this.boss && this.boss.alive) { this.boss.alive = false; this.forgetBoss(this.boss); }
    const t = this.schedTime;
    if (which === 'final') {
      const p = this.player, a = this.rng.range(0, Math.PI * 2);
      const b = this.spawnEnemy('boss', p.x + Math.cos(a) * 18, p.z + Math.sin(a) * 18, false, this.bossHpMul(Math.max(t, this.opts.bossTime)) * (this.map.boss.hpMul ?? 1));
      if (b) { this.boss = b; this.bossName = this.map.boss.name; this.attachForms(b, this.bossName); this.events.push({ t: 'bossSpawn' }); }
      return b;
    }
    this.spawnMiniboss(Math.max(0, Math.min(MINIBOSSES.length - 1, which)), t);
    return this.boss;
  }
  /** put a boss with forms into form 1..3: form 1 = fresh at full health; 2 / 3 = health at the gate before it, then the
   *  real transformation beat into it (invulnerable while it plays) */
  forceBossForm(e: Enemy, form: number): void {
    const st = this.bossSt.get(e.id); if (!st || !e.alive) return;
    const f = Math.max(1, Math.min(3, Math.round(form))) - 1;
    this.bossSt.set(e.id, newBossState(st.name));
    e.formT = 0;
    if (f === 0) { e.form = 0; e.hp = e.maxHp; return; }
    e.form = f - 1; e.hp = e.maxHp * FORM_GATES[f - 1];
    startTransform(e, this.bossSt.get(e.id)!, this.bossHost);
  }
  /** remove every real foe (no kills, rewards or events), the boss, its marks and hostile shots */
  trainClearEnemies(): void {
    for (const e of this.enemies) if (e.alive && !e.dummy) { e.alive = false; this.bossSt.delete(e.id); }
    this.boss = null; this.finalBoss = null; this.bossMarks.length = 0;
    for (const pr of this.projectiles) if (pr.alive && pr.hostile) pr.alive = false;
  }
  /** jump the run clock to t seconds: the schedule (waves, elites, guardians, overtime, boss) carries on from there */
  jumpClock(t: number): void {
    this.time = Math.max(0, t) / this.schedK;
    const s = this.schedTime, bt = this.opts.bossTime;
    this.wave = s >= 60 ? Math.floor(s / 60) - 1 : 0; this.lastElite = s; this.lastSwarm = s - 30;
    this.guardiansSpawned = bt >= 600 ? MINIBOSS_AT.filter((f) => bt * f < s - 1).length : 0;
    this.bossSpawned = s > bt + 1; if (this.bossSpawned && (this.opts.endless ?? bt >= 600)) { this.endless = true; this.victoryAt = bt; this.nextEndlessGuardian = s + 300; }
    this.heraldN = 0; this.nextHeraldAt = -1; // THE NULL HERALD: the schedule restarts from the new clock
  }

  rollChoices(): Choice[] {
    const pool = this.draftPool();
    const out: Choice[] = [];
    // SKILL FUSIONS: a ready fusion always leads the draft (gold card); skipping it is free, it comes back next draft
    const fz = this.fusionOffer();
    if (fz) out.push(this.fusionChoice(fz));
    // guarantee evolution shows when available
    const evo = pool.find((c) => c.kind === 'evolve');
    if (evo) out.push(evo);
    const fortune = this.plv('fortune');
    while (out.length < 3 && pool.length > 0) {
      const c = pool.splice(fortune > 0 ? this.fortunePick(pool, fortune) : this.rng.int(pool.length), 1)[0]; // rank 0: the original draw
      if (!out.some((o) => o.kind === c.kind && o.id === c.id)) out.push(c);
    }
    // owner 2026-11-01: once the build is complete, offer Vampire-Survivors-style rewards instead of a dead pick
    if (out.length < 3) {
      const extra = this.bonusChoices();
      while (out.length < 3 && extra.length) out.push(extra.splice(this.rng.int(extra.length), 1)[0]);
    }
    while (out.length < 3) out.push(this.mods.noheal ? { kind: 'bonus', id: 'cache', name: 'Shard Cache', desc: `+${15 + Math.floor(this.time / 60) * 3} ◆ Fracture Shards for Power Ups`, level: 0 } : { kind: 'heal', id: 'heal', name: 'Restore', desc: 'Heal 40 health', level: 0 });
    return out;
  }

  /** Fortune (Group D trait): index of a weighted draw from `pool`. Upgrades of held skills / traits and evolution cards
   *  weigh 1 + PASSIVE_K.fortuneDraft per rank; new cards weigh 1. One rng draw, so it stays deterministic. */
  fortunePick(pool: readonly Choice[], rank: number): number {
    const up = 1 + PASSIVE_K.fortuneDraft * rank, w = (c: Choice) => (c.kind === 'evolve' || c.level > 1 ? up : 1);
    let total = 0; for (const c of pool) total += w(c);
    let r = this.rng.next() * total;
    for (let i = 0; i < pool.length; i++) { r -= w(pool[i]); if (r < 0) return i; }
    return pool.length - 1;
  }

  /** Every card a draft could offer right now (evolutions listed twice = weighted). No rng; relic caches and chests read it. */
  draftPool(): Choice[] {
    const pool: Choice[] = [];
    for (const id of Object.keys(WEAPONS) as WeaponId[]) {
      if (this.banned.has(id)) continue;
      const w = WEAPONS[id];
      if (w.retired) continue; // removed skills (Overdrive) are never offered
      const lvl = this.weapons.get(id);
      if (lvl === undefined) {
        if (this.techSlotsUsed < this.skillSlots) pool.push({ kind: 'weapon', id, name: w.name, desc: w.desc[0], level: 1 });
      } else if (this.evolved.has(id)) {
        continue;
      } else if (lvl >= MAX_WEAPON_LEVEL && this.plv(w.evolvesWith) > 0) {
        pool.push({ kind: 'evolve', id, name: w.evolvedName, desc: w.evolvedDesc, level: 0 });
        pool.push({ kind: 'evolve', id, name: w.evolvedName, desc: w.evolvedDesc, level: 0 }); // weighted
      } else if (lvl < MAX_WEAPON_LEVEL) {
        pool.push({ kind: 'weapon', id, name: w.name, desc: w.desc[lvl] ?? 'Upgrade', level: lvl + 1 });
      }
    }
    for (const id of Object.keys(PASSIVES) as PassiveId[]) {
      if (this.banned.has(id)) continue;
      const lvl = this.plv(id);
      if (lvl === 0 && this.traitSlotsUsed >= MAX_PASSIVES) continue;
      let desc = PASSIVES[id].desc;
      if (this.mods.noheal && NOHEAL_TRAITS.includes(id)) {
        // No Healing: a pure-healing trait does nothing, so it is only offered as rank 1 while a skill the hero holds still
        // needs it to evolve (Renewal -> Ascendant Form for the innate Surge Form, Siphon -> Hall of Mirrors); see noHealCatalyst
        const forSkill = lvl === 0 ? this.noHealCatalyst(id) : null;
        if (!forSkill) continue;
        desc = `Heals nothing under No Healing; needed to evolve ${forSkill}`;
      }
      if (id === 'secondwind' && lvl >= 1) desc = `A second save this run, and every save leaves you at ${PK2.swHp2 * 100}% health`; // rank 2 card
      if (lvl < (PASSIVES[id].max ?? MAX_PASSIVE_LEVEL)) pool.push({ kind: 'passive', id, name: PASSIVES[id].name, desc, level: lvl + 1 });
    }
    return pool;
  }

  /** No Healing: the evolved name of a held, unevolved skill whose evolution needs this healing trait, else null */
  noHealCatalyst(id: PassiveId): string | null {
    for (const [wid, lvl] of this.weapons) {
      const w = WEAPONS[wid];
      if (lvl > 0 && !w.retired && w.evolvesWith === id && !this.evolved.has(wid) && !this.banned.has(wid)) return `${w.name} into ${w.evolvedName}`;
    }
    return null;
  }

  /** Max-build rewards: Recover, Shard Cache, and random Attunements (a stat boost on one skill or on all of them). */
  private bonusChoices(): Choice[] {
    const p = this.player, minute = Math.floor(this.time / 60);
    const cache = 15 + minute * 3;
    const owned: string[] = [...this.weapons.keys()].filter((id) => id !== 'surge');
    for (const f of this.fusion.owned.keys()) owned.push(f); // SKILL FUSIONS: fusions can be attuned too
    const out: Choice[] = [
      ...(this.mods.noheal ? [] : [{ kind: 'bonus' as const, id: 'recover', name: 'Recover', desc: `Heal ${Math.round(p.maxHp * 0.35)} health (35%)`, level: 0 }]), // No Healing: never offered
      { kind: 'bonus', id: 'cache', name: 'Shard Cache', desc: `+${cache} ◆ Fracture Shards for Power Ups`, level: 0 },
    ];
    const global = [
      { id: 'g:might', name: 'Attune · Might', desc: '+6% damage for every skill' },
      { id: 'g:haste', name: 'Attune · Tempo', desc: '-4% cooldown for every skill' },
      { id: 'g:reach', name: 'Attune · Reach', desc: '+7% area for every skill' },
      { id: 'g:fleet', name: 'Attune · Stride', desc: '+5% move speed' },
      { id: 'g:vigor', name: 'Attune · Vigor', desc: '+20 max health (and heal 20)' },
    ];
    for (let k = 0; k < 2; k++) {
      if (owned.length && this.rng.next() < 0.6) {
        const id = this.rng.pick(owned), nm = isFusionId(id) ? FUSIONS[id].name : this.evolved.has(id as WeaponId) ? WEAPONS[id as WeaponId].evolvedName : WEAPONS[id as WeaponId].name;
        out.push({ kind: 'bonus', id: 'w:' + id, name: `Attune · ${nm}`, desc: `+15% damage for ${nm} (now +${Math.round((this.attuneDmg[id] ?? 0) * 100)}%)`, level: 0 });
      } else { const g = this.rng.pick(global); out.push({ kind: 'bonus', id: g.id, name: g.name, desc: g.desc, level: 0 }); }
    }
    return out;
  }
  /** Attunement state (max-build rewards) */
  attuneDmg: Partial<Record<string, number>> = {};
  bonusDmg = 0; bonusCd = 0; bonusArea = 0; bonusSpeed = 0;

  choose(index: number): void {
    if (this.state !== 'levelup') return;
    const c = this.choices[index]; if (!c) return;
    this.applyChoice(c);
    this.pendingLevels--;
    this.choices = [];
    this.draftTag = '';
    this.state = 'playing';
    this.resumeQueues();
  }

  /** Apply one draft card (level-up pick, or a chest upgrade). */
  applyChoice(c: Choice): void {
    const p = this.player;
    if (c.kind === 'weapon') this.weapons.set(c.id as WeaponId, c.level);
    else if (c.kind === 'evolve') this.evolved.add(c.id as WeaponId);
    else if (c.kind === 'fusion') { if (isFusionId(c.id)) this.fuse(c.id); }
    else if (c.kind === 'passive') {
      this.passives.set(c.id as PassiveId, c.level);
      if (c.id === 'vitality') { p.maxHp += PASSIVE_K.vitality; this.heal(PASSIVE_K.vitality); }
    } else if (c.kind === 'bonus') {
      if (c.id === 'recover') this.heal(p.maxHp * 0.35);
      else if (c.id === 'cache') this.shards += 15 + Math.floor(this.time / 60) * 3;
      else if (c.id.startsWith('w:')) { const w = c.id.slice(2); this.attuneDmg[w] = (this.attuneDmg[w] ?? 0) + 0.15; }
      else if (c.id === 'g:might') this.bonusDmg += 0.06;
      else if (c.id === 'g:haste') this.bonusCd = Math.min(0.4, this.bonusCd + 0.04);
      else if (c.id === 'g:reach') this.bonusArea += 0.07;
      else if (c.id === 'g:fleet') this.bonusSpeed = Math.min(0.5, this.bonusSpeed + 0.05);
      else if (c.id === 'g:vigor') { p.maxHp += 20; this.heal(20); }
    } else if (c.kind === 'heal') this.heal(40); // heal() respects the No Healing modifier
  }

  /** MAP PICKUPS: a free draft (no level gained). Opens now when the run is in play, else waits in the queue. */
  grantPick(tag: string): void {
    this.pendingLevels++;
    if (this.state === 'playing') { this.draftTag = tag; this.openLevelUp(); }
  }

  /** MAP PICKUPS Sunburst: hit every foe within r of (x, z): ordinary foes are destroyed, elites lose eliteK and bosses bossK
   *  of their max health. Returns the number of foes hit. */
  fieldBurst(x: number, z: number, r: number, eliteK: number, bossK: number): number {
    this.curSrc = 'sunburst';
    this.blast(KILL_SRC.explosion, 0xfff2b0, x, z, r);
    let n = 0; const r2 = r * r;
    for (const e of this.enemies) {
      if (!e.alive || e.edgeT >= 0 || (e.x - x) ** 2 + (e.z - z) ** 2 > r2) continue;
      n++;
      const boss = this.bossLike(e);
      const d = boss ? e.maxHp * bossK : e.elite ? e.maxHp * eliteK : e.hp + 1;
      e.hp -= d; e.flash = 0.15;
      if (this.dmgSince.sunburst === undefined) this.dmgSince.sunburst = this.time;
      this.dmgBySrc.sunburst = (this.dmgBySrc.sunburst ?? 0) + d;
      this.events.push({ t: 'hit', x: e.x, y: e.y, z: e.z, dmg: d, crit: false, color: 0xfff2b0 });
      if (e.hp <= 0) this.killEnemy(e);
    }
    this.bSrc = 0;
    return n;
  }

  // ---------- queries ----------
  nearestEnemy(x: number, z: number, maxR: number): number {
    let best = -1, bd = maxR * maxR;
    this.grid.query(x, z, maxR, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const d = (e.x - x) ** 2 + (e.z - z) ** 2; if (d < bd) { bd = d; best = i; }
    });
    return best;
  }

  nearestN(x: number, z: number, maxR: number, n: number): number[] {
    const cand: { i: number; d: number }[] = [];
    this.grid.query(x, z, maxR, (i) => {
      const e = this.enemies[i]; if (!e.alive) return;
      const d = (e.x - x) ** 2 + (e.z - z) ** 2; if (d < maxR * maxR) cand.push({ i, d });
    });
    cand.sort((a, b) => a.d - b.d);
    return cand.slice(0, n).map((c) => c.i);
  }
}
